import { Fragment, useState, type ReactNode, type RefObject } from "react";

import type {
  ClinicianWorkbenchActionInput,
  ClinicianWorkbenchClinicalNoteTargetKind,
  ClinicianWorkbenchClinicalNoteVisibilityState,
  ClinicianWorkbenchClinicalRecordKind,
  ClinicianWorkbenchClinicalRecordStatus,
  ClinicianWorkbenchOutboundManualAnswerV1,
  ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
  ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ClinicianWorkbenchQuestionnaireDefinitionV1,
} from "@amplecare/contracts";
import { designTokens } from "@amplecare/design-tokens";
import { translationKeys, type TranslationKey } from "@amplecare/i18n";
import {
  ActivityIndicator,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type ViewStyle,
} from "react-native";

import { styles } from "../AppShell.styles.js";
import { HybridReviewPanel } from "./hybrid-review-panel.js";
import { LineIcon, type LineIconName } from "../line-icons.js";
import {
  workforceOperationalLayout,
  workforceOperationalTypography,
} from "../workforce-design-system.js";
import {
  applyCalendarDayTap,
  buildMonthGrid,
  buildPresetRanges,
  countPatientsByDay,
  formatDayHeaderText,
  formatMonthTitleText,
  formatRangeLabelText,
  groupPatientsByDateOfService,
  isDateInRange,
  type CalendarRange,
} from "./list-calendar.js";
import {
  resolvePatientQueueAccessibilityLabel,
  resolvePatientQueueRowCopy,
  resolvePatientQueueStatusChips,
} from "./patient-list-row.js";
import {
  chartPackDocuments,
  computeChartPackDocumentGaps,
} from "./chart-pack/templates.js";
import { resolveHorizontalTabNavigationIndex } from "./keyboard-navigation.js";
import {
  formatClinicianProgramContextText,
  formatClinicianQuestionnaireClinicalNoteText,
  parseClinicianDateTimeText,
  parseClinicianDetailText,
  resolveClinicianOutboundSessionContext,
} from "./presentation.js";
import { isClinicianInlineEditorDirty } from "./editor-safety.js";
import {
  ClinicianPatientProfileSurface,
  type ClinicianPatientProfileFocusTarget,
} from "./patient-profile.js";
import { resolveQuestionnaireReviewPromptText } from "./questionnaire-entry-core.js";
import { renderPatientRecordRailContent } from "./patient-rail.js";
import {
  getLegacyDetailPreviewPalette,
  getLegacySectionChromeColors,
  resolveLegacyNameplate,
  resolveLegacyPreviewFallbackPrimaryText,
  resolveMessageDeliveryLabelKey,
  resolvePatientInitials,
  type AppShellTheme,
  type LegacySectionChromeTone,
} from "../shell-core.js";
import {
  createWebGradientBackgroundStyle,
  renderWorkbenchActionButton,
} from "../shell-primitives.js";
import type {
  ChatMessageViewModel,
  ClinicalNoteViewModel,
  DetailRowKind,
  DetailSectionViewModel,
  DetailTabId,
  PatientListSummaryViewModel,
  PatientListTabId,
  PatientWorkspaceViewModel,
  VoiceCallSummaryViewModel,
  WorkbenchPanelId,
  WorkbenchPanelViewModel,
} from "../types.js";
import { workbenchPanelTabs } from "../workbench-adapter.js";

type WebTabKeyDownEvent = {
  currentTarget?: unknown;
  key?: string;
  nativeEvent?: { key?: string };
  preventDefault?: () => void;
};

function handleHorizontalTabKeyDown(
  event: WebTabKeyDownEvent,
  currentIndex: number,
  itemCount: number,
  onSelect: (nextIndex: number) => void,
): void {
  const nextIndex = resolveHorizontalTabNavigationIndex(
    event.key ?? event.nativeEvent?.key ?? "",
    currentIndex,
    itemCount,
  );

  if (nextIndex == null) {
    return;
  }

  event.preventDefault?.();
  onSelect(nextIndex);

  const currentTarget = event.currentTarget as HTMLElement | null | undefined;
  const tabList = currentTarget?.closest?.('[role="tablist"]');

  setTimeout(() => {
    tabList
      ?.querySelectorAll<HTMLElement>('[role="tab"]')
      .item(nextIndex)
      ?.focus();
  }, 0);
}

// Suppress the default browser focus ring on interactive workbench elements.
// These already render an intentional selected/hover state, so the stray
// blue/teal UA outline is redundant noise.
const webOutlineReset: ViewStyle | null =
  Platform.OS === "web"
    ? ({ outlineStyle: "none", outlineWidth: 0 } as unknown as ViewStyle)
    : null;

function resolveWorkbenchActionTone(
  action: ClinicianWorkbenchActionInput | null,
): WorkbenchActionTone {
  if (action == null) {
    return "accent";
  }

  switch (action.kind) {
    case "approve-message-review":
    case "complete-task-assignment":
    case "mark-document-reviewed":
    case "refresh-case-summary":
      return "success";
    case "clear-patient-attention":
    case "archive-clinical-note":
      return "warning";
    case "reject-message-review":
      return "danger";
    case "reactivate-clinical-note":
      return "success";
    default:
      return "accent";
  }
}

function formatLegacyHeaderBadgeLabel(valueText: string): string {
  return valueText
    .trim()
    .split(/\s+/u)
    .filter((part) => part.length > 0)
    .join(" ")
    .toUpperCase();
}

function formatOutboundAnswerMetadataLabel(value: string): string {
  return value
    .replace(/_/gu, " ")
    .replace(/\b\w/gu, (match) => match.toUpperCase());
}

function formatOutboundQuestionKeyLabel(value: string): string {
  return value
    .trim()
    .replace(/_/gu, " ")
    .replace(/\b\w/gu, (match) => match.toUpperCase());
}

function formatOutboundCallStatusLabel(value: string | null | undefined): string {
  switch (value) {
    case "identity_verification":
      return "Verifying identity";
    case "in_progress":
      return "In progress";
    case "review_required":
      return "Review required";
    case "not_started":
      return "Not started";
    case "live_answer":
      return "Live answer";
    case "no_answer":
      return "No answer";
    default:
      return value ? formatOutboundQuestionKeyLabel(value) : "Not started";
  }
}

function resolveOutboundMedicationEntryCount(
  answer: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
): number {
  return Math.max(
    0,
    answer.medicationEntryCount ?? answer.medicationEntries?.length ?? 0,
  );
}

function formatOutboundMedicationEntryCountLabel(count: number): string {
  return `${String(count)} patient-reported medication entr${
    count === 1 ? "y" : "ies"
  }`;
}

function formatOutboundMedicationReconciliationLabel(
  answer: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
): string | null {
  const entryCount = resolveOutboundMedicationEntryCount(answer);

  if (entryCount > 0) {
    return `${formatOutboundMedicationEntryCountLabel(
      entryCount,
    )} captured; matching not enabled`;
  }

  if (answer.medicationReconciliationStatus === "no_patient_reported_medications") {
    return "No patient-reported medications captured; matching not enabled";
  }

  if (answer.medicationReconciliationStatus === "captured_patient_reported") {
    return "Medication details need review; no structured entries captured";
  }

  return answer.medicationReconciliationStatus
    ? `${formatOutboundAnswerMetadataLabel(
        answer.medicationReconciliationStatus,
      )}; matching not enabled`
    : null;
}

function formatOutboundQuestionnaireVersionMeta(
  call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
): string | null {
  if (call.questionnaireVersionPinnedToSession) {
    return `Session ${
      call.questionnaireVersionLabel ?? "version not recorded"
    }; campaign current ${
      call.campaignQuestionnaireVersionLabel ?? "not recorded"
    }`;
  }

  return call.questionnaireVersionLabel != null
    ? `Session ${call.questionnaireVersionLabel}`
    : null;
}

function buildOutboundAnswerReviewLabels(
  answer: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
): string[] {
  const labels: string[] = [];

  if (answer.reviewSignal === "red_flag" || answer.redFlagTriggered) {
    labels.push(answer.reviewSignalLabel ?? "Red flag");
  } else if (
    answer.reviewSignal === "yellow_flag" ||
    answer.clinicianReviewRequired
  ) {
    labels.push(answer.reviewSignalLabel ?? "Yellow flag");
  }

	  if (
	    answer.answerStatus !== "redacted" &&
	    (answer.changedSinceFirstAnswer === true ||
	      answer.answerStatus === "changed")
	  ) {
	    labels.push("Changed answer");
	  }

  if (answer.answerStatus === "redacted") {
    labels.push("Changed path removed");
  }

	  const medicationLabel = formatOutboundMedicationReconciliationLabel(answer);

	  if (medicationLabel) {
	    labels.push(medicationLabel);
	  }

  return labels;
}

function countPendingApprovalMessages(
  messages: ChatMessageViewModel[],
): number {
  return messages.filter(
    (message) =>
      message.review?.state === "pending-approval" &&
      !isNoisyDemoProbeMessage(message),
  ).length;
}

function normalizeJsonPrefixedBodyText(bodyText: string): string {
  const trimmedBodyText = bodyText.trim();

  return trimmedBodyText.toUpperCase().startsWith("JSON:")
    ? trimmedBodyText.slice("JSON:".length).trim()
    : trimmedBodyText;
}

function resolveProductClinicalText(valueText: string): string {
  const normalizedProductText = valueText
    .replace(
      /\bR1CR1-\d+\s+hosted lifecycle note\s+[0-9T:.\-Z+]+/giu,
      "Clinical review note",
    )
    .replace(
      /\bR1CR1-\d+\s+verification note created through the live clinician workbench action API;\s*patient-visible for propagation checks\.?/giu,
      "Clinical note available for patient-facing care context.",
    )
    .replace(/\bR1CR1-\d+\s+hosted appointment edited\b/giu, "Updated appointment")
    .replace(/\bhosted lifecycle note\b/giu, "Clinical review note")
    .replace(/\bhosted verification\b/giu, "care-team review")
    .replace(/\bverification note\b/giu, "clinical note")
    .replace(/\blive clinician workbench action API\b/giu, "clinical workspace")
    .replace(/\bpropagation checks\b/giu, "patient-facing review");
  const hasInternalDemoProbe =
    /\bcodex\b/iu.test(normalizedProductText) ||
    /\bdb-only\b/iu.test(normalizedProductText) ||
    /\bsmoke\s+\d{4}/iu.test(normalizedProductText) ||
    /\bsmoke\/probe\b/iu.test(normalizedProductText) ||
    /\baction API\b/iu.test(normalizedProductText) ||
    /\bpropagation checks\b/iu.test(normalizedProductText) ||
    /^\s*json\s*:/iu.test(normalizedProductText);

  if (hasInternalDemoProbe) {
    return "Care-team review context is available for this item.";
  }

  return normalizedProductText
    .replace(/^\s*JSON:\s*/iu, "")
    .replace(
      /\bstored as prepared draft text for the demo agent\b/giu,
      "available for clinician review",
    )
    .replace(
      /\bprepared draft text for the demo agent\b/giu,
      "clinician review context",
    )
    .replace(/\bAmpleCare CR1 backfill\b/giu, "AmpleCare clinical summary")
    .replace(/\bdemo agent\b/giu, "care team")
    .replace(/\bprompt-ready\s+text\b/giu, "prepared draft text")
    .replace(/\bprompt-ready\b/giu, "ready for review")
    .replace(
      /\bstored as prepared draft text for the care team\b/giu,
      "available for clinician review",
    )
    .replace(/\bprepared draft text\b/giu, "review context")
    .replace(/\bsource documents\b/giu, "clinical documents")
    .replace(/\bsource document\b/giu, "clinical document")
    .replace(/\blive prompt context\b/giu, "care context")
    .replace(/\bprompt context\b/giu, "care context")
    .replace(/\bprompt\b/giu, "guidance");
}

function resolveProductClinicalNoteBodyText(bodyText: string): string {
  const normalizedBodyText = normalizeJsonPrefixedBodyText(bodyText);
  const questionnaireSummary =
    formatClinicianQuestionnaireClinicalNoteText(bodyText);

  if (questionnaireSummary) {
    return questionnaireSummary;
  }

  if (
    /["']?(?:questionKey|answerStatus|rawAnswerText|answerSummary)["']?\s*[:=]/iu.test(
      normalizedBodyText,
    )
  ) {
    return "Questionnaire responses were captured in this clinical note.";
  }

  return resolveProductClinicalText(bodyText);
}

function resolveProductMessageBodyText(bodyText: string): string {
  const trimmedBodyText = normalizeJsonPrefixedBodyText(bodyText);

  if (
    trimmedBodyText.length < 2 ||
    (!trimmedBodyText.startsWith("{") && !trimmedBodyText.startsWith("["))
  ) {
    const displayText = bodyText.trim().toUpperCase().startsWith("JSON:")
      ? trimmedBodyText
      : bodyText;

    return resolveProductClinicalText(displayText);
  }

  try {
    const parsedBody = JSON.parse(trimmedBodyText) as unknown;
    const candidateTextValues =
      parsedBody != null && typeof parsedBody === "object"
        ? [
            (parsedBody as { bodyText?: unknown }).bodyText,
            (parsedBody as { draftMessage?: unknown }).draftMessage,
            (parsedBody as { replyText?: unknown }).replyText,
            (parsedBody as { draftText?: unknown }).draftText,
            (parsedBody as { answerText?: unknown }).answerText,
            (parsedBody as { message?: unknown }).message,
            (parsedBody as { content?: unknown }).content,
            (parsedBody as { text?: unknown }).text,
            (parsedBody as { response?: unknown }).response,
            (parsedBody as { body?: unknown }).body,
          ]
        : [];
    const visibleText = candidateTextValues.find(
      (value): value is string =>
        typeof value === "string" && value.trim().length > 0,
    );

    return resolveProductClinicalText(
      visibleText ?? "AI draft needs formatting review.",
    );
  } catch {
    return "AI draft needs formatting review.";
  }
}

function isNoisyDemoProbeMessage(message: ChatMessageViewModel): boolean {
  const normalizedText = [
    message.senderText,
    message.bodyText,
    message.id,
  ]
    .join(" ")
    .toLowerCase();

  if (!normalizedText.includes("codex")) {
    return false;
  }

  return true;
}

export function buildReviewFirstChatMessages(
  messages: ChatMessageViewModel[],
): ChatMessageViewModel[] {
  const visibleMessages = messages.filter(
    (message) => !isNoisyDemoProbeMessage(message),
  );
  const pendingMessages = visibleMessages.filter(
    (message) => message.review?.state === "pending-approval",
  );

  if (pendingMessages.length === 0) {
    return visibleMessages;
  }

  const pendingMessageIds = new Set(pendingMessages.map((message) => message.id));

  return [
    ...pendingMessages,
    ...visibleMessages.filter((message) => !pendingMessageIds.has(message.id)),
  ];
}

function resolveClinicianInfoIconName(labelText: string): LineIconName {
  const normalizedLabel = labelText.toLowerCase();

  if (normalizedLabel.includes("appointment")) {
    return "appointment";
  }

  if (normalizedLabel.includes("clinician")) {
    return "clinician";
  }

  if (normalizedLabel.includes("program")) {
    return "program";
  }

  if (normalizedLabel.includes("phone")) {
    return "phone";
  }

  if (normalizedLabel.includes("mail")) {
    return "mail";
  }

  if (normalizedLabel.includes("contact")) {
    return "clock";
  }

  if (normalizedLabel.includes("identifier")) {
    return "hash";
  }

  return "info";
}

function findDetailRowValue(
  detailSections: PatientWorkspaceViewModel["detailSections"],
  rowId: string,
): string | null {
  for (const section of detailSections) {
    const matchingRow = section.rows.find((row) => row.id === rowId);

    if (matchingRow?.valueText) {
      return matchingRow.valueText;
    }
  }

  return null;
}

function findDetailSection(
  detailSections: PatientWorkspaceViewModel["detailSections"],
  sectionId: DetailTabId,
): DetailSectionViewModel | null {
  return detailSections.find((section) => section.id === sectionId) ?? null;
}

function buildLegacyClinicianConversationBadges(input: {
  selectedPatient: PatientWorkspaceViewModel;
  t: (key: TranslationKey) => string;
  theme: AppShellTheme;
}) {
  const { selectedPatient, t, theme } = input;
  const badges: Array<{
    backgroundColor: string;
    borderColor: string;
    id: string;
    labelText: string;
    textColor: string;
  }> = selectedPatient.derivedFlagLabelKeys.map((badgeKey) => {
    const badgeColors = getDerivedFlagBadgeColors(badgeKey, theme, false);

    return {
      id: badgeKey,
      labelText: formatLegacyHeaderBadgeLabel(t(badgeKey)),
      backgroundColor: badgeColors.backgroundColor,
      borderColor: theme.borderSubtle,
      textColor: badgeColors.textColor,
    };
  });

  const pushFallbackBadge = (
    id: string,
    labelText: string,
    tone: "accent" | "info",
  ) => {
    if (
      badges.some((badge) => badge.id === id) ||
      labelText.trim().length === 0
    ) {
      return;
    }

    badges.push({
      id,
      labelText: formatLegacyHeaderBadgeLabel(labelText),
      backgroundColor: theme.surfaceMuted,
      borderColor: theme.borderSubtle,
      textColor: theme.accentStrong,
    });
  };

  if (badges.length < 2) {
    const proceduresSection = findDetailSection(
      selectedPatient.detailSections,
      "procedures",
    );
    const appointmentsSection = findDetailSection(
      selectedPatient.detailSections,
      "appointments",
    );

    if (proceduresSection?.rows.length) {
      pushFallbackBadge("procedures", proceduresSection.titleText, "info");
    }

    if (badges.length < 2 && selectedPatient.programText.trim().length > 0) {
      pushFallbackBadge("program", selectedPatient.programText, "accent");
    }

    if (badges.length < 2 && appointmentsSection?.rows.length) {
      pushFallbackBadge("appointments", appointmentsSection.titleText, "info");
    }

    if (badges.length < 2 && selectedPatient.statusText.trim().length > 0) {
      pushFallbackBadge("status", selectedPatient.statusText, "accent");
    }
  }

  return badges.slice(0, 2);
}

function getDerivedFlagBadgeColors(
  badgeKey: TranslationKey,
  theme: AppShellTheme,
  isSelected: boolean,
) {
  if (badgeKey === translationKeys.shell.flagPreOp) {
    return {
      backgroundColor: isSelected ? theme.surface : theme.surfaceMuted,
      borderColor: theme.borderSubtle,
      textColor: theme.info,
    };
  }

  if (badgeKey === translationKeys.shell.flagPostOp) {
    return {
      backgroundColor: isSelected ? theme.surface : theme.surfaceMuted,
      borderColor: theme.borderSubtle,
      textColor: theme.success,
    };
  }

  if (badgeKey === translationKeys.shell.flagChronicFollowUp) {
    return {
      backgroundColor: isSelected ? theme.surface : theme.surfaceMuted,
      borderColor: theme.borderSubtle,
      textColor: theme.warning,
    };
  }

  if (badgeKey === translationKeys.shell.flagImagingActive) {
    return {
      backgroundColor: isSelected ? theme.surface : theme.surfaceMuted,
      borderColor: theme.borderSubtle,
      textColor: theme.info,
    };
  }

  if (badgeKey === translationKeys.shell.flagPreAdmission) {
    return {
      backgroundColor: isSelected ? theme.surface : theme.surfaceMuted,
      borderColor: theme.borderSubtle,
      textColor: theme.warning,
    };
  }

  return {
    backgroundColor: isSelected ? theme.surface : theme.surfaceMuted,
    borderColor: theme.borderSubtle,
    textColor: theme.accentStrong,
  };
}

type BaseRenderContext = {
  layout: typeof designTokens.semanticLayout;
  t: (key: TranslationKey) => string;
  theme: AppShellTheme;
  tokens: typeof designTokens;
};

type ChatDashboardVariant = "clinician" | "patient";
export type ClinicianComposerMode = "reply" | "note" | "handoff";
type SubmitWorkbenchAction = (
  action: ClinicianWorkbenchActionInput,
  options?: { onSuccess?: () => void; onError?: (message: string) => void },
) => void;
export type ClinicianListViewMode = "list" | "by-dos";

export type ClinicianCenterPaneView =
  | "conversation"
  | "phone-calls"
  | "patient-profile";
export type ClinicianCallTypeFilter = "all" | "questionnaire" | "inbound";

type ListRenderContext = BaseRenderContext & {
  calendarMonth: { year: number; monthIndex: number };
  calendarPendingStart: string | null;
  isRefreshing: boolean;
  isWideLayout: boolean;
  legacyStageHeight?: number;
  listSummary: PatientListSummaryViewModel;
  listViewMode: ClinicianListViewMode;
  requiresAttentionOnly: boolean;
  searchQuery: string;
  selectedCalendarRange: CalendarRange | null;
  selectedPatientId: string | null;
  selectedPatientTabId: PatientListTabId;
  setCalendarMonth: (value: { year: number; monthIndex: number }) => void;
  setCalendarPendingStart: (value: string | null) => void;
  setCalendarRange: (value: CalendarRange | null) => void;
  setListViewMode: (value: ClinicianListViewMode) => void;
  setRequiresAttentionOnly: (value: boolean) => void;
  setSearchQuery: (value: string) => void;
  setSelectedPatientId: (value: string | null) => void;
  setSelectedPatientTabId: (value: PatientListTabId) => void;
  visiblePatients: PatientWorkspaceViewModel[];
};

type WorkbenchPanelRenderContext = BaseRenderContext & {
  isWorkbenchActionSubmitting: boolean;
  onSubmitWorkbenchAction: SubmitWorkbenchAction;
  selectedPatient: PatientWorkspaceViewModel | null;
  selectedWorkbenchPanel: WorkbenchPanelViewModel | undefined;
  selectedWorkbenchPanelId: WorkbenchPanelId;
  setSelectedDetailTabId: (value: DetailTabId) => void;
  setSelectedPatientId: (value: string | null) => void;
  setSelectedWorkbenchPanelId: (value: WorkbenchPanelId) => void;
  workbenchPanels: WorkbenchPanelViewModel[];
};

type ChatRenderContext = BaseRenderContext & {
  callTypeFilter: ClinicianCallTypeFilter;
  centerPaneView: ClinicianCenterPaneView;
  clinicianConversationScrollRef: RefObject<ScrollView | null>;
  clinicianUserId: string;
  composerDraft: string;
  composerMode: ClinicianComposerMode;
  editedReviewBodiesByMessageId: Record<string, string>;
  isWorkbenchActionSubmitting: boolean;
  isWideLayout: boolean;
  legacyStageHeight?: number;
  legacyClinicianAvatarSource: { uri: string };
  legacyHospitalLogoSource: { uri: string };
  onAddSelectedPatientToMyList: () => void;
  onApproveMessageReview: (
    messageId: string,
    editedBodyText?: string | null,
  ) => void;
  onBeginReviewEdit: (messageId: string, bodyText: string) => void;
  onChangeReviewEditDraft: (value: string) => void;
  onConversationContentSizeChange: () => void;
  onConversationScroll: (
    event: NativeSyntheticEvent<NativeScrollEvent>,
  ) => void;
  onCreateComposerNoteFromDraft: (mode: Exclude<ClinicianComposerMode, "reply">) => void;
  onRejectMessageReview: (messageId: string) => void;
  onSaveReviewEdit: (messageId: string, originalBodyText: string) => void;
  onSendReply: () => void;
  reviewEditDraft: string;
  reviewEditingMessageId: string | null;
  onDownloadOutboundQuestionnaireAnswersPdf: (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ) => void;
  onOpenOutboundQuestionnaireAnswers: (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ) => void;
  onOpenVoiceCallTranscript: (voiceCall: VoiceCallSummaryViewModel) => void;
  onProfileDiscardConfirmationOpenChange: (open: boolean) => void;
  onProfileDirtyChange: (dirty: boolean) => void;
  onProfileFocusSectionHandled: () => void;
  onProfileModalOpenChange: (open: boolean) => void;
  onStartOutboundQuestionnaireCall: (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ) => void;
  onSubmitWorkbenchAction: SubmitWorkbenchAction;
  onSubmitOutboundManualQuestionnaireCommand: (
    command: {
      action: "record-manual-answers" | "complete-session-manually";
      clearedQuestionKeys: string[];
      expectedSessionRevision: string;
      manualAnswers: ClinicianWorkbenchOutboundManualAnswerV1[];
      sessionId: string;
      targetId: string;
    },
    options?: {
      onError?: (message: string) => void;
      onSuccess?: (
        summaryText: string,
        sessionRevision: string | null,
      ) => void;
    },
  ) => void;
  outboundManualCommandPending: boolean;
  outboundQuestionnaireCalls: ClinicianWorkbenchOutboundQuestionnaireCallV1[];
  outboundQuestionnaireErrorText: string | null;
  outboundQuestionnaireIsLoading: boolean;
  outboundQuestionnaireNoticePending: boolean;
  outboundQuestionnaireNoticeText: string | null;
  outboundQuestionnairePdfPendingTargetId: string | null;
  outboundQuestionnairePendingTargetId: string | null;
  onRefreshOutboundQuestionnaireCalls: (patientId: string) => void;
  patientIdentityText: string | null;
  profileDiscardChangesRequestKey: number;
  profileDiscardConfirmationDismissRequestKey: number;
  profileFocusSection: ClinicianPatientProfileFocusTarget | null;
  profileNavigationConfirmationOpen: boolean;
  questionnaireDefinition: ClinicianWorkbenchQuestionnaireDefinitionV1 | null;
  selectedPatient: PatientWorkspaceViewModel | null;
  setCallTypeFilter: (value: ClinicianCallTypeFilter) => void;
  setCenterPaneView: (value: ClinicianCenterPaneView) => void;
  setComposerDraft: (value: string) => void;
  setComposerMode: (value: ClinicianComposerMode) => void;
  showsLegacyWebAssets: boolean;
  workbenchActionFeedbackKey: TranslationKey | null;
  workbenchActionFeedbackText: string | null;
};

type DetailsRenderContext = BaseRenderContext & {
  detailSections: DetailSectionViewModel[];
  inlineClinicalEditor: ClinicianInlineClinicalEditorState;
  isDocumentUploadSubmitting: boolean;
  isWorkbenchActionSubmitting: boolean;
  isWideLayout: boolean;
  legacyStageHeight?: number;
  outboundQuestionnaireCalls: ClinicianWorkbenchOutboundQuestionnaireCallV1[];
  outboundQuestionnaireErrorText: string | null;
  outboundQuestionnaireIsLoading: boolean;
  outboundQuestionnaireNoticeText: string | null;
  outboundQuestionnairePdfPendingTargetId: string | null;
  outboundQuestionnairePendingTargetId: string | null;
  onOpenClinicalNoteEditor: (input: ClinicianClinicalNoteEditorInput) => void;
  onOpenClinicalRecordEditor: (
    input: ClinicianClinicalRecordEditorInput,
  ) => void;
  onOpenPatientProfileSection: (
    section: ClinicianPatientProfileFocusTarget,
  ) => void;
  onOpenVoiceCallTranscript: (voiceCall: VoiceCallSummaryViewModel) => void;
  onDownloadOutboundQuestionnaireAnswersPdf: (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ) => void;
  onRefreshOutboundQuestionnaireCalls: (patientId: string) => void;
  onStartOutboundQuestionnaireCall: (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ) => void;
  onSubmitWorkbenchAction: SubmitWorkbenchAction;
  onUploadDocument: () => void;
  patientIdentityText: string | null;
  selectedDetailSection: DetailSectionViewModel | null;
  selectedDetailModalViewId: OptionBModalViewId;
  selectedDetailTabId: DetailTabId;
  selectedPatient: PatientWorkspaceViewModel | null;
  showDetailDialog: boolean;
  setInlineClinicalEditor: (
    value:
      | ClinicianInlineClinicalEditorState
      | ((
          current: ClinicianInlineClinicalEditorState,
        ) => ClinicianInlineClinicalEditorState),
  ) => void;
  setSelectedDetailModalViewId: (value: OptionBModalViewId) => void;
  setSelectedDetailTabId: (value: DetailTabId) => void;
};

type OptionBModalViewId = "all" | "secondary";

export type ClinicianClinicalRecordEditorInput = {
  defaultDetailText: string;
  defaultTitle: string;
  recordId?: string | null;
  targetKind: ClinicianWorkbenchClinicalRecordKind;
};

export type ClinicianClinicalNoteEditorInput = {
  defaultBodyText: string;
  defaultTitle: string;
  defaultVisibility?: ClinicianWorkbenchClinicalNoteVisibilityState;
  noteId?: string | null;
  targetId?: string | null;
  targetKind: ClinicianWorkbenchClinicalNoteTargetKind;
};

type WorkbenchActionTone =
  | "accent"
  | "success"
  | "danger"
  | "warning"
  | "info";

type OptionBModalActionConfig = {
  disabled: boolean;
  label: string;
  onPress: () => void;
  tone: WorkbenchActionTone;
};

export type ClinicianInlineClinicalEditorState =
  | ({
      bodyText: string;
      defaultBodyText: string;
      defaultTitle: string;
      defaultVisibility?: ClinicianWorkbenchClinicalNoteVisibilityState;
      discardCloseTarget?: "editor" | "modal" | null;
      kind: "note";
      noteId?: string | null;
      /** Owner: the editor submits only for the patient it was opened for. */
      ownerPatientId?: string | null;
      targetId?: string | null;
      targetKind: ClinicianWorkbenchClinicalNoteTargetKind;
      title: string;
      visibility: ClinicianWorkbenchClinicalNoteVisibilityState;
    })
  | ({
      defaultDetailText: string;
      defaultTitle: string;
      detailText: string;
      discardCloseTarget?: "editor" | "modal" | null;
      kind: "record";
      /** Owner: the editor submits only for the patient it was opened for. */
      ownerPatientId?: string | null;
      recordId?: string | null;
      targetKind: ClinicianWorkbenchClinicalRecordKind;
      title: string;
    })
  | null;

type OptionBTimelineMetric = {
  labelText: string;
  valueText: string;
};

type OptionBModalStateChip = {
  id: string;
  labelText: string;
};

type OptionBTimelineItem = {
  actionConfig: OptionBModalActionConfig | null;
  actionConfigs?: OptionBModalActionConfig[];
  badgeText: string;
  bodyLines: string[];
  clinicalStatus?: ClinicianWorkbenchClinicalRecordStatus;
  dateLabel: string;
  detailRows?: OptionBTimelineMetric[];
  groupLabel: string;
  id: string;
  metrics: OptionBTimelineMetric[];
  statusText: string;
  timeLabel: string;
  titleText: string;
};

type ClinicianDetailMetricSize = "compact" | "regular" | "wide";

const clinicianCompactActionButtonWidth = 156;
const clinicianModalActionButtonWidth = 148;
const clinicianReplyActionButtonWidth = 132;
const clinicianWideActionButtonWidth = 184;
const clinicianRailInset = 10;

function resolveModalActionIconName(
  label: string,
  fallback: LineIconName,
): LineIconName {
  const normalized = label.toLowerCase();
  if (normalized.includes("edit")) {
    return "edit";
  }
  if (normalized.includes("archive")) {
    return "archive";
  }
  if (normalized.includes("reactivate") || normalized.includes("restore")) {
    return "sync";
  }
  if (normalized.includes("complete") || normalized.includes("approve")) {
    return "check";
  }
  if (normalized.includes("add")) {
    return "plus";
  }
  if (
    normalized.includes("open") ||
    normalized.includes("view") ||
    normalized.includes("transcript")
  ) {
    return "eye";
  }
  return fallback;
}

function ClinicianExpandableModalRow(props: {
  borderColor: string;
  surfaceColor: string;
  renderHeader: (open: boolean) => ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <View
      style={{
        backgroundColor: props.surfaceColor,
        borderColor: props.borderColor,
        borderRadius: workforceOperationalLayout.panelRadius,
        borderWidth: 1,
        overflow: "hidden",
      }}
    >
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        aria-expanded={open}
        onPress={() => setOpen((value) => !value)}
      >
        {props.renderHeader(open)}
      </Pressable>
      {open ? props.children : null}
    </View>
  );
}

function resolveDetailTabClinicalRecordKind(
  detailTabId: DetailTabId,
): ClinicianWorkbenchClinicalRecordKind | null {
  switch (detailTabId) {
    case "appointments":
      return "appointment";
    case "procedures":
      return "procedure";
    case "medications":
      return "medication";
    case "documents":
      return "document";
    default:
      return null;
  }
}

function formatClinicalRecordKindLabel(
  targetKind: ClinicianWorkbenchClinicalRecordKind,
): string {
  switch (targetKind) {
    case "appointment":
      return "appointment";
    case "procedure":
      return "procedure";
    case "medication":
      return "medication";
    case "document":
      return "document";
  }
}

function formatClinicalNoteTargetKindLabel(
  targetKind: ClinicianWorkbenchClinicalNoteTargetKind,
): string {
  switch (targetKind) {
    case "patient":
      return "patient";
    case "appointment":
      return "appointment";
    case "procedure":
      return "procedure";
    case "medication":
      return "medication";
    case "document":
      return "document";
  }
}

type ClinicianDetailMetricRow = {
  id: string;
  labelText: string;
  valueText: string;
  size?: ClinicianDetailMetricSize;
};

function resolveClinicianMetricTileStyle(
  row: ClinicianDetailMetricRow,
): Readonly<{
  flexBasis: number;
  flexGrow: number;
  minWidth: number;
}> {
  const labelLength = row.labelText.trim().length;
  const valueLength = row.valueText.trim().length;
  const longestWordLength = Math.max(
    0,
    ...`${row.labelText} ${row.valueText}`
      .split(/\s+/u)
      .map((part) => part.trim().length),
  );

  if (
    row.size === "wide" ||
    labelLength >= 14 ||
    valueLength >= 18 ||
    longestWordLength >= 14
  ) {
    return {
      flexBasis: 220,
      flexGrow: 1,
      minWidth: 184,
    };
  }

  if (
    row.size === "compact" &&
    labelLength <= 10 &&
    valueLength <= 6 &&
    longestWordLength <= 10
  ) {
    return {
      flexBasis: 96,
      flexGrow: 0,
      minWidth: 88,
    };
  }

  return {
    flexBasis: 152,
    flexGrow: 1,
    minWidth: 136,
  };
}

function resolveDetailTabIdForWorkbenchPanel(
  panelId: WorkbenchPanelId,
): DetailTabId {
  if (panelId === "tasks") {
    return "case-summary";
  }

  if (panelId === "documents") {
    return "documents";
  }

  return "information";
}

function resolveDocumentBadgeText(labelText: string): string {
  const normalized = labelText
    .trim()
    .split(/\s+/u)
    .filter((part) => part.length > 0);

  if (normalized.length === 0) {
    return "DOC";
  }

  const joined = normalized
    .slice(0, 2)
    .map((part) => part.slice(0, 2))
    .join("")
    .toUpperCase();

  return joined.slice(0, 3) || "DOC";
}

function resolveDocumentLeadStatusText(statusText: string): string {
  const normalized = statusText
    .trim()
    .split(/\s+/u)
    .filter((part) => part.length > 0);

  if (normalized.length === 0) {
    return "Metadata";
  }

  if (normalized.length <= 2) {
    return normalized.join(" ");
  }

  const normalizedText = normalized.join(" ");

  if (/review/iu.test(normalizedText)) {
    return "Review";
  }

  if (/download/iu.test(normalizedText) || /ready/iu.test(normalizedText)) {
    return "Ready";
  }

  if (/metadata/iu.test(normalizedText)) {
    return "Metadata";
  }

  const compact = normalized.slice(0, 2).join(" ");

  return compact.length > 14 ? normalized[0] ?? "Metadata" : compact;
}

function getClinicianDetailModalChrome(theme: AppShellTheme) {
  return {
    bodyCardBackgroundColor: theme.backgroundSubtle,
    borderColor: theme.borderSubtle,
    closeButtonBackgroundColor: theme.surface,
    closeButtonBorderColor: theme.borderSubtle,
    closeButtonTextColor: theme.accentStrong,
    headerCardBackgroundColor: theme.accentSubtle,
    leadBackgroundColor: theme.backgroundSubtle,
    leadTextColor: theme.accentStrong,
    metaPillBackgroundColor: theme.surface,
    metaPillTextColor: theme.textSecondary,
  };
}

function parseWorkbenchDateTimeText(valueText: string): Date | null {
  return parseClinicianDateTimeText(valueText);
}

function formatLegacyPreviewDatePrimaryText(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  })
    .format(date)
    .toUpperCase();
}

function formatOptionBTimelineDateText(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  })
    .format(date)
    .toUpperCase();
}

function formatOptionBTimelineTimeText(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    hour12: true,
    minute: "2-digit",
  }).format(date);
}

function formatOutboundAnswerPreviewTimestampText(
  value: string | null,
): string | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return [
    formatOptionBTimelineDateText(parsed),
    formatOptionBTimelineTimeText(parsed),
  ].join(" ");
}

function formatOptionBModalFactValueText(row: ClinicianDetailMetricRow): string {
  const productValueText = resolveProductClinicalText(row.valueText);

  if (!/appointment|received|created|updated/iu.test(row.labelText)) {
    return productValueText;
  }

  const parsedDetailValue = parseOptionBDetailValue(productValueText);

  if (parsedDetailValue.date != null) {
    const dateText = new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      month: "short",
      year: "numeric",
    }).format(parsedDetailValue.date);
    const statusText = resolveProductClinicalText(
      parsedDetailValue.statusText,
    ).trim();
    const detailText =
      parsedDetailValue.bodyLines
        .map(resolveProductClinicalText)
        .find((part) => part.trim().length > 0)
        ?.trim() ?? "";

    return [
      statusText.length > 0 ? `${dateText}, ${statusText}` : dateText,
      detailText,
    ]
      .filter((part) => part.length > 0)
      .join(" - ");
  }

  const parsedDate = parseWorkbenchDateTimeText(productValueText);

  if (parsedDate == null) {
    return productValueText;
  }

  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsedDate);
}

function parseOptionBDetailValue(valueText: string) {
  return parseClinicianDetailText(valueText);
}

function resolveOptionBStatusText(input: {
  fallbackText?: string;
  titleText: string;
  valueText: string;
}) {
  const parsed = parseOptionBDetailValue(input.valueText);
  const explicit = parsed.statusText.trim();

  if (explicit.length > 0) {
    return explicit;
  }

  if (/performed|completed|resolved|ready|download/iu.test(input.titleText)) {
    return "Completed";
  }

  return input.fallbackText ?? "Scheduled";
}

export function renderWorkbenchPanelCard(context: WorkbenchPanelRenderContext) {
  const {
    isWorkbenchActionSubmitting,
    layout,
    onSubmitWorkbenchAction,
    selectedPatient,
    selectedWorkbenchPanel,
    selectedWorkbenchPanelId,
    setSelectedDetailTabId,
    setSelectedPatientId,
    setSelectedWorkbenchPanelId,
    t,
    theme,
    tokens,
    workbenchPanels,
  } = context;
  const firstActionableItem =
    selectedWorkbenchPanel?.items.find(
      (item) => item.action != null && item.actionLabelKey != null,
    ) ?? null;
  const currentLaneLabel =
    selectedWorkbenchPanel != null
      ? t(
          selectedWorkbenchPanel.id === "documents"
            ? translationKeys.navigation.documents
            : selectedWorkbenchPanel.id === "tasks"
              ? translationKeys.navigation.tasks
              : translationKeys.navigation.inbox,
        )
      : "Inbox";
  const focusDetailLabel =
    selectedWorkbenchPanel?.id === "documents"
      ? "Documents"
      : selectedWorkbenchPanel?.id === "tasks"
        ? "Case summary"
        : "Patient information";
  const workflowFocusItems = [
    {
      id: "workflow-lane",
      labelText: "Current lane",
      valueText: currentLaneLabel,
    },
    {
      id: "workflow-patient",
      labelText: "Selected patient",
      valueText: selectedPatient?.displayName ?? "No patient selected",
    },
    {
      id: "workflow-focus",
      labelText: "Focus panel",
      valueText: focusDetailLabel,
    },
  ];
  const selectedPanelItemCount = selectedWorkbenchPanel?.items.length ?? 0;
  const actionableItemCount =
    selectedWorkbenchPanel?.items.filter(
      (item) => item.action != null && item.actionLabelKey != null,
    ).length ?? 0;
  const patientLinkedItemCount =
    selectedWorkbenchPanel?.items.filter((item) => item.targetPatientId != null)
      .length ?? 0;
  const detailRouteCount =
    selectedWorkbenchPanel?.items.filter(
      (item) => item.targetDetailTabId != null,
    ).length ?? 0;
  const laneSummaryItems = [
    {
      id: "lane-items",
      labelText: "Queue items",
      valueText: String(selectedPanelItemCount),
      toneColor: theme.accentStrong,
    },
    {
      id: "lane-actions",
      labelText: "Actionable",
      valueText: String(actionableItemCount),
      toneColor: theme.warning,
    },
    {
      id: "lane-patients",
      labelText: "Patient routes",
      valueText: String(patientLinkedItemCount),
      toneColor: theme.info,
    },
    {
      id: "lane-details",
      labelText: "Detail routes",
      valueText: String(detailRouteCount),
      toneColor: theme.textPrimary,
    },
  ];
  const workbenchCommandItems = [
    {
      id: "command-open-focus",
      disabled: selectedWorkbenchPanel == null,
      label:
        selectedWorkbenchPanel?.id === "documents"
          ? "Open document rail"
          : selectedWorkbenchPanel?.id === "tasks"
            ? "Open case summary"
            : "Open patient info",
      onPress: () =>
        setSelectedDetailTabId(
          selectedWorkbenchPanel?.id === "documents"
            ? "documents"
            : selectedWorkbenchPanel?.id === "tasks"
              ? "case-summary"
              : "information",
        ),
      tone: "accent" as const,
    },
    {
      id: "command-open-docs",
      disabled: selectedPatient == null,
      label: "Open documents",
      onPress: () => setSelectedDetailTabId("documents"),
      tone: "success" as const,
    },
    {
      id: "command-open-case",
      disabled: selectedPatient == null,
      label: "Open case summary",
      onPress: () => setSelectedDetailTabId("case-summary"),
      tone: "accent" as const,
    },
    ...(firstActionableItem?.action && firstActionableItem.actionLabelKey
      ? [
          {
            id: "command-advance",
            disabled: isWorkbenchActionSubmitting,
            label: t(firstActionableItem.actionLabelKey),
            onPress: () => onSubmitWorkbenchAction(firstActionableItem.action!),
            tone: resolveWorkbenchActionTone(firstActionableItem.action),
          },
        ]
      : []),
  ];
  const resolveWorkbenchDetailRouteLabel = (
    detailTabId: DetailTabId | null,
  ) => {
    if (detailTabId === "case-summary") {
      return "Case summary";
    }

    if (detailTabId === "appointments") {
      return "Appointments";
    }

    if (detailTabId === "procedures") {
      return "Procedures";
    }

    if (detailTabId === "medications") {
      return "Medications";
    }

    if (detailTabId === "documents") {
      return "Documents";
    }

    if (detailTabId === "phone-calls") {
      return "Phone calls";
    }

    return "Patient information";
  };

  return (
    <View
      style={[
        styles.surfaceCard,
        {
          backgroundColor: theme.surfaceElevated,
          borderColor: theme.borderStrong,
          borderRadius: layout.cardRadius,
          gap: tokens.spacing.md,
          padding: tokens.spacing.lg,
          width: "100%",
        },
      ]}
    >
      <Text
        style={[
          styles.surfaceTitle,
          {
            color: theme.textPrimary,
            fontFamily: layout.displayFontFamily,
            fontSize: tokens.typography.fontSize.lg,
            lineHeight: tokens.typography.lineHeight.lg,
          },
        ]}
      >
        {t(translationKeys.shell.workspacePanelTitle)}
      </Text>

      <Text
        style={[
          styles.sectionSubtitle,
          {
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.sm,
            lineHeight: tokens.typography.lineHeight.md,
          },
        ]}
      >
        {t(translationKeys.shell.workspacePanelSubtitle)}
      </Text>

      <View
        accessibilityLabel="Workbench panels"
        role="tablist"
        style={[
          styles.sectionChromeStack,
          {
            gap: tokens.spacing.sm,
          },
        ]}
      >
        {workbenchPanelTabs.map((panel, panelIndex) => {
          const isSelected = panel.id === selectedWorkbenchPanelId;
          const panelViewModel = workbenchPanels.find(
            (candidatePanel) => candidatePanel.id === panel.id,
          );
          const chromeColors = getLegacySectionChromeColors(
            resolveWorkbenchPanelSectionTone(panel.id),
            isSelected,
            theme,
          );

          return (
            <Pressable
              accessibilityLabel={t(panel.labelKey)}
              accessibilityRole="tab"
              accessibilityState={{ selected: isSelected }}
              aria-selected={isSelected}
              key={panel.id}
              onPress={() => {
                setSelectedWorkbenchPanelId(panel.id);
                setSelectedDetailTabId(
                  resolveDetailTabIdForWorkbenchPanel(panel.id),
                );
              }}
              tabIndex={isSelected ? 0 : -1}
              {...(Platform.OS === "web"
                ? {
                    onKeyDown: (event: unknown) =>
                      handleHorizontalTabKeyDown(
                        event as WebTabKeyDownEvent,
                        panelIndex,
                        workbenchPanelTabs.length,
                        (nextIndex) => {
                          const nextPanel = workbenchPanelTabs[nextIndex];

                          setSelectedWorkbenchPanelId(nextPanel.id);
                          setSelectedDetailTabId(
                            resolveDetailTabIdForWorkbenchPanel(nextPanel.id),
                          );
                        },
                      ),
                  }
                : {})}
              style={[
                styles.sectionChromeButton,
                {
                  backgroundColor: chromeColors.backgroundColor,
                  borderColor: chromeColors.borderColor,
                  borderRadius: layout.controlRadius,
                  gap: tokens.spacing.sm,
                  padding: tokens.spacing.xs,
                },
              ]}
            >
              <View
                style={[
                  styles.sectionChromeLead,
                  {
                    backgroundColor: chromeColors.leadBackgroundColor,
                    borderColor: chromeColors.leadBorderColor,
                    borderRadius: layout.controlRadius,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.sectionChromeLeadText,
                    {
                      color: chromeColors.leadTextColor,
                      fontFamily: layout.bodyFontFamily,
                    },
                  ]}
                >
                  {resolveWorkbenchPanelSectionLeadText(panel.id)}
                </Text>
              </View>

              <View
                style={[
                  styles.sectionChromeCopy,
                  {
                    gap: tokens.spacing.xs,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: chromeColors.titleColor,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  {t(panel.labelKey)}
                </Text>
                {panelViewModel ? (
                  <Text
                    numberOfLines={2}
                    style={[
                      styles.sectionChromeSupportText,
                      {
                        color: chromeColors.supportColor,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        lineHeight: tokens.typography.lineHeight.sm,
                      },
                    ]}
                  >
                    {t(panelViewModel.subtitleKey)}
                  </Text>
                ) : null}
              </View>

              <View
                style={[
                  styles.sectionChromeCountBadge,
                  {
                    backgroundColor: chromeColors.countBackgroundColor,
                    borderColor: chromeColors.countBorderColor,
                    borderRadius: 8,
                    paddingHorizontal: tokens.spacing.sm,
                    paddingVertical: tokens.spacing.xs,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.sectionChromeCountText,
                    {
                      color: chromeColors.countTextColor,
                      fontFamily: layout.bodyFontFamily,
                    },
                  ]}
                >
                  {String(panelViewModel?.items.length ?? 0)}
                </Text>
              </View>
            </Pressable>
          );
        })}
      </View>

      <Text
        style={[
          styles.sectionSubtitle,
          {
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.sm,
            lineHeight: tokens.typography.lineHeight.sm,
          },
        ]}
      >
        {selectedWorkbenchPanel
          ? selectedWorkbenchPanel.id === "inbox"
            ? t(selectedWorkbenchPanel.subtitleKey)
            : selectedPatient
              ? `${t(selectedWorkbenchPanel.subtitleKey)} ${t(translationKeys.shell.workspaceSelectedPatient)}: ${selectedPatient.displayName}`
              : t(selectedWorkbenchPanel.subtitleKey)
          : selectedPatient
            ? `${t(translationKeys.shell.workspaceSelectedPatient)}: ${selectedPatient.displayName}`
            : t(translationKeys.shell.workspaceNoPatientSelectedBody)}
      </Text>

      <View
        style={[
          styles.detailHeaderCard,
          {
            backgroundColor: theme.backgroundSubtle,
            borderColor: theme.borderStrong,
            borderRadius: layout.controlRadius,
            gap: tokens.spacing.sm,
            padding: tokens.spacing.sm,
          },
        ]}
      >
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "center",
              gap: tokens.spacing.sm,
            },
          ]}
        >
          <View
            style={[
              styles.detailHeaderAvatar,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                shadowColor: theme.textPrimary,
                shadowOffset: { width: 0, height: 22 },
                shadowOpacity: 0.16,
                shadowRadius: 34,
              },
            ]}
          >
            <Text
              style={[
                styles.detailHeaderAvatarText,
                {
                  color: theme.accentStrong,
                  fontFamily: layout.displayFontFamily,
                },
              ]}
            >
              {selectedWorkbenchPanel?.id === "documents"
                ? "DOC"
                : selectedWorkbenchPanel?.id === "tasks"
                  ? "TASK"
                  : "IN"}
            </Text>
          </View>
          <View
            style={[
              styles.patientIdentityCopy,
              {
                gap: tokens.spacing.xs,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                  textTransform: "uppercase",
                },
              ]}
            >
              Workflow focus
            </Text>
            <Text
              style={[
                styles.surfaceTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: tokens.typography.fontSize.md,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              {selectedWorkbenchPanel ? currentLaneLabel : "Patient lane"}
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              Current messages, tasks, and documents for the selected patient.
            </Text>
          </View>
        </View>

        <View
          style={[
            styles.detailHeaderMetricsGrid,
            {
              gap: tokens.spacing.sm,
            },
          ]}
        >
          {workflowFocusItems.map((item) => (
            <View
              key={item.id}
              style={[
                styles.detailHeaderMetricTile,
                {
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  gap: tokens.spacing.xs,
                  padding: tokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                  },
                ]}
              >
                {item.labelText}
              </Text>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {item.valueText}
              </Text>
            </View>
          ))}
        </View>

        <View
          style={[
            styles.patientMetaGrid,
            {
              gap: tokens.spacing.sm,
            },
          ]}
        >
          {laneSummaryItems.map((item) => (
            <View
              key={item.id}
              style={[
                styles.patientMetaCard,
                {
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  gap: tokens.spacing.xs,
                  padding: tokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {item.labelText}
              </Text>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: item.toneColor,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {item.valueText}
              </Text>
            </View>
          ))}
        </View>

        <View
          style={[
            styles.listGroup,
            {
              gap: tokens.spacing.sm,
            },
          ]}
        >
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.xs,
                textTransform: "uppercase",
              },
            ]}
          >
            Actions
          </Text>

          <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
            {workbenchCommandItems.map((action) => (
              <Fragment key={action.id}>
                {renderWorkbenchActionButton({
                  disabled: action.disabled,
                  label: action.label,
                  labelLines: 1,
                  minHeight: workforceOperationalLayout.mobileControlHeight,
                  minWidth: clinicianWideActionButtonWidth,
                  onPress: action.onPress,
                  size: "compact",
                  theme,
                  tone: action.tone,
                  tokens,
                  width: clinicianWideActionButtonWidth,
                })}
              </Fragment>
            ))}
          </View>
        </View>
      </View>

      {selectedWorkbenchPanel?.summaryMetrics?.length ? (
        <View
          style={[
            styles.tabRow,
            {
              gap: tokens.spacing.sm,
            },
          ]}
        >
          {selectedWorkbenchPanel.summaryMetrics.map((metric) => (
            <View
              key={metric.id}
              style={[
                styles.metricItemCard,
                {
                  backgroundColor: getDetailSummaryMetricBackground(
                    metric.tone,
                    theme,
                  ),
                  borderColor: theme.borderStrong,
                  borderRadius: layout.controlRadius,
                  gap: tokens.spacing.xs,
                  minWidth: 72,
                  paddingHorizontal: tokens.spacing.sm,
                  paddingVertical: tokens.spacing.xs,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                  },
                ]}
              >
                {t(metric.labelKey)}
              </Text>
              <Text
                style={[
                  styles.metricValue,
                  {
                    color: getDetailSummaryMetricTextColor(metric.tone, theme),
                    fontFamily: layout.displayFontFamily,
                    fontSize: tokens.typography.fontSize.md,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                {metric.valueText}
              </Text>
            </View>
          ))}
        </View>
      ) : null}

      <View
        style={[
          styles.listGroup,
          {
            gap: tokens.spacing.sm,
          },
        ]}
      >
        {(selectedWorkbenchPanel?.items.length ?? 0) > 0 ? (
          selectedWorkbenchPanel?.items.map((item) => {
            const leadColors = getWorkbenchPanelLeadColors(item, theme);
            const leadText = resolveWorkbenchPanelLeadText(item);
            const routeText = [
              item.targetDetailTabId
                ? `Opens ${resolveWorkbenchDetailRouteLabel(item.targetDetailTabId).toLowerCase()}`
                : item.targetPatientId
                  ? "Opens patient workspace"
                  : null,
            ]
              .filter((part): part is string => part != null && part.length > 0)
              .join(" | ");
            const canOpenRoute =
              item.targetPatientId != null || item.targetDetailTabId != null;

            return (
              <View
                key={item.id}
                style={[
                  styles.workbenchPanelItemCard,
                  {
                    backgroundColor: getWorkbenchPanelItemBackground(
                      item.tone,
                      theme,
                    ),
                    borderColor: theme.borderSubtle,
                    borderRadius: layout.controlRadius,
                    gap: tokens.spacing.xs,
                    padding: tokens.spacing.sm,
                  },
                ]}
              >
                <Pressable
                  accessibilityLabel={item.titleText}
                  accessibilityRole="button"
                  accessibilityState={{
                    disabled: !item.targetPatientId && !item.targetDetailTabId,
                  }}
                  aria-disabled={
                    !item.targetPatientId && !item.targetDetailTabId
                  }
                  disabled={!item.targetPatientId && !item.targetDetailTabId}
                  onPress={() => {
                    if (item.targetPatientId) {
                      setSelectedPatientId(item.targetPatientId);
                    }

                    if (item.targetDetailTabId) {
                      setSelectedDetailTabId(item.targetDetailTabId);
                    }
                  }}
                  style={[
                    styles.workbenchPanelItemPressable,
                    {
                      gap: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.workbenchPanelItemLead,
                      {
                        backgroundColor: leadColors.backgroundColor,
                        borderColor: leadColors.borderColor,
                        borderRadius: layout.controlRadius,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.workbenchPanelItemLeadText,
                        {
                          color: leadColors.textColor,
                          fontFamily: layout.bodyFontFamily,
                        },
                      ]}
                    >
                      {leadText}
                    </Text>
                  </View>

                  <View
                    style={[
                      styles.workbenchPanelItemBody,
                      {
                        gap: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.navigationText,
                          {
                            color: theme.textPrimary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.sm,
                          },
                        ]}
                      >
                        {item.titleText}
                      </Text>
                      {item.badgeLabelKey ? (
                        <View
                          style={[
                            styles.inlinePill,
                            {
                              backgroundColor: getWorkbenchPanelBadgeBackground(
                                item,
                                theme,
                              ),
                              borderColor: theme.borderSubtle,
                              borderRadius: 8,
                              paddingHorizontal: tokens.spacing.sm,
                              paddingVertical: tokens.spacing.xs,
                            },
                          ]}
                        >
                          <Text
                            style={[
                              styles.badgeText,
                              {
                                color: getWorkbenchPanelBadgeTextColor(
                                  item,
                                  theme,
                                ),
                                fontFamily: layout.bodyFontFamily,
                                fontSize: tokens.typography.fontSize.xs,
                              },
                            ]}
                          >
                            {t(item.badgeLabelKey)}
                          </Text>
                        </View>
                      ) : null}
                    </View>

                    {item.subtitleText ? (
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.xs,
                          },
                        ]}
                      >
                        {item.subtitleText}
                      </Text>
                    ) : null}

                    {item.supportingText ? (
                      <Text
                        style={[
                          styles.sectionSubtitle,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.sm,
                            lineHeight: tokens.typography.lineHeight.md,
                          },
                        ]}
                      >
                        {resolveProductClinicalText(item.supportingText)}
                      </Text>
                    ) : null}

                    {item.metrics.length > 0 ? (
                      <View
                        style={[
                          styles.flagRow,
                          {
                            gap: tokens.spacing.xs,
                          },
                        ]}
                      >
                        {item.metrics.map((metric) => (
                          <View
                            key={metric.id}
                            style={[
                              styles.inlinePill,
                              {
                                backgroundColor: theme.surface,
                                borderColor: theme.borderSubtle,
                                borderRadius: 8,
                                paddingHorizontal: tokens.spacing.sm,
                                paddingVertical: tokens.spacing.xs,
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.pillValueText,
                                {
                                  color: getDetailSummaryMetricTextColor(
                                    metric.tone,
                                    theme,
                                  ),
                                  fontFamily: layout.bodyFontFamily,
                                },
                              ]}
                            >
                              {`${t(metric.labelKey)} ${metric.valueText}`}
                            </Text>
                          </View>
                        ))}
                      </View>
                    ) : null}

                    {routeText.length > 0 ? (
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.xs,
                          },
                        ]}
                      >
                        {routeText}
                      </Text>
                    ) : null}
                  </View>
                </Pressable>

                {canOpenRoute || (item.action && item.actionLabelKey) ? (
                  <View
                    style={[
                      styles.patientRowHeader,
                      {
                        flexWrap: "wrap",
                        gap: tokens.spacing.xs,
                        justifyContent: "flex-end",
                      },
                    ]}
                  >
                    {canOpenRoute
                      ? renderWorkbenchActionButton({
                          disabled: false,
                          label: item.targetDetailTabId
                            ? "Open detail"
                            : "Open patient",
                          labelLines: 1,
                          minHeight:
                            workforceOperationalLayout.mobileControlHeight,
                          minWidth: clinicianCompactActionButtonWidth,
                          onPress: () => {
                            if (item.targetPatientId) {
                              setSelectedPatientId(item.targetPatientId);
                            }

                            if (item.targetDetailTabId) {
                              setSelectedDetailTabId(item.targetDetailTabId);
                            }
                          },
                          size: "compact",
                          theme,
                          tone: "accent",
                          tokens,
                          width: clinicianCompactActionButtonWidth,
                        })
                      : null}
                    {item.action && item.actionLabelKey
                      ? renderWorkbenchActionButton({
                          disabled: isWorkbenchActionSubmitting,
                          label: t(item.actionLabelKey),
                          labelLines: 1,
                          minHeight:
                            workforceOperationalLayout.mobileControlHeight,
                          minWidth: clinicianCompactActionButtonWidth,
                          onPress: () => {
                            if (item.action != null) {
                              onSubmitWorkbenchAction(item.action);
                            }
                          },
                          size: "compact",
                          theme,
                          tone: resolveWorkbenchActionTone(item.action),
                          tokens,
                          width: clinicianCompactActionButtonWidth,
                        })
                      : null}
                  </View>
                ) : null}
              </View>
            );
          })
        ) : (
          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: theme.surfaceMuted,
                borderColor: theme.borderSubtle,
                borderRadius: workforceOperationalLayout.controlRadius,
                gap: 10,
                padding: tokens.spacing.md,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              {t(
                selectedWorkbenchPanel?.emptyStateKey ??
                  translationKeys.shell.workspacePanelEmptyInbox,
              )}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}

export function renderPatientListCard(context: ListRenderContext) {
  const {
    calendarMonth,
    calendarPendingStart,
    isWideLayout,
    layout,
    legacyStageHeight,
    isRefreshing,
    listSummary,
    listViewMode,
    requiresAttentionOnly,
    searchQuery,
    selectedCalendarRange,
    selectedPatientId,
    selectedPatientTabId,
    setCalendarMonth,
    setCalendarPendingStart,
    setCalendarRange,
    setListViewMode,
    setRequiresAttentionOnly,
    setSearchQuery,
    setSelectedPatientId,
    setSelectedPatientTabId,
    t,
    theme,
    tokens,
    visiblePatients,
  } = context;
  const searchPlaceholderText = t(
    translationKeys.shell.workspaceListSearchPlaceholder,
  );
  const workspaceListTitleText = t(translationKeys.shell.workspaceListTitle);
  const visiblePatientsText = t(
    translationKeys.shell.workspaceListVisiblePatients,
  ).toLowerCase();
  const queueControlHeight = isWideLayout
    ? workforceOperationalLayout.controlHeight
    : workforceOperationalLayout.mobileControlHeight;
  const renderTabHeader = (input: {
    countText: string;
    isSelected: boolean;
    labelText?: string;
    labelKey: TranslationKey;
    onKeyDown?: (event: WebTabKeyDownEvent) => void;
    onPress: () => void;
    tabIndex: 0 | -1;
  }) => {
    const visibleLabelText = input.labelText ?? t(input.labelKey);

    return (
      <Pressable
        accessibilityLabel={t(input.labelKey)}
        accessibilityRole="tab"
        accessibilityState={{ selected: input.isSelected }}
        aria-selected={input.isSelected}
        key={input.labelKey}
        onPress={input.onPress}
        tabIndex={input.tabIndex}
        {...(Platform.OS === "web" && input.onKeyDown
          ? {
              onKeyDown: (event: unknown) =>
                input.onKeyDown?.(event as WebTabKeyDownEvent),
            }
          : {})}
        style={[
          styles.inlinePill,
          {
            alignItems: "center",
            backgroundColor: input.isSelected
              ? theme.accent
              : theme.backgroundSubtle,
            borderColor: input.isSelected
              ? theme.accent
              : theme.borderSubtle,
            borderRadius: workforceOperationalLayout.controlRadius,
            flexDirection: "row",
            flexShrink: 0,
            gap: 4,
            justifyContent: "center",
            minHeight: queueControlHeight,
            paddingHorizontal: 10,
            paddingVertical: 6,
          },
        ]}
      >
        <Text
          numberOfLines={1}
          style={[
            styles.navigationText,
            {
              color: input.isSelected
                ? theme.accentContrast
                : theme.textPrimary,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.table.fontSize,
              fontWeight: "600",
              lineHeight: workforceOperationalTypography.table.lineHeight,
            },
          ]}
        >
          {visibleLabelText}
        </Text>
        <Text
          numberOfLines={1}
          style={[
            styles.listTabCountText,
            {
              color: input.isSelected
                ? theme.accentContrast
                : theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.metadata.fontSize,
              lineHeight: workforceOperationalTypography.metadata.lineHeight,
            },
          ]}
        >
          {input.countText}
        </Text>
      </Pressable>
    );
  };

  const renderAttentionFilterPill = () => (
    <Pressable
      accessibilityLabel="Show patients needing attention"
      accessibilityRole="switch"
      accessibilityState={{ checked: requiresAttentionOnly }}
      aria-checked={requiresAttentionOnly}
      onPress={() => setRequiresAttentionOnly(!requiresAttentionOnly)}
      style={(state) => {
        const stateRecord =
          state && typeof state === "object"
            ? (state as unknown as Record<string, unknown>)
            : {};
        const hovered = stateRecord.hovered === true;
        const focused = stateRecord.focused === true;
        const pressed = stateRecord.pressed === true;

        return [
          styles.inlinePill,
          {
            alignItems: "center",
            backgroundColor: requiresAttentionOnly
              ? theme.statusChip.warning.background
              : pressed || hovered || focused
                ? theme.backgroundSubtle
                : theme.surface,
            borderColor: requiresAttentionOnly
              ? theme.statusChip.warning.border
              : focused || hovered
                ? theme.accentStrong
                : theme.borderSubtle,
            borderRadius: workforceOperationalLayout.controlRadius,
            flex: 1,
            flexDirection: "row",
            gap: 6,
            justifyContent: "center",
            minHeight: queueControlHeight,
            paddingHorizontal: 10,
            paddingVertical: 6,
          },
        ];
      }}
    >
      <LineIcon
        color={requiresAttentionOnly ? theme.warning : theme.textSecondary}
        name="flag"
        size={12}
        strokeWidth={2.4}
      />
      <Text
        numberOfLines={1}
        style={[
          styles.metricLabel,
          {
            color: requiresAttentionOnly ? theme.warning : theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: workforceOperationalTypography.table.fontSize,
            fontWeight: "600",
            lineHeight: workforceOperationalTypography.table.lineHeight,
          },
        ]}
      >
        Attn
      </Text>
      <Text
        style={[
          styles.listTabCountText,
          {
            color: requiresAttentionOnly ? theme.warning : theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: workforceOperationalTypography.metadata.fontSize,
            lineHeight: workforceOperationalTypography.metadata.lineHeight,
          },
        ]}
      >
        {String(listSummary.attentionPatientCount)}
      </Text>
    </Pressable>
  );

  const renderPatientRow = (patient: PatientWorkspaceViewModel) => {
    const isSelected = patient.id === selectedPatientId;
    const pendingApprovalCount = countPendingApprovalMessages(
      patient.chatMessages,
    );
    const latestVisibleMessage = [...patient.chatMessages]
      .reverse()
      .find((message) => !isNoisyDemoProbeMessage(message));
    const latestPreviewText = resolveProductMessageBodyText(
      latestVisibleMessage?.bodyText ?? patient.queueSummaryText,
    );
    const activityTimeText = patient.lastActivityText;
    const rowTextColor = theme.textPrimary;
    const rowMutedTextColor = theme.textSecondary;
    const rowCopy = resolvePatientQueueRowCopy({
      activityPreviewText: latestPreviewText,
      displayName: patient.displayName,
      programText: patient.programText,
      requiresAttention: patient.requiresAttention,
      statusText: patient.statusText,
      summaryText: patient.queueSummaryText,
    });
    const statusChips = resolvePatientQueueStatusChips({
      hasVoiceCallHandoff: patient.hasVoiceCallHandoff,
      openOutboundCallCount: patient.openOutboundCallCount,
      pendingApprovalCount,
      pendingRedFlagCount: patient.pendingRedFlagCount,
      unreadMessageCount: patient.unreadMessageCount,
    });

    return (
      <Pressable
        accessibilityLabel={resolvePatientQueueAccessibilityLabel({
          rowCopy,
          statusChips,
        })}
        accessibilityRole="button"
        accessibilityState={{ selected: isSelected }}
        aria-pressed={isSelected}
        key={patient.id}
        onPress={() => setSelectedPatientId(patient.id)}
        style={(state) => {
          const stateRecord = state as unknown as Record<string, unknown>;
          const hovered = stateRecord.hovered === true;
          const focused = stateRecord.focused === true;

          return [
            styles.patientRow,
            webOutlineReset,
            {
              backgroundColor: isSelected
                ? theme.accentSubtle
                : hovered || focused
                  ? theme.backgroundSubtle
                  : theme.surface,
              borderColor: isSelected ? theme.accentStrong : theme.borderStrong,
              borderLeftColor: isSelected ? theme.accent : theme.borderStrong,
              borderLeftWidth: isSelected ? 3 : 1,
              borderBottomWidth: 1,
              borderRadius: 0,
              borderWidth: 1,
              minHeight: workforceOperationalLayout.rowMinHeight,
              paddingHorizontal: 12,
              paddingVertical: 9,
              shadowOpacity: 0,
            },
          ];
        }}
      >
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "flex-start",
              gap: tokens.spacing.sm,
            },
          ]}
        >
          <View
            style={[
              styles.patientAvatar,
              {
                backgroundColor: theme.onGradientAvatarBackground,
                borderColor: theme.onGradientAvatarBorder,
                borderRadius: 9,
                height: 34,
                width: 34,
              },
            ]}
          >
            <Text
              style={[
                styles.patientAvatarText,
                {
                  color: theme.onGradientAvatarText,
                  fontFamily: layout.bodyFontFamily,
                },
              ]}
            >
              {resolvePatientInitials(patient.displayName)}
            </Text>
          </View>

          <View
            style={[
              styles.patientIdentityCopy,
              {
                gap: 2,
                minWidth: 0,
              },
            ]}
          >
            <Text
              numberOfLines={2}
              style={[
                styles.navigationText,
                {
                  color: rowTextColor,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 13.5,
                  fontWeight: "700",
                  lineHeight: 17,
                },
              ]}
            >
              {rowCopy.nameText}
            </Text>
            {rowCopy.metadataText ? (
              <Text
                numberOfLines={1}
                style={[
                  styles.metricLabel,
                  {
                    color: patient.requiresAttention
                      ? theme.warning
                      : rowMutedTextColor,
                    fontFamily: layout.bodyFontFamily,
                    fontSize:
                      workforceOperationalTypography.metadata.fontSize,
                    fontWeight: patient.requiresAttention ? "700" : "600",
                    lineHeight:
                      workforceOperationalTypography.metadata.lineHeight,
                  },
                ]}
              >
                {rowCopy.metadataText}
              </Text>
            ) : null}
          </View>

          {activityTimeText ? (
            <Text
              style={[
                styles.metricLabel,
                {
                  color: rowMutedTextColor,
                  flexShrink: 0,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  lineHeight:
                    workforceOperationalTypography.metadata.lineHeight,
                },
              ]}
            >
              {activityTimeText}
            </Text>
          ) : null}
        </View>

        {statusChips.length > 0 ? (
          <View
            style={[
              styles.flagRow,
              {
                gap: 5,
                marginLeft: 42,
                marginTop: 6,
              },
            ]}
          >
            {statusChips.map((chip) => {
              const palette =
                chip.tone === "danger"
                  ? theme.statusChip.danger
                  : chip.tone === "warning"
                    ? theme.statusChip.warning
                    : {
                        background: theme.accentSubtle,
                        border: theme.borderSubtle,
                        text: theme.accentStrong,
                      };

              return (
                <View
                  key={chip.key}
                  style={[
                    styles.inlinePill,
                    {
                      backgroundColor: palette.background,
                      borderColor: palette.border,
                      borderRadius: 999,
                      flexShrink: 0,
                      paddingHorizontal: 7,
                      paddingVertical: 2,
                    },
                  ]}
                >
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.pillValueText,
                      {
                        color: palette.text,
                        fontFamily: layout.bodyFontFamily,
                        fontSize:
                          workforceOperationalTypography.metadata.fontSize,
                        fontWeight: "800",
                        lineHeight:
                          workforceOperationalTypography.metadata.lineHeight,
                      },
                    ]}
                  >
                    {chip.labelText}
                  </Text>
                </View>
              );
            })}
          </View>
        ) : null}

        {rowCopy.previewText ? (
          <Text
            numberOfLines={isSelected ? 2 : 1}
            style={[
              styles.sectionSubtitle,
              {
                color: rowMutedTextColor,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                lineHeight:
                  workforceOperationalTypography.metadata.lineHeight,
                marginLeft: 42,
                marginTop: statusChips.length > 0 ? 5 : 4,
              },
            ]}
          >
            {rowCopy.previewText}
          </Text>
        ) : null}
      </Pressable>
    );
  };

  const calendarFilteredPatients =
    selectedCalendarRange != null
      ? visiblePatients.filter(
          (patient) =>
            patient.dateOfService != null &&
            isDateInRange(patient.dateOfService, selectedCalendarRange),
        )
      : calendarPendingStart != null
        ? visiblePatients.filter(
            (patient) => patient.dateOfService === calendarPendingStart,
          )
        : visiblePatients;
  const calendarPresetRanges = buildPresetRanges(
    new Date().toISOString().slice(0, 10),
  );
  const clearCalendarSelection = () => {
    setCalendarRange(null);
    setCalendarPendingStart(null);
  };
  const dateOfServiceBuckets = groupPatientsByDateOfService(
    calendarFilteredPatients,
  );
  const calendarDayCounts = countPatientsByDay(visiblePatients);
  const calendarWeeks = buildMonthGrid(
    calendarMonth.year,
    calendarMonth.monthIndex,
  );
  const shiftCalendarMonth = (offset: number) => {
    const next = new Date(
      Date.UTC(calendarMonth.year, calendarMonth.monthIndex + offset, 1),
    );

    setCalendarMonth({
      year: next.getUTCFullYear(),
      monthIndex: next.getUTCMonth(),
    });
  };
  const renderMonthCalendar = () => (
    <View
      style={{
        backgroundColor: theme.backgroundSubtle,
        borderColor: theme.borderSubtle,
        borderRadius: workforceOperationalLayout.controlRadius,
        borderWidth: 1,
        gap: 4,
        marginBottom: 4,
        padding: 8,
      }}
    >
      <View
        style={{
          alignItems: "center",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Previous month"
          onPress={() => shiftCalendarMonth(-1)}
          style={{
            alignItems: "center",
            height: queueControlHeight,
            justifyContent: "center",
            width: queueControlHeight,
          }}
        >
          <LineIcon
            color={theme.accentStrong}
            name="chevron-left"
            size={17}
          />
        </Pressable>
        <Text
          style={{
            color: theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: 11.5,
            fontWeight: "800",
            lineHeight: 15,
          }}
        >
          {formatMonthTitleText(calendarMonth.year, calendarMonth.monthIndex)}
        </Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Next month"
          onPress={() => shiftCalendarMonth(1)}
          style={{
            alignItems: "center",
            height: queueControlHeight,
            justifyContent: "center",
            width: queueControlHeight,
          }}
        >
          <LineIcon
            color={theme.accentStrong}
            name="chevron-right"
            size={17}
          />
        </Pressable>
      </View>
      <View style={{ flexDirection: "row", gap: 3 }}>
        {calendarPresetRanges.map((preset) => {
          const isActive =
            selectedCalendarRange != null &&
            selectedCalendarRange.start === preset.range.start &&
            selectedCalendarRange.end === preset.range.end;

          return (
            <Pressable
              accessibilityRole="button"
              accessibilityState={{ selected: isActive }}
              aria-pressed={isActive}
              key={preset.key}
              onPress={() => {
                if (isActive) {
                  clearCalendarSelection();
                  return;
                }

                setCalendarRange(preset.range);
                setCalendarPendingStart(null);

                const startYear = Number(preset.range.start.slice(0, 4));
                const startMonthIndex = Number(preset.range.start.slice(5, 7)) - 1;

                setCalendarMonth({ year: startYear, monthIndex: startMonthIndex });
              }}
              style={{
                alignItems: "center",
                backgroundColor: isActive ? theme.accent : theme.surface,
                borderColor: isActive ? theme.accent : theme.borderStrong,
                borderRadius: workforceOperationalLayout.controlRadius,
                borderWidth: 1,
                flexGrow: 1,
                minHeight: isWideLayout ? 30 : queueControlHeight,
                paddingHorizontal: 3,
                paddingVertical: 4,
              }}
            >
              <Text
                numberOfLines={1}
                style={{
                  color: isActive ? theme.accentContrast : theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  fontWeight: "800",
                  lineHeight: workforceOperationalTypography.metadata.lineHeight,
                }}
              >
                {preset.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
      <View style={{ flexDirection: "row" }}>
        {["S", "M", "T", "W", "T", "F", "S"].map((label, index) => (
          <Text
            key={`weekday-${String(index)}`}
            style={{
              color: theme.textSecondary,
              flex: 1,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.metadata.fontSize,
              fontWeight: "700",
              lineHeight: workforceOperationalTypography.metadata.lineHeight,
              textAlign: "center",
            }}
          >
            {label}
          </Text>
        ))}
      </View>
      {calendarWeeks.map((week, weekIndex) => (
        <View key={`week-${String(weekIndex)}`} style={{ flexDirection: "row" }}>
          {week.map((cell) => {
            const count = calendarDayCounts.get(cell.isoDate) ?? 0;
            const isPendingCap = calendarPendingStart === cell.isoDate;
            const isCap =
              isPendingCap ||
              (selectedCalendarRange != null &&
                (cell.isoDate === selectedCalendarRange.start ||
                  cell.isoDate === selectedCalendarRange.end));
            const isWithinRange =
              !isCap &&
              selectedCalendarRange != null &&
              isDateInRange(cell.isoDate, selectedCalendarRange);
            const isSelected = isCap;

            return (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`${cell.isoDate}, ${String(count)} patients`}
                accessibilityState={{
                  disabled: !cell.inMonth,
                  selected: isSelected,
                }}
                aria-disabled={!cell.inMonth}
                aria-pressed={isSelected}
                disabled={!cell.inMonth}
                key={cell.isoDate}
                onPress={() => {
                  const result = applyCalendarDayTap(
                    selectedCalendarRange,
                    calendarPendingStart,
                    cell.isoDate,
                  );

                  setCalendarRange(result.range);
                  setCalendarPendingStart(result.pendingStart);
                }}
                style={{
                  alignItems: "center",
                  backgroundColor: isCap
                    ? theme.accent
                    : isWithinRange
                      ? theme.accentSubtle
                      : "transparent",
                  borderColor: isPendingCap ? theme.accentContrast : "transparent",
                  borderRadius: 6,
                  borderStyle: isPendingCap ? "dashed" : "solid",
                  borderWidth: isPendingCap ? 1.5 : 0,
                  flex: 1,
                  gap: 0,
                  minHeight: isWideLayout ? 26 : queueControlHeight,
                  opacity: cell.inMonth ? 1 : 0.35,
                  paddingVertical: 2,
                }}
              >
                <Text
                  style={{
                    color: isSelected
                      ? theme.accentContrast
                      : theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.metadata.fontSize,
                    fontWeight: count > 0 ? "800" : "500",
                    lineHeight:
                      workforceOperationalTypography.metadata.lineHeight,
                  }}
                >
                  {String(cell.dayNumber)}
                </Text>
                {count > 0 ? (
                  <View
                    style={{
                      backgroundColor: isSelected
                        ? theme.accentContrast
                        : theme.accentStrong,
                      borderRadius: 999,
                      minWidth: 12,
                      paddingHorizontal: 2,
                    }}
                  >
                    <Text
                      style={{
                        color: isSelected ? theme.accent : theme.accentContrast,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: workforceOperationalTypography.metadata.fontSize,
                        fontWeight: "800",
                        lineHeight:
                          workforceOperationalTypography.metadata.lineHeight,
                        textAlign: "center",
                      }}
                    >
                      {String(count)}
                    </Text>
                  </View>
                ) : null}
              </Pressable>
            );
          })}
        </View>
      ))}
      {selectedCalendarRange != null || calendarPendingStart != null ? (
        <View
          style={{
            alignItems: "center",
            flexDirection: "row",
            gap: 6,
            justifyContent: "space-between",
            paddingTop: 2,
          }}
        >
          <Text
            numberOfLines={1}
            style={{
              color: theme.textPrimary,
              flexShrink: 1,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.metadata.fontSize,
              fontWeight: "700",
              lineHeight: workforceOperationalTypography.metadata.lineHeight,
            }}
          >
            {selectedCalendarRange != null
              ? `${formatRangeLabelText(selectedCalendarRange)} · ${String(calendarFilteredPatients.length)} patient${calendarFilteredPatients.length === 1 ? "" : "s"}`
              : `From ${formatDayHeaderText(calendarPendingStart ?? "")}\u2026 tap an end date`}
          </Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Clear date selection"
            onPress={clearCalendarSelection}
            style={{
              alignItems: "center",
              justifyContent: "center",
              minHeight: queueControlHeight,
              minWidth: queueControlHeight,
            }}
          >
            <LineIcon
              color={theme.textSecondary}
              name="x"
              size={16}
              strokeWidth={2.3}
            />
          </Pressable>
        </View>
      ) : null}
    </View>
  );
  const renderPatientsByDateOfService = () => (
    <>
      {renderMonthCalendar()}
      {dateOfServiceBuckets.length === 0 ? (
        <Text
          style={{
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: 11,
            lineHeight: 14,
            paddingVertical: 6,
            textAlign: "center",
          }}
        >
          No patients in this range.
        </Text>
      ) : null}
      {dateOfServiceBuckets.map((bucket) => (
        <View key={bucket.isoDate ?? "no-dos"} style={{ gap: 6 }}>
          <Text
            style={{
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.metadata.fontSize,
              fontWeight: "800",
              letterSpacing: 0,
              lineHeight: workforceOperationalTypography.metadata.lineHeight,
              paddingTop: 4,
              textTransform: "uppercase",
            }}
          >
            {bucket.headerText}
          </Text>
          {bucket.patients.map(renderPatientRow)}
        </View>
      ))}
    </>
  );
  const listContent = (
    <View
      style={[
        styles.listRailContent,
        {
          backgroundColor: "transparent",
          borderColor: "transparent",
          borderTopWidth: 0,
          flex: 1,
          gap: 8,
          minHeight: 0,
          overflow: "hidden",
          paddingHorizontal: clinicianRailInset,
          paddingVertical: clinicianRailInset,
        },
      ]}
    >
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "center",
            gap: tokens.spacing.xs,
            paddingHorizontal: 2,
            paddingVertical: 0,
          },
        ]}
      >
        <View style={[styles.listGroup, { flex: 1, gap: 1, minWidth: 0 }]}>
          <Text
            style={[
              styles.navigationText,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: 15,
                lineHeight: 18,
              },
            ]}
            numberOfLines={1}
          >
            {selectedPatientTabId === "my"
              ? "My Patients"
              : workspaceListTitleText}
          </Text>
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                fontWeight: "600",
                letterSpacing: 0,
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              },
            ]}
            numberOfLines={1}
          >
            {`${listSummary.visiblePatientCount} ${visiblePatientsText} - ${listSummary.attentionPatientCount} need attention`}
          </Text>
        </View>
        {isRefreshing ? (
          <View
            style={[
              styles.inlinePill,
              {
                alignItems: "center",
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                flexDirection: "row",
                gap: tokens.spacing.xs,
                minHeight: 28,
                paddingHorizontal: tokens.spacing.sm,
                paddingVertical: 3,
              },
            ]}
          >
            <ActivityIndicator color={theme.accentStrong} size="small" />
            <Text
              style={[
                styles.pillValueText,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                },
              ]}
            >
              Updating
            </Text>
          </View>
        ) : null}
      </View>

      <View
        style={[
          styles.searchSurface,
          {
            alignItems: "center",
            backgroundColor: theme.surface,
            borderColor: theme.borderSubtle,
            borderRadius: workforceOperationalLayout.controlRadius,
            borderWidth: 1,
            flexDirection: "row",
            gap: tokens.spacing.xs,
            minHeight: queueControlHeight,
            paddingHorizontal: 12,
            paddingVertical: 6,
          },
        ]}
      >
        <LineIcon
          color={theme.textSecondary}
          name="search"
          size={15}
          strokeWidth={2.25}
        />
        <TextInput
          accessibilityLabel="Search patients"
          onChangeText={setSearchQuery}
          placeholder={searchPlaceholderText}
          placeholderTextColor={theme.textTertiary}
          style={[
            styles.searchInput,
            {
              color: theme.textPrimary,
              flex: 1,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.body.fontSize,
              lineHeight: workforceOperationalTypography.body.lineHeight,
            },
          ]}
          value={searchQuery}
        />
      </View>

      <View
        style={[
          styles.flagRow,
          {
            flexWrap: "nowrap",
            gap: 5,
            paddingHorizontal: 0,
          },
        ]}
      >
        <View
          accessibilityLabel="Patient queue scopes"
          role="tablist"
          style={{ flexDirection: "row", flexShrink: 1, gap: 5 }}
        >
          {renderTabHeader({
            countText: String(listSummary.myPatientsCount),
            isSelected: selectedPatientTabId === "my",
            labelText: "My",
            labelKey: translationKeys.shell.workspaceListMyPatients,
            onKeyDown: (event) =>
              handleHorizontalTabKeyDown(event, 0, 2, (nextIndex) =>
                setSelectedPatientTabId(nextIndex === 0 ? "my" : "all"),
              ),
            onPress: () => {
              setSelectedPatientTabId("my");
            },
            tabIndex: selectedPatientTabId === "my" ? 0 : -1,
          })}
          {renderTabHeader({
            countText: String(listSummary.allPatientsCount),
            isSelected: selectedPatientTabId === "all",
            labelText: "All",
            labelKey: translationKeys.shell.workspaceListAllPatients,
            onKeyDown: (event) =>
              handleHorizontalTabKeyDown(event, 1, 2, (nextIndex) =>
                setSelectedPatientTabId(nextIndex === 0 ? "my" : "all"),
              ),
            onPress: () => {
              setSelectedPatientTabId("all");
            },
            tabIndex: selectedPatientTabId === "all" ? 0 : -1,
          })}
        </View>
        {renderAttentionFilterPill()}
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ selected: listViewMode === "by-dos" }}
          aria-pressed={listViewMode === "by-dos"}
          accessibilityLabel="Toggle surgery-date view"
          onPress={() => {
            setListViewMode(listViewMode === "by-dos" ? "list" : "by-dos");
            setCalendarPendingStart(null);
          }}
          style={{
            alignItems: "center",
            backgroundColor:
              listViewMode === "by-dos" ? theme.accent : theme.surface,
            borderColor:
              listViewMode === "by-dos" ? theme.accent : theme.borderStrong,
            borderRadius: workforceOperationalLayout.controlRadius,
            borderWidth: 1,
            justifyContent: "center",
            marginLeft: "auto",
            minHeight: queueControlHeight,
            minWidth: queueControlHeight,
            paddingHorizontal: 7,
            paddingVertical: 6,
          }}
        >
          <LineIcon
            color={
              listViewMode === "by-dos"
                ? theme.accentContrast
                : theme.textSecondary
            }
            name="appointment"
            size={16}
            strokeWidth={2.25}
          />
        </Pressable>
      </View>

      <ScrollView
        showsVerticalScrollIndicator={false}
        style={[
          styles.patientListScroll,
          legacyStageHeight != null
            ? {
                flex: 1,
                minHeight: 0,
              }
            : {
                maxHeight: 620,
              },
        ]}
        contentContainerStyle={[
          styles.listGroup,
          {
            gap: 0,
          },
        ]}
      >
        {visiblePatients.length === 0 ? (
          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: theme.surfaceMuted,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                padding: tokens.spacing.md,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              {t(translationKeys.shell.workspaceListNoPatients)}
            </Text>
            <Pressable
              accessibilityRole="button"
              onPress={() => {
                setSearchQuery("");
                setRequiresAttentionOnly(false);
                setListViewMode("list");
                clearCalendarSelection();
              }}
              style={({ pressed }) => ({
                alignItems: "center",
                alignSelf: "flex-start",
                backgroundColor: theme.surface,
                borderColor: theme.borderStrong,
                borderRadius: workforceOperationalLayout.controlRadius,
                borderWidth: 1,
                flexDirection: "row",
                gap: 6,
                justifyContent: "center",
                minHeight: queueControlHeight,
                opacity: pressed ? 0.7 : 1,
                paddingHorizontal: 11,
              })}
            >
              <LineIcon color={theme.textSecondary} name="x" size={15} />
              <Text
                style={{
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.table.fontSize,
                  fontWeight: "700",
                  lineHeight: workforceOperationalTypography.table.lineHeight,
                }}
              >
                Clear filters
              </Text>
            </Pressable>
          </View>
        ) : listViewMode === "by-dos" ? (
          renderPatientsByDateOfService()
        ) : (
          visiblePatients.map(renderPatientRow)
        )}
      </ScrollView>
    </View>
  );

  return (
    <View
      style={[
        styles.listCard,
        {
          backgroundColor: theme.surface,
          borderColor: theme.borderStrong,
          borderRadius: workforceOperationalLayout.panelRadius,
          borderWidth: 1,
          flexShrink: 0,
          gap: 0,
          height: legacyStageHeight ?? undefined,
          maxWidth: isWideLayout
            ? workforceOperationalLayout.queueWidth
            : undefined,
          maxHeight: legacyStageHeight ?? 400,
          minHeight: legacyStageHeight ?? undefined,
          overflow: "hidden",
          padding: 0,
          shadowOpacity: 0,
          width: isWideLayout ? workforceOperationalLayout.queueWidth : "100%",
        },
      ]}
    >
      {listContent}
    </View>
  );
}

export function renderChatCard(context: ChatRenderContext) {
  const {
    callTypeFilter,
    centerPaneView,
    clinicianConversationScrollRef,
    composerDraft,
    composerMode,
    editedReviewBodiesByMessageId,
    isWorkbenchActionSubmitting,
    isWideLayout,
    layout,
    legacyStageHeight,
    legacyClinicianAvatarSource,
    onAddSelectedPatientToMyList,
    onApproveMessageReview,
    onBeginReviewEdit,
    onChangeReviewEditDraft,
    onConversationContentSizeChange,
    onConversationScroll,
    onCreateComposerNoteFromDraft,
    onRejectMessageReview,
    onSaveReviewEdit,
    onDownloadOutboundQuestionnaireAnswersPdf,
    onOpenOutboundQuestionnaireAnswers,
    onOpenVoiceCallTranscript,
    onProfileDiscardConfirmationOpenChange,
    onProfileDirtyChange,
    onProfileFocusSectionHandled,
    onProfileModalOpenChange,
    onSendReply,
    onStartOutboundQuestionnaireCall,
    onSubmitWorkbenchAction,
    onSubmitOutboundManualQuestionnaireCommand,
    outboundManualCommandPending,
    outboundQuestionnaireCalls,
    outboundQuestionnaireErrorText,
    outboundQuestionnaireIsLoading,
    outboundQuestionnaireNoticePending,
    outboundQuestionnaireNoticeText,
    outboundQuestionnairePdfPendingTargetId,
    outboundQuestionnairePendingTargetId,
    onRefreshOutboundQuestionnaireCalls,
    patientIdentityText,
    profileDiscardChangesRequestKey,
    profileDiscardConfirmationDismissRequestKey,
    profileFocusSection,
    profileNavigationConfirmationOpen,
    questionnaireDefinition,
    reviewEditDraft,
    reviewEditingMessageId,
    selectedPatient,
    setCallTypeFilter,
    setCenterPaneView,
    setComposerDraft,
    setComposerMode,
    showsLegacyWebAssets,
    t,
    theme,
    tokens,
    workbenchActionFeedbackKey,
    workbenchActionFeedbackText,
  } = context;
  const showAddAction =
    selectedPatient != null &&
    !selectedPatient.inMyPatients &&
    selectedPatient.canAddToMyPatients;
  const showLegacyChatStage =
    showsLegacyWebAssets && isWideLayout && selectedPatient != null;
  const wideLegacyStageHeight = legacyStageHeight ?? 535;
  const conversationMessages = selectedPatient
    ? buildReviewFirstChatMessages(selectedPatient.chatMessages)
    : [];
  const pendingApprovalCount = selectedPatient
    ? countPendingApprovalMessages(conversationMessages)
    : 0;
  const pendingReviewMessages = conversationMessages.filter(
    (message) => message.review?.state === "pending-approval",
  );
  const inboundHubCalls = selectedPatient?.voiceCallSummaries ?? [];
  const isTerminalOutboundQuestionnaireCall = (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ): boolean =>
    call.sessionStatus === "completed" || call.sessionStatus === "review_required";
  const formatOutboundQuestionnaireProgressText = (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ): string => {
    if (
      call.sessionStatus === "review_required" &&
      call.totalQuestionCount > call.answeredQuestionCount
    ) {
      return `${String(call.answeredQuestionCount)} captured; review required`;
    }

    if (
      call.sessionStatus === "completed" &&
      call.totalQuestionCount > call.answeredQuestionCount
    ) {
      return `${String(call.answeredQuestionCount)} captured; conditional questions skipped`;
    }

    return call.totalQuestionCount > 0
      ? `${String(call.answeredQuestionCount)} of ${String(
          call.totalQuestionCount,
        )} answered`
      : `${String(call.answeredQuestionCount)} answered`;
  };
  const resolveOutboundHubCallRank = (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ): number => {
    if (
      call.sessionStatus != null &&
      ![
        "cancelled",
        "completed",
        "expired",
        "failed",
        "review_required",
      ].includes(call.sessionStatus)
    ) {
      return 0;
    }

    if (call.queuedScheduledFor != null || call.canStartQueuedCall) {
      return 1;
    }

    return 2;
  };
  const sortedOutboundHubCalls = [...outboundQuestionnaireCalls].sort(
    (left, right) =>
      resolveOutboundHubCallRank(left) - resolveOutboundHubCallRank(right),
  );
  const primaryOutboundCallAction = (() => {
    const actionableCall = sortedOutboundHubCalls.find(
      (call) => call.primaryAction != null,
    );

    if (!actionableCall) {
      return null;
    }

    return {
      call: actionableCall,
      labelText: actionableCall.primaryActionLabel ?? "Start call",
    };
  })();
  const totalHubCallCount =
    outboundQuestionnaireCalls.length + inboundHubCalls.length;
  const hubAttemptCount =
    outboundQuestionnaireCalls.reduce(
      (sum, call) => sum + call.attemptCount,
      0,
    ) + inboundHubCalls.length;
  const hubAnsweredCount = outboundQuestionnaireCalls.reduce(
    (sum, call) => sum + call.answeredQuestionCount,
    0,
  );
  const hubTotalQuestionCount = outboundQuestionnaireCalls.reduce(
    (sum, call) => sum + call.totalQuestionCount,
    0,
  );
  const hubRedFlagCount = outboundQuestionnaireCalls.reduce(
    (sum, call) => sum + call.redFlagCount,
    0,
  );
  const hasTerminalOnlyOutboundProgress =
    outboundQuestionnaireCalls.length > 0 &&
    outboundQuestionnaireCalls.every(isTerminalOutboundQuestionnaireCall) &&
    hubTotalQuestionCount > hubAnsweredCount;
  const showPhoneCallsView =
    centerPaneView === "phone-calls" && selectedPatient != null;
  const showPatientProfileView =
    centerPaneView === "patient-profile" && selectedPatient != null;
  const compactSurfaceTitleText =
    centerPaneView === "patient-profile"
      ? "Patient details"
      : centerPaneView === "phone-calls"
        ? "Phone calls"
        : t(translationKeys.shell.workspaceChatTitle);
  const compactSurfaceSubtitleText =
    centerPaneView === "patient-profile"
      ? "Identity, case, contacts, coverage, and chart documents."
      : centerPaneView === "phone-calls"
        ? "Questionnaire rounds, inbound calls, and follow-up history."
        : t(translationKeys.shell.workspaceChatSubtitle);
  const humanizeHubStatusText = (value: string): string => {
    const normalized = value.replaceAll("_", " ").trim();

    return normalized.length > 0
      ? `${normalized[0]?.toUpperCase() ?? ""}${normalized.slice(1)}`
      : "Not started";
  };
  const renderHubActionButton = (input: {
    disabled: boolean;
    labelText: string;
    onPress: () => void;
    tone: "ghost" | "secondary";
  }) => (
    <Pressable
      accessibilityRole="button"
      disabled={input.disabled}
      key={input.labelText}
      onPress={input.onPress}
      style={{
        alignItems: "center",
        backgroundColor:
          input.tone === "secondary" ? theme.surface : "transparent",
        borderColor:
          input.tone === "secondary" ? theme.borderStrong : "transparent",
        borderRadius: workforceOperationalLayout.controlRadius,
        borderWidth: input.tone === "secondary" ? 1 : 0,
        justifyContent: "center",
        minHeight: isWideLayout
          ? workforceOperationalLayout.controlHeight
          : workforceOperationalLayout.mobileControlHeight,
        opacity: input.disabled ? 0.55 : 1,
        paddingHorizontal: 12,
        paddingVertical: 5,
      }}
    >
      <Text
        style={[
          styles.pillValueText,
          {
            color:
              input.tone === "secondary"
                ? theme.textPrimary
                : theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: workforceOperationalTypography.table.fontSize,
            fontWeight: "700",
            lineHeight: workforceOperationalTypography.table.lineHeight,
          },
        ]}
      >
        {input.labelText}
      </Text>
    </Pressable>
  );
  const renderClinicianCenterPaneToggle = () => {
    if (selectedPatient == null) {
      return null;
    }

    const toggleOptions: ReadonlyArray<{
      countText: string | null;
      iconName: LineIconName;
      id: ClinicianCenterPaneView;
      labelText: string;
    }> = [
      {
        countText: null,
        iconName: "message",
        id: "conversation",
        labelText: "Conversation",
      },
      {
        countText: outboundQuestionnaireIsLoading
          ? null
          : String(totalHubCallCount),
        iconName: "phone",
        id: "phone-calls",
        labelText: "Phone calls",
      },
      {
        countText: null,
        iconName: "user",
        id: "patient-profile",
        labelText: "Details",
      },
    ];

    return (
      <View
        style={{
          alignItems: "center",
          paddingBottom: 4,
          paddingTop: 10,
          width: "100%",
        }}
      >
        <View
          accessibilityLabel="Patient workspace sections"
          role="tablist"
          style={{
            backgroundColor: theme.backgroundSubtle,
            borderColor: theme.borderStrong,
            borderRadius: workforceOperationalLayout.controlRadius,
            borderWidth: 1,
            flexDirection: "row",
            gap: 3,
            justifyContent: "center",
            maxWidth: "100%",
            padding: 3,
            width: "100%",
          }}
        >
          {toggleOptions.map((option, optionIndex) => {
            const isSelected = centerPaneView === option.id;

            return (
              <Pressable
                accessibilityRole="tab"
                accessibilityState={{ selected: isSelected }}
                aria-selected={isSelected}
                key={option.id}
                onPress={() => setCenterPaneView(option.id)}
                tabIndex={isSelected ? 0 : -1}
                {...(Platform.OS === "web"
                  ? {
                      onKeyDown: (event: unknown) =>
                        handleHorizontalTabKeyDown(
                          event as WebTabKeyDownEvent,
                          optionIndex,
                          toggleOptions.length,
                          (nextIndex) =>
                            setCenterPaneView(toggleOptions[nextIndex].id),
                        ),
                    }
                  : {})}
                style={{
                  alignItems: "center",
                  backgroundColor: isSelected ? theme.accent : "transparent",
                  borderRadius: workforceOperationalLayout.controlRadius,
                  flex: 1,
                  flexDirection: "row",
                  gap: 6,
                  justifyContent: "center",
                  minHeight: isWideLayout
                    ? workforceOperationalLayout.controlHeight
                    : workforceOperationalLayout.mobileControlHeight,
                  minWidth: 0,
                  paddingHorizontal: isWideLayout ? 14 : 6,
                  paddingVertical: 6,
                }}
              >
                <LineIcon
                  color={
                    isSelected ? theme.accentContrast : theme.textSecondary
                  }
                  name={option.iconName}
                  size={16}
                  strokeWidth={2.25}
                />
                <Text
                  numberOfLines={1}
                  style={[
                    styles.pillValueText,
                    {
                      color: isSelected
                        ? theme.accentContrast
                        : theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      flexShrink: 1,
                      fontSize: workforceOperationalTypography.table.fontSize,
                      fontWeight: "700",
                      lineHeight:
                        workforceOperationalTypography.table.lineHeight,
                    },
                  ]}
                >
                  {option.labelText}
                </Text>
                {option.countText != null ? (
                  <View
                    style={{
                      backgroundColor: isSelected
                        ? "rgba(255, 255, 255, 0.25)"
                        : theme.borderStrong,
                      borderRadius: 999,
                      paddingHorizontal: 7,
                      paddingVertical: 1,
                    }}
                  >
                    <Text
                      style={[
                        styles.pillValueText,
                        {
                          color: isSelected
                            ? theme.accentContrast
                            : theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize:
                            workforceOperationalTypography.metadata.fontSize,
                          fontWeight: "800",
                          lineHeight:
                            workforceOperationalTypography.metadata.lineHeight,
                        },
                      ]}
                    >
                      {option.countText}
                    </Text>
                  </View>
                ) : null}
              </Pressable>
            );
          })}
        </View>
      </View>
    );
  };
  const renderOutboundHubCard = (
    call: ClinicianWorkbenchOutboundQuestionnaireCallV1,
  ) => {
    const pending = outboundQuestionnairePendingTargetId === call.targetId;
    const pdfPending = outboundQuestionnairePdfPendingTargetId === call.targetId;
    const rank = resolveOutboundHubCallRank(call);
    const statusChip =
      call.sessionStatus === "completed"
        ? {
            ...theme.statusChip.success,
            labelText: "Completed",
          }
        : call.sessionStatus === "review_required"
          ? {
              ...theme.statusChip.warning,
              labelText: "Review required",
            }
        : rank === 0
          ? {
              ...theme.statusChip.warning,
              labelText: "In progress",
            }
          : rank === 1
            ? {
                ...theme.statusChip.success,
                labelText: "Scheduled",
              }
            : {
                background: theme.surfaceMuted,
                border: theme.borderSubtle,
                labelText: humanizeHubStatusText(
                  call.sessionStatus ?? call.targetStatus,
                ),
                text: theme.textSecondary,
              };
    const progressPercent =
      call.sessionStatus === "completed"
        ? 100
        : call.totalQuestionCount > 0
        ? Math.min(
            100,
            (call.answeredQuestionCount / call.totalQuestionCount) * 100,
          )
        : 0;
    const lastAttemptText = formatOutboundAnswerPreviewTimestampText(
      call.lastAttemptAt,
    );
    const previewEntries = call.answerPreview.slice(-3);
    const yellowFlagCount = call.answerPreview.filter(
      (entry) =>
        entry.reviewSignal === "yellow_flag" ||
        (entry.clinicianReviewRequired && !entry.redFlagTriggered),
    ).length;
    const changedAnswerCount = call.answerPreview.filter(
      (entry) =>
        entry.answerStatus !== "redacted" &&
        (entry.changedSinceFirstAnswer === true ||
          entry.answerStatus === "changed"),
    ).length;
    const redactedAnswerCount = call.answerPreview.filter(
      (entry) => entry.answerStatus === "redacted",
    ).length;
    const medicationEntryCount = call.answerPreview.reduce(
      (total, entry) => total + resolveOutboundMedicationEntryCount(entry),
      0,
    );
    const medicationStatusCount = call.answerPreview.filter(
      (entry) => formatOutboundMedicationReconciliationLabel(entry) != null,
    ).length;
    const versionMeta = formatOutboundQuestionnaireVersionMeta(call);
    const metaParts = [formatOutboundQuestionnaireProgressText(call)];

    if (versionMeta != null) {
      metaParts.push(versionMeta);
    }

    if (call.redFlagCount > 0) {
      metaParts.push(
        `${String(call.redFlagCount)} red flag${
          call.redFlagCount === 1 ? "" : "s"
        } routed to review`,
      );
    }
    if (yellowFlagCount > 0) {
      metaParts.push(
        `${String(yellowFlagCount)} yellow flag${
          yellowFlagCount === 1 ? "" : "s"
        }`,
      );
    }
    if (changedAnswerCount > 0) {
      metaParts.push(
        `${String(changedAnswerCount)} changed answer${
          changedAnswerCount === 1 ? "" : "s"
        }`,
      );
    }
    if (redactedAnswerCount > 0) {
      metaParts.push(`${String(redactedAnswerCount)} changed path removed`);
    }
    if (medicationEntryCount > 0) {
      metaParts.push(formatOutboundMedicationEntryCountLabel(medicationEntryCount));
    } else if (medicationStatusCount > 0) {
      metaParts.push(
        `${String(medicationStatusCount)} medication status${
          medicationStatusCount === 1 ? "" : "es"
        }`,
      );
    }

    return (
      <View
        key={call.targetId}
        style={{
          backgroundColor: theme.surface,
          borderColor: theme.borderStrong,
          borderLeftColor: theme.accent,
          borderLeftWidth: 3,
          borderRadius: workforceOperationalLayout.panelRadius,
          borderWidth: 1,
          gap: 8,
          padding: 13,
        }}
      >
        <View
          style={[
            styles.patientRowHeader,
            { alignItems: "center", gap: 9 },
          ]}
        >
          <View
            style={{
              alignItems: "center",
              backgroundColor: theme.accentSubtle,
              borderRadius: workforceOperationalLayout.controlRadius,
              height: 32,
              justifyContent: "center",
              width: 32,
            }}
          >
            <LineIcon color={theme.accentStrong} name="case" size={17} />
          </View>
          <View style={{ flex: 1, gap: 1, minWidth: 0 }}>
            <Text
              numberOfLines={isWideLayout ? 1 : 2}
              style={[
                styles.navigationText,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.body.fontSize,
                  fontWeight: "800",
                  lineHeight: workforceOperationalTypography.body.lineHeight,
                },
              ]}
            >
              {isWideLayout
                ? `${call.questionnaireDisplayName} · attempt ${String(
                    call.attemptCount,
                  )}`
                : call.questionnaireDisplayName}
            </Text>
            <Text
              numberOfLines={isWideLayout ? 1 : 2}
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  lineHeight: workforceOperationalTypography.metadata.lineHeight,
                },
              ]}
            >
              {[
                isWideLayout
                  ? call.campaignDisplayName
                  : `Attempt ${String(call.attemptCount)}`,
                lastAttemptText,
                call.callWindowLabel,
              ]
                .filter((part) => part != null && part.length > 0)
                .join(" · ")}
            </Text>
          </View>
          <View
            style={{
              backgroundColor: statusChip.background,
              borderColor: statusChip.border,
              borderRadius: 999,
              borderWidth: 1,
              paddingHorizontal: 9,
              paddingVertical: 2,
            }}
          >
            <Text
              style={[
                styles.pillValueText,
                {
                  color: statusChip.text,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  fontWeight: "800",
                  lineHeight: workforceOperationalTypography.metadata.lineHeight,
                },
              ]}
            >
              {statusChip.labelText}
            </Text>
          </View>
        </View>

        {call.totalQuestionCount > 0 ? (
          <View
            style={{
              backgroundColor: theme.borderSubtle,
              borderRadius: 999,
              height: 5,
              overflow: "hidden",
              width: "100%",
            }}
          >
            <View
              style={{
                backgroundColor: theme.accent,
                borderRadius: 999,
                height: 5,
                width: `${String(progressPercent)}%` as `${number}%`,
              }}
            />
          </View>
        ) : null}

        {previewEntries.length > 0 ? (
          <View
            style={{
              backgroundColor: theme.backgroundSubtle,
              borderColor: theme.borderSubtle,
              borderRadius: workforceOperationalLayout.controlRadius,
              borderWidth: 1,
              paddingHorizontal: 11,
              paddingVertical: 8,
            }}
          >
            <View style={{ gap: 6 }}>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.metadata.fontSize,
                    fontWeight: "800",
                    lineHeight:
                      workforceOperationalTypography.metadata.lineHeight,
                    textTransform: "uppercase",
                  },
                ]}
              >
                Latest answers
              </Text>
              {previewEntries.map((entry) => (
                <View key={entry.questionKey} style={{ gap: 2 }}>
                  <Text
                    numberOfLines={2}
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 11,
                        fontWeight: "700",
                        lineHeight: 15,
                      },
                    ]}
                  >
                    {`Q: ${resolveQuestionnaireReviewPromptText(
                      entry.questionKey,
                      entry.promptText,
                    )}`}
                  </Text>
                  <Text
                    numberOfLines={2}
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: entry.redFlagTriggered
                          ? theme.statusChip.danger.text
                          : theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 11,
                        lineHeight: 15,
                      },
                    ]}
                  >
                    {entry.redFlagTriggered
                      ? `A: ${entry.answerSummary} (red flag)`
                      : `A: ${entry.answerSummary}`}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        ) : null}

        <View
          style={[
            styles.patientRowHeader,
            { alignItems: "center", flexWrap: "wrap", gap: 8 },
          ]}
        >
          <Text
            numberOfLines={1}
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                flex: 1,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                fontWeight: "700",
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              },
            ]}
          >
            {metaParts.join(" · ")}
          </Text>
          {call.answerPreview.length > 0
            ? renderHubActionButton({
                disabled: false,
                labelText: "View answers",
                onPress: () => onOpenOutboundQuestionnaireAnswers(call),
                tone: "ghost",
              })
            : null}
          {call.redFlagCount > 0 && call.redFlagsReviewed === false
            ? renderHubActionButton({
                // Reviewing red flags requires the FULL answer context — the
                // card previews only the last three answers, so this opens
                // the answers modal where every flagged answer is visible and
                // the review workflow (assign / mark reviewed / escalate)
                // lives.
                disabled: isWorkbenchActionSubmitting,
                labelText: "Review flags",
                onPress: () => onOpenOutboundQuestionnaireAnswers(call),
                tone: "secondary",
              })
            : null}
          {call.primaryAction != null
            ? renderHubActionButton({
                disabled:
                  outboundQuestionnairePendingTargetId != null ||
                  isWorkbenchActionSubmitting,
                labelText: pending
                  ? "Working..."
                  : call.primaryActionLabel ?? "Start call",
                onPress: () => onStartOutboundQuestionnaireCall(call),
                tone: "secondary",
              })
            : null}
          {call.answeredQuestionCount > 0
            ? renderHubActionButton({
                disabled: pdfPending,
                labelText: pdfPending
                  ? "Preparing PDF..."
                  : "Download PDF",
                onPress: () =>
                  onDownloadOutboundQuestionnaireAnswersPdf(call),
                tone: "secondary",
              })
            : null}
        </View>
      </View>
    );
  };
  const renderInboundHubCard = (voiceCall: VoiceCallSummaryViewModel) => (
    <View
      key={voiceCall.id}
      style={{
        backgroundColor: theme.surface,
        borderColor: theme.borderStrong,
        borderLeftColor: theme.textTertiary,
        borderLeftWidth: 3,
        borderRadius: workforceOperationalLayout.panelRadius,
        borderWidth: 1,
        gap: 8,
        padding: 13,
      }}
    >
      <View
        style={[styles.patientRowHeader, { alignItems: "center", gap: 9 }]}
      >
        <View
          style={{
            alignItems: "center",
            backgroundColor: theme.surfaceMuted,
            borderRadius: workforceOperationalLayout.controlRadius,
            height: 32,
            justifyContent: "center",
            width: 32,
          }}
        >
          <LineIcon color={theme.textSecondary} name="phone" size={17} />
        </View>
        <View style={{ flex: 1, gap: 1, minWidth: 0 }}>
          <Text
            numberOfLines={1}
            style={[
              styles.navigationText,
              {
                color: theme.textPrimary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.body.fontSize,
                fontWeight: "800",
                lineHeight: workforceOperationalTypography.body.lineHeight,
              },
            ]}
          >
            {`Inbound · ${voiceCall.questionClassText ?? "patient call"}`}
          </Text>
          <Text
            numberOfLines={1}
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              },
            ]}
          >
            {`${voiceCall.receivedAtText} · ${String(
              voiceCall.turnCount,
            )} turns`}
          </Text>
        </View>
        <View
          style={{
            backgroundColor: voiceCall.hasClinicianHandoff
              ? theme.statusChip.danger.background
              : theme.surfaceMuted,
            borderColor: voiceCall.hasClinicianHandoff
              ? theme.statusChip.danger.border
              : theme.borderSubtle,
            borderRadius: 999,
            borderWidth: 1,
            paddingHorizontal: 9,
            paddingVertical: 2,
          }}
        >
          <Text
            style={[
              styles.pillValueText,
              {
                color: voiceCall.hasClinicianHandoff
                  ? theme.statusChip.danger.text
                  : theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                fontWeight: "800",
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              },
            ]}
          >
            {voiceCall.hasClinicianHandoff ? "Staff handoff" : "Inbound"}
          </Text>
        </View>
      </View>
      <View
        style={[styles.patientRowHeader, { alignItems: "center", gap: 8 }]}
      >
        <Text
          numberOfLines={1}
          style={[
            styles.metricLabel,
            {
              color: theme.textSecondary,
              flex: 1,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.metadata.fontSize,
              fontWeight: "700",
              lineHeight: workforceOperationalTypography.metadata.lineHeight,
            },
          ]}
        >
          {voiceCall.previewText}
        </Text>
        {renderHubActionButton({
          disabled: false,
          labelText: "View transcript",
          onPress: () => onOpenVoiceCallTranscript(voiceCall),
          tone: "ghost",
        })}
      </View>
    </View>
  );
  const renderClinicianPhoneCallsSurface = () => {
    if (selectedPatient == null) {
      return null;
    }

    const visibleOutboundCalls =
      callTypeFilter === "inbound" ? [] : sortedOutboundHubCalls;
    const visibleInboundCalls =
      callTypeFilter === "questionnaire" ? [] : inboundHubCalls;
    const filterOptions: ReadonlyArray<{
      id: ClinicianCallTypeFilter;
      labelText: string;
    }> = [
      {
        id: "all",
        labelText: outboundQuestionnaireIsLoading
          ? "All"
          : `All ${String(totalHubCallCount)}`,
      },
      {
        id: "questionnaire",
        labelText: `${
          isWideLayout ? "Questionnaire" : "AI calls"
        }${
          outboundQuestionnaireIsLoading
            ? ""
            : ` ${String(outboundQuestionnaireCalls.length)}`
        }`,
      },
      {
        id: "inbound",
        labelText: `Inbound ${String(inboundHubCalls.length)}`,
      },
    ];
    const metricTiles = [
      {
        iconName: "phone" as LineIconName,
        id: "attempts",
        labelText: "Calls and attempts",
        valueText: String(hubAttemptCount),
      },
      // The answered counter only describes outbound questionnaire progress —
      // for a patient with inbound-only history a "0 of 0 Answered" tile is
      // noise, so it renders only when questionnaire calls exist.
      ...(outboundQuestionnaireCalls.length > 0
        ? [
            {
              iconName:
                hubRedFlagCount > 0 ? ("flag" as LineIconName) : ("check" as LineIconName),
              id: "progress",
              labelText:
                hasTerminalOnlyOutboundProgress
                  ? "Captured answers"
                  : hubRedFlagCount > 0
                    ? "Answered · red flags"
                    : "Answered",
              valueText:
                hubRedFlagCount > 0
                  ? hasTerminalOnlyOutboundProgress
                    ? `${String(hubAnsweredCount)} captured · ${String(
                        hubRedFlagCount,
                      )} flagged`
                    : `${String(hubAnsweredCount)} of ${String(
                        hubTotalQuestionCount,
                      )} · ${String(hubRedFlagCount)} flagged`
                  : hasTerminalOnlyOutboundProgress
                    ? `${String(hubAnsweredCount)} captured`
                    : `${String(hubAnsweredCount)} of ${String(
                        hubTotalQuestionCount,
                      )}`,
            },
          ]
        : []),
    ];

    return (
      <View
        style={{
          flex: showLegacyChatStage ? 1 : undefined,
          gap: 10,
          minHeight: 0,
          paddingBottom: 14,
          paddingHorizontal: 16,
          paddingTop: 6,
          width: "100%",
        }}
      >
        <View
          accessibilityLabel="Call history filters"
          role="tablist"
          style={[
            styles.patientRowHeader,
            { alignItems: "center", flexWrap: "wrap", gap: 6 },
          ]}
        >
          {filterOptions.map((option, optionIndex) => {
            const isSelected = callTypeFilter === option.id;

            return (
              <Pressable
                accessibilityRole="tab"
                accessibilityState={{ selected: isSelected }}
                aria-selected={isSelected}
                key={option.id}
                onPress={() => setCallTypeFilter(option.id)}
                tabIndex={isSelected ? 0 : -1}
                {...(Platform.OS === "web"
                  ? {
                      onKeyDown: (event: unknown) =>
                        handleHorizontalTabKeyDown(
                          event as WebTabKeyDownEvent,
                          optionIndex,
                          filterOptions.length,
                          (nextIndex) =>
                            setCallTypeFilter(filterOptions[nextIndex].id),
                        ),
                    }
                  : {})}
                style={{
                  backgroundColor: isSelected
                    ? theme.accentSubtle
                    : theme.surface,
                  borderColor: isSelected
                    ? theme.accent
                    : theme.borderStrong,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  borderWidth: 1,
                  flexBasis: isWideLayout ? undefined : 0,
                  flexGrow: isWideLayout ? 0 : 1,
                  minHeight: isWideLayout
                    ? workforceOperationalLayout.controlHeight
                    : 52,
                  justifyContent: "center",
                  minWidth: 0,
                  paddingHorizontal: 13,
                  paddingVertical: isWideLayout ? 0 : 4,
                }}
              >
                <Text
                  numberOfLines={isWideLayout ? 1 : 2}
                  style={[
                    styles.pillValueText,
                    {
                      color: isSelected
                        ? theme.accentStrong
                        : theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: workforceOperationalTypography.table.fontSize,
                      fontWeight: "700",
                      lineHeight: workforceOperationalTypography.table.lineHeight,
                      textAlign: "center",
                    },
                  ]}
                >
                  {option.labelText}
                </Text>
              </Pressable>
            );
          })}
          {isWideLayout ? (
            <Text
              numberOfLines={1}
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  flex: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  lineHeight:
                    workforceOperationalTypography.metadata.lineHeight,
                  textAlign: "right",
                },
              ]}
            >
              {`Scoped to ${selectedPatient.displayName}`}
            </Text>
          ) : null}
        </View>

        {!outboundQuestionnaireIsLoading || totalHubCallCount > 0 ? (
          <View
            style={[
              styles.patientRowHeader,
              { alignItems: "stretch", gap: 8 },
            ]}
          >
            {metricTiles.map((tile) => (
              <View
                key={tile.id}
                style={{
                  alignItems: "center",
                  backgroundColor: theme.backgroundSubtle,
                  borderColor: theme.borderSubtle,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  borderWidth: 1,
                  flex: 1,
                  flexDirection: "row",
                  gap: 10,
                  minHeight: isWideLayout ? undefined : 76,
                  paddingHorizontal: 12,
                  paddingVertical: 9,
                }}
              >
                <View
                  style={{
                    alignItems: "center",
                    backgroundColor: theme.accentSubtle,
                    borderRadius: workforceOperationalLayout.controlRadius,
                    height: 32,
                    justifyContent: "center",
                    width: 32,
                  }}
                >
                  <LineIcon
                    color={theme.accentStrong}
                    name={tile.iconName}
                    size={17}
                  />
                </View>
                <View style={{ flex: 1, gap: 1, minWidth: 0 }}>
                  <Text
                    numberOfLines={isWideLayout ? 1 : 2}
                    style={[
                      styles.navigationText,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 13,
                        fontWeight: "800",
                        lineHeight: 16,
                      },
                    ]}
                  >
                    {tile.valueText}
                  </Text>
                  <Text
                    numberOfLines={isWideLayout ? 1 : 2}
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize:
                          workforceOperationalTypography.metadata.fontSize,
                        fontWeight: "700",
                        letterSpacing: 0,
                        lineHeight:
                          workforceOperationalTypography.metadata.lineHeight,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    {tile.labelText}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        ) : null}

        {outboundQuestionnaireNoticeText ? (
          <View
            accessibilityLiveRegion="polite"
            style={{
              alignItems: "center",
              backgroundColor: outboundQuestionnaireNoticePending
                ? theme.statusChip.info.background
                : theme.statusChip.success.background,
              borderColor: outboundQuestionnaireNoticePending
                ? theme.statusChip.info.border
                : theme.statusChip.success.border,
              borderRadius: workforceOperationalLayout.controlRadius,
              borderWidth: 1,
              flexDirection: "row",
              gap: 8,
              paddingHorizontal: 11,
              paddingVertical: 9,
            }}
          >
            <LineIcon
              color={
                outboundQuestionnaireNoticePending
                  ? theme.statusChip.info.text
                  : theme.statusChip.success.text
              }
              name={outboundQuestionnaireNoticePending ? "clock" : "check"}
              size={16}
              strokeWidth={2.3}
            />
            <Text
              style={{
                color: outboundQuestionnaireNoticePending
                  ? theme.statusChip.info.text
                  : theme.statusChip.success.text,
                flex: 1,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.table.fontSize,
                lineHeight: workforceOperationalTypography.table.lineHeight,
              }}
            >
              {outboundQuestionnaireNoticeText}
            </Text>
          </View>
        ) : null}

        {outboundQuestionnaireErrorText ? (
          <View
            accessibilityRole="alert"
            style={{
              alignItems: isWideLayout ? "center" : "stretch",
              backgroundColor: theme.statusChip.danger.background,
              borderColor: theme.statusChip.danger.border,
              borderRadius: workforceOperationalLayout.controlRadius,
              borderWidth: 1,
              flexDirection: isWideLayout ? "row" : "column",
              gap: 8,
              paddingHorizontal: 11,
              paddingVertical: 9,
            }}
          >
            <View
              style={{
                alignItems: "center",
                flex: 1,
                flexDirection: "row",
                gap: 8,
              }}
            >
              <LineIcon
                color={theme.statusChip.danger.text}
                name="info"
                size={16}
                strokeWidth={2.3}
              />
              <Text
                style={{
                  color: theme.statusChip.danger.text,
                  flex: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.table.fontSize,
                  lineHeight: workforceOperationalTypography.table.lineHeight,
                }}
              >
                {outboundQuestionnaireErrorText}
              </Text>
            </View>
            <Pressable
              accessibilityLabel="Retry loading questionnaire calls"
              accessibilityRole="button"
              accessibilityState={{
                disabled: outboundQuestionnaireIsLoading,
              }}
              aria-disabled={outboundQuestionnaireIsLoading}
              disabled={outboundQuestionnaireIsLoading}
              onPress={() =>
                onRefreshOutboundQuestionnaireCalls(selectedPatient.id)
              }
              style={{
                alignItems: "center",
                backgroundColor: theme.surface,
                borderColor: theme.statusChip.danger.border,
                borderRadius: workforceOperationalLayout.controlRadius,
                borderWidth: 1,
                justifyContent: "center",
                minHeight: isWideLayout
                  ? workforceOperationalLayout.controlHeight
                  : workforceOperationalLayout.mobileControlHeight,
                opacity: outboundQuestionnaireIsLoading ? 0.55 : 1,
                paddingHorizontal: 12,
              }}
            >
              <Text
                style={{
                  color: theme.statusChip.danger.text,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.table.fontSize,
                  fontWeight: "700",
                }}
              >
                {outboundQuestionnaireIsLoading ? "Retrying..." : "Retry"}
              </Text>
            </Pressable>
          </View>
        ) : null}

        <ScrollView
          contentContainerStyle={{ gap: 10, paddingBottom: 4 }}
          showsVerticalScrollIndicator={false}
          style={{ flex: showLegacyChatStage ? 1 : undefined, minHeight: 0 }}
        >
          {outboundQuestionnaireIsLoading &&
          visibleOutboundCalls.length === 0 &&
          visibleInboundCalls.length === 0 ? (
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  lineHeight: 17,
                  paddingVertical: 18,
                  textAlign: "center",
                },
              ]}
            >
              Loading questionnaire calls...
            </Text>
          ) : outboundQuestionnaireErrorText != null &&
            visibleOutboundCalls.length === 0 &&
            visibleInboundCalls.length === 0 ? null : visibleOutboundCalls.length === 0 &&
            visibleInboundCalls.length === 0 ? (
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  lineHeight: 17,
                  paddingVertical: 18,
                  textAlign: "center",
                },
              ]}
            >
              No calls match this filter yet.
            </Text>
          ) : (
            <>
              {visibleOutboundCalls.map((call) => renderOutboundHubCard(call))}
              {visibleInboundCalls.map((voiceCall) =>
                renderInboundHubCard(voiceCall),
              )}
            </>
          )}
        </ScrollView>
      </View>
    );
  };
  const composerModeConfigs: Array<{
    helperText: string;
    id: ClinicianComposerMode;
    labelText: string;
    submitText: string;
  }> = [
    {
      helperText: t(translationKeys.shell.workspaceComposerActionNotice),
      id: "reply",
      labelText: "Reply",
      submitText: t(translationKeys.shell.workspaceActionSendReply),
    },
    {
      helperText: "Draft a clinical note...",
      id: "note",
      labelText: "Note",
      submitText: "Review note",
    },
    {
      helperText: "Draft a staff handoff...",
      id: "handoff",
      labelText: "Handoff",
      submitText: "Review handoff",
    },
  ];
  const selectedComposerModeConfig =
    composerModeConfigs.find((config) => config.id === composerMode) ??
    composerModeConfigs[0];
  const composerSubmitLabel = isWorkbenchActionSubmitting
    ? t(translationKeys.shell.workspaceActionSubmitting)
    : selectedComposerModeConfig.submitText;
  const canSendReply =
    selectedPatient != null &&
    composerDraft.trim().length > 0 &&
    !isWorkbenchActionSubmitting;
  const submitComposerDraft = () => {
    if (composerMode === "reply") {
      onSendReply();
      return;
    }

    onCreateComposerNoteFromDraft(composerMode);
  };
  const legacyConversationBadges =
    selectedPatient == null
      ? []
      : buildLegacyClinicianConversationBadges({
          selectedPatient,
          t,
          theme,
        });
  const legacyPatientTitle =
    selectedPatient == null
      ? ""
      : resolveLegacyNameplate(selectedPatient.displayName);
  const legacyPrimaryBadge = legacyConversationBadges[0] ?? null;
  const renderComposerModeControl = () => (
    <View style={[styles.flagRow, { gap: 6 }]}>
      {composerModeConfigs.map((config) => {
        const isSelected = composerMode === config.id;

        return (
          <Pressable
            accessibilityLabel={`${config.labelText} composer mode`}
            accessibilityRole="button"
            accessibilityState={{ selected: isSelected }}
            aria-pressed={isSelected}
            key={config.id}
            onPress={() => setComposerMode(config.id)}
            style={({ pressed }) => [
              styles.inlinePill,
              {
                backgroundColor: isSelected
                  ? theme.accentSubtle
                  : pressed
                    ? theme.backgroundSubtle
                    : theme.surface,
                borderColor: isSelected ? theme.accentStrong : theme.borderSubtle,
                borderRadius: workforceOperationalLayout.controlRadius,
                minHeight: isWideLayout
                  ? workforceOperationalLayout.controlHeight
                  : workforceOperationalLayout.mobileControlHeight,
                paddingHorizontal: 10,
                paddingVertical: 6,
              },
            ]}
          >
            <Text
              style={[
                styles.pillValueText,
                {
                  color: isSelected ? theme.accentStrong : theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.table.fontSize,
                  fontWeight: "700",
                  lineHeight: workforceOperationalTypography.table.lineHeight,
                },
              ]}
            >
              {config.labelText}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
  const renderPendingReviewStack = () => {
    if (pendingReviewMessages.length === 0) {
      return null;
    }

    return (
      <View
        style={[
          styles.detailBodyCard,
          {
            backgroundColor: theme.statusChip.warning.background,
            borderColor: theme.statusChip.warning.border,
            borderRadius: showLegacyChatStage
              ? 0
              : workforceOperationalLayout.panelRadius,
            borderWidth: 1,
            gap: 8,
            marginBottom: showLegacyChatStage ? 0 : tokens.spacing.sm,
            padding: tokens.spacing.sm,
          },
        ]}
      >
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "center",
              gap: tokens.spacing.sm,
              justifyContent: "space-between",
            },
          ]}
        >
          <View style={[styles.listGroup, { flex: 1, gap: 2, minWidth: 0 }]}>
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.warning,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.metadata.fontSize,
                  fontWeight: "800",
                  lineHeight: workforceOperationalTypography.metadata.lineHeight,
                  textTransform: "uppercase",
                },
              ]}
            >
              Needs review
            </Text>
            <Text
              numberOfLines={1}
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  fontWeight: "700",
                  lineHeight: 15,
                },
              ]}
            >
              {`${pendingReviewMessages.length} pending approval${pendingReviewMessages.length === 1 ? "" : "s"}`}
            </Text>
          </View>
          <LineIcon
            color={theme.warning}
            name="flag"
            size={20}
            strokeWidth={2.3}
          />
        </View>

        <ScrollView
          showsVerticalScrollIndicator={false}
          style={{
            maxHeight: pendingReviewMessages.length > 1 ? 248 : undefined,
          }}
          contentContainerStyle={{ gap: 8 }}
        >
        {pendingReviewMessages.slice(0, 3).map((message) => {
          const displayedMessageBodyText = resolveProductMessageBodyText(
            editedReviewBodiesByMessageId[message.id] ?? message.bodyText,
          );
          const submittedEditedReviewBodyText =
            (editedReviewBodiesByMessageId[message.id] ?? "").trim().length > 0
              ? editedReviewBodiesByMessageId[message.id]
              : null;

          return (
            <View
              key={`pending-stack-${message.id}`}
              style={[
                styles.detailSectionCard,
                {
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  gap: 7,
                  padding: tokens.spacing.sm,
                },
              ]}
            >
              <View
                style={[
                  styles.patientRowHeader,
                  {
                    alignItems: "flex-start",
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                <View style={[styles.listGroup, { flex: 1, gap: 3, minWidth: 0 }]}>
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.navigationText,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.displayFontFamily,
                        fontSize: 13,
                        lineHeight: 16,
                      },
                    ]}
                  >
                    {message.senderText}
                  </Text>
                  <Text
                    numberOfLines={2}
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        lineHeight: 16,
                      },
                    ]}
                  >
                    {displayedMessageBodyText}
                  </Text>
                </View>
                {message.review?.confidencePercent != null ? (
                  <View
                    style={[
                      styles.inlinePill,
                      {
                        backgroundColor: theme.backgroundSubtle,
                        borderColor: theme.borderSubtle,
                        borderRadius: 999,
                        paddingHorizontal: 8,
                        paddingVertical: 3,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.pillValueText,
                        {
                          color: theme.warning,
                          fontFamily: layout.bodyFontFamily,
                          fontSize:
                            workforceOperationalTypography.metadata.fontSize,
                          lineHeight:
                            workforceOperationalTypography.metadata.lineHeight,
                        },
                      ]}
                    >
                      {`${message.review.confidencePercent}%`}
                    </Text>
                  </View>
                ) : null}
              </View>
              <View style={[styles.patientRowFooter, { gap: 5 }]}>
                {message.review?.canReject
                  ? renderLegacyReviewActionButton({
                      disabled: isWorkbenchActionSubmitting,
                      label: t(translationKeys.shell.workspaceActionReject),
                      onPress: () => onRejectMessageReview(message.id),
                      theme,
                      tone: "danger",
                      tokens,
                    })
                  : null}
                {message.review?.canEdit
                  ? renderLegacyReviewActionButton({
                      disabled: isWorkbenchActionSubmitting,
                      label: t(translationKeys.shell.workspaceActionEdit),
                      onPress: () =>
                        onBeginReviewEdit(message.id, displayedMessageBodyText),
                      theme,
                      tone: "warning",
                      tokens,
                    })
                  : null}
                {message.review?.canApprove
                  ? renderLegacyReviewActionButton({
                      disabled: isWorkbenchActionSubmitting,
                      label: t(translationKeys.shell.workspaceActionApprove),
                      onPress: () =>
                        onApproveMessageReview(
                          message.id,
                          submittedEditedReviewBodyText,
                        ),
                      theme,
                      tone: "success",
                      tokens,
                    })
                  : null}
              </View>
            </View>
          );
        })}
        </ScrollView>
      </View>
    );
  };
  const renderTaskFirstSnapshot = () => {
    if (selectedPatient == null) {
      return null;
    }

    const snapshotItems = [
      {
        color: theme.accentStrong,
        iconName: "check" as LineIconName,
        id: "tasks",
        labelText: "Tasks",
        valueText: String(selectedPatient.taskAssignments.length),
      },
      {
        color: theme.warning,
        iconName: "flag" as LineIconName,
        id: "reviews",
        labelText: "Reviews",
        valueText: String(pendingApprovalCount),
      },
      {
        color: theme.info,
        iconName: "document" as LineIconName,
        id: "docs",
        labelText: "Docs",
        valueText: String(selectedPatient.pendingDocumentCount),
      },
      {
        color: theme.success,
        iconName: "appointment" as LineIconName,
        id: "next",
        labelText: "Next",
        valueText: selectedPatient.nextAppointmentText,
      },
    ];

    return (
      <View
        style={[
          styles.detailBodyCard,
          {
            backgroundColor: theme.backgroundSubtle,
            borderColor: theme.borderSubtle,
            borderRadius: showLegacyChatStage
              ? 0
              : workforceOperationalLayout.panelRadius,
            borderWidth: 1,
            gap: 8,
            padding: showLegacyChatStage ? 10 : tokens.spacing.sm,
          },
        ]}
      >
        {selectedPatient.summaryText.trim().length > 0 ||
        (showAddAction && !showLegacyChatStage) ? (
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: "flex-start",
                gap: tokens.spacing.sm,
                justifyContent: "space-between",
              },
            ]}
          >
            {selectedPatient.summaryText.trim().length > 0 ? (
              <View style={[styles.listGroup, { flex: 1, gap: 2, minWidth: 0 }]}>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.accentStrong,
                      fontFamily: layout.bodyFontFamily,
                      fontSize:
                        workforceOperationalTypography.metadata.fontSize,
                      fontWeight: "800",
                      lineHeight:
                        workforceOperationalTypography.metadata.lineHeight,
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  Today's work
                </Text>
                <Text
                  numberOfLines={2}
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      fontWeight: "700",
                      lineHeight: 16,
                    },
                  ]}
                >
                  {resolveProductClinicalText(selectedPatient.summaryText)}
                </Text>
              </View>
            ) : null}
            {showAddAction && !showLegacyChatStage
              ? renderWorkbenchActionButton({
                  disabled: isWorkbenchActionSubmitting,
                  iconName: "plus",
                  label: t(translationKeys.shell.workspaceAddToMyPatients),
                  labelLines: 1,
                  minWidth: 112,
                  onPress: onAddSelectedPatientToMyList,
                  size: "compact",
                  theme,
                  tone: "accent",
                  tokens,
                  width: 112,
                })
              : null}
          </View>
        ) : null}

        <View
          style={[
            styles.metricGroupRow,
            {
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 6,
            },
          ]}
        >
          {snapshotItems.map((item) => (
            <View
              key={item.id}
              style={[
                styles.detailBodyCard,
                {
                  alignItems: "center",
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  flex: 1,
                  flexBasis: showLegacyChatStage ? 0 : "46%",
                  flexDirection: "row",
                  gap: 6,
                  minHeight: 34,
                  minWidth: showLegacyChatStage ? 0 : 126,
                  paddingHorizontal: 8,
                  paddingVertical: 5,
                },
              ]}
            >
              <LineIcon
                color={item.color}
                name={item.iconName}
                size={15}
                strokeWidth={2.35}
              />
              <View style={[styles.listGroup, { flex: 1, gap: 0, minWidth: 0 }]}>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: workforceOperationalTypography.metadata.fontSize,
                      fontWeight: "700",
                      lineHeight:
                        workforceOperationalTypography.metadata.lineHeight,
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  {item.labelText}
                </Text>
                <Text
                  numberOfLines={2}
                  style={[
                    styles.pillValueText,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: workforceOperationalTypography.table.fontSize,
                      lineHeight: workforceOperationalTypography.table.lineHeight,
                    },
                  ]}
                >
                  {item.valueText}
                </Text>
              </View>
            </View>
          ))}
        </View>
      </View>
    );
  };

  return (
    <View
      style={[
        styles.surfaceCard,
        {
          backgroundColor:
            showLegacyChatStage && selectedPatient != null
              ? theme.surface
              : theme.surfaceElevated,
          borderColor:
            showLegacyChatStage && selectedPatient != null
              ? theme.borderStrong
              : theme.borderStrong,
          borderRadius:
            showLegacyChatStage && selectedPatient != null
              ? workforceOperationalLayout.panelRadius
              : workforceOperationalLayout.panelRadius,
          borderWidth:
            showLegacyChatStage && selectedPatient != null ? 1 : undefined,
          flex: 1,
          gap:
            showLegacyChatStage && selectedPatient != null
              ? 0
              : tokens.spacing.sm,
          overflow:
            showLegacyChatStage && selectedPatient != null
              ? "hidden"
              : "visible",
          height:
            showLegacyChatStage && selectedPatient != null
              ? wideLegacyStageHeight
              : undefined,
          maxHeight:
            showLegacyChatStage && selectedPatient != null
              ? wideLegacyStageHeight
              : undefined,
          minHeight:
            showLegacyChatStage && selectedPatient != null
              ? wideLegacyStageHeight
              : undefined,
          minWidth: 0,
          padding:
            showLegacyChatStage && selectedPatient != null
              ? 0
              : tokens.spacing.md,
          shadowOpacity: 0,
        },
      ]}
    >
      {!showLegacyChatStage ? (
        <>
          <Text
            style={[
              styles.surfaceTitle,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: tokens.typography.fontSize.md,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {compactSurfaceTitleText}
          </Text>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.sm,
              },
            ]}
          >
            {compactSurfaceSubtitleText}
          </Text>
        </>
      ) : null}

      {selectedPatient == null ? (
        <View
          style={[
            styles.detailBodyCard,
            {
              backgroundColor: theme.surfaceMuted,
              borderColor: theme.borderSubtle,
              borderRadius: layout.controlRadius,
              gap: tokens.spacing.sm,
              padding: tokens.spacing.md,
            },
          ]}
        >
          <Text
            style={[
              styles.surfaceTitle,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: tokens.typography.fontSize.md,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {t(translationKeys.shell.workspaceNoPatientSelectedTitle)}
          </Text>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {t(translationKeys.shell.workspaceNoPatientSelectedBody)}
          </Text>
        </View>
      ) : (
        <>
          {showLegacyChatStage ? (
            <View
              style={[
                styles.legacyClinicianConversationHeader,
                {
                  backgroundColor: theme.backgroundSubtle,
                  borderBottomColor: theme.borderSubtle,
                  borderBottomWidth: 1,
                  borderColor: theme.borderSubtle,
                  gap: tokens.spacing.xs,
                  paddingHorizontal: 16,
                  paddingVertical: 12,
                },
              ]}
            >
              <View
                style={[
                  styles.patientRowHeader,
                  {
                    alignItems: "center",
                    gap: tokens.spacing.xs,
                  },
                ]}
              >
                <View
                  style={[
                    styles.messagePatientIdentityRow,
                    {
                      alignItems: "center",
                      flex: 1,
                      gap: tokens.spacing.sm,
                      minWidth: 0,
                      width: "auto",
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.messageAvatar,
                      {
                        backgroundColor: theme.surface,
                        borderColor: theme.borderStrong,
                        borderRadius: 16,
                        height: 32,
                        width: 32,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.messageAvatarText,
                        {
                          color: theme.accentStrong,
                          fontFamily: layout.bodyFontFamily,
                        },
                      ]}
                    >
                      {resolvePatientInitials(selectedPatient.displayName)}
                    </Text>
                  </View>

                  <View
                    style={[
                      styles.patientIdentityCopy,
                      {
                        alignItems: "center",
                        flexDirection: "row",
                        gap: 9,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.sectionTitle,
                        {
                          color: theme.textPrimary,
                          flexShrink: 1,
                          fontFamily: layout.displayFontFamily,
                          fontSize: 15,
                          lineHeight: 18,
                        },
                      ]}
                    >
                      {legacyPatientTitle}
                    </Text>
                    {legacyPrimaryBadge ? (
                      <View
                        style={{
                          backgroundColor: theme.accentSubtle,
                          borderRadius: 6,
                          flexShrink: 0,
                          paddingHorizontal: 9,
                          paddingVertical: 3,
                        }}
                      >
                        <Text
                          numberOfLines={1}
                          style={[
                            styles.pillValueText,
                            {
                              color: theme.accentStrong,
                              fontFamily: layout.bodyFontFamily,
                              fontSize:
                                workforceOperationalTypography.metadata.fontSize,
                              fontWeight: "800",
                              letterSpacing: 0,
                              lineHeight:
                                workforceOperationalTypography.metadata
                                  .lineHeight,
                              textTransform: "uppercase",
                            },
                          ]}
                        >
                          {legacyPrimaryBadge.labelText}
                        </Text>
                      </View>
                    ) : null}
                    {showAddAction ? (
                      <Pressable
                        accessibilityRole="button"
                        disabled={isWorkbenchActionSubmitting}
                        onPress={onAddSelectedPatientToMyList}
                        style={({ pressed }) => ({
                          alignItems: "center",
                          backgroundColor: theme.surface,
                          borderColor: theme.borderStrong,
                          borderRadius:
                            workforceOperationalLayout.controlRadius,
                          borderWidth: 1.5,
                          flexShrink: 0,
                          justifyContent: "center",
                          minHeight:
                            workforceOperationalLayout.controlHeight,
                          opacity:
                            pressed || isWorkbenchActionSubmitting ? 0.6 : 1,
                          paddingHorizontal: 10,
                          paddingVertical: 4,
                        })}
                      >
                        <Text
                          numberOfLines={1}
                          style={[
                            styles.pillValueText,
                            {
                              color: theme.accentStrong,
                              fontFamily: layout.bodyFontFamily,
                              fontSize:
                                workforceOperationalTypography.table.fontSize,
                              fontWeight: "700",
                              lineHeight:
                                workforceOperationalTypography.table.lineHeight,
                            },
                          ]}
                        >
                          {`+ ${t(
                            translationKeys.shell.workspaceAddToMyPatients,
                          )}`}
                        </Text>
                      </Pressable>
                    ) : null}
                  </View>
                </View>

                <View
                  style={[
                    styles.headerAside,
                    {
                      alignItems: "center",
                      flexDirection: "row",
                      gap: tokens.spacing.xs,
                    },
                  ]}
                >
                  {primaryOutboundCallAction ? (
                    <Pressable
                      accessibilityRole="button"
                      disabled={
                        isWorkbenchActionSubmitting ||
                        outboundQuestionnairePendingTargetId != null
                      }
                      onPress={() =>
                        onStartOutboundQuestionnaireCall(
                          primaryOutboundCallAction.call,
                        )
                      }
                      style={({ pressed }) => ({
                        alignItems: "center",
                        backgroundColor: theme.accent,
                        borderRadius: workforceOperationalLayout.controlRadius,
                        flexDirection: "row",
                        gap: 6,
                        minHeight: workforceOperationalLayout.controlHeight,
                        opacity:
                          pressed ||
                          isWorkbenchActionSubmitting ||
                          outboundQuestionnairePendingTargetId != null
                            ? 0.7
                            : 1,
                        paddingHorizontal: 13,
                        paddingVertical: 7,
                      })}
                    >
                      <LineIcon
                        color={theme.accentContrast}
                        name="phone"
                        size={16}
                        strokeWidth={2.25}
                      />
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.pillValueText,
                          {
                            color: theme.accentContrast,
                            fontFamily: layout.bodyFontFamily,
                            fontSize:
                              workforceOperationalTypography.table.fontSize,
                            fontWeight: "700",
                            lineHeight:
                              workforceOperationalTypography.table.lineHeight,
                          },
                        ]}
                      >
                        {primaryOutboundCallAction.labelText}
                      </Text>
                    </Pressable>
                  ) : null}
                </View>
              </View>
            </View>
          ) : (
            <View
              style={[
                styles.messageHeaderCard,
                {
                  backgroundColor: theme.surfaceMuted,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  gap: tokens.spacing.sm,
                  padding: tokens.spacing.sm,
                },
              ]}
            >
              <View
                style={[
                  styles.messagePatientIdentityRow,
                  {
                    gap: tokens.spacing.md,
                  },
                ]}
              >
                <View
                  style={[
                    styles.messageAvatar,
                    {
                      backgroundColor: theme.headerBand,
                      borderColor: theme.headerBand,
                      borderRadius: 24,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.messageAvatarText,
                      {
                        color: theme.headerText,
                        fontFamily: layout.bodyFontFamily,
                      },
                    ]}
                  >
                    {resolvePatientInitials(selectedPatient.displayName)}
                  </Text>
                </View>

                <View
                  style={[
                    styles.patientIdentityCopy,
                    {
                      gap: tokens.spacing.xs,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.patientRowHeader,
                      {
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        gap: tokens.spacing.sm,
                        justifyContent: "flex-start",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.navigationText,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.md,
                        },
                      ]}
                    >
                      {selectedPatient.displayName}
                    </Text>
                    <View
                      style={[
                        styles.inlinePill,
                        {
                          backgroundColor: theme.surface,
                          borderColor: theme.borderSubtle,
                          borderRadius: 8,
                          maxWidth: "100%",
                          paddingHorizontal: tokens.spacing.sm,
                          paddingVertical: tokens.spacing.xs,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.pillValueText,
                          {
                            color: theme.textSecondary,
                            flexShrink: 1,
                            fontFamily: layout.bodyFontFamily,
                          },
                        ]}
                      >
                        {patientIdentityText ?? selectedPatient.listMetaText}
                      </Text>
                    </View>
                  </View>

                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                        lineHeight: tokens.typography.lineHeight.md,
                      },
                    ]}
                  >
                    {resolveProductClinicalText(selectedPatient.summaryText)}
                  </Text>
                </View>

              </View>

              {showAddAction
                ? renderWorkbenchActionButton({
                    disabled: isWorkbenchActionSubmitting,
                    label: t(translationKeys.shell.workspaceAddToMyPatients),
                    labelLines: 1,
                    minWidth: clinicianWideActionButtonWidth,
                    onPress: onAddSelectedPatientToMyList,
                    theme,
                    tone: "accent",
                    tokens,
                    width: clinicianWideActionButtonWidth,
                  })
                : null}

              <View
                style={[
                  styles.flagRow,
                  {
                    gap: tokens.spacing.xs,
                  },
                ]}
              >
                {!showAddAction ? (
                  <View
                    style={[
                      styles.inlinePill,
                      {
                        backgroundColor: theme.surface,
                        borderColor: theme.borderSubtle,
                        borderRadius: 8,
                        paddingHorizontal: tokens.spacing.sm,
                        paddingVertical: tokens.spacing.xs,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.pillValueText,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                        },
                      ]}
                    >
                      {t(translationKeys.shell.workspaceAssignedToMyPatients)}
                    </Text>
                  </View>
                ) : null}
                <View
                  style={[
                    styles.inlinePill,
                    {
                      backgroundColor: theme.surface,
                      borderColor: theme.borderSubtle,
                      borderRadius: 8,
                      paddingHorizontal: tokens.spacing.sm,
                      paddingVertical: tokens.spacing.xs,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.pillValueText,
                      {
                        color: theme.accentStrong,
                        fontFamily: layout.bodyFontFamily,
                      },
                    ]}
                  >
                    {`${t(translationKeys.shell.workspacePanelUnread)} ${selectedPatient.unreadMessageCount}`}
                  </Text>
                </View>
                <View
                  style={[
                    styles.inlinePill,
                    {
                      backgroundColor: theme.surface,
                      borderColor: theme.borderSubtle,
                      borderRadius: 8,
                      paddingHorizontal: tokens.spacing.sm,
                      paddingVertical: tokens.spacing.xs,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.pillValueText,
                      {
                        color: theme.warning,
                        fontFamily: layout.bodyFontFamily,
                      },
                    ]}
                  >
                    {`${t(translationKeys.shell.workspaceDetailApprovals)} ${pendingApprovalCount}`}
                  </Text>
                </View>

                <View
                  style={[
                    styles.inlinePill,
                    {
                      backgroundColor: theme.surface,
                      borderColor: theme.borderSubtle,
                      borderRadius: 8,
                      paddingHorizontal: tokens.spacing.sm,
                      paddingVertical: tokens.spacing.xs,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.pillValueText,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                      },
                    ]}
                  >
                    {`${t(translationKeys.shell.workspacePanelPendingDocuments)} ${selectedPatient.pendingDocumentCount}`}
                  </Text>
                </View>

                {selectedPatient.derivedFlagLabelKeys.map((badgeKey) => (
                  <View
                    key={badgeKey}
                    style={[
                      styles.inlinePill,
                      {
                        backgroundColor: theme.surface,
                        borderColor: theme.borderSubtle,
                        borderRadius: 8,
                        paddingHorizontal: tokens.spacing.sm,
                        paddingVertical: tokens.spacing.xs,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.pillValueText,
                        {
                          color: theme.accentStrong,
                          fontFamily: layout.bodyFontFamily,
                        },
                      ]}
                    >
                      {t(badgeKey)}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {renderClinicianCenterPaneToggle()}
          {showPatientProfileView && selectedPatient != null ? (
            <ClinicianPatientProfileSurface
              // Remount per patient: the profile's draft state and the
              // questionnaire surface's answers are component state, and this
              // subtree never unmounts on patient switch — without a key,
              // one patient's in-progress answers can be saved to another's chart.
              key={selectedPatient.id}
              actionFeedbackText={
                workbenchActionFeedbackText ?? (workbenchActionFeedbackKey
                  ? t(workbenchActionFeedbackKey)
                  : null)
              }
              actionFeedbackTone={
                workbenchActionFeedbackKey ===
                translationKeys.shell.workspaceActionError
                  ? "error"
                  : workbenchActionFeedbackKey ===
                      translationKeys.shell.workspaceActionSubmitting
                    ? "pending"
                    : workbenchActionFeedbackKey
                      ? "success"
                      : null
              }
              discardConfirmationDismissRequestKey={
                profileDiscardConfirmationDismissRequestKey
              }
              discardChangesRequestKey={profileDiscardChangesRequestKey}
              focusSection={profileFocusSection}
              isWorkbenchActionSubmitting={isWorkbenchActionSubmitting}
              isOutboundSessionLoading={outboundQuestionnaireIsLoading}
              layout={layout}
              materialChangeToken={selectedPatient.materialChangeToken}
              onDiscardConfirmationOpenChange={
                onProfileDiscardConfirmationOpenChange
              }
              onDirtyStateChange={onProfileDirtyChange}
              onFocusSectionHandled={onProfileFocusSectionHandled}
              onModalOpenChange={onProfileModalOpenChange}
              onSubmitWorkbenchAction={onSubmitWorkbenchAction}
              navigationConfirmationOpen={
                profileNavigationConfirmationOpen
              }
              patientDisplayName={selectedPatient.displayName}
              patientId={selectedPatient.id}
              patientIdentityText={patientIdentityText}
              questionnaireDefinition={questionnaireDefinition}
              record={selectedPatient.record}
              theme={theme}
              tokens={tokens}
              {...(() => {
                // Manual continuation (GP workflow): surface the patient's
                // unfinished AI-call session so the questionnaire entry
                // prefills from it and can complete it manually.
                const { activeCall, statusCall } =
                  resolveClinicianOutboundSessionContext(
                    sortedOutboundHubCalls,
                  );
                if (!activeCall?.sessionId) {
                  return {
                    outboundSessionStatus: statusCall?.sessionStatus ?? null,
                  };
                }
                return {
                  isOutboundCommandSubmitting: outboundManualCommandPending,
                  onSubmitOutboundManualCommand:
                    onSubmitOutboundManualQuestionnaireCommand,
                  outboundAnswerPreview: activeCall.answerPreview,
                  outboundSessionStatus:
                    statusCall?.sessionStatus ?? activeCall.sessionStatus,
                  outboundSession: {
                    answeredQuestionCount: activeCall.answeredQuestionCount,
                    sessionId: activeCall.sessionId,
                    sessionRevision: activeCall.sessionRevision,
                    sessionStatus: activeCall.sessionStatus ?? "in_progress",
                    targetId: activeCall.targetId,
                    totalQuestionCount: activeCall.totalQuestionCount,
                  },
                };
              })()}
            />
          ) : showPhoneCallsView ? (
            renderClinicianPhoneCallsSurface()
          ) : (
          <>
          {renderTaskFirstSnapshot()}

          <View
            style={[
              showLegacyChatStage ? styles.legacyChatStage : styles.listGroup,
              {
                flex: showLegacyChatStage ? 1 : undefined,
                gap: showLegacyChatStage ? 0 : tokens.spacing.md,
              },
            ]}
          >
            <View
              style={[
                showLegacyChatStage
                  ? styles.legacyChatConversationColumn
                  : styles.listGroup,
                {
                  gap: showLegacyChatStage ? 0 : tokens.spacing.md,
                },
              ]}
            >
              {renderPendingReviewStack()}

              <View
                style={[
                  styles.messageThreadFrame,
                  {
                    backgroundColor: showLegacyChatStage
                      ? theme.background
                      : theme.backgroundSubtle,
                    borderColor: showLegacyChatStage
                      ? "transparent"
                      : theme.borderSubtle,
                    borderRadius: showLegacyChatStage ? 0 : layout.controlRadius,
                    flex: showLegacyChatStage ? 1 : undefined,
                    minHeight: showLegacyChatStage ? 0 : undefined,
                    padding: showLegacyChatStage
                      ? 18
                      : tokens.spacing.sm,
                  },
                ]}
              >
                <ScrollView
                  ref={clinicianConversationScrollRef}
                  onContentSizeChange={onConversationContentSizeChange}
                  onScroll={onConversationScroll}
                  scrollEventThrottle={16}
                  showsVerticalScrollIndicator={false}
                  style={[
                    styles.messageScroll,
                    showLegacyChatStage
                      ? {
                          flex: 1,
                          maxHeight: undefined,
                          minHeight: 0,
                        }
                      : null,
                  ]}
                  contentContainerStyle={[
                    styles.messageThreadContent,
                    {
                      flexGrow:
                        showLegacyChatStage && conversationMessages.length === 0
                          ? 1
                          : undefined,
                      gap: showLegacyChatStage ? 14 : tokens.spacing.md,
                      paddingHorizontal: showLegacyChatStage ? tokens.spacing.sm : 0,
                      paddingVertical: showLegacyChatStage ? tokens.spacing.sm : 0,
                    },
                  ]}
                >
                  {conversationMessages.length === 0 ? (
                    <View
                      style={{
                        alignItems: "center",
                        flex: showLegacyChatStage ? 1 : undefined,
                        gap: tokens.spacing.xs,
                        justifyContent: "center",
                        minHeight: showLegacyChatStage ? 180 : 140,
                        padding: tokens.spacing.lg,
                      }}
                    >
                      <View
                        style={[
                          styles.detailHeaderAvatar,
                          {
                            backgroundColor: theme.surface,
                            borderColor: theme.borderStrong,
                            borderRadius: 999,
                            height: 40,
                            width: 40,
                          },
                        ]}
                      >
                        <LineIcon
                          color={theme.textSecondary}
                          name="message"
                          size={19}
                        />
                      </View>
                      <Text
                        style={[
                          styles.navigationText,
                          {
                            color: theme.textPrimary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.sm,
                            fontWeight: "700",
                            lineHeight: tokens.typography.lineHeight.md,
                            textAlign: "center",
                          },
                        ]}
                      >
                        No conversation recorded
                      </Text>
                      <Text
                        style={[
                          styles.sectionSubtitle,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.sm,
                            lineHeight: tokens.typography.lineHeight.md,
                            textAlign: "center",
                          },
                        ]}
                      >
                        This patient has no messages yet.
                      </Text>
                    </View>
                  ) : null}
                  {conversationMessages.map((message, index) => {
                    const previousMessage =
                      conversationMessages[index - 1];
                    const rawBubbleBackground = getMessageBubbleBackground(
                      message,
                      theme,
                      "clinician",
                    );
                    const legacyPatientBubbleBackground = theme.surfaceMuted;
                    const bubbleBackground = showLegacyChatStage
                      ? message.review?.state === "pending-approval"
                        ? theme.surface
                        : message.senderKind === "patient"
                        ? legacyPatientBubbleBackground
                        : message.senderKind === "system"
                          ? theme.surfaceMuted
                          : theme.surface
                      : rawBubbleBackground;
                    const rawBubbleBorderColor = getMessageBubbleBorderColor(
                      message,
                      theme,
                      "clinician",
                    );
                    const bubbleBorderColor = showLegacyChatStage
                      ? message.review?.state === "pending-approval"
                        ? theme.borderSubtle
                        : message.senderKind === "patient"
                        ? legacyPatientBubbleBackground
                        : message.senderKind === "system"
                          ? theme.borderSubtle
                          : theme.borderStrong
                      : rawBubbleBorderColor;
                    const bubbleTextColor = showLegacyChatStage
                      ? message.senderKind === "system"
                        ? theme.textSecondary
                        : theme.textPrimary
                      : getMessageTextColor(message, theme, "clinician");
                    const senderTextColor = showLegacyChatStage
                      ? message.review?.state === "pending-approval"
                        ? theme.textSecondary
                        : message.senderKind === "clinician"
                          ? theme.accentStrong
                          : theme.textSecondary
                      : getMessageSenderTextColor(
                          message,
                          theme,
                          "clinician",
                        );
                    const timestampTextColor = showLegacyChatStage
                      ? theme.textTertiary
                      : getMessageTimestampTextColor(
                          message,
                          theme,
                          "clinician",
                        );
                    const deliveryLabelKey = resolveMessageDeliveryLabelKey(
                      message.deliveryState,
                    );
                    const reviewAccentColor =
                      message.review == null
                        ? theme.textSecondary
                        : getMessageReviewTextColor(
                            message.review.state,
                            theme,
                          );
                    const reviewBackground =
                      message.review == null
                        ? theme.surfaceMuted
                        : getMessageReviewBackground(
                            message.review.state,
                            theme,
                          );
                    const rawDisplayedMessageBodyText =
                      editedReviewBodiesByMessageId[message.id] ??
                      message.bodyText;
                    const displayedMessageBodyText =
                      resolveProductMessageBodyText(rawDisplayedMessageBodyText);
                    const isEditingReviewMessage =
                      reviewEditingMessageId === message.id;
                    const draftReviewBodyText = isEditingReviewMessage
                      ? reviewEditDraft
                      : (editedReviewBodiesByMessageId[message.id] ?? "");
                    const trimmedDraftReviewBodyText =
                      draftReviewBodyText.trim();
                    const submittedEditedReviewBodyText =
                      trimmedDraftReviewBodyText.length > 0 &&
                      trimmedDraftReviewBodyText !== message.bodyText.trim()
                        ? trimmedDraftReviewBodyText
                        : null;
                    const isPendingReviewMessage =
                      message.review?.state === "pending-approval";
                    const isLegacyPendingReviewMessage =
                      showLegacyChatStage && isPendingReviewMessage;
                    const reviewLabelKey =
                      message.review?.state === "approved" &&
                      editedReviewBodiesByMessageId[message.id]
                        ? translationKeys.shell
                            .workspaceChatReviewApprovedEdited
                        : message.review != null
                          ? resolveMessageReviewLabelKey(message.review.state)
                          : null;
                    const isLegacyMetaOutside =
                      showLegacyChatStage &&
                      message.senderKind !== "system" &&
                      message.review?.state !== "pending-approval";
                    const showSenderName = !isLegacyMetaOutside;
                    const showAvatar = message.senderKind !== "system";
                    const showDeliveryStatus =
                      deliveryLabelKey != null &&
                      message.senderKind === "clinician" &&
                      !isLegacyMetaOutside;
                    const showLegacyTimestampFooter = false;
                    const startsNewLegacyLane =
                      previousMessage == null ||
                      previousMessage.senderKind !== message.senderKind ||
                      (previousMessage.review?.state ?? "none") !==
                        (message.review?.state ?? "none");
                    const legacyRowMarginTop =
                      !showLegacyChatStage || index === 0
                        ? 0
                        : startsNewLegacyLane
                          ? tokens.spacing.sm
                          : 2;
                    const legacyMessageIndexText = `M${String(index + 1).padStart(2, "0")}`;
                    const legacyMessageLaneText =
                      message.review?.state === "pending-approval"
                        ? "Review lane"
                        : message.senderKind === "patient"
                          ? "Patient update"
                          : message.senderKind === "clinician"
                            ? "Clinician reply"
                            : "System event";
                    const messageBodyPanelBackground = showLegacyChatStage
                      ? "transparent"
                      : getMessageBodyPanelBackground(
                          message,
                          theme,
                          "clinician",
                        );
                    const messageBodyPanelBorderColor = showLegacyChatStage
                      ? "transparent"
                      : getMessageBodyPanelBorderColor(
                          message,
                          theme,
                          "clinician",
                        );
                    const showsLegacyClinicianAvatar =
                      showsLegacyWebAssets &&
                      message.senderKind === "clinician";
                    const bubbleMaxWidth = showLegacyChatStage
                      ? message.senderKind === "patient"
                        ? "50%"
                        : message.review != null
                          ? "82%"
                          : message.senderKind === "system"
                            ? "76%"
                            : "78%"
                      : message.alignment === "center"
                        ? "100%"
                        : "82%";
                    const legacyBubbleBorderColor =
                      showLegacyChatStage && message.senderKind === "patient"
                        ? legacyPatientBubbleBackground
                        : bubbleBorderColor;
                    const showsAgentAccentRail =
                      showLegacyChatStage &&
                      message.senderKind === "clinician" &&
                      message.review?.state !== "pending-approval";
                    const messageAvatar = (
                      <View
                        style={[
                          styles.messageAvatar,
                          {
                            backgroundColor:
                              message.alignment === "right"
                                ? theme.headerBand
                                : theme.surface,
                            borderColor:
                              message.alignment === "right"
                                ? theme.headerBand
                                : theme.borderStrong,
                            borderRadius: 22,
                          },
                        ]}
                      >
                        {showsLegacyClinicianAvatar ? (
                          <Image
                            accessibilityLabel={message.senderText}
                            resizeMode="cover"
                            source={legacyClinicianAvatarSource}
                            style={styles.messageAvatarImage}
                          />
                        ) : (
                          <Text
                            style={[
                              styles.messageAvatarText,
                              {
                                color:
                                  message.alignment === "right"
                                    ? theme.headerText
                                    : theme.accentStrong,
                                fontFamily: layout.bodyFontFamily,
                              },
                            ]}
                          >
                            {resolvePatientInitials(message.senderText)}
                          </Text>
                        )}
                      </View>
                    );

                    return (
                      <View
                        key={message.id}
                        style={{
                          alignItems:
                            message.alignment === "right"
                              ? "flex-end"
                              : message.alignment === "center"
                                ? "center"
                                : "flex-start",
                          gap: 3,
                          marginTop: legacyRowMarginTop,
                          width: "100%",
                        }}
                      >
                        {isLegacyMetaOutside ? (
                          <Text
                            numberOfLines={1}
                            style={[
                              styles.messageMetaText,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 10.5,
                                lineHeight: 13,
                                marginHorizontal: 36,
                              },
                            ]}
                          >
                            <Text
                              style={{
                                color: senderTextColor,
                                fontWeight: "700",
                              }}
                            >
                              {message.senderText}
                            </Text>
                            {` · ${message.timestampText}`}
                            {deliveryLabelKey != null &&
                            message.senderKind === "clinician" ? (
                              <Text
                                style={{
                                  color:
                                    message.deliveryState === "delivered"
                                      ? theme.success
                                      : theme.textTertiary,
                                  fontWeight: "700",
                                }}
                              >
                                {` · ${
                                  message.deliveryState === "delivered"
                                    ? `${String.fromCodePoint(0x2713)} `
                                    : ""
                                }${t(deliveryLabelKey)}`}
                              </Text>
                            ) : null}
                          </Text>
                        ) : null}
                        <View
                          style={[
                            styles.messageRow,
                            message.alignment === "right"
                              ? styles.messageRowRight
                              : message.alignment === "center"
                                ? styles.messageRowCenter
                                : styles.messageRowLeft,
                            {
                              gap: showLegacyChatStage
                                ? tokens.spacing.xs
                                : tokens.spacing.sm,
                              width: "100%",
                            },
                          ]}
                        >
                        {showAvatar && message.alignment !== "right"
                          ? messageAvatar
                          : null}

                        <View
                          style={[
                            styles.messageCard,
                            styles.messageBubble,
                            message.alignment === "right"
                              ? styles.messageBubbleOutgoing
                              : message.alignment === "center"
                                ? styles.messageBubbleSystem
                                : styles.messageBubbleIncoming,
                            {
                              backgroundColor: bubbleBackground,
                              borderColor: legacyBubbleBorderColor,
                              borderBottomWidth: 1,
                              borderRadius: showLegacyChatStage
                                ? 10
                                : layout.controlRadius,
                              borderTopWidth: 1,
                              borderLeftColor: showsAgentAccentRail
                                ? theme.accent
                                : legacyBubbleBorderColor,
                              borderLeftWidth: showsAgentAccentRail ? 3 : 1,
                              borderRightWidth: 1,
                              gap: showLegacyChatStage
                                ? 4
                                : tokens.spacing.sm,
                              maxWidth: bubbleMaxWidth,
                              padding: showLegacyChatStage
                                ? message.senderKind === "patient"
                                  ? 9
                                  : 11
                                : tokens.spacing.md,
                              shadowColor: showLegacyChatStage
                                ? theme.textPrimary
                                : undefined,
                              shadowOffset: showLegacyChatStage
                                ? { width: 0, height: 1 }
                                : undefined,
                              shadowOpacity: showLegacyChatStage
                                ? message.senderKind === "clinician"
                                  ? 0.05
                                  : 0
                                : undefined,
                              shadowRadius: showLegacyChatStage ? 4 : undefined,
                            },
                          ]}
                        >
                          {!isLegacyMetaOutside ? (
                            <View
                              style={[
                                styles.messageBubbleHeader,
                                {
                                  gap: tokens.spacing.sm,
                                  justifyContent: showSenderName
                                    ? "space-between"
                                    : "flex-end",
                                },
                              ]}
                            >
                              {showSenderName ? (
                                <Text
                                  style={[
                                    styles.messageSenderText,
                                    {
                                      color: senderTextColor,
                                      fontFamily: layout.bodyFontFamily,
                                      fontSize: tokens.typography.fontSize.xs,
                                    },
                                  ]}
                                >
                                  {message.senderText}
                                </Text>
                              ) : null}
                              {!showLegacyTimestampFooter ? (
                                <Text
                                  style={[
                                    styles.messageMetaText,
                                    {
                                      color: timestampTextColor,
                                      fontFamily: layout.bodyFontFamily,
                                      fontSize: tokens.typography.fontSize.xs,
                                    },
                                  ]}
                                >
                                  {message.timestampText}
                                </Text>
                              ) : null}
                            </View>
                          ) : null}

                          <View
                            style={[
                              styles.messageBodyPanel,
                              {
                                backgroundColor: messageBodyPanelBackground,
                                borderColor: messageBodyPanelBorderColor,
                                borderRadius: showLegacyChatStage
                                  ? 0
                                  : layout.controlRadius,
                                paddingHorizontal: showLegacyChatStage
                                  ? 0
                                  : tokens.spacing.sm,
                                paddingVertical: showLegacyChatStage
                                  ? 0
                                  : tokens.spacing.sm,
                              },
                            ]}
                          >
                            {isEditingReviewMessage ? (
                              <TextInput
                                editable={!isWorkbenchActionSubmitting}
                                multiline={true}
                                onChangeText={onChangeReviewEditDraft}
                                placeholder={t(
                                  translationKeys.shell
                                    .workspaceComposerInputPlaceholder,
                                )}
                                placeholderTextColor={
                                  showLegacyChatStage
                                    ? theme.headerTextMuted
                                    : theme.textTertiary
                                }
                                style={[
                                  styles.messageReviewEditorInput,
                                  {
                                    color: bubbleTextColor,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: tokens.typography.fontSize.sm,
                                    lineHeight: tokens.typography.lineHeight.md,
                                  },
                                ]}
                                value={reviewEditDraft}
                              />
                            ) : (
                              <Text
                                style={[
                                  styles.messageBodyText,
                                  {
                                    color: bubbleTextColor,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: showLegacyChatStage
                                      ? 13
                                      : tokens.typography.fontSize.sm,
                                    lineHeight: showLegacyChatStage
                                      ? 18
                                      : tokens.typography.lineHeight.md,
                                  },
                                ]}
                              >
                                {displayedMessageBodyText}
                              </Text>
                            )}
                          </View>

                          {showLegacyTimestampFooter || showDeliveryStatus ? (
                            <View
                              style={[
                                styles.messageDeliveryRow,
                                {
                                  justifyContent: showLegacyChatStage
                                    ? "space-between"
                                    : "flex-start",
                                  gap: tokens.spacing.xs,
                                },
                              ]}
                            >
                              {showLegacyTimestampFooter ? (
                                <View
                                  style={[
                                    styles.listGroup,
                                    {
                                      flex: 1,
                                      gap: 2,
                                    },
                                  ]}
                                >
                                  <Text
                                    style={[
                                      styles.metricLabel,
                                      {
                                        color: theme.textSecondary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.xs,
                                        textTransform: "uppercase",
                                      },
                                    ]}
                                  >
                                    {`${legacyMessageIndexText} ${legacyMessageLaneText}`}
                                  </Text>
                                  <Text
                                    style={[
                                      styles.metricLabel,
                                      {
                                        color: theme.textSecondary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.xs,
                                      },
                                    ]}
                                  >
                                    {message.senderKind === "patient"
                                      ? legacyPatientTitle
                                      : message.senderText}
                                  </Text>
                                </View>
                              ) : (
                                <View />
                              )}

                              <View
                                style={[
                                  styles.activityStatusRow,
                                  {
                                    gap: tokens.spacing.xs,
                                  },
                                ]}
                              >
                                {showLegacyTimestampFooter ? (
                                  <Text
                                    style={[
                                      styles.messageMetaText,
                                      {
                                        color: timestampTextColor,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.xs,
                                      },
                                    ]}
                                  >
                                    {message.timestampText}
                                  </Text>
                                ) : null}

                                {showDeliveryStatus ? (
                                  <View
                                    style={[
                                      styles.inlinePill,
                                      {
                                        backgroundColor:
                                          getMessageDeliveryBackground(
                                            message.deliveryState,
                                            theme,
                                          ),
                                        borderColor:
                                          message.deliveryState === "delivered"
                                            ? theme.statusChip.success.border
                                            : theme.borderSubtle,
                                        borderRadius: 999,
                                        paddingHorizontal: tokens.spacing.sm,
                                        paddingVertical: tokens.spacing.xs,
                                      },
                                    ]}
                                  >
                                    <Text
                                      style={[
                                        styles.pillValueText,
                                        {
                                          color: getMessageDeliveryTextColor(
                                            message.deliveryState,
                                            theme,
                                          ),
                                          fontFamily: layout.bodyFontFamily,
                                        },
                                      ]}
                                    >
                                      {t(deliveryLabelKey)}
                                    </Text>
                                  </View>
                                ) : null}
                              </View>
                            </View>
                          ) : null}

                          {message.review != null &&
                          message.review.state !== "none" ? (
                            <View
                              style={[
                                styles.detailBodyCard,
                                styles.messageReviewCard,
                                {
                                  backgroundColor: isLegacyPendingReviewMessage
                                    ? theme.statusChip.warning.background
                                    : showLegacyChatStage
                                      ? theme.surface
                                      : reviewBackground,
                                  borderColor: isLegacyPendingReviewMessage
                                    ? theme.statusChip.warning.border
                                    : showLegacyChatStage
                                      ? theme.textPrimary
                                      : reviewAccentColor,
                                  borderRadius: isLegacyPendingReviewMessage
                                    ? 16
                                    : layout.controlRadius,
                                  borderLeftWidth: isLegacyPendingReviewMessage
                                    ? 0
                                    : 4,
                                  borderWidth: isLegacyPendingReviewMessage
                                    ? 1
                                    : 1,
                                  gap: showLegacyChatStage
                                    ? tokens.spacing.xs
                                    : tokens.spacing.sm,
                                  padding: isLegacyPendingReviewMessage
                                    ? tokens.spacing.md
                                    : showLegacyChatStage
                                      ? tokens.spacing.xs
                                      : tokens.spacing.md,
                                },
                              ]}
                            >
                              <View
                                style={[
                                  styles.messageReviewBanner,
                                  {
                                    backgroundColor: showLegacyChatStage
                                      ? "transparent"
                                      : theme.surface,
                                    borderColor: showLegacyChatStage
                                      ? "transparent"
                                      : theme.borderSubtle,
                                    borderRadius: layout.controlRadius,
                                    gap: tokens.spacing.xs,
                                    borderWidth: showLegacyChatStage ? 0 : 1,
                                    padding: showLegacyChatStage
                                      ? 0
                                      : tokens.spacing.sm,
                                  },
                                ]}
                              >
                                <View
                                  style={[
                                    styles.messageReviewTopRow,
                                    {
                                      alignItems: showLegacyChatStage
                                        ? "flex-start"
                                        : "center",
                                      gap: tokens.spacing.sm,
                                      justifyContent: "space-between",
                                    },
                                  ]}
                                >
                                  <Text
                                    style={[
                                      styles.badgeText,
                                      {
                                        color: isLegacyPendingReviewMessage
                                          ? theme.warning
                                          : reviewAccentColor,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.xs,
                                      },
                                    ]}
                                  >
                                    {isPendingReviewMessage
                                      ? "Needs clinician review"
                                      : reviewLabelKey
                                        ? t(reviewLabelKey)
                                        : ""}
                                  </Text>

                                  <View
                                    style={[
                                      styles.messageReviewTopRight,
                                      {
                                        gap: tokens.spacing.xs,
                                      },
                                    ]}
                                  >
                                    {!showLegacyChatStage &&
                                    showDeliveryStatus ? (
                                      <View
                                        style={[
                                          styles.inlinePill,
                                          {
                                            backgroundColor:
                                              getMessageDeliveryBackground(
                                                message.deliveryState,
                                                theme,
                                              ),
                                            borderColor: theme.borderSubtle,
                                            borderRadius: 8,
                                            paddingHorizontal:
                                              tokens.spacing.sm,
                                            paddingVertical: tokens.spacing.xs,
                                          },
                                        ]}
                                      >
                                        <Text
                                          style={[
                                            styles.pillValueText,
                                            {
                                              color:
                                                getMessageDeliveryTextColor(
                                                  message.deliveryState,
                                                  theme,
                                                ),
                                              fontFamily: layout.bodyFontFamily,
                                            },
                                          ]}
                                        >
                                          {t(deliveryLabelKey)}
                                        </Text>
                                      </View>
                                    ) : null}
                                  </View>
                                </View>

                                {message.review.confidencePercent != null ? (
                                  <View
                                    style={[
                                      styles.messageReviewConfidenceRow,
                                      {
                                        gap: tokens.spacing.sm,
                                      },
                                    ]}
                                  >
                                    <Text
                                      style={[
                                        styles.metricLabel,
                                        {
                                          color: isLegacyPendingReviewMessage
                                            ? theme.textSecondary
                                            : reviewAccentColor,
                                          flexShrink: 1,
                                          fontFamily: layout.bodyFontFamily,
                                          fontSize:
                                            tokens.typography.fontSize.xs,
                                        },
                                      ]}
                                    >
                                      {isLegacyPendingReviewMessage
                                        ? `Draft confidence: ${message.review.confidencePercent}%`
                                        : `${t(
                                            translationKeys.shell
                                              .workspaceChatConfidence,
                                          )} ${message.review.confidencePercent}%`}
                                    </Text>
                                    <View
                                      style={[
                                        styles.messageReviewProgressTrack,
                                        {
                                          backgroundColor:
                                            isLegacyPendingReviewMessage
                                              ? theme.statusChip.warning.border
                                              : theme.headerBand,
                                          width: showLegacyChatStage ? 94 : 112,
                                        },
                                      ]}
                                    >
                                      <View
                                        style={[
                                          styles.messageReviewProgressFill,
                                          {
                                            backgroundColor:
                                              isLegacyPendingReviewMessage
                                                ? theme.success
                                                : theme.surface,
                                            width: `${message.review.confidencePercent}%`,
                                          },
                                        ]}
                                      />
                                    </View>
                                  </View>
                                ) : null}

                                {(message.review.canApprove ||
                                  message.review.canReject ||
                                  message.review.canEdit) &&
                                !isLegacyPendingReviewMessage ? (
                                  <Text
                                    style={[
                                      styles.sectionSubtitle,
                                      {
                                        color: theme.textSecondary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.xs,
                                        lineHeight:
                                          tokens.typography.lineHeight.sm,
                                      },
                                    ]}
                                  >
                                    {message.review.confidencePercent != null
                                      ? message.review.criticalityPercent !=
                                        null
                                        ? `${t(
                                            translationKeys.shell
                                              .workspaceChatCriticality,
                                          )} ${message.review.criticalityPercent}%`
                                        : t(
                                            translationKeys.shell
                                              .workspaceChatReviewActionNotice,
                                          )
                                      : t(
                                          translationKeys.shell
                                            .workspaceChatReviewActionNotice,
                                        )}
                                  </Text>
                                ) : null}
                              </View>

                              {message.review.clinicianNotesText ? (
                                <View
                                  style={[
                                    styles.messageReviewNotesCard,
                                    {
                                      backgroundColor:
                                        isLegacyPendingReviewMessage
                                          ? theme.surface
                                          : showLegacyChatStage
                                            ? "transparent"
                                            : theme.surface,
                                      borderColor: isLegacyPendingReviewMessage
                                        ? theme.statusChip.warning.border
                                        : theme.borderSubtle,
                                      borderRadius: isLegacyPendingReviewMessage
                                        ? 4
                                        : showLegacyChatStage
                                          ? 0
                                          : layout.controlRadius,
                                      borderTopWidth:
                                        isLegacyPendingReviewMessage ? 0 : 1,
                                      borderWidth: showLegacyChatStage ? 0 : 1,
                                      gap: tokens.spacing.xs,
                                      padding: isLegacyPendingReviewMessage
                                        ? tokens.spacing.sm
                                        : showLegacyChatStage
                                          ? 0
                                          : tokens.spacing.sm,
                                      paddingTop: isLegacyPendingReviewMessage
                                        ? tokens.spacing.sm
                                        : showLegacyChatStage
                                          ? tokens.spacing.xs
                                          : tokens.spacing.sm,
                                    },
                                  ]}
                                >
                                  <Text
                                    style={[
                                      styles.metricLabel,
                                      {
                                        color: isLegacyPendingReviewMessage
                                          ? theme.textSecondary
                                          : theme.textSecondary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.xs,
                                      },
                                    ]}
                                  >
                                    {isLegacyPendingReviewMessage
                                      ? "Review notes"
                                      : t(
                                          translationKeys.shell
                                            .workspaceChatClinicianNotes,
                                        )}
                                  </Text>
                                  <Text
                                    style={[
                                      styles.sectionSubtitle,
                                      {
                                        color: isLegacyPendingReviewMessage
                                          ? theme.textPrimary
                                          : theme.textPrimary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.sm,
                                        lineHeight:
                                          tokens.typography.lineHeight.md,
                                      },
                                    ]}
                                  >
                                    {message.review.clinicianNotesText}
                                  </Text>
                                </View>
                              ) : null}

                              {message.review.canApprove ||
                              message.review.canReject ||
                              message.review.canEdit ? (
                                <View
                                  style={[
                                    styles.messageReviewLegacyActions,
                                    {
                                      gap: showLegacyChatStage ? 4 : 2,
                                      marginTop: showLegacyChatStage
                                        ? tokens.spacing.xs
                                        : 0,
                                    },
                                  ]}
                                >
                                  {message.review.canReject
                                    ? renderLegacyReviewActionButton({
                                        disabled:
                                          isWorkbenchActionSubmitting ||
                                          isEditingReviewMessage,
                                        label: t(
                                          translationKeys.shell
                                            .workspaceActionReject,
                                        ),
                                        onPress: () =>
                                          onRejectMessageReview(message.id),
                                        theme,
                                        tone: "danger",
                                        tokens,
                                      })
                                    : null}
                                  {message.review.canEdit
                                    ? renderLegacyReviewActionButton({
                                        disabled:
                                          isWorkbenchActionSubmitting ||
                                          (isEditingReviewMessage &&
                                            reviewEditDraft.trim().length ===
                                              0),
                                        label: t(
                                          isEditingReviewMessage
                                            ? translationKeys.shell
                                                .workspaceActionSave
                                            : translationKeys.shell
                                                .workspaceActionEdit,
                                        ),
                                        onPress: () =>
                                          isEditingReviewMessage
                                            ? onSaveReviewEdit(
                                                message.id,
                                                message.bodyText,
                                              )
                                            : onBeginReviewEdit(
                                                message.id,
                                                rawDisplayedMessageBodyText,
                                              ),
                                        theme,
                                        tone: "warning",
                                        tokens,
                                      })
                                    : null}
                                  {message.review.canApprove
                                    ? renderLegacyReviewActionButton({
                                        disabled:
                                          isWorkbenchActionSubmitting ||
                                          isEditingReviewMessage,
                                        label: t(
                                          translationKeys.shell
                                            .workspaceActionApprove,
                                        ),
                                        onPress: () =>
                                          onApproveMessageReview(
                                            message.id,
                                            submittedEditedReviewBodyText,
                                          ),
                                        theme,
                                        tone: "success",
                                        tokens,
                                      })
                                    : null}
                                </View>
                              ) : null}
                            </View>
                          ) : null}
                        </View>

                        {showAvatar && message.alignment === "right"
                          ? messageAvatar
                          : null}
                        </View>
                      </View>
                    );
                  })}

                  {isWorkbenchActionSubmitting ? (
                    <View
                      style={[
                        styles.messageRow,
                        showLegacyChatStage
                          ? styles.messageRowRight
                          : styles.messageRowLeft,
                        {
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      {!showLegacyChatStage ? (
                        <View
                          style={[
                            styles.messageAvatar,
                            {
                              backgroundColor: theme.surface,
                              borderColor: theme.borderStrong,
                              borderRadius: 22,
                            },
                          ]}
                        >
                          <Text
                            style={[
                              styles.messageAvatarText,
                              {
                                color: theme.accentStrong,
                                fontFamily: layout.bodyFontFamily,
                              },
                            ]}
                          >
                            AC
                          </Text>
                        </View>
                      ) : null}

                      <View
                        style={[
                          styles.messageCard,
                          styles.messageBubble,
                          styles.messageBubbleIncoming,
                          {
                            backgroundColor: theme.surface,
                            borderColor: theme.borderSubtle,
                            borderRadius: layout.controlRadius,
                            gap: tokens.spacing.sm,
                            maxWidth: showLegacyChatStage ? "100%" : "82%",
                            padding: tokens.spacing.md,
                          },
                        ]}
                      >
                        <View
                          style={[
                            styles.messageSubmittingCard,
                            {
                              gap: tokens.spacing.sm,
                            },
                          ]}
                        >
                          <View
                            style={[
                              styles.messageSubmittingHeader,
                              {
                                gap: tokens.spacing.sm,
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.messageSenderText,
                                {
                                  color: theme.textPrimary,
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: tokens.typography.fontSize.xs,
                                },
                              ]}
                            >
                              {t(
                                translationKeys.shell.workspaceActionSubmitting,
                              )}
                            </Text>

                            <View
                              style={[
                                styles.messageTypingDots,
                                {
                                  gap: tokens.spacing.xs,
                                },
                              ]}
                            >
                              {["dot-1", "dot-2", "dot-3"].map(
                                (dotId, index) => (
                                  <View
                                    key={dotId}
                                    style={[
                                      styles.messageTypingDot,
                                      {
                                        backgroundColor: theme.accentStrong,
                                        opacity: index === 1 ? 0.7 : 1,
                                      },
                                    ]}
                                  />
                                ),
                              )}
                            </View>
                          </View>

                          <Text
                            style={[
                              styles.sectionSubtitle,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: tokens.typography.fontSize.sm,
                                lineHeight: tokens.typography.lineHeight.md,
                              },
                            ]}
                          >
                            {t(
                              translationKeys.shell.workspaceChatSubmittingBody,
                            )}
                          </Text>
                        </View>
                      </View>
                    </View>
                  ) : null}
                </ScrollView>
              </View>

              <View
                style={[
                  styles.composerRow,
                  {
                    backgroundColor: theme.surfaceElevated,
                    borderColor: showLegacyChatStage
                      ? theme.borderSubtle
                      : theme.borderStrong,
                    borderRadius: workforceOperationalLayout.panelRadius,
                    gap: showLegacyChatStage
                      ? tokens.spacing.xs
                      : tokens.spacing.sm,
                    paddingHorizontal: tokens.spacing.sm,
                    paddingVertical: tokens.spacing.sm,
                  },
                ]}
              >
                {showLegacyChatStage ? (
                  <>
                    {renderComposerModeControl()}
                    <View
                      style={[
                        styles.legacyComposerForm,
                        {
                          alignItems: "center",
                          flexDirection: isWideLayout ? "row" : "column",
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <View
                        style={[
                          styles.legacyComposerInputSurface,
                          {
                            backgroundColor: theme.backgroundSubtle,
                            borderColor: theme.borderSubtle,
                            borderRadius:
                              workforceOperationalLayout.controlRadius,
                            flex: 1,
                            paddingHorizontal: tokens.spacing.md,
                            paddingVertical: 7,
                          },
                        ]}
                      >
                        <TextInput
                          accessibilityLabel={
                            composerMode === "reply"
                              ? "Reply message"
                              : composerMode === "note"
                                ? "Clinical note"
                                : "Handoff note"
                          }
                          editable={!isWorkbenchActionSubmitting}
                          multiline={true}
                          onChangeText={setComposerDraft}
                          placeholder={selectedComposerModeConfig.helperText}
                          placeholderTextColor={theme.textTertiary}
                          style={[
                            styles.composerInput,
                            {
                              color: theme.textPrimary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize:
                                workforceOperationalTypography.body.fontSize,
                              lineHeight:
                                workforceOperationalTypography.body.lineHeight,
                              minHeight: workforceOperationalLayout.controlHeight,
                            },
                          ]}
                          value={composerDraft}
                        />
                      </View>

                      <View
                        style={[
                          styles.legacyComposerButtonWrap,
                          {
                            justifyContent: isWideLayout
                              ? "center"
                              : "flex-end",
                          },
                        ]}
                      >
                        <Pressable
                          accessibilityLabel={composerSubmitLabel}
                          accessibilityRole="button"
                          accessibilityState={{ disabled: !canSendReply }}
                          aria-disabled={!canSendReply}
                          disabled={!canSendReply}
                          onPress={submitComposerDraft}
                          style={(state) => {
                            const stateRecord = state as unknown as Record<
                              string,
                              unknown
                            >;
                            const hovered = stateRecord.hovered === true;
                            const focused = stateRecord.focused === true;

                            return [
                              styles.inlinePill,
                              {
                                alignItems: "center",
                                backgroundColor: canSendReply
                                  ? hovered || focused || state.pressed
                                    ? theme.accentStrong
                                    : theme.accent
                                  : theme.surfaceMuted,
                                borderColor: canSendReply
                                  ? theme.accent
                                  : theme.borderSubtle,
                                borderRadius:
                                  workforceOperationalLayout.controlRadius,
                                height: isWideLayout
                                  ? workforceOperationalLayout.controlHeight
                                  : workforceOperationalLayout.mobileControlHeight,
                                justifyContent: "center",
                                opacity: canSendReply ? 1 : 0.62,
                                shadowOpacity: 0,
                                width: isWideLayout
                                  ? workforceOperationalLayout.controlHeight
                                  : workforceOperationalLayout.mobileControlHeight,
                              },
                            ];
                          }}
                        >
                          <LineIcon
                            color={
                              canSendReply
                                ? theme.headerText
                                : theme.textSecondary
                            }
                            name={
                              isWorkbenchActionSubmitting
                                ? "clock"
                                : composerMode === "reply"
                                  ? "chevron-right"
                                  : "edit"
                            }
                            size={21}
                            strokeWidth={2.45}
                          />
                        </Pressable>
                      </View>
                    </View>

                    {workbenchActionFeedbackKey ? (
                      <View
                        style={[
                          styles.flagRow,
                          {
                            gap: tokens.spacing.xs,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.sectionSubtitle,
                            {
                              color: resolveWorkbenchActionFeedbackColor(
                                workbenchActionFeedbackKey,
                                theme,
                              ),
                              fontFamily: layout.bodyFontFamily,
                              fontSize: tokens.typography.fontSize.sm,
                              lineHeight: tokens.typography.lineHeight.md,
                            },
                          ]}
                        >
                          {workbenchActionFeedbackText ?? t(workbenchActionFeedbackKey)}
                        </Text>
                      </View>
                    ) : null}
                  </>
                ) : (
                  <>
                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          flexWrap: "wrap",
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <View
                        style={[
                          styles.listGroup,
                          {
                            flexShrink: 1,
                            gap: tokens.spacing.xs,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.surfaceTitle,
                            {
                              color: theme.textPrimary,
                              fontFamily: layout.displayFontFamily,
                              fontSize: tokens.typography.fontSize.md,
                              lineHeight: tokens.typography.lineHeight.md,
                            },
                          ]}
                        >
                          {`${selectedComposerModeConfig.labelText} composer`}
                        </Text>
                        <Text
                          style={[
                            styles.sectionSubtitle,
                            {
                              color: theme.textSecondary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: tokens.typography.fontSize.sm,
                              lineHeight: tokens.typography.lineHeight.md,
                            },
                          ]}
                        >
                          {selectedComposerModeConfig.helperText}
                        </Text>
                      </View>

                      {pendingApprovalCount > 0 ? (
                        <View
                          style={[
                            styles.inlinePill,
                            {
                              backgroundColor: theme.surfaceMuted,
                              borderColor: theme.borderSubtle,
                              borderRadius: 8,
                              paddingHorizontal: tokens.spacing.sm,
                              paddingVertical: tokens.spacing.xs,
                            },
                          ]}
                        >
                          <Text
                            style={[
                              styles.pillValueText,
                              {
                                color: theme.warning,
                                fontFamily: layout.bodyFontFamily,
                              },
                            ]}
                          >
                            {`${t(translationKeys.shell.workspaceDetailApprovals)} ${pendingApprovalCount}`}
                          </Text>
                        </View>
                      ) : null}
                    </View>

                    {renderComposerModeControl()}

                    <View
                      style={[
                        styles.legacyComposerForm,
                        {
                          alignItems: isWideLayout ? "flex-end" : "stretch",
                          flexDirection: isWideLayout ? "row" : "column",
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <View
                        style={[
                          styles.legacyComposerInputSurface,
                          {
                            backgroundColor: theme.backgroundSubtle,
                            borderColor: theme.borderSubtle,
                            borderRadius: 16,
                            paddingHorizontal: tokens.spacing.sm,
                            paddingVertical: tokens.spacing.xs,
                          },
                        ]}
                      >
                        <TextInput
                          editable={!isWorkbenchActionSubmitting}
                          multiline={true}
                          onChangeText={setComposerDraft}
                          placeholder={selectedComposerModeConfig.helperText}
                          placeholderTextColor={theme.textTertiary}
                          style={[
                            styles.composerInput,
                            {
                              color: theme.textPrimary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: tokens.typography.fontSize.sm,
                              lineHeight: tokens.typography.lineHeight.md,
                              minHeight: 56,
                            },
                          ]}
                          value={composerDraft}
                        />
                      </View>

                      <View
                        style={[
                          styles.legacyComposerButtonWrap,
                          {
                            justifyContent: isWideLayout
                              ? "center"
                              : "flex-end",
                          },
                        ]}
                      >
                        {renderWorkbenchActionButton({
                          disabled: !canSendReply,
                          label: composerSubmitLabel,
                          labelLines: 1,
                          minHeight:
                            workforceOperationalLayout.mobileControlHeight,
                          minWidth: clinicianReplyActionButtonWidth,
                          onPress: submitComposerDraft,
                          theme,
                          tone: "accent",
                          tokens,
                          width: clinicianReplyActionButtonWidth,
                        })}
                      </View>
                    </View>
                    {workbenchActionFeedbackKey ? (
                      <Text
                        style={[
                          styles.sectionSubtitle,
                          {
                            color: resolveWorkbenchActionFeedbackColor(
                              workbenchActionFeedbackKey,
                              theme,
                            ),
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.sm,
                            lineHeight: tokens.typography.lineHeight.md,
                          },
                        ]}
                      >
                        {workbenchActionFeedbackText ?? t(workbenchActionFeedbackKey)}
                      </Text>
                    ) : null}
                  </>
                )}
              </View>
            </View>
          </View>
          </>
          )}
        </>
      )}
    </View>
  );
}

function renderLegacyReviewActionButton(input: {
  disabled: boolean;
  iconName?: LineIconName | null;
  label: string;
  onPress: () => void;
  theme: AppShellTheme;
  tone: "success" | "danger" | "warning";
  tokens: typeof designTokens;
}) {
  const backgroundColor =
    input.tone === "success"
      ? input.theme.accent
      : input.tone === "danger"
        ? input.theme.surface
        : input.theme.surface;
  const textColor =
    input.tone === "success"
      ? input.theme.accentContrast
      : input.tone === "warning"
      ? input.theme.textSecondary
      : input.theme.danger;
  const borderColor =
    input.tone === "success"
      ? input.theme.accent
      : input.tone === "danger"
        ? input.theme.statusChip.danger.border
        : input.theme.borderStrong;
  const normalizedLabel = input.label.trim().toLowerCase();
  const iconName =
    input.iconName === undefined
      ? normalizedLabel.includes("approve") ||
        normalizedLabel.includes("complete")
        ? "check"
        : normalizedLabel.includes("reject") || input.tone === "danger"
          ? "x"
          : normalizedLabel.includes("edit")
            ? "edit"
            : normalizedLabel.includes("archive")
              ? "archive"
              : null
      : input.iconName;

  return (
    <Pressable
      accessibilityLabel={input.label}
      accessibilityRole="button"
      accessibilityState={{ disabled: input.disabled }}
      aria-disabled={input.disabled}
      disabled={input.disabled}
      onPress={input.onPress}
      style={[
        styles.messageReviewLegacyActionButton,
        {
          backgroundColor: input.disabled
            ? input.theme.surfaceMuted
            : backgroundColor,
          borderRadius: 14,
          borderColor: input.disabled ? input.theme.borderSubtle : borderColor,
          flexDirection: "row",
          gap: iconName == null ? 0 : 7,
          minHeight: workforceOperationalLayout.mobileControlHeight,
          opacity: input.disabled ? 0.65 : 1,
          paddingHorizontal: input.tokens.spacing.md,
          paddingVertical: input.tokens.spacing.sm,
        },
      ]}
    >
      {iconName == null ? null : (
        <LineIcon
          color={input.disabled ? input.theme.textSecondary : textColor}
          name={iconName}
          size={15}
          strokeWidth={2.45}
        />
      )}
      <Text
        style={[
          styles.messageReviewLegacyActionLabel,
          {
            color: input.disabled ? input.theme.textSecondary : textColor,
            fontFamily: designTokens.semanticLayout.bodyFontFamily,
          },
        ]}
      >
        {input.label}
      </Text>
    </Pressable>
  );
}

function resolveWorkbenchActionFeedbackColor(
  key: TranslationKey,
  theme: AppShellTheme,
): string {
  if (key === translationKeys.shell.workspaceActionError) {
    return theme.danger;
  }

  if (key === translationKeys.shell.workspaceActionSubmitting) {
    return theme.textSecondary;
  }

  return theme.success;
}

export function renderDetailsCard(context: DetailsRenderContext) {
  const {
    detailSections,
    inlineClinicalEditor,
    isDocumentUploadSubmitting,
    isWorkbenchActionSubmitting,
    isWideLayout,
    layout,
    legacyStageHeight,
    outboundQuestionnaireCalls,
    outboundQuestionnaireErrorText,
    outboundQuestionnaireIsLoading,
    outboundQuestionnaireNoticeText,
    outboundQuestionnairePdfPendingTargetId,
    outboundQuestionnairePendingTargetId,
    onOpenClinicalNoteEditor,
    onOpenClinicalRecordEditor,
    onOpenPatientProfileSection,
    onOpenVoiceCallTranscript,
    onDownloadOutboundQuestionnaireAnswersPdf,
    onRefreshOutboundQuestionnaireCalls,
    onStartOutboundQuestionnaireCall,
    onSubmitWorkbenchAction,
    onUploadDocument,
    patientIdentityText,
    selectedDetailSection,
    selectedDetailModalViewId,
    selectedDetailTabId,
    selectedPatient,
    showDetailDialog,
    setInlineClinicalEditor,
    setSelectedDetailModalViewId,
    setSelectedDetailTabId,
    t,
    theme,
    tokens,
  } = context;
  const contactWindowText =
    selectedPatient == null
      ? null
      : findDetailRowValue(selectedPatient.detailSections, "contact-window");
  const wideLegacyStageHeight = legacyStageHeight ?? 535;
  const showLegacyDetailModal = selectedPatient != null && showDetailDialog;
  const showLegacyInlineAccordion =
    selectedPatient != null &&
    isWideLayout &&
    legacyStageHeight != null &&
    !showLegacyDetailModal;
  const upcomingProcedureText =
    selectedPatient == null
      ? null
      : findDetailRowValue(
          selectedPatient.detailSections,
          "upcoming-procedure",
        );
  const documentGroups =
    selectedPatient == null
      ? []
      : groupDocumentSummariesByAvailability(selectedPatient.documentSummaries);
  const chartPackGapCounts =
    selectedPatient == null
      ? []
      : chartPackDocuments.map(
          (definition) =>
            computeChartPackDocumentGaps(selectedPatient.record, definition)
              .length,
        );
  const chartPackReadyCount = chartPackGapCounts.filter(
    (count) => count === 0,
  ).length;
  const chartPackMissingFieldCount = chartPackGapCounts.reduce(
    (total, count) => total + count,
    0,
  );
  const chartPackStatusText =
    chartPackReadyCount === chartPackDocuments.length
      ? "Ready to print"
      : `${String(chartPackMissingFieldCount)} field${chartPackMissingFieldCount === 1 ? "" : "s"} missing`;
  const chartPackSummaryText =
    "Printable day-of-surgery documents populated from this patient's record. Missing fields print as blanks.";
  const informationSection =
    selectedPatient == null
      ? null
      : findDetailSection(selectedPatient.detailSections, "information");
  const primaryInformationRows =
    selectedPatient == null
      ? []
      : [
          {
            id: "identifier",
            labelText: t(translationKeys.shell.workspacePatientIdentifier),
            valueText: selectedPatient.listMetaText,
          },
          ...(informationSection?.rows ?? []),
        ].filter((row) => row.valueText.trim().length > 0);
  const clinicianModalChrome = getClinicianDetailModalChrome(theme);
  const clinicianModalControlHeight = isWideLayout
    ? workforceOperationalLayout.controlHeight
    : workforceOperationalLayout.mobileControlHeight;
  const detailSectionIconName: Record<DetailTabId, LineIconName> = {
    appointments: "appointment",
    "case-summary": "case",
    documents: "document",
    information: "user",
    medications: "medication",
    "phone-calls": "phone",
    procedures: "procedure",
  };
  const resolveClinicianModalActionColor = (tone: WorkbenchActionTone) =>
    tone === "danger" ? theme.danger : theme.accentStrong;
  const resolveClinicianModalActionIcon = (
    tone: WorkbenchActionTone,
  ): LineIconName =>
    tone === "success"
      ? "check"
      : tone === "warning" || tone === "danger"
        ? "stop"
        : tone === "info"
          ? "info"
          : "chevron-right";
  const renderClinicianModalActionButton = (input: {
    disabled: boolean;
    label: string;
    minWidth?: number;
    onPress: () => void;
    tone: WorkbenchActionTone;
    width?: number;
  }) => {
    const toneColor = resolveClinicianModalActionColor(input.tone);
    const disabled = input.disabled;

    return (
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled }}
        aria-disabled={disabled}
        disabled={disabled}
        onPress={input.onPress}
        style={({ pressed }) => [
          styles.inlinePill,
          {
            alignItems: "center",
            alignSelf: "flex-start",
            backgroundColor: disabled
              ? theme.surfaceMuted
              : pressed
                ? theme.backgroundSubtle
                : theme.surface,
            borderColor: disabled ? theme.borderSubtle : toneColor,
            borderRadius: workforceOperationalLayout.controlRadius,
            flexDirection: "row",
            gap: 7,
            justifyContent: "center",
            minHeight: clinicianModalControlHeight,
            minWidth: input.minWidth ?? 96,
            opacity: disabled ? 0.55 : 1,
            paddingHorizontal: 10,
            paddingVertical: 5,
            width: input.width,
          },
        ]}
      >
        <View
          style={[
            styles.patientDesktopAgentIconButton,
            {
              backgroundColor: disabled ? theme.surface : theme.backgroundSubtle,
              borderColor: disabled ? theme.borderSubtle : toneColor,
              borderRadius: 9,
              height: 18,
              width: 18,
            },
          ]}
        >
          <LineIcon
            color={disabled ? theme.textSecondary : toneColor}
            name={resolveClinicianModalActionIcon(input.tone)}
            size={12}
            strokeWidth={2.4}
          />
        </View>
        <Text
          ellipsizeMode="tail"
          numberOfLines={1}
          style={[
            styles.badgeText,
            {
              color: disabled ? theme.textSecondary : toneColor,
              fontFamily: layout.bodyFontFamily,
              fontSize: 12,
              lineHeight: 16,
            },
          ]}
        >
          {input.label}
        </Text>
      </Pressable>
    );
  };

  const renderClinicianDetailSummaryCard = (input: {
    eyebrowText: string;
    leadText: string;
    metricRows: ReadonlyArray<{
      id: string;
      labelText: string;
      valueText: string;
    }>;
    summaryText: string;
    titleText: string;
    tone: "neutral" | "accent" | "warning" | "info";
  }) => {
    const palette = getLegacyDetailPreviewPalette(input.tone, theme);
    const useCompactModalDensity = showLegacyDetailModal;
    const cardBackgroundColor = useCompactModalDensity
      ? clinicianModalChrome.headerCardBackgroundColor
      : palette.backgroundColor;
    const cardBorderColor = useCompactModalDensity
      ? clinicianModalChrome.borderColor
      : palette.borderColor;
    const leadBackgroundColor = useCompactModalDensity
      ? clinicianModalChrome.leadBackgroundColor
      : palette.leadingBackgroundColor;
    const leadTextColor = useCompactModalDensity
      ? clinicianModalChrome.leadTextColor
      : palette.leadingTextColor;

    return (
      <View
        style={[
          styles.detailHeaderCard,
          {
            backgroundColor: cardBackgroundColor,
            borderColor: cardBorderColor,
            borderRadius: 18,
            gap: useCompactModalDensity ? tokens.spacing.xs : tokens.spacing.sm,
            padding: useCompactModalDensity ? 6 : tokens.spacing.sm,
          },
        ]}
      >
        <View
          style={[
            styles.detailHeaderIdentityRow,
            {
              gap: useCompactModalDensity ? tokens.spacing.xs : tokens.spacing.sm,
            },
          ]}
        >
          <View
            style={[
              styles.detailHeaderAvatar,
              useCompactModalDensity
                ? styles.clinicianDetailModalAvatarCompact
                : null,
              {
                backgroundColor: leadBackgroundColor,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
              },
            ]}
          >
            <Text
              style={[
                styles.detailHeaderAvatarText,
                {
                  color: leadTextColor,
                  fontFamily: layout.displayFontFamily,
                },
              ]}
            >
              {input.leadText}
            </Text>
          </View>

          <View
            style={[
              styles.patientIdentityCopy,
              {
                gap: tokens.spacing.xs,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: leadTextColor,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                  textTransform: "uppercase",
                },
              ]}
            >
              {input.eyebrowText}
            </Text>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: useCompactModalDensity
                    ? tokens.typography.fontSize.sm
                    : tokens.typography.fontSize.md,
                  lineHeight: useCompactModalDensity
                    ? tokens.typography.lineHeight.sm
                    : tokens.typography.lineHeight.md,
                },
              ]}
            >
              {input.titleText}
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: useCompactModalDensity
                    ? tokens.typography.fontSize.xs
                    : tokens.typography.fontSize.sm,
                  lineHeight: useCompactModalDensity
                    ? tokens.typography.lineHeight.xs
                    : tokens.typography.lineHeight.md,
                },
              ]}
            >
              {resolveProductClinicalText(input.summaryText)}
            </Text>
          </View>
        </View>

        <View
          style={[
            styles.detailHeaderMetricsGrid,
            {
              gap: useCompactModalDensity ? tokens.spacing.xs : tokens.spacing.sm,
            },
          ]}
        >
          {input.metricRows
            .filter((row) => row.valueText.trim().length > 0)
            .map((row) => (
              <View
                key={row.id}
                style={[
                  styles.detailHeaderMetricTile,
                  useCompactModalDensity
                    ? resolveClinicianMetricTileStyle(row)
                    : null,
                  {
                    backgroundColor: theme.surface,
                    borderColor: theme.borderSubtle,
                    borderRadius: 14,
                    gap: tokens.spacing.xs,
                    padding: useCompactModalDensity
                      ? tokens.spacing.xs
                      : tokens.spacing.sm,
                  },
                ]}
              >
                <Text
                  numberOfLines={1}
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                    },
                  ]}
                >
                  {row.labelText}
                </Text>
                <Text
                  numberOfLines={2}
                  style={[
                    styles.navigationText,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: useCompactModalDensity
                        ? tokens.typography.fontSize.xs
                        : tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  {row.valueText}
                </Text>
              </View>
            ))}
        </View>
      </View>
    );
  };

  const buildClinicianDetailSummaryMetricRows = (
    section: DetailSectionViewModel,
  ): ClinicianDetailMetricRow[] => {
    if (selectedPatient == null) {
      return [];
    }

    if (section.id === "case-summary") {
      return [
        {
          id: "case-tasks",
          labelText: t(translationKeys.shell.workspaceDetailTaskAssignments),
          valueText: String(selectedPatient.taskAssignments.length),
          size: "compact",
        },
        {
          id: "case-approvals",
          labelText: t(translationKeys.shell.workspaceDetailApprovals),
          valueText: String(selectedPatient.approvalSummaries.length),
          size: "compact",
        },
        {
          id: "case-deliveries",
          labelText: t(translationKeys.shell.workspaceDetailDeliveries),
          valueText: String(selectedPatient.deliverySummaries.length),
          size: "compact",
        },
      ];
    }

    if (section.id === "appointments") {
      return [
        {
          id: "appointments-count",
          labelText: "Items tracked",
          valueText: String(
            section.rows.filter((row) => row.valueText.trim().length > 0)
              .length,
          ),
          size: "regular",
        },
        {
          id: "appointments-coordinator",
          labelText: "Coordinator",
          valueText: selectedPatient.coordinatorText,
          size: "regular",
        },
        {
          id: "appointments-contact-window",
          labelText: "Contact window",
          valueText: contactWindowText ?? "",
          size: "wide",
        },
      ];
    }

    if (section.id === "procedures") {
      return [
        {
          id: "procedures-count",
          labelText: "Items tracked",
          valueText: String(
            section.rows.filter((row) => row.valueText.trim().length > 0)
              .length,
          ),
          size: "regular",
        },
        {
          id: "procedures-coordinator",
          labelText: "Coordinator",
          valueText: selectedPatient.coordinatorText,
          size: "regular",
        },
        {
          id: "procedures-next-appointment",
          labelText: "Next appointment",
          valueText: selectedPatient.nextAppointmentText,
          size: "wide",
        },
      ];
    }

    if (section.id === "medications") {
      return [
        {
          id: "medications-count",
          labelText: "Items tracked",
          valueText: String(
            section.rows.filter((row) => row.valueText.trim().length > 0)
              .length,
          ),
          size: "regular",
        },
        {
          id: "medications-program",
          labelText: "Program",
          valueText: selectedPatient.programText,
          size: "regular",
        },
        {
          id: "medications-procedure",
          labelText: "Procedure",
          valueText: upcomingProcedureText ?? "",
          size: "wide",
        },
      ];
    }

    if (section.id === "documents") {
      return [
        {
          id: "documents-total",
          labelText: "Documents",
          valueText: String(selectedPatient.documentSummaries.length),
          size: "compact",
        },
        {
          id: "documents-pending",
          labelText: "Pending",
          valueText: String(selectedPatient.pendingDocumentCount),
          size: "compact",
        },
        {
          id: "documents-download-ready",
          labelText: "Available for review",
          valueText: String(
            selectedPatient.documentSummaries.filter(
              (document) => document.availability === "download-ready",
            ).length,
          ),
          size: "regular",
        },
      ];
    }

    if (section.id === "phone-calls") {
      return [
        {
          id: "phone-calls-total",
          labelText: "Calls",
          valueText: String(selectedPatient.voiceCallSummaries.length),
          size: "compact",
        },
        {
          id: "phone-calls-responses",
          labelText: "Assistant responses",
          valueText: String(
            selectedPatient.voiceCallSummaries.reduce(
              (sum, voiceCall) =>
                sum +
                voiceCall.transcriptTurns.filter(
                  (turn) => turn.speakerKind === "system",
                ).length,
              0,
            ),
          ),
          size: "regular",
        },
        {
          id: "phone-calls-handoffs",
          labelText: "Staff handoffs",
          valueText: String(
            selectedPatient.voiceCallSummaries.filter(
              (voiceCall) => voiceCall.hasClinicianHandoff,
            ).length,
          ),
          size: "regular",
        },
      ];
    }

    return section.summaryMetrics.map((metric) => ({
      id: metric.id,
      labelText: t(metric.labelKey),
      valueText: metric.valueText,
      size: "regular",
    }));
  };

  const resolveClinicianDetailRowLeadText = (
    row: DetailSectionViewModel["rows"][number],
  ) => {
    const parsedDate = parseWorkbenchDateTimeText(row.valueText);

    return parsedDate
      ? formatLegacyPreviewDatePrimaryText(parsedDate)
      : resolveLegacyPreviewFallbackPrimaryText(row.labelText);
  };

  const buildClinicianDetailBodyMetricRows = (
    sectionId: DetailTabId,
    row: DetailSectionViewModel["rows"][number],
  ): ClinicianDetailMetricRow[] => {
    if (selectedPatient == null) {
      return [];
    }

    if (sectionId === "appointments") {
      return [
        {
          id: `${row.id}-coordinator`,
          labelText: "Coordinator",
          valueText: selectedPatient.coordinatorText,
          size: "regular",
        },
        {
          id: `${row.id}-program`,
          labelText: "Program",
          valueText: selectedPatient.programText,
          size: "regular",
        },
        {
          id: `${row.id}-contact-window`,
          labelText: "Contact window",
          valueText: contactWindowText ?? "",
          size: "wide",
        },
      ];
    }

    if (sectionId === "procedures") {
      return [
        {
          id: `${row.id}-program`,
          labelText: "Program",
          valueText: selectedPatient.programText,
          size: "regular",
        },
        {
          id: `${row.id}-coordinator`,
          labelText: "Coordinator",
          valueText: selectedPatient.coordinatorText,
          size: "regular",
        },
        {
          id: `${row.id}-appointment`,
          labelText: "Next appointment",
          valueText: selectedPatient.nextAppointmentText,
          size: "wide",
        },
      ];
    }

    if (sectionId === "medications") {
      return [
        {
          id: `${row.id}-program`,
          labelText: "Program",
          valueText: selectedPatient.programText,
          size: "regular",
        },
        {
          id: `${row.id}-procedure`,
          labelText: "Upcoming procedure",
          valueText: upcomingProcedureText ?? "",
          size: "wide",
        },
        {
          id: `${row.id}-contact-window`,
          labelText: "Contact window",
          valueText: contactWindowText ?? "",
          size: "wide",
        },
      ];
    }

    return [];
  };

  const renderClinicianDetailBodyCard = (input: {
    actionConfig?: {
      disabled: boolean;
      label: string;
      onPress: () => void;
      tone: WorkbenchActionTone;
    };
    eyebrowText: string;
    id: string;
    leadLabelText?: string;
    leadText: string;
    metricRows: ReadonlyArray<ClinicianDetailMetricRow>;
    notes?: ReadonlyArray<ClinicalNoteViewModel>;
    summaryText: string;
    titleText: string;
    tone: "neutral" | "accent" | "warning" | "info";
  }) => {
    const palette = getLegacyDetailPreviewPalette(input.tone, theme);
    const useCompactModalDensity = showLegacyDetailModal;
    const cardBackgroundColor = useCompactModalDensity
      ? clinicianModalChrome.bodyCardBackgroundColor
      : palette.backgroundColor;
    const cardBorderColor = useCompactModalDensity
      ? clinicianModalChrome.borderColor
      : palette.borderColor;
    const leadBackgroundColor = useCompactModalDensity
      ? clinicianModalChrome.leadBackgroundColor
      : palette.leadingBackgroundColor;
    const leadTextColor = useCompactModalDensity
      ? clinicianModalChrome.leadTextColor
      : palette.leadingTextColor;
    return (
      <View
        key={input.id}
        style={[
          styles.detailSectionCard,
          {
            backgroundColor: cardBackgroundColor,
            borderColor: cardBorderColor,
            borderRadius: 16,
            gap: useCompactModalDensity ? tokens.spacing.xs : tokens.spacing.sm,
            padding: useCompactModalDensity ? 6 : tokens.spacing.sm,
          },
        ]}
      >
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "flex-start",
              gap: useCompactModalDensity ? tokens.spacing.xs : tokens.spacing.sm,
              justifyContent: "flex-start",
            },
          ]}
        >
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: "flex-start",
                flex: 1,
                gap: useCompactModalDensity ? tokens.spacing.xs : tokens.spacing.sm,
                justifyContent: "flex-start",
              },
            ]}
          >
            <View
              style={[
                styles.detailDocumentLeadColumn,
                useCompactModalDensity
                  ? styles.clinicianDetailModalLeadColumnCompact
                  : null,
                {
                  backgroundColor: leadBackgroundColor,
                  borderColor: theme.borderSubtle,
                  borderRadius: 14,
                  gap: useCompactModalDensity ? 2 : tokens.spacing.xs,
                  padding: useCompactModalDensity
                    ? 6
                    : tokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.detailDocumentGlyphText,
                  {
                    color: leadTextColor,
                    fontFamily: layout.displayFontFamily,
                  },
                ]}
              >
                {input.leadText}
              </Text>
              <Text
                numberOfLines={1}
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: useCompactModalDensity
                      ? 10
                      : tokens.typography.fontSize.xs,
                    lineHeight: useCompactModalDensity
                      ? 12
                      : tokens.typography.lineHeight.xs,
                    textAlign: "center",
                    textTransform: "uppercase",
                  },
                ]}
              >
                {input.leadLabelText ?? input.eyebrowText}
              </Text>
            </View>

            <View
              style={[
                styles.patientIdentityCopy,
                {
                  gap: useCompactModalDensity ? 2 : tokens.spacing.xs,
                },
              ]}
            >
              <Text
              style={[
                styles.metricLabel,
                {
                  color: leadTextColor,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                  textTransform: "uppercase",
                  },
                ]}
              >
                {input.eyebrowText}
              </Text>
              <Text
                style={[
                  styles.surfaceTitle,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                    fontSize: useCompactModalDensity
                      ? tokens.typography.fontSize.sm
                      : tokens.typography.fontSize.md,
                    lineHeight: useCompactModalDensity
                      ? tokens.typography.lineHeight.sm
                      : tokens.typography.lineHeight.md,
                  },
                ]}
              >
                {input.titleText}
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: useCompactModalDensity
                      ? tokens.typography.fontSize.xs
                      : tokens.typography.fontSize.sm,
                    lineHeight: useCompactModalDensity
                      ? tokens.typography.lineHeight.xs
                      : tokens.typography.lineHeight.md,
                  },
                ]}
              >
                {resolveProductClinicalText(input.summaryText)}
              </Text>
              {(input.notes ?? []).length > 0 ? (
                <View
                  style={[
                    styles.detailHeaderMetricTile,
                    {
                      backgroundColor: theme.surface,
                      borderColor: theme.borderSubtle,
                      borderRadius: layout.controlRadius,
                      gap: tokens.spacing.xs,
                      padding: useCompactModalDensity ? 6 : tokens.spacing.sm,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    Clinical note
                  </Text>
                  {(input.notes ?? []).map((note) => (
                    <Text
                      key={note.id}
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: useCompactModalDensity
                            ? tokens.typography.fontSize.xs
                            : tokens.typography.fontSize.sm,
                          lineHeight: useCompactModalDensity
                            ? tokens.typography.lineHeight.xs
                            : tokens.typography.lineHeight.md,
                        },
                      ]}
                    >
                      {note.bodyText}
                    </Text>
                  ))}
                </View>
              ) : null}
            </View>
          </View>

          {input.actionConfig ? (
            <View
              style={{
                alignItems: "flex-end",
                flexShrink: 0,
                justifyContent: "flex-start",
                paddingTop: tokens.spacing.xs,
              }}
            >
              {useCompactModalDensity
                ? renderClinicianModalActionButton({
                    disabled: input.actionConfig.disabled,
                    label: input.actionConfig.label,
                    minWidth: clinicianModalActionButtonWidth,
                    onPress: input.actionConfig.onPress,
                    tone: input.actionConfig.tone,
                  })
                : renderWorkbenchActionButton({
                    disabled: input.actionConfig.disabled,
                    label: input.actionConfig.label,
                    labelLines: 1,
                    minWidth: clinicianModalActionButtonWidth,
                    onPress: input.actionConfig.onPress,
                    size: "compact",
                    theme,
                    tone: input.actionConfig.tone,
                    tokens,
                    width: clinicianModalActionButtonWidth,
                  })}
            </View>
          ) : null}
        </View>

        {(() => {
          const inlineMetaText = input.metricRows
            .filter((row) => row.valueText.trim().length > 0)
            .map((row) => `${row.labelText}: ${row.valueText}`)
            .join("  ·  ");

          return inlineMetaText.length > 0 ? (
            <Text
              numberOfLines={3}
              style={[
                styles.metricLabel,
                {
                  color: theme.textTertiary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11,
                  lineHeight: 15,
                },
              ]}
            >
              {inlineMetaText}
            </Text>
          ) : null;
        })()}
      </View>
    );
  };

  const detailStageContent =
    selectedPatient == null || selectedDetailSection == null ? null : (
      <View
        style={[
          styles.detailSectionGroup,
          {
            gap: tokens.spacing.sm,
          },
        ]}
      >
            {selectedDetailSection.id === "documents" ? (
              <View
                style={[
                  styles.patientRowHeader,
                  {
                    gap: tokens.spacing.xs,
                    justifyContent: "flex-start",
                  },
                ]}
              >
                {renderWorkbenchActionButton({
                  disabled:
                    isDocumentUploadSubmitting || isWorkbenchActionSubmitting,
                  label: isDocumentUploadSubmitting
                    ? "Uploading document"
                    : "Upload document",
                  minWidth: 168,
                  onPress: onUploadDocument,
                  size: "compact",
                  theme,
                  tone: "accent",
                  tokens,
                })}
              </View>
            ) : null}
            {selectedDetailSection.id === "documents"
              ? renderClinicianDetailBodyCard({
                  actionConfig: {
                    disabled: false,
                    label: "Open chart pack",
                    onPress: () => onOpenPatientProfileSection("chart-pack"),
                    tone: "accent",
                  },
                  eyebrowText: "Chart pack",
                  id: "chart-pack",
                  leadLabelText: "PRINT",
                  leadText: "PACK",
                  metricRows: [
                    {
                      id: "chart-pack-documents",
                      labelText: "Documents",
                      valueText: String(chartPackDocuments.length),
                      size: "compact",
                    },
                    {
                      id: "chart-pack-ready",
                      labelText: "Ready to print",
                      valueText: `${String(chartPackReadyCount)} of ${String(chartPackDocuments.length)}`,
                      size: "regular",
                    },
                    {
                      id: "chart-pack-missing",
                      labelText: "Missing fields",
                      valueText: String(chartPackMissingFieldCount),
                      size: "compact",
                    },
                  ],
                  summaryText: chartPackSummaryText,
                  titleText: "Full chart pack",
                  tone: "accent",
                })
              : null}
            {selectedDetailSection.id === "information" ? (
              <View
                style={[
                  styles.detailSectionGroup,
                  {
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.displayFontFamily,
                      fontSize: tokens.typography.fontSize.lg,
                      lineHeight: tokens.typography.lineHeight.lg,
                    },
                  ]}
                >
                  {selectedPatient.displayName}
                </Text>
                {primaryInformationRows.map((row) => (
                  <View
                    key={row.id}
                    style={[
                      styles.detailSectionGroup,
                      {
                        gap: tokens.spacing.xs,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      {row.labelText}
                    </Text>
                    <Text
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                          lineHeight: tokens.typography.lineHeight.md,
                        },
                      ]}
                    >
                      {row.valueText}
                    </Text>
                  </View>
                ))}
                {selectedPatient.summaryText.trim().length > 0 ? (
                  <View
                    style={[
                      styles.detailSectionCard,
                      {
                        backgroundColor: theme.backgroundSubtle,
                        borderColor: theme.borderSubtle,
                        borderRadius: layout.controlRadius,
                        gap: tokens.spacing.xs,
                        padding: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      Current status
                    </Text>
                    <Text
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                          lineHeight: tokens.typography.lineHeight.md,
                        },
                      ]}
                    >
                      {resolveProductClinicalText(selectedPatient.summaryText)}
                    </Text>
                  </View>
                ) : null}
                {[
                  {
                    id: "program",
                    labelText: t(
                      translationKeys.shell.workspaceInfoPrimaryProgram,
                    ),
                    valueText: selectedPatient.programText,
                  },
                  {
                    id: "appointment",
                    labelText: t(
                      translationKeys.shell.workspaceInfoNextAppointment,
                    ),
                    valueText: selectedPatient.nextAppointmentText,
                  },
                  {
                    id: "contact-window",
                    labelText: "Contact window",
                    valueText: contactWindowText ?? "",
                  },
                ]
                  .filter(
                    (row) =>
                      row.valueText.length > 0 &&
                      !primaryInformationRows.some(
                        (informationRow) =>
                          informationRow.labelText.trim().toLowerCase() ===
                          row.labelText.trim().toLowerCase(),
                      ),
                  )
                  .map((row) => (
                    <View
                      key={row.id}
                      style={[
                        styles.detailSectionCard,
                        {
                          backgroundColor: theme.surface,
                          borderColor: theme.borderSubtle,
                          borderRadius: layout.controlRadius,
                          gap: tokens.spacing.xs,
                          padding: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.xs,
                          },
                        ]}
                      >
                        {row.labelText}
                      </Text>
                      <Text
                        style={[
                          styles.navigationText,
                          {
                            color: theme.textPrimary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.sm,
                          },
                        ]}
                      >
                        {row.valueText}
                      </Text>
                    </View>
                  ))}
              </View>
            ) : selectedDetailSection.id === "case-summary" ? (
              <View
                style={[
                  styles.detailSectionGroup,
                  {
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                {!showLegacyDetailModal
                  ? renderClinicianDetailSummaryCard({
                      eyebrowText: "Case summary",
                      leadText: "CASE",
                      metricRows: buildClinicianDetailSummaryMetricRows(
                        selectedDetailSection,
                      ),
                      summaryText:
                        selectedDetailSection.summaryText ??
                        "Task assignments, approvals, and delivery updates.",
                      titleText: selectedDetailSection.titleText,
                      tone: "accent",
                    })
                  : null}

                <View
                  style={[
                    styles.patientRowHeader,
                    {
                      justifyContent: "flex-end",
                    },
                  ]}
                >
                  {renderWorkbenchActionButton({
                    disabled: isWorkbenchActionSubmitting,
                    label: isWorkbenchActionSubmitting
                      ? t(translationKeys.shell.workspaceActionSubmitting)
                      : t(translationKeys.shell.workspaceActionRefreshSummary),
                    labelLines: 1,
                    minWidth: 142,
                    onPress: () =>
                      onSubmitWorkbenchAction({
                        expectedMaterialChangeToken:
                          selectedPatient.materialChangeToken,
                        force: true,
                        kind: "refresh-case-summary",
                        patientId: selectedPatient.id,
                      }),
                    size: "compact",
                    theme,
                    tone: "success",
                    tokens,
                    width: 142,
                  })}
                </View>

                {selectedPatient.taskAssignments.length > 0 ? (
                  <View
                    style={[
                      styles.detailSectionGroup,
                      {
                        gap: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.surfaceTitle,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.displayFontFamily,
                          fontSize: tokens.typography.fontSize.md,
                          lineHeight: tokens.typography.lineHeight.md,
                        },
                      ]}
                    >
                      {t(translationKeys.shell.workspaceDetailTaskAssignments)}
                    </Text>

                    {selectedPatient.taskAssignments.map((task) =>
                      renderClinicianDetailBodyCard({
                        actionConfig: task.canComplete
                          ? {
                              disabled: isWorkbenchActionSubmitting,
                              label: t(
                                translationKeys.shell
                                  .workspaceActionCompleteTask,
                              ),
                              onPress: () =>
                                onSubmitWorkbenchAction({
                                  kind: "complete-task-assignment",
                                  patientId: selectedPatient.id,
                                  expectedMaterialChangeToken:
                                    selectedPatient.materialChangeToken,
                                  assignmentId: task.id,
                                }),
                              tone: "success",
                            }
                          : undefined,
                        eyebrowText: t(
                          translationKeys.shell.workspaceDetailRowTask,
                        ),
                        id: task.id,
                        leadText: "TASK",
                        metricRows: [
                          {
                            id: `${task.id}-status`,
                            labelText: "Status",
                            valueText: task.statusText,
                          },
                          {
                            id: `${task.id}-program`,
                            labelText: "Program",
                            valueText: selectedPatient.programText,
                          },
                          {
                            id: `${task.id}-contact-window`,
                            labelText: "Contact window",
                            valueText: contactWindowText ?? "",
                          },
                        ],
                        summaryText: `${task.targetKindText}: ${task.targetLabel}`,
                        titleText: task.taskLabel,
                        leadLabelText: "Task",
                        tone: "accent",
                      }),
                    )}
                  </View>
                ) : null}

                {selectedPatient.approvalSummaries.length > 0 ? (
                  <View
                    style={[
                      styles.detailSectionGroup,
                      {
                        gap: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.surfaceTitle,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.displayFontFamily,
                          fontSize: tokens.typography.fontSize.md,
                          lineHeight: tokens.typography.lineHeight.md,
                        },
                      ]}
                    >
                      {t(translationKeys.shell.workspaceDetailApprovals)}
                    </Text>

                    {selectedPatient.approvalSummaries.map((approval) =>
                      renderClinicianDetailBodyCard({
                        eyebrowText: t(
                          translationKeys.shell.workspaceDetailRowApproval,
                        ),
                        id: approval.id,
                        leadText: "APP",
                        metricRows: [
                          {
                            id: `${approval.id}-owner`,
                            labelText: "Owner",
                            valueText: approval.ownerText,
                          },
                          {
                            id: `${approval.id}-status`,
                            labelText: "Status",
                            valueText: approval.statusText,
                          },
                          {
                            id: `${approval.id}-updated`,
                            labelText: "Updated",
                            valueText: approval.updatedAtText,
                          },
                        ],
                        summaryText: `Review owner ${approval.ownerText}`,
                        titleText: approval.labelText,
                        leadLabelText: "Approval",
                        tone: "warning",
                      }),
                    )}
                  </View>
                ) : null}

                {selectedPatient.deliverySummaries.length > 0 ? (
                  <View
                    style={[
                      styles.detailSectionGroup,
                      {
                        gap: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.surfaceTitle,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.displayFontFamily,
                          fontSize: tokens.typography.fontSize.md,
                          lineHeight: tokens.typography.lineHeight.md,
                        },
                      ]}
                    >
                      {t(translationKeys.shell.workspaceDetailDeliveries)}
                    </Text>

                    {selectedPatient.deliverySummaries.map((delivery) =>
                      renderClinicianDetailBodyCard({
                        eyebrowText: t(
                          translationKeys.shell.workspaceDetailRowDelivery,
                        ),
                        id: delivery.id,
                        leadText: "SEND",
                        metricRows: [
                          {
                            id: `${delivery.id}-recipient`,
                            labelText: "Recipient",
                            valueText: delivery.recipientText,
                          },
                          {
                            id: `${delivery.id}-status`,
                            labelText: "Status",
                            valueText: delivery.statusText,
                          },
                          {
                            id: `${delivery.id}-updated`,
                            labelText: "Updated",
                            valueText: delivery.updatedAtText,
                          },
                        ],
                        summaryText: `Delivery to ${delivery.recipientText}`,
                        titleText: delivery.channelText,
                        leadLabelText: "Delivery",
                        tone: "info",
                      }),
                    )}
                  </View>
                ) : null}
              </View>
            ) : selectedDetailSection.id === "appointments" ? (
              <View
                style={[
                  styles.detailSectionGroup,
                  {
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                {!showLegacyDetailModal
                  ? renderClinicianDetailSummaryCard({
                      eyebrowText: "Appointment detail",
                      leadText: "APT",
                      metricRows: buildClinicianDetailSummaryMetricRows(
                        selectedDetailSection,
                      ),
                      summaryText:
                        selectedDetailSection.summaryText ??
                        "Richer appointment detail bodies with denser scheduling and coordinator context.",
                      titleText: selectedDetailSection.titleText,
                      tone: "accent",
                    })
                  : null}
                {selectedDetailSection.rows
                  .filter((row) => row.valueText.length > 0)
                  .map((row) =>
                    renderClinicianDetailBodyCard({
                      id: row.id,
                      eyebrowText: "Appointment item",
                      leadText: resolveClinicianDetailRowLeadText(row),
                      metricRows: buildClinicianDetailBodyMetricRows(
                        "appointments",
                        row,
                      ),
                      summaryText: row.valueText,
                      notes: row.notes,
                      titleText: row.labelText,
                      leadLabelText: "APT",
                      tone: "accent",
                    }),
                  )}
              </View>
            ) : selectedDetailSection.id === "procedures" ? (
              <View
                style={[
                  styles.detailSectionGroup,
                  {
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                {!showLegacyDetailModal
                  ? renderClinicianDetailSummaryCard({
                      eyebrowText: "Procedure detail",
                      leadText: "PROC",
                      metricRows: buildClinicianDetailSummaryMetricRows(
                        selectedDetailSection,
                      ),
                      summaryText:
                        selectedDetailSection.summaryText ??
                        "Procedure detail bodies with scheduling, coordinator, and follow-up context.",
                      titleText: selectedDetailSection.titleText,
                      tone: "warning",
                    })
                  : null}
                {selectedDetailSection.rows
                  .filter((row) => row.valueText.length > 0)
                  .map((row) =>
                    renderClinicianDetailBodyCard({
                      id: row.id,
                      eyebrowText: "Procedure item",
                      leadText: resolveClinicianDetailRowLeadText(row),
                      metricRows: buildClinicianDetailBodyMetricRows(
                        "procedures",
                        row,
                      ),
                      summaryText: row.valueText,
                      notes: row.notes,
                      titleText: row.labelText,
                      leadLabelText: "PROC",
                      tone: "warning",
                    }),
                  )}
              </View>
            ) : selectedDetailSection.id === "medications" ? (
              <View
                style={[
                  styles.detailSectionGroup,
                  {
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                {!showLegacyDetailModal
                  ? renderClinicianDetailSummaryCard({
                      eyebrowText: "Medication detail",
                      leadText: "RX",
                      metricRows: buildClinicianDetailSummaryMetricRows(
                        selectedDetailSection,
                      ),
                      summaryText:
                        selectedDetailSection.summaryText ??
                        "Medication detail bodies with program context and upcoming procedure notes.",
                      titleText: selectedDetailSection.titleText,
                      tone: "info",
                    })
                  : null}
                {selectedDetailSection.rows
                  .filter((row) => row.valueText.length > 0)
                  .map((row) =>
                    renderClinicianDetailBodyCard({
                      id: row.id,
                      eyebrowText: "Medication item",
                      leadText: "RX",
                      metricRows: buildClinicianDetailBodyMetricRows(
                        "medications",
                        row,
                      ),
                      summaryText: row.valueText,
                      notes: row.notes,
                      titleText: row.labelText,
                      leadLabelText: "MED",
                      tone: "info",
                    }),
                  )}
              </View>
            ) : selectedDetailSection.id === "phone-calls" ? (
              <View
                style={[
                  styles.detailSectionGroup,
                  {
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                {!showLegacyDetailModal
                  ? renderClinicianDetailSummaryCard({
                      eyebrowText: "Phone calls",
                      leadText: "CALL",
                      metricRows: buildClinicianDetailSummaryMetricRows(
                        selectedDetailSection,
                      ),
                      summaryText:
                        selectedPatient.voiceCallSummaries.length > 0
                          ? "Voice assistant transcripts are grouped here without mixing them into the regular clinician chat."
                          : "Phone calls will appear here when voice transcripts are available.",
                        titleText: selectedDetailSection.titleText,
                        tone: "info",
                      })
                  : null}
                {selectedPatient.voiceCallSummaries.length > 0 ? (
                  selectedPatient.voiceCallSummaries.map((voiceCall) => {
                    const receivedAt = parseWorkbenchDateTimeText(
                      voiceCall.receivedAtText,
                    );

                    return renderClinicianDetailBodyCard({
                      actionConfig: {
                        disabled: false,
                        label: "Open transcript",
                        onPress: () => onOpenVoiceCallTranscript(voiceCall),
                        tone: "accent",
                      },
                      eyebrowText: "Phone call",
                      id: voiceCall.id,
                      leadText: receivedAt
                        ? formatLegacyPreviewDatePrimaryText(receivedAt)
                        : "CALL",
                      metricRows: [
                        {
                          id: `${voiceCall.id}-received-at`,
                          labelText: "Received",
                          valueText: voiceCall.receivedAtText,
                          size: "regular",
                        },
                        {
                          id: `${voiceCall.id}-turns`,
                          labelText: "Transcript turns",
                          valueText: String(voiceCall.turnCount),
                          size: "compact",
                        },
                        {
                          id: `${voiceCall.id}-source`,
                          labelText: "Source",
                          valueText:
                            voiceCall.sourceText ??
                            "Not provided",
                          size: "wide",
                        },
                        {
                          id: `${voiceCall.id}-handoff`,
                          labelText: "Disposition",
                          valueText: voiceCall.hasClinicianHandoff
                            ? "Staff handoff"
                            : "No staff handoff",
                          size: "wide",
                        },
                      ],
                      summaryText:
                        voiceCall.previewText.length > 0
                          ? voiceCall.previewText
                          : "Transcript available",
                      titleText: voiceCall.questionClassText
                        ? `Call: ${voiceCall.questionClassText}`
                        : "Voice assistant call",
                      leadLabelText: "CALL",
                      tone: "info",
                    });
                  })
                ) : (
                  <View
                    style={[
                      styles.detailSectionCard,
                      {
                        backgroundColor: theme.surfaceMuted,
                        borderColor: theme.borderSubtle,
                        borderRadius: layout.controlRadius,
                        gap: tokens.spacing.xs,
                        padding: tokens.spacing.md,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.surfaceTitle,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.displayFontFamily,
                          fontSize: tokens.typography.fontSize.md,
                          lineHeight: tokens.typography.lineHeight.md,
                        },
                      ]}
                    >
                      No phone calls yet
                    </Text>
                    <Text
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                          lineHeight: tokens.typography.lineHeight.md,
                        },
                      ]}
                    >
                      Voice assistant transcripts will show up here when calls are recorded for this patient.
                    </Text>
                  </View>
                )}
              </View>
            ) : selectedDetailSection.id === "documents" &&
              documentGroups.length > 0 ? (
              <View
                style={[
                  styles.detailSectionGroup,
                  {
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                {!showLegacyDetailModal
                  ? renderClinicianDetailSummaryCard({
                      eyebrowText: "Document detail",
                      leadText: "DOC",
                      metricRows: buildClinicianDetailSummaryMetricRows(
                        selectedDetailSection,
                      ),
                      summaryText:
                        selectedDetailSection.summaryText ??
                        "Download-ready files and metadata-only records grouped into fuller document bodies.",
                      titleText: selectedDetailSection.titleText,
                      tone: "neutral",
                    })
                  : null}
                {documentGroups.map((group) => (
                  <View
                    key={group.availability}
                    style={[
                      styles.detailSectionGroup,
                      {
                        gap: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.surfaceTitle,
                          {
                            color: theme.textPrimary,
                            fontFamily: layout.displayFontFamily,
                            fontSize: tokens.typography.fontSize.md,
                            lineHeight: tokens.typography.lineHeight.md,
                          },
                        ]}
                      >
                        {t(
                          resolveDocumentAvailabilityLabelKey(
                            group.availability,
                          ),
                        )}
                      </Text>
                      <View
                        style={[
                          styles.inlinePill,
                          {
                            backgroundColor: theme.surfaceMuted,
                            borderColor: theme.borderSubtle,
                            borderRadius: 8,
                            paddingHorizontal: tokens.spacing.sm,
                            paddingVertical: tokens.spacing.xs,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.pillValueText,
                            {
                              color: theme.textSecondary,
                              fontFamily: layout.bodyFontFamily,
                            },
                          ]}
                        >
                          {String(group.items.length)}
                        </Text>
                      </View>
                    </View>

                    {group.items.map((document) => {
                      const isDownloadReady =
                        document.availability === "download-ready";

                      return (
                        <View
                          key={document.id}
                          style={[
                            styles.detailDocumentCard,
                            {
                              backgroundColor:
                                clinicianModalChrome.bodyCardBackgroundColor,
                              borderColor: clinicianModalChrome.borderColor,
                              borderRadius: layout.controlRadius,
                              gap: tokens.spacing.sm,
                              padding: tokens.spacing.md,
                            },
                          ]}
                        >
                          <View
                            style={[
                              styles.patientRowHeader,
                              {
                                alignItems: "flex-start",
                                gap: tokens.spacing.sm,
                              },
                            ]}
                          >
                            <View
                              style={[
                                styles.detailDocumentLeadColumn,
                                styles.clinicianDetailModalLeadColumnCompact,
                                {
                                  backgroundColor:
                                    clinicianModalChrome.leadBackgroundColor,
                                  borderColor: theme.borderSubtle,
                                  borderRadius: layout.controlRadius,
                                  gap: tokens.spacing.xs,
                                  padding: tokens.spacing.xs,
                                },
                              ]}
                            >
                              <View
                                style={[
                                  styles.detailDocumentGlyph,
                                  {
                                    backgroundColor: theme.surface,
                                    borderColor: theme.borderSubtle,
                                    borderRadius: layout.controlRadius,
                                  },
                                ]}
                              >
                                <Text
                                  style={[
                                    styles.detailDocumentGlyphText,
                                    {
                                      color: theme.accentStrong,
                                      fontFamily: layout.bodyFontFamily,
                                    },
                                  ]}
                                >
                                  {resolveDocumentBadgeText(document.labelText)}
                                </Text>
                              </View>

                              <Text
                                numberOfLines={1}
                                style={[
                                  styles.metricLabel,
                                  {
                                    color: theme.textSecondary,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: 10,
                                    lineHeight: 12,
                                    textAlign: "center",
                                    textTransform: "uppercase",
                                  },
                                ]}
                              >
                                {resolveDocumentLeadStatusText(
                                  document.statusText,
                                )}
                              </Text>
                            </View>

                            <View
                              style={[
                                styles.patientIdentityCopy,
                                {
                                  gap: tokens.spacing.sm,
                                },
                              ]}
                            >
                              <View
                                style={[
                                  styles.patientRowHeader,
                                  {
                                    alignItems: "flex-start",
                                    gap: tokens.spacing.sm,
                                    justifyContent: "flex-start",
                                  },
                                ]}
                              >
                                <View
                                  style={[
                                    styles.listGroup,
                                    {
                                      flex: 1,
                                      minWidth: 0,
                                    },
                                  ]}
                                >
                                  <Text
                                    style={[
                                      styles.navigationText,
                                      {
                                        color: theme.textPrimary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.sm,
                                      },
                                    ]}
                                  >
                                    {document.labelText}
                                  </Text>
                                </View>
                                <View
                                  style={[
                                    styles.inlinePill,
                                    {
                                      backgroundColor: isDownloadReady
                                        ? theme.backgroundSubtle
                                        : theme.surface,
                                      borderColor: isDownloadReady
                                        ? theme.accentStrong
                                        : theme.borderSubtle,
                                      borderRadius: 8,
                                      paddingHorizontal: tokens.spacing.sm,
                                      paddingVertical: tokens.spacing.xs,
                                    },
                                  ]}
                                >
                                  <Text
                                    style={[
                                      styles.badgeText,
                                      {
                                        color: isDownloadReady
                                          ? theme.accentStrong
                                          : theme.textSecondary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.xs,
                                      },
                                    ]}
                                  >
                                    {t(
                                      resolveDocumentAvailabilityLabelKey(
                                        document.availability,
                                      ),
                                    )}
                                  </Text>
                                </View>
                              </View>

                              <Text
                                style={[
                                  styles.sectionSubtitle,
                                  {
                                    color: theme.textSecondary,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: tokens.typography.fontSize.sm,
                                    lineHeight: tokens.typography.lineHeight.md,
                                  },
                                ]}
                              >
                                {document.summaryText}
                              </Text>
                              {document.notes.length > 0 ? (
                                <View
                                  style={[
                                    styles.detailDocumentMetaCard,
                                    {
                                      backgroundColor: theme.surface,
                                      borderColor: theme.borderSubtle,
                                      borderRadius: layout.controlRadius,
                                      gap: tokens.spacing.xs,
                                      padding: tokens.spacing.sm,
                                    },
                                  ]}
                                >
                                  <Text
                                    style={[
                                      styles.metricLabel,
                                      {
                                        color: theme.textSecondary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.xs,
                                        textTransform: "uppercase",
                                      },
                                    ]}
                                  >
                                    Clinical note
                                  </Text>
                                  {document.notes.map((note) => (
                                    <Text
                                      key={note.id}
                                      style={[
                                        styles.sectionSubtitle,
                                        {
                                          color: theme.textPrimary,
                                          fontFamily: layout.bodyFontFamily,
                                          fontSize:
                                            tokens.typography.fontSize.sm,
                                          lineHeight:
                                            tokens.typography.lineHeight.md,
                                        },
                                      ]}
                                    >
                                      {note.bodyText}
                                    </Text>
                                  ))}
                                </View>
                              ) : null}

                              <View
                                style={[
                                  styles.detailDocumentMetaGrid,
                                  {
                                    gap: tokens.spacing.sm,
                                  },
                                ]}
                              >
                                <View
                                  style={[
                                    styles.detailDocumentMetaCard,
                                    {
                                      backgroundColor: theme.surface,
                                      borderColor: theme.borderSubtle,
                                      borderRadius: layout.controlRadius,
                                      flexBasis: 180,
                                      flexGrow: 0.8,
                                      gap: tokens.spacing.xs,
                                      padding: tokens.spacing.sm,
                                    },
                                  ]}
                                >
                                  <Text
                                    style={[
                                      styles.metricLabel,
                                      {
                                        color: theme.textSecondary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.xs,
                                      },
                                    ]}
                                  >
                                    {t(
                                      translationKeys.shell
                                        .workspaceDocumentCreatedBy,
                                    )}
                                  </Text>
                                  <Text
                                    style={[
                                      styles.navigationText,
                                      {
                                        color: theme.textPrimary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.sm,
                                      },
                                    ]}
                                  >
                                    {document.createdByText}
                                  </Text>
                                </View>

                                <View
                                  style={[
                                    styles.detailDocumentMetaCard,
                                    {
                                      backgroundColor: theme.surface,
                                      borderColor: theme.borderSubtle,
                                      borderRadius: layout.controlRadius,
                                      flexBasis: 260,
                                      flexGrow: 1.1,
                                      gap: tokens.spacing.xs,
                                      padding: tokens.spacing.sm,
                                    },
                                  ]}
                                >
                                  <Text
                                    style={[
                                      styles.metricLabel,
                                      {
                                        color: theme.textSecondary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.xs,
                                      },
                                    ]}
                                  >
                                    {t(
                                      translationKeys.shell
                                        .workspaceDocumentCreatedAt,
                                    )}
                                  </Text>
                                  <Text
                                    style={[
                                      styles.navigationText,
                                      {
                                        color: theme.textPrimary,
                                        fontFamily: layout.bodyFontFamily,
                                        fontSize: tokens.typography.fontSize.sm,
                                      },
                                    ]}
                                  >
                                    {document.createdAtText}
                                  </Text>
                                </View>
                              </View>
                            </View>
                          </View>

                          {document.canMarkReviewed ? (
                            <View
                              style={[
                                styles.patientRowHeader,
                                {
                                  justifyContent: "flex-end",
                                },
                              ]}
                            >
                              {renderWorkbenchActionButton({
                                disabled: isWorkbenchActionSubmitting,
                                label: t(
                                  translationKeys.shell
                                    .workspaceActionMarkDocumentReviewed,
                                ),
                                labelLines: 1,
                                minWidth: clinicianModalActionButtonWidth,
                                onPress: () =>
                                  onSubmitWorkbenchAction({
                                    kind: "mark-document-reviewed",
                                    patientId: selectedPatient.id,
                                    expectedMaterialChangeToken:
                                      selectedPatient.materialChangeToken,
                                    documentId: document.id,
                                  }),
                                size: "compact",
                                theme,
                                tone: "success",
                                tokens,
                                width: clinicianModalActionButtonWidth,
                              })}
                            </View>
                          ) : null}
                        </View>
                      );
                    })}
                  </View>
                ))}
              </View>
            ) : (
              selectedDetailSection.rows.map((row) => (
                <View
                  key={row.id}
                  style={[
                    styles.detailRow,
                    {
                      backgroundColor: getDetailRowBackground(row.kind, theme),
                      borderColor: theme.borderSubtle,
                      borderRadius: layout.controlRadius,
                      gap: tokens.spacing.xs,
                      padding: tokens.spacing.md,
                    },
                  ]}
                >
                  {row.badgeLabelKey ? (
                    <View
                      style={[
                        styles.inlinePill,
                        {
                          backgroundColor: getDetailRowBadgeBackground(
                            row.kind,
                            theme,
                          ),
                          borderColor: theme.borderSubtle,
                          borderRadius: 8,
                          paddingHorizontal: tokens.spacing.sm,
                          paddingVertical: tokens.spacing.xs,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.badgeText,
                          {
                            color: getDetailRowBadgeTextColor(row.kind, theme),
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.xs,
                          },
                        ]}
                      >
                        {t(row.badgeLabelKey)}
                      </Text>
                    </View>
                  ) : null}
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                      },
                    ]}
                  >
                    {row.labelText}
                  </Text>
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {row.valueText}
                  </Text>
                </View>
              ))
            )}
      </View>
    );

  const renderSelectedDetailStage = () => {
    if (detailStageContent == null) {
      return null;
    }

    return (
      <View
        style={[
          styles.detailStageCard,
          {
            backgroundColor: theme.surface,
            borderColor: "transparent",
            borderRadius: 18,
            borderWidth: 0,
            flex: showLegacyInlineAccordion ? 0 : 1,
            flexShrink: showLegacyInlineAccordion ? 0 : 1,
            gap: tokens.spacing.xs,
            minHeight: showLegacyInlineAccordion ? undefined : 0,
            overflow: showLegacyInlineAccordion ? "visible" : "hidden",
            paddingHorizontal: tokens.spacing.xs,
            paddingVertical: tokens.spacing.xs,
          },
        ]}
      >
        {showLegacyInlineAccordion ? (
          <View
            style={[
              styles.listGroup,
              {
                gap: tokens.spacing.xs,
                width: "100%",
              },
            ]}
          >
            {detailStageContent}
          </View>
        ) : (
          <ScrollView
            nestedScrollEnabled={true}
            scrollEnabled={true}
            showsVerticalScrollIndicator={false}
            style={styles.detailInlineScroll}
            contentContainerStyle={[
              styles.listGroup,
              {
                gap: tokens.spacing.xs,
              },
            ]}
          >
            {detailStageContent}
          </ScrollView>
        )}
      </View>
    );
  };

  const renderDetailModal = () => {
    if (
      !showLegacyDetailModal ||
      selectedPatient == null ||
      selectedDetailSection == null ||
      selectedDetailTabId === "information" ||
      detailStageContent == null
    ) {
      return null;
    }

    const requestCloseInlineClinicalEditor = (
      target: "editor" | "modal",
    ): void => {
      if (isWorkbenchActionSubmitting) {
        return;
      }

      if (!isClinicianInlineEditorDirty(inlineClinicalEditor)) {
        setInlineClinicalEditor(null);

        if (target === "modal") {
          setSelectedDetailTabId("information");
        }

        return;
      }

      setInlineClinicalEditor((current) =>
        current == null
          ? current
          : {
              ...current,
              discardCloseTarget: target,
            },
      );
    };
    const closeModal = () => {
      if (inlineClinicalEditor != null) {
        requestCloseInlineClinicalEditor("modal");
        return;
      }

      setInlineClinicalEditor(null);
      setSelectedDetailTabId("information");
    };
    const openInlineRecordEditor = (
      input: ClinicianClinicalRecordEditorInput,
    ) => {
      onOpenClinicalRecordEditor(input);
    };
    const openInlineNoteEditor = (input: ClinicianClinicalNoteEditorInput) => {
      onOpenClinicalNoteEditor(input);
    };
    const submitInlineClinicalEditor = () => {
      if (inlineClinicalEditor == null || isWorkbenchActionSubmitting) {
        return;
      }
      const submittedEditor = inlineClinicalEditor;
      const closeSubmittedEditor = () => {
        setInlineClinicalEditor((current) =>
          current === submittedEditor ? null : current,
        );
      };

      // Owner guard: never submit an editor opened for a different patient
      // (the post-render reset can land a frame after a selection change).
      if (
        inlineClinicalEditor.ownerPatientId != null &&
        inlineClinicalEditor.ownerPatientId !== selectedPatient.id
      ) {
        setInlineClinicalEditor(null);
        return;
      }

      if (inlineClinicalEditor.kind === "record") {
        const title = inlineClinicalEditor.title.trim();
        const detailText = inlineClinicalEditor.detailText.trim();

        if (!title || !detailText) {
          return;
        }

        onSubmitWorkbenchAction(
          {
            detailText,
            expectedMaterialChangeToken: selectedPatient.materialChangeToken,
            kind: "upsert-clinical-record",
            patientId: selectedPatient.id,
            recordId: inlineClinicalEditor.recordId ?? null,
            targetKind: inlineClinicalEditor.targetKind,
            title,
          },
          { onSuccess: closeSubmittedEditor },
        );
        return;
      }

      const title = inlineClinicalEditor.title.trim();
      const bodyText = inlineClinicalEditor.bodyText.trim();

      if (!title || !bodyText) {
        return;
      }

      onSubmitWorkbenchAction(
        {
          bodyText,
          expectedMaterialChangeToken: selectedPatient.materialChangeToken,
          kind: "upsert-clinical-note",
          noteId: inlineClinicalEditor.noteId ?? null,
          patientId: selectedPatient.id,
          targetId: inlineClinicalEditor.targetId ?? null,
          targetKind: inlineClinicalEditor.targetKind,
          title,
          visibility: inlineClinicalEditor.visibility,
        },
        { onSuccess: closeSubmittedEditor },
      );
    };
    const sectionBadgeCount = resolveDetailSectionBadgeCount(
      selectedDetailSection,
      selectedPatient,
    );
    const sectionMetricRows = buildClinicianDetailSummaryMetricRows(
      selectedDetailSection,
    ).filter((row) => row.valueText.trim().length > 0);
    const isCaseSummaryModal = selectedDetailSection.id === "case-summary";
    const isAppointmentsModal = selectedDetailSection.id === "appointments";
    const isProceduresModal = selectedDetailSection.id === "procedures";
    const isDocumentsModal = selectedDetailSection.id === "documents";
    const isSummaryStyleModal =
      isCaseSummaryModal ||
      isAppointmentsModal ||
      isProceduresModal ||
      isDocumentsModal;
    const modalPatientIdentityText =
      patientIdentityText ?? "Patient identifiers unavailable";
    const caseSummaryTaskCount = selectedPatient.taskAssignments.length;
    const caseSummaryApprovalCount = selectedPatient.approvalSummaries.length;
    const caseSummaryPendingApprovalCount =
      selectedPatient.approvalSummaries.filter((approval) =>
        /pending|review|needs|open/iu.test(approval.statusText),
      ).length;
    const isCaseSummaryDeliveryReviewItem = (
      delivery: (typeof selectedPatient.deliverySummaries)[number],
    ) => /pending|approval|held|needs/iu.test(delivery.statusText);
    const caseSummaryDeliveryReviewCount =
      selectedPatient.deliverySummaries.filter(isCaseSummaryDeliveryReviewItem)
        .length;
    const caseSummaryNoteCount = selectedPatient.clinicalNotes.filter(
      (note) => note.status !== "archived",
    ).length;
    const caseSummaryOpenWorkCount =
      selectedPatient.taskAssignments.filter((task) => task.canComplete)
        .length +
      selectedPatient.approvalSummaries.filter((approval) =>
        /pending|review|needs|open/iu.test(approval.statusText),
      ).length +
      caseSummaryDeliveryReviewCount;
    const explicitSectionSummaryText = resolveProductClinicalText(
      selectedDetailSection.summaryText ?? "",
    ).trim();
    const sectionSummaryText =
      isCaseSummaryModal
        ? "Current clinical picture, next visit, and recent care activity."
        : isAppointmentsModal
          ? "Visit timing, readiness, and coordinator follow-up."
        : isProceduresModal
          ? "Procedure timing, prep readiness, and clinical milestones."
        : isDocumentsModal
          ? "Document status, review progress, and attached files."
        : explicitSectionSummaryText.length > 0
          ? explicitSectionSummaryText
          : selectedDetailSection.id === "appointments"
            ? "Upcoming appointments and coordinator follow-up for this patient."
            : selectedDetailSection.id === "procedures"
              ? "Procedure orders, prep items, and upcoming milestones."
              : selectedDetailSection.id === "medications"
                ? "Medication details, program context, and procedure notes."
                : selectedDetailSection.id === "documents"
                  ? "Document status, review progress, and attached files."
                  : selectedDetailSection.id === "phone-calls"
                    ? "Voice assistant calls, transcript activity, and handoffs."
                    : resolveProductClinicalText(selectedPatient.summaryText);

    const sectionMetricTiles = [
      ...sectionMetricRows.slice(0, 3),
      ...(sectionBadgeCount != null &&
      !sectionMetricRows.some(
        (row) => row.valueText.trim() === sectionBadgeCount.trim(),
      )
        ? [
            {
              id: `${selectedDetailSection.id}-count`,
              labelText: "Records",
              size: "compact" as const,
              valueText: sectionBadgeCount,
            },
          ]
        : []),
    ];
    const railFactRows = primaryInformationRows.slice(0, 5);
    const sectionRows = selectedDetailSection.rows.filter(
      (row) => row.valueText.trim().length > 0,
    );
    const formatClinicianModalTimestampText = (
      valueText: string | null | undefined,
    ): string => {
      const productText = resolveProductClinicalText(valueText ?? "").trim();

      if (productText.length === 0) {
        return "";
      }

      const parsedDate = parseWorkbenchDateTimeText(productText);

      if (parsedDate != null) {
        return [
          formatOptionBTimelineDateText(parsedDate),
          formatOptionBTimelineTimeText(parsedDate),
        ]
          .filter((part) => part.length > 0)
          .join(" ");
      }

      return productText
        .replace(
          /\b(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:\+00(?::00)?|Z)?\b/gu,
          "$2/$3/$1 $4:$5",
        )
        .replace(/\s*\+00(?::00)?\b/gu, "");
    };
    const buildClinicianModalDateParts = (
      valueText: string | null | undefined,
    ): {
      dateLabel: string;
      timeLabel: string;
    } => {
      const parsedDate = parseWorkbenchDateTimeText(valueText ?? "");

      return parsedDate == null
        ? { dateLabel: "", timeLabel: "" }
        : {
            dateLabel: formatOptionBTimelineDateText(parsedDate),
            timeLabel: formatOptionBTimelineTimeText(parsedDate),
          };
    };
    const formatClinicianTimelineNoteLine = (
      note: ClinicalNoteViewModel,
    ): string => `Clinical note: ${resolveProductClinicalNoteBodyText(note.bodyText)}`;
    const selectedClinicalNoteTargetKind:
      | ClinicianWorkbenchClinicalNoteTargetKind
      | null =
      selectedDetailSection.id === "case-summary"
        ? "patient"
        : selectedDetailSection.id === "appointments"
          ? "appointment"
          : selectedDetailSection.id === "procedures"
            ? "procedure"
            : selectedDetailSection.id === "medications"
              ? "medication"
              : selectedDetailSection.id === "documents"
                ? "document"
                : null;
    const selectedClinicalRecordKind = resolveDetailTabClinicalRecordKind(
      selectedDetailSection.id,
    );
    const supportsInlineClinicalRecordEditor =
      selectedClinicalRecordKind != null;
    const supportsInlineClinicalNoteEditor =
      selectedClinicalNoteTargetKind != null;
    const buildClinicalRecordActionConfigs = (input: {
      clinicalStatus: ClinicianWorkbenchClinicalRecordStatus;
      detailText: string;
      recordId: string;
      targetKind: ClinicianWorkbenchClinicalRecordKind;
      title: string;
    }): OptionBModalActionConfig[] => [
      {
        disabled: isWorkbenchActionSubmitting,
        label: "Edit",
        onPress: () =>
          supportsInlineClinicalRecordEditor
            ? openInlineRecordEditor({
                defaultDetailText: input.detailText,
                defaultTitle: input.title,
                recordId: input.recordId,
                targetKind: input.targetKind,
              })
            : onOpenClinicalRecordEditor({
                defaultDetailText: input.detailText,
                defaultTitle: input.title,
                recordId: input.recordId,
                targetKind: input.targetKind,
              }),
        tone: "accent",
      },
      input.clinicalStatus === "archived"
        ? {
            disabled: isWorkbenchActionSubmitting,
            label: "Reactivate",
            onPress: () =>
              onSubmitWorkbenchAction({
                expectedMaterialChangeToken:
                  selectedPatient.materialChangeToken,
                kind: "reactivate-clinical-record" as const,
                patientId: selectedPatient.id,
                recordId: input.recordId,
                targetKind: input.targetKind,
              }),
            tone: "success",
          }
        : {
            disabled: isWorkbenchActionSubmitting,
            label: "Archive",
            onPress: () =>
              onSubmitWorkbenchAction({
                expectedMaterialChangeToken:
                  selectedPatient.materialChangeToken,
                kind: "archive-clinical-record" as const,
                patientId: selectedPatient.id,
                recordId: input.recordId,
                targetKind: input.targetKind,
              }),
            tone: "warning",
          },
    ];
    const buildAddClinicalNoteActionConfig = (input: {
      defaultTitle: string;
      targetId?: string | null;
      targetKind: ClinicianWorkbenchClinicalNoteTargetKind;
    }): OptionBModalActionConfig => ({
      disabled: isWorkbenchActionSubmitting,
      label: "Add note",
      onPress: () =>
        supportsInlineClinicalNoteEditor
          ? openInlineNoteEditor({
              defaultBodyText: "",
              defaultTitle: input.defaultTitle,
              targetId: input.targetId ?? null,
              targetKind: input.targetKind,
            })
          : onOpenClinicalNoteEditor({
              defaultBodyText: "",
              defaultTitle: input.defaultTitle,
              targetId: input.targetId ?? null,
              targetKind: input.targetKind,
            }),
      tone: "accent",
    });
    const buildClinicalNoteActionConfigs = (
      note: ClinicalNoteViewModel,
    ): OptionBModalActionConfig[] => [
      {
        disabled: isWorkbenchActionSubmitting,
        label: "Edit note",
        onPress: () =>
          supportsInlineClinicalNoteEditor
            ? openInlineNoteEditor({
                defaultBodyText: note.bodyText,
                defaultTitle: note.titleText,
                defaultVisibility: note.visibility,
                noteId: note.id,
                targetId: note.targetId,
                targetKind: note.targetKind,
              })
            : onOpenClinicalNoteEditor({
                defaultBodyText: note.bodyText,
                defaultTitle: note.titleText,
                defaultVisibility: note.visibility,
                noteId: note.id,
                targetId: note.targetId,
                targetKind: note.targetKind,
              }),
        tone: "accent",
      },
      note.status === "archived"
        ? {
            disabled: isWorkbenchActionSubmitting,
            label: "Reactivate",
            onPress: () =>
              onSubmitWorkbenchAction({
                expectedMaterialChangeToken:
                  selectedPatient.materialChangeToken,
                kind: "reactivate-clinical-note" as const,
                noteId: note.id,
                patientId: selectedPatient.id,
              }),
            tone: "success",
          }
        : {
            disabled: isWorkbenchActionSubmitting,
            label: "Archive",
            onPress: () =>
              onSubmitWorkbenchAction({
                expectedMaterialChangeToken:
                  selectedPatient.materialChangeToken,
                kind: "archive-clinical-note" as const,
                noteId: note.id,
                patientId: selectedPatient.id,
              }),
            tone: "warning",
          },
    ];
    const addClinicalRecordActionConfig: OptionBModalActionConfig | null =
      selectedClinicalRecordKind == null
        ? null
        : {
            disabled: isWorkbenchActionSubmitting,
            label: `Add ${formatClinicalRecordKindLabel(
              selectedClinicalRecordKind,
            )}`,
            onPress: () => {
              const input = {
                defaultDetailText: "",
                defaultTitle: "",
                targetKind: selectedClinicalRecordKind,
              };

              if (supportsInlineClinicalRecordEditor) {
                openInlineRecordEditor(input);
                return;
              }

              onOpenClinicalRecordEditor(input);
            },
            tone: "accent",
          };
    const addPatientNoteActionConfig: OptionBModalActionConfig | null =
      selectedDetailSection.id === "case-summary"
        ? {
            disabled: isWorkbenchActionSubmitting,
            label: "Add note",
            onPress: () =>
              openInlineNoteEditor({
                defaultBodyText: "",
                defaultTitle: "",
                targetId: selectedPatient.id,
                targetKind: "patient",
              }),
            tone: "accent",
          }
        : null;
    const uploadDocumentActionConfig: OptionBModalActionConfig | null =
      selectedDetailSection.id === "documents"
        ? {
            disabled: isDocumentUploadSubmitting || isWorkbenchActionSubmitting,
            label: isDocumentUploadSubmitting
              ? "Uploading document"
              : "Upload document",
            onPress: onUploadDocument,
            tone: "accent",
          }
        : null;
    const headerActionConfigs =
      inlineClinicalEditor == null
        ? [
            ...(uploadDocumentActionConfig == null
              ? []
              : [uploadDocumentActionConfig]),
            ...(addClinicalRecordActionConfig == null
              ? []
              : [addClinicalRecordActionConfig]),
            ...(addPatientNoteActionConfig == null
              ? []
              : [addPatientNoteActionConfig]),
          ]
        : [];
    const clinicalNoteTimelineItems: OptionBTimelineItem[] =
      selectedClinicalNoteTargetKind == null
        ? []
        : selectedPatient.clinicalNotes
            .filter((note) =>
              selectedClinicalNoteTargetKind === "patient"
                ? note.targetKind === "patient"
                : note.targetKind === selectedClinicalNoteTargetKind,
            )
            .map((note) => {
              const authoredDateParts = buildClinicianModalDateParts(
                note.authoredAtText,
              );

              return {
                actionConfig: null,
                actionConfigs: buildClinicalNoteActionConfigs(note),
                badgeText: "NOTE",
                bodyLines: [resolveProductClinicalNoteBodyText(note.bodyText)],
                clinicalStatus:
                  note.status === "archived" ? "archived" : "active",
                dateLabel: authoredDateParts.dateLabel,
                groupLabel:
                  note.status === "archived"
                    ? isCaseSummaryModal
                      ? "Archived context"
                      : "Archived notes"
                    : isCaseSummaryModal
                      ? "Clinical context"
                      : "Clinical notes",
                id: `note-${note.id}`,
                metrics: [
                  {
                    labelText: "Author",
                    valueText: resolveProductClinicalText(note.authoredByText),
                  },
                  {
                    labelText: "Visibility",
                    valueText:
                      note.visibility === "clinical"
                        ? "Clinical"
                        : "Patient visible",
                  },
                  {
                    labelText: "Target",
                    valueText: formatClinicalNoteTargetKindLabel(note.targetKind),
                  },
                ],
                statusText:
                  note.status === "archived"
                    ? "Archived"
                    : note.visibility === "clinical"
                      ? "Clinical"
                      : "Patient visible",
                timeLabel: authoredDateParts.timeLabel,
                titleText: resolveProductClinicalText(note.titleText),
              };
            });
    const timelineItems: OptionBTimelineItem[] =
      selectedDetailSection.id === "case-summary"
        ? [
            ...selectedPatient.taskAssignments.map((task) => ({
              actionConfig: task.canComplete
                ? {
                    disabled: isWorkbenchActionSubmitting,
                    label: t(translationKeys.shell.workspaceActionCompleteTask),
                    onPress: () =>
                      onSubmitWorkbenchAction({
                        assignmentId: task.id,
                        expectedMaterialChangeToken:
                          selectedPatient.materialChangeToken,
                        kind: "complete-task-assignment" as const,
                        patientId: selectedPatient.id,
                      }),
                    tone: "success" as const,
                  }
                : null,
              badgeText: "TASK",
              bodyLines: [
                `${resolveProductClinicalText(
                  task.targetKindText,
                )}: ${resolveProductClinicalText(task.targetLabel)}`,
              ],
              dateLabel: "",
              groupLabel: task.canComplete ? "Tasks to complete" : "Task history",
              id: task.id,
              metrics: [
                {
                  labelText: "Status",
                  valueText: resolveProductClinicalText(task.statusText),
                },
                {
                  labelText: "Program",
                  valueText: resolveProductClinicalText(
                    selectedPatient.programText,
                  ),
                },
                { labelText: "Contact window", valueText: contactWindowText ?? "" },
              ],
              statusText: resolveProductClinicalText(task.statusText),
              timeLabel: "",
              titleText: resolveProductClinicalText(task.taskLabel),
            })),
            ...selectedPatient.approvalSummaries.map((approval) => {
              const approvalNeedsAction =
                /pending|review|needs|open/iu.test(approval.statusText);

              return {
                actionConfig: null,
                badgeText: "APP",
                bodyLines: [
                  `Review owner: ${resolveProductClinicalText(
                    approval.ownerText,
                  )}`,
                ],
                dateLabel: "",
                groupLabel: approvalNeedsAction
                  ? "Approvals to review"
                  : "Approval history",
                id: approval.id,
                metrics: [
                  {
                    labelText: "Owner",
                    valueText: resolveProductClinicalText(approval.ownerText),
                  },
                  {
                    labelText: "Status",
                    valueText: resolveProductClinicalText(approval.statusText),
                  },
                  {
                    labelText: "Updated",
                    valueText: formatClinicianModalTimestampText(
                      approval.updatedAtText,
                    ),
                  },
                ],
                statusText: resolveProductClinicalText(approval.statusText),
                timeLabel: "",
                titleText: resolveProductClinicalText(approval.labelText),
              };
            }),
            ...selectedPatient.deliverySummaries
              .filter(isCaseSummaryDeliveryReviewItem)
              .map((delivery) => ({
                actionConfig: null,
                badgeText: "SEND",
                bodyLines: [
                  `Recipient: ${resolveProductClinicalText(
                    delivery.recipientText,
                  )}`,
                ],
                dateLabel: "",
                groupLabel: "Patient updates to review",
                id: delivery.id,
                metrics: [
                  {
                    labelText: "Recipient",
                    valueText: resolveProductClinicalText(delivery.recipientText),
                  },
                  {
                    labelText: "Status",
                    valueText: resolveProductClinicalText(delivery.statusText),
                  },
                  {
                    labelText: "Updated",
                    valueText: formatClinicianModalTimestampText(
                      delivery.updatedAtText,
                    ),
                  },
                ],
                statusText: resolveProductClinicalText(delivery.statusText),
                timeLabel: "",
                titleText: resolveProductClinicalText(delivery.channelText),
              })),
            ...clinicalNoteTimelineItems,
          ]
        : selectedDetailSection.id === "documents"
          ? [
              {
                actionConfig: {
                  disabled: false,
                  label: "Open chart pack",
                  onPress: () => {
                    closeModal();
                    onOpenPatientProfileSection("chart-pack");
                  },
                  tone: "accent" as const,
                },
                badgeText: "PACK",
                bodyLines: [chartPackSummaryText],
                dateLabel: "",
                groupLabel: "Chart pack",
                id: "chart-pack",
                metrics: [
                  {
                    labelText: "Documents",
                    valueText: String(chartPackDocuments.length),
                  },
                  {
                    labelText: "Ready to print",
                    valueText: `${String(chartPackReadyCount)} of ${String(chartPackDocuments.length)}`,
                  },
                  {
                    labelText: "Missing fields",
                    valueText: String(chartPackMissingFieldCount),
                  },
                ],
                statusText: chartPackStatusText,
                timeLabel: "",
                titleText: "Full chart pack",
              },
              ...clinicalNoteTimelineItems,
              ...selectedPatient.documentSummaries.map((document) => ({
              actionConfig: document.canMarkReviewed
                ? {
                    disabled: isWorkbenchActionSubmitting,
                    label: "Review",
                    onPress: () =>
                      onSubmitWorkbenchAction({
                        documentId: document.id,
                        expectedMaterialChangeToken:
                          selectedPatient.materialChangeToken,
                        kind: "mark-document-reviewed" as const,
                        patientId: selectedPatient.id,
                      }),
                    tone: "success" as const,
                  }
                : null,
              actionConfigs: buildClinicalRecordActionConfigs({
                  clinicalStatus: document.clinicalStatus,
                  detailText: document.summaryText,
                  recordId: document.id,
                  targetKind: "document",
                  title: document.labelText,
                }).concat(
                  buildAddClinicalNoteActionConfig({
                    defaultTitle: `${document.labelText} note`,
                    targetId: document.id,
                    targetKind: "document",
                  }),
                ),
              badgeText: resolveDocumentBadgeText(document.labelText),
              bodyLines: [
                resolveProductClinicalText(document.summaryText),
                ...document.notes.map(formatClinicianTimelineNoteLine),
              ],
              clinicalStatus: document.clinicalStatus,
              dateLabel: "",
              groupLabel:
                document.clinicalStatus === "archived"
                  ? "ARCHIVED"
                  :
                document.availability === "download-ready"
                  ? "AVAILABLE FOR REVIEW"
                  : "METADATA ONLY",
              id: document.id,
              metrics: [
                {
                  labelText: "Created by",
                  valueText: resolveProductClinicalText(document.createdByText),
                },
                {
                  labelText: "Created",
                  valueText: formatClinicianModalTimestampText(
                    document.createdAtText,
                  ),
                },
                {
                  labelText: "Status",
                  valueText: resolveProductClinicalText(document.statusText),
                },
              ],
              statusText:
                document.clinicalStatus === "archived"
                  ? "Archived"
                  :
                document.availability === "download-ready"
                  ? "Available"
                  : document.statusText,
              timeLabel: "",
              titleText: document.labelText,
            })),
            ]
          : selectedDetailSection.id === "phone-calls"
            ? selectedPatient.voiceCallSummaries.map((voiceCall) => {
                const parsedDate = parseWorkbenchDateTimeText(
                  voiceCall.receivedAtText,
                );

                return {
                  actionConfig: {
                    disabled: false,
                    label: "Open transcript",
                    onPress: () => onOpenVoiceCallTranscript(voiceCall),
                    tone: "accent" as const,
                  },
                  badgeText: "CALL",
                  bodyLines: [resolveProductClinicalText(voiceCall.previewText)],
                  dateLabel:
                    parsedDate == null
                      ? voiceCall.receivedAtText
                      : formatOptionBTimelineDateText(parsedDate),
                  groupLabel: "RECENT CALLS",
                  id: voiceCall.id,
                  metrics: [
                    {
                      labelText: "Received",
                      valueText: formatClinicianModalTimestampText(
                        voiceCall.receivedAtText,
                      ),
                    },
                    {
                      labelText: "Transcript turns",
                      valueText: String(voiceCall.turnCount),
                    },
                    {
                      labelText: "Captured by",
                      valueText: resolveProductClinicalText(
                        voiceCall.sourceText ?? "",
                      ),
                    },
                  ],
                  statusText: voiceCall.hasClinicianHandoff
                    ? "Handoff"
                    : "Recorded",
                  timeLabel:
                    parsedDate == null
                      ? ""
                      : formatOptionBTimelineTimeText(parsedDate),
                  titleText: voiceCall.questionClassText
                    ? `Call: ${voiceCall.questionClassText}`
                    : "Phone call",
                };
              })
            : [
                ...clinicalNoteTimelineItems,
                ...sectionRows.map((row) => {
                const parsed = parseOptionBDetailValue(row.valueText);
                const usesSourceTitle =
                  /^(?:upcoming (?:visit|procedure)|medication note|medications)$/iu.test(
                    row.labelText.trim(),
                  );
                const titleText =
                  usesSourceTitle && parsed.primaryText
                    ? resolveProductClinicalText(parsed.primaryText)
                    : row.labelText;
                const parsedBodyLines = parsed.bodyLines
                  .map(resolveProductClinicalText)
                  .filter(
                    (lineText) =>
                      lineText.trim().toLowerCase() !==
                      titleText.trim().toLowerCase(),
                  );
                const parsedDetailRows = parsed.detailRows
                  .map((detailRow) => ({
                    labelText: resolveProductClinicalText(detailRow.labelText),
                    valueText: resolveProductClinicalText(detailRow.valueText),
                  }))
                  .filter(
                    (detailRow) =>
                      !/^(?:appointment|procedure|medication|name|service|title)$/iu.test(
                        detailRow.labelText,
                      ) ||
                      detailRow.valueText.trim().toLowerCase() !==
                        titleText.trim().toLowerCase(),
                  );
                const statusText = resolveOptionBStatusText({
                  fallbackText:
                    selectedDetailSection.id === "medications"
                      ? "Active"
                      : undefined,
                  titleText,
                  valueText: row.valueText,
                });
                const isCompleted = /completed|performed|resolved|ready/iu.test(
                  statusText,
                );
                const defaultGroup =
                  row.clinicalStatus === "archived"
                    ? "ARCHIVED"
                    :
                  selectedDetailSection.id === "medications"
                    ? "ACTIVE"
                    : isCompleted
                      ? "COMPLETED"
                      : "SCHEDULED";

                return {
                  actionConfig: null,
                  actionConfigs:
                    selectedClinicalRecordKind == null
                      ? []
                      : buildClinicalRecordActionConfigs({
                          clinicalStatus: row.clinicalStatus,
                          detailText: row.valueText,
                          recordId: row.id,
                          targetKind: selectedClinicalRecordKind,
                          title: row.labelText,
                        }).concat(
                          buildAddClinicalNoteActionConfig({
                            defaultTitle: `${row.labelText} note`,
                            targetId: row.id,
                            targetKind: selectedClinicalRecordKind,
                          }),
                        ),
                  badgeText:
                    selectedDetailSection.id === "appointments"
                      ? "APPO"
                      : selectedDetailSection.id === "procedures" && isCompleted
                        ? "PERF"
                        : selectedDetailSection.id === "procedures"
                          ? "PROC"
                          : selectedDetailSection.id === "medications"
                            ? "RX"
                            : resolveDetailSectionLeadText(
                                selectedDetailSection.id,
                              ),
                  bodyLines: [
                    ...(parsedBodyLines.length > 0 ||
                    parsedDetailRows.length > 0 ||
                    parsed.date != null ||
                    parsed.statusText.trim().length > 0 ||
                    usesSourceTitle
                      ? parsedBodyLines
                      : [resolveProductClinicalText(row.valueText)]),
                    ...row.notes.map(formatClinicianTimelineNoteLine),
                  ],
                  clinicalStatus: row.clinicalStatus,
                  dateLabel:
                    parsed.date == null
                      ? ""
                      : formatOptionBTimelineDateText(parsed.date),
                  detailRows: parsedDetailRows,
                  groupLabel: defaultGroup,
                  id: row.id,
                  metrics: buildClinicianDetailBodyMetricRows(
                    selectedDetailSection.id,
                    row,
                  ).map((metric) => ({
                    labelText: metric.labelText,
                    valueText:
                      /created|updated|received|appointment|procedure/iu.test(
                        metric.labelText,
                      )
                        ? formatClinicianModalTimestampText(metric.valueText)
                        : resolveProductClinicalText(metric.valueText),
                  })),
                  statusText:
                    row.clinicalStatus === "archived"
                      ? "Archived"
                      : statusText,
                  timeLabel:
                    parsed.date == null
                      ? ""
                      : formatOptionBTimelineTimeText(parsed.date),
                  titleText,
                };
              }),
              ];
    const upcomingTimelineCount = timelineItems.filter((item) =>
      /scheduled|upcoming|active/iu.test(item.groupLabel) ||
      /scheduled|active/iu.test(item.statusText),
    ).length;
    const secondaryTimelineItems = timelineItems.filter((item) => {
      if (selectedDetailSection.id === "documents") {
        return (
          item.id !== "chart-pack" &&
          item.badgeText !== "NOTE" &&
          (/ready|download|available/iu.test(item.statusText) ||
            /download ready|available for review/iu.test(item.groupLabel))
        );
      }

      if (selectedDetailSection.id === "case-summary") {
        return item.badgeText === "APP" || /approval/iu.test(item.groupLabel);
      }

      if (selectedDetailSection.id === "phone-calls") {
        return /handoff|pending|needs|scheduled/iu.test(item.statusText) ||
          /handoff|pending|needs|scheduled/iu.test(item.groupLabel);
      }

      return /scheduled|upcoming|active/iu.test(item.groupLabel) ||
        /scheduled|active/iu.test(item.statusText);
    });
    const completedTimelineCount = timelineItems.filter(
      (item) =>
        /completed|performed|recorded|ready/iu.test(item.statusText) ||
        /completed|download ready|available for review/iu.test(item.groupLabel),
    ).length;
    const buildSectionStateChips = (): OptionBModalStateChip[] => {
      const chips: OptionBModalStateChip[] = [];
      const pushChip = (id: string, labelText: string) => {
        const normalizedLabel = labelText.trim();

        if (
          normalizedLabel.length === 0 ||
          chips.some((chip) => chip.labelText === normalizedLabel)
        ) {
          return;
        }

        chips.push({
          id,
          labelText: normalizedLabel,
        });
      };

      if (selectedDetailSection.id === "case-summary") {
        pushChip("case-open-work", `Open work ${caseSummaryOpenWorkCount}`);
        pushChip(
          "case-approvals",
          `Approvals ${caseSummaryApprovalCount}`,
        );
      } else if (selectedDetailSection.id === "appointments") {
        pushChip("appointments-total", `Appointments ${timelineItems.length}`);
        pushChip("appointments-upcoming", `Upcoming ${upcomingTimelineCount}`);
      } else if (selectedDetailSection.id === "procedures") {
        pushChip("procedures-total", `Procedures ${timelineItems.length}`);
        pushChip("procedures-upcoming", `Upcoming ${upcomingTimelineCount}`);
      } else if (selectedDetailSection.id === "medications") {
        pushChip("medications-total", `Medications ${timelineItems.length}`);
        pushChip(
          "medications-program",
          formatClinicianProgramContextText(selectedPatient.programText),
        );
      } else if (selectedDetailSection.id === "documents") {
        pushChip(
          "documents-total",
          `Documents ${selectedPatient.documentSummaries.length}`,
        );
        pushChip(
          selectedPatient.pendingDocumentCount > 0
            ? "documents-pending"
            : "documents-ready",
          selectedPatient.pendingDocumentCount > 0
            ? `Pending ${selectedPatient.pendingDocumentCount}`
            : `Ready ${secondaryTimelineItems.length}`,
        );
      } else if (selectedDetailSection.id === "phone-calls") {
        pushChip("phone-calls-total", `Calls ${timelineItems.length}`);
        pushChip("phone-calls-handoffs", `Handoffs ${secondaryTimelineItems.length}`);
      } else {
        pushChip(`${selectedDetailSection.id}-total`, `Records ${timelineItems.length}`);
        pushChip(`${selectedDetailSection.id}-status`, selectedPatient.statusText);
      }

      if (chips.length < 2) {
        selectedPatient.derivedFlagLabelKeys
          .map((key) => t(key))
          .forEach((labelText, index) =>
            pushChip(`${selectedDetailSection.id}-flag-${index}`, labelText),
          );
      }

      return chips.slice(0, 2);
    };
    const sectionStateChips = buildSectionStateChips();
    const modalTabs: {
      countText: string;
      id: OptionBModalViewId;
      labelText: string;
    }[] = [
      {
        countText:
          selectedDetailSection.id === "documents"
            ? String(selectedPatient.documentSummaries.length)
            : String(timelineItems.length),
        id: "all",
        labelText:
          selectedDetailSection.id === "documents"
            ? "All documents"
            : selectedDetailSection.id === "phone-calls"
              ? "All calls"
              : selectedDetailSection.id === "case-summary"
                ? "Case work"
                : "All items",
      },
      {
        countText: String(secondaryTimelineItems.length),
        id: "secondary",
        labelText:
          selectedDetailSection.id === "documents"
            ? "Ready"
            : selectedDetailSection.id === "case-summary"
              ? "Approvals"
              : selectedDetailSection.id === "phone-calls"
                ? "Handoffs"
                : selectedDetailSection.id === "medications"
                  ? "Active"
                  : "Upcoming",
      },
    ];
    const displayedTimelineItems =
      selectedDetailModalViewId === "secondary"
        ? secondaryTimelineItems
        : timelineItems;
    const emptyTimelineText =
      selectedDetailModalViewId === "secondary"
        ? selectedDetailSection.id === "documents"
          ? "No documents are currently available for review for this patient."
          : selectedDetailSection.id === "case-summary"
            ? "No approval work is currently waiting for this patient."
            : selectedDetailSection.id === "phone-calls"
              ? "No staff handoffs are recorded for this patient."
              : selectedDetailSection.id === "medications"
                ? "No active medication records are available for this patient."
                : "No upcoming records are available for this patient."
        : selectedDetailSection.id === "case-summary"
          ? "No case work is currently open for this patient."
          : "No records are available for this section yet.";
    const groupTimelineItems = (items: OptionBTimelineItem[]) =>
      items.reduce(
        (groups, item) => {
          const existingGroup = groups.find(
            (group) => group.groupLabel === item.groupLabel,
          );

          if (existingGroup) {
            existingGroup.items.push(item);
            return groups;
          }

          groups.push({
            groupLabel: item.groupLabel,
            items: [item],
          });
          return groups;
        },
        [] as {
          groupLabel: string;
          items: OptionBTimelineItem[];
        }[],
      );
    const groupedTimelineItems = groupTimelineItems(displayedTimelineItems);
    const railGlanceRows: ClinicianDetailMetricRow[] =
      selectedDetailSection.id === "procedures"
        ? [
            {
              id: `${selectedDetailSection.id}-tracked`,
              labelText: "Items tracked",
              valueText: String(timelineItems.length),
            },
            {
              id: `${selectedDetailSection.id}-completed`,
              labelText: "Completed",
              valueText: String(completedTimelineCount),
            },
            {
              id: `${selectedDetailSection.id}-upcoming`,
              labelText: "Upcoming",
              valueText: String(upcomingTimelineCount),
            },
          ]
        : selectedDetailSection.id === "appointments"
          ? [
              {
                id: `${selectedDetailSection.id}-tracked`,
                labelText: "Items tracked",
                valueText: String(timelineItems.length),
              },
              {
                id: `${selectedDetailSection.id}-upcoming`,
                labelText: "Upcoming",
                valueText: String(upcomingTimelineCount),
              },
              {
                id: `${selectedDetailSection.id}-contact-window`,
                labelText: "Contact window",
                valueText: contactWindowText ?? "",
              },
            ]
          : selectedDetailSection.id === "documents"
            ? [
                {
                  id: `${selectedDetailSection.id}-documents`,
                  labelText: "Documents",
                  valueText: String(selectedPatient.documentSummaries.length),
                },
                {
                  id: `${selectedDetailSection.id}-ready`,
                  labelText: "Ready",
                  valueText: String(secondaryTimelineItems.length),
                },
                {
                  id: `${selectedDetailSection.id}-pending`,
                  labelText: "Pending",
                  valueText: String(selectedPatient.pendingDocumentCount),
                },
              ]
            : sectionMetricTiles;
    const caseSummaryRailGlanceRows: ClinicianDetailMetricRow[] = [
      {
        id: "case-open-work",
        labelText: "Open work",
        valueText: String(caseSummaryOpenWorkCount),
      },
      {
        id: "case-tasks",
        labelText: "Tasks",
        valueText: String(caseSummaryTaskCount),
      },
      {
        id: "case-approvals",
        labelText: "Approvals",
        valueText: String(caseSummaryApprovalCount),
      },
      {
        id: "case-notes",
        labelText: "Clinical notes",
        valueText: String(caseSummaryNoteCount),
      },
    ];
    const visibleRailGlanceRows = isCaseSummaryModal
      ? caseSummaryRailGlanceRows
      : railGlanceRows;
    const findRailFactValue = (patterns: RegExp[]): string =>
      railFactRows
        .map((row) => ({
          labelText: row.labelText,
          valueText: formatOptionBModalFactValueText(row),
        }))
        .find((row) =>
          patterns.some((pattern) => pattern.test(row.labelText)),
        )?.valueText ?? "";
    const railIdentifierText = findRailFactValue([/identifier/iu]);
    const railAssignedClinicianText = findRailFactValue([
      /assigned clinician/iu,
      /clinician/iu,
    ]);
    const railProgramText =
      findRailFactValue([/primary program/iu, /care program/iu, /program/iu]) ||
      resolveProductClinicalText(selectedPatient.programText);
    const railContactWindowText =
      findRailFactValue([/contact window/iu, /best contact/iu]) ||
      contactWindowText ||
      "";
    const railNextVisitText =
      findRailFactValue([/next appointment/iu, /next visit/iu]) ||
      resolveProductClinicalText(selectedPatient.nextAppointmentText);
    const railCaseContextRows = [
      {
        iconName: "clinician" as LineIconName,
        id: "assigned-clinician",
        labelText: "Clinician",
        valueText: railAssignedClinicianText,
      },
      {
        iconName: "program" as LineIconName,
        id: "program",
        labelText: "Program",
        valueText: railProgramText,
      },
      {
        iconName: "clock" as LineIconName,
        id: "contact-window",
        labelText: "Contact",
        valueText: railContactWindowText,
      },
    ].filter((row) => row.valueText.trim().length > 0);
    const railGlanceGridRows = visibleRailGlanceRows.slice(0, 4);
    const activePatientClinicalNotes = selectedPatient.clinicalNotes.filter(
      (note) => note.status !== "archived",
    );
    const latestPatientClinicalNote = activePatientClinicalNotes[0] ?? null;
    const patientVisibleClinicalNoteCount = activePatientClinicalNotes.filter(
      (note) => note.visibility !== "clinical",
    ).length;
    const caseSummarySnapshotRows: string[] = [
      selectedPatient.nextAppointmentText.trim().length > 0
        ? `Upcoming visit: ${resolveProductClinicalText(
            selectedPatient.nextAppointmentText,
          )}`
        : "No upcoming visit scheduled",
      caseSummaryOpenWorkCount === 0
        ? "No open tasks or approvals"
        : `${caseSummaryOpenWorkCount} open item${
            caseSummaryOpenWorkCount === 1 ? "" : "s"
          } to review`,
      `${caseSummaryNoteCount} clinical note${
        caseSummaryNoteCount === 1 ? "" : "s"
      } on file`,
      contactWindowText != null && contactWindowText.trim().length > 0
        ? `Contact window: ${contactWindowText}`
        : "",
    ].filter((row) => row.trim().length > 0);
    const caseSummaryParsedNextAppointment = parseOptionBDetailValue(
      resolveProductClinicalText(selectedPatient.nextAppointmentText),
    );
    const caseSummaryNextAppointmentWhenText =
      caseSummaryParsedNextAppointment.date == null
        ? ""
        : [
            formatOptionBTimelineDateText(
              caseSummaryParsedNextAppointment.date,
            ),
            formatOptionBTimelineTimeText(
              caseSummaryParsedNextAppointment.date,
            ),
          ].join(", ");
    const caseSummaryNextAppointmentLines = [
      caseSummaryParsedNextAppointment.primaryText ?? "",
      caseSummaryNextAppointmentWhenText,
    ].filter((line) => line.trim().length > 0);
    const caseSummarySideCards = [
      {
        detailText:
          caseSummaryNextAppointmentLines.length > 1
            ? caseSummaryNextAppointmentLines[1]
            : caseSummaryNextAppointmentLines.length > 0
              ? "Scheduled care touchpoint"
              : "No scheduled visit",
        iconName: "appointment" as LineIconName,
        id: "next-appointment",
        labelText: "Next appointment",
        valueText: caseSummaryNextAppointmentLines[0] ?? "Not scheduled",
      },
      {
        detailText:
          patientVisibleClinicalNoteCount === 0
            ? "No patient-visible notes"
            : `${patientVisibleClinicalNoteCount} active note${
                patientVisibleClinicalNoteCount === 1 ? "" : "s"
              } visible`,
        iconName: "eye" as LineIconName,
        id: "patient-visible-items",
        labelText: "Patient-visible items",
        valueText: `${patientVisibleClinicalNoteCount} item${
          patientVisibleClinicalNoteCount === 1 ? "" : "s"
        }`,
      },
      {
        detailText:
          selectedPatient.documentSummaries.length === 0
            ? "No attached documents"
            : "Available case records",
        iconName: "document" as LineIconName,
        id: "documents",
        labelText: "Documents",
        valueText: `${selectedPatient.documentSummaries.length} document${
          selectedPatient.documentSummaries.length === 1 ? "" : "s"
        }`,
      },
      {
        detailText:
          caseSummaryOpenWorkCount === 0
            ? "No open work"
            : "Needs review",
        iconName: "pulse" as LineIconName,
        id: "open-items",
        labelText: "Open items",
        valueText:
          caseSummaryOpenWorkCount === 0
            ? "None"
            : `${caseSummaryOpenWorkCount} open`,
      },
    ];
    const caseSummaryTimelineRows = [
      ...(selectedPatient.nextAppointmentText.trim().length === 0
        ? []
        : [
            {
              badgeText: "VISIT",
              bodyText: "Upcoming care touchpoint",
              id: "summary-next-appointment",
              titleText: "Upcoming visit",
              whenText:
                caseSummaryNextAppointmentWhenText ||
                "Schedule not recorded",
            },
          ]),
    ];
    const appointmentRecordItems = timelineItems.filter(
      (item) => item.badgeText === "APPO",
    );
    const nextAppointmentItem =
      secondaryTimelineItems.find((item) => item.badgeText === "APPO") ??
      appointmentRecordItems[0] ??
      null;
    const parsedSelectedNextAppointment = parseOptionBDetailValue(
      selectedPatient.nextAppointmentText,
    );
    const parsedSelectedNextAppointmentDateText =
      parsedSelectedNextAppointment.date == null
        ? ""
        : [
            formatOptionBTimelineDateText(
              parsedSelectedNextAppointment.date,
            ),
            formatOptionBTimelineTimeText(
              parsedSelectedNextAppointment.date,
            ),
          ].join(", ");
    const appointmentDateTimeText =
      nextAppointmentItem == null
        ? parsedSelectedNextAppointmentDateText || "Not scheduled"
        : [nextAppointmentItem.dateLabel, nextAppointmentItem.timeLabel]
            .filter((part) => part.trim().length > 0)
            .join(", ") ||
          parsedSelectedNextAppointmentDateText ||
          "Not scheduled";
    const appointmentTitleText =
      nextAppointmentItem?.titleText ??
      parsedSelectedNextAppointment.primaryText ??
      "Upcoming visit";
    const appointmentSnapshotRows: string[] = [
      appointmentDateTimeText !== "Not scheduled"
        ? `Next visit scheduled for ${appointmentDateTimeText}`
        : "No upcoming visit scheduled",
      selectedPatient.programText.trim().length > 0
        ? formatClinicianProgramContextText(
            resolveProductClinicalText(selectedPatient.programText),
          )
        : "",
      selectedPatient.coordinatorText.trim().length > 0
        ? `Coordinator: ${resolveProductClinicalText(
            selectedPatient.coordinatorText,
          )}`
        : "",
      contactWindowText != null && contactWindowText.trim().length > 0
        ? `Contact window: ${contactWindowText}`
        : "",
    ].filter((row) => row.trim().length > 0);
    const appointmentReadinessText =
      caseSummaryPendingApprovalCount > 0 || selectedPatient.requiresAttention
        ? "Needs review"
        : "Ready";
    const appointmentSideCards = [
      {
        detailText:
          selectedPatient.programText.trim().length > 0
            ? formatClinicianProgramContextText(
                resolveProductClinicalText(selectedPatient.programText),
              )
            : "Care program not assigned",
        iconName: "appointment" as LineIconName,
        id: "next-visit",
        labelText: "Next visit",
        valueText: appointmentDateTimeText,
      },
      {
        detailText:
          caseSummaryPendingApprovalCount > 0
            ? `${caseSummaryPendingApprovalCount} pending approval${
                caseSummaryPendingApprovalCount === 1 ? "" : "s"
              }`
            : "No pending approvals",
        iconName: "flag" as LineIconName,
        id: "readiness",
        labelText: "Readiness",
        valueText: appointmentReadinessText,
      },
      {
        detailText: "Best time to coordinate",
        iconName: "clock" as LineIconName,
        id: "contact-window",
        labelText: "Contact window",
        valueText:
          contactWindowText != null && contactWindowText.trim().length > 0
            ? contactWindowText
            : "Not recorded",
      },
      {
        detailText:
          selectedPatient.documentSummaries.length === 0
            ? "No attached documents"
            : "Available case records",
        iconName: "document" as LineIconName,
        id: "linked-records",
        labelText: "Linked records",
        valueText: `${selectedPatient.documentSummaries.length} document${
          selectedPatient.documentSummaries.length === 1 ? "" : "s"
        }`,
      },
    ];
    const nextAppointmentActionConfigs =
      nextAppointmentItem == null
        ? []
        : [
            ...(nextAppointmentItem.actionConfig == null
              ? []
              : [nextAppointmentItem.actionConfig]),
            ...(nextAppointmentItem.actionConfigs ?? []),
          ];
    const procedureRecordItems = timelineItems.filter(
      (item) => item.badgeText === "PROC" || item.badgeText === "PERF",
    );
    const nextProcedureItem =
      secondaryTimelineItems.find(
        (item) => item.badgeText === "PROC" || item.badgeText === "PERF",
      ) ??
      procedureRecordItems[0] ??
      null;
    const resolvedUpcomingProcedureText = resolveProductClinicalText(
      upcomingProcedureText ?? "",
    );
    const parsedUpcomingProcedure = parseOptionBDetailValue(
      resolvedUpcomingProcedureText,
    );
    const parsedUpcomingProcedureDateText =
      parsedUpcomingProcedure.date == null
        ? ""
        : [
            formatOptionBTimelineDateText(parsedUpcomingProcedure.date),
            formatOptionBTimelineTimeText(parsedUpcomingProcedure.date),
          ].join(", ");
    const procedureDateTimeText =
      nextProcedureItem == null
        ? parsedUpcomingProcedureDateText || "Not scheduled"
        : [nextProcedureItem.dateLabel, nextProcedureItem.timeLabel]
            .filter((part) => part.trim().length > 0)
            .join(", ") ||
          parsedUpcomingProcedureDateText ||
          "Not scheduled";
    const procedureTitleText =
      nextProcedureItem?.titleText ??
      parsedUpcomingProcedure.primaryText ??
      "Upcoming procedure";
    const procedureRelatedVisitText =
      railNextVisitText.trim().length > 0
        ? railNextVisitText
        : appointmentDateTimeText;
    const procedureProgramText = resolveProductClinicalText(
      selectedPatient.programText,
    );
    const procedureClinicianText = resolveProductClinicalText(
      selectedPatient.coordinatorText,
    );
    const procedureSnapshotRows: string[] = [
      procedureDateTimeText !== "Not scheduled"
        ? `Upcoming procedure scheduled for ${procedureDateTimeText}`
        : "No upcoming procedure scheduled",
      procedureProgramText.trim().length > 0
        ? formatClinicianProgramContextText(procedureProgramText)
        : "Program not assigned",
      procedureClinicianText.trim().length > 0
        ? `Clinician: ${procedureClinicianText}`
        : "",
      procedureRelatedVisitText !== "Not scheduled"
        ? `Related visit: ${procedureRelatedVisitText}`
        : "",
    ].filter((row) => row.trim().length > 0);
    const procedurePrepStatusText =
      caseSummaryPendingApprovalCount > 0 || selectedPatient.requiresAttention
        ? "Needs review"
        : "Ready";
    const procedureSideCards = [
      {
        detailText:
          procedureProgramText.trim().length > 0
            ? formatClinicianProgramContextText(procedureProgramText)
            : "Procedure program not assigned",
        iconName: "procedure" as LineIconName,
        id: "next-procedure",
        labelText: "Next procedure",
        valueText: procedureDateTimeText,
      },
      {
        detailText:
          caseSummaryPendingApprovalCount > 0
            ? `${caseSummaryPendingApprovalCount} pending approval${
                caseSummaryPendingApprovalCount === 1 ? "" : "s"
              }`
            : "No pending approvals",
        iconName: "flag" as LineIconName,
        id: "prep-status",
        labelText: "Prep status",
        valueText: procedurePrepStatusText,
      },
      {
        detailText: "Pre-op appointment",
        iconName: "appointment" as LineIconName,
        id: "related-visit",
        labelText: "Related visit",
        valueText: procedureRelatedVisitText,
      },
      {
        detailText:
          selectedPatient.documentSummaries.length === 0
            ? "No attached documents"
            : "Available case records",
        iconName: "document" as LineIconName,
        id: "linked-records",
        labelText: "Linked records",
        valueText: `${selectedPatient.documentSummaries.length} document${
          selectedPatient.documentSummaries.length === 1 ? "" : "s"
        }`,
      },
    ];
    const nextProcedureActionConfigs =
      nextProcedureItem == null
        ? []
        : [
            ...(nextProcedureItem.actionConfig == null
              ? []
              : [nextProcedureItem.actionConfig]),
            ...(nextProcedureItem.actionConfigs ?? []),
          ];
    const chartPackTimelineItem =
      timelineItems.find((item) => item.id === "chart-pack") ?? null;
    const documentRecordItems = timelineItems.filter(
      (item) => item.id !== "chart-pack" && item.badgeText !== "NOTE",
    );
    const readyDocumentItems = documentRecordItems.filter(
      (item) =>
        /ready|download|available/iu.test(item.statusText) ||
        /download ready|available for review/iu.test(item.groupLabel),
    );
    const reviewDocumentItems = documentRecordItems.filter(
      (item) =>
        !readyDocumentItems.some((readyItem) => readyItem.id === item.id),
    );
    const primaryReadyDocumentItem = readyDocumentItems[0] ?? null;
    const primaryReviewDocumentItem = reviewDocumentItems[0] ?? null;
    const latestDocumentItem =
      documentRecordItems.find((item) =>
        item.metrics.some((metric) =>
          /created|received|uploaded/iu.test(metric.labelText),
        ),
      ) ??
      documentRecordItems[0] ??
      null;
    const getDocumentMetricValue = (
      item: OptionBTimelineItem | null,
      pattern: RegExp,
    ) =>
      item?.metrics.find((metric) => pattern.test(metric.labelText))
        ?.valueText ?? "";
    const documentReadyCount = readyDocumentItems.length;
    const documentPendingCount = selectedPatient.pendingDocumentCount;
    const latestDocumentSourceText = getDocumentMetricValue(
      latestDocumentItem,
      /^created by$/iu,
    );
    const latestDocumentCreatedText = getDocumentMetricValue(
      latestDocumentItem,
      /^created$/iu,
    );
    const latestDocumentUploadText =
      latestDocumentItem?.bodyLines[0] ?? latestDocumentCreatedText;
    const documentSnapshotRows: string[] = [
      `${selectedPatient.documentSummaries.length} document${
        selectedPatient.documentSummaries.length === 1 ? "" : "s"
      } attached to this case`,
      `${documentReadyCount} available for review`,
      `${documentPendingCount} pending clinician review`,
      latestDocumentSourceText.trim().length > 0
        ? `Latest source: ${latestDocumentSourceText}`
        : "No document uploads recorded",
    ].filter((row) => row.trim().length > 0);
    const documentRelatedVisitText =
      railNextVisitText.trim().length > 0
        ? railNextVisitText
        : appointmentDateTimeText;
    const documentSideCards = [
      {
        detailText:
          primaryReadyDocumentItem?.titleText ?? "No available documents",
        iconName: "document" as LineIconName,
        id: "ready-documents",
        labelText: "Available for review",
        valueText: `${documentReadyCount} document${
          documentReadyCount === 1 ? "" : "s"
        }`,
      },
      {
        detailText:
          documentPendingCount > 0
            ? "Needs clinician pass"
            : "No pending review",
        iconName: "flag" as LineIconName,
        id: "pending-review",
        labelText: "Pending review",
        valueText: `${documentPendingCount} document${
          documentPendingCount === 1 ? "" : "s"
        }`,
      },
      {
        detailText: latestDocumentSourceText || "No upload source",
        iconName: "clock" as LineIconName,
        id: "latest-upload",
        labelText: "Latest upload",
        valueText: latestDocumentUploadText || "Not recorded",
      },
      {
        detailText: "Pre-op appointment",
        iconName: "appointment" as LineIconName,
        id: "related-visit",
        labelText: "Related visit",
        valueText: documentRelatedVisitText,
      },
    ];

    const renderCaseSummarySectionCard = (input: {
      children: ReactNode;
      titleText: string;
    }) => (
      <View
        style={[
          styles.detailBodyCard,
          {
            backgroundColor: theme.surface,
            borderColor: theme.borderSubtle,
            borderRadius: 8,
            gap: tokens.spacing.sm,
            padding: 16,
          },
        ]}
      >
        <Text
          style={[
            styles.navigationText,
            {
              color: theme.textPrimary,
              fontFamily: layout.displayFontFamily,
              fontSize: 15,
              fontWeight: "700",
              lineHeight: 19,
            },
          ]}
        >
          {input.titleText}
        </Text>
        {input.children}
      </View>
    );

    const renderClinicalRecordDetails = (item: OptionBTimelineItem) => {
      const bodyLines = item.bodyLines
        .filter((lineText) => lineText.trim().length > 0);
      const detailRows = (item.detailRows ?? [])
        .filter((row) => row.valueText.trim().length > 0);

      if (bodyLines.length === 0 && detailRows.length === 0) {
        return null;
      }

      return (
        <View style={[styles.listGroup, { gap: tokens.spacing.xs }]}>
          {bodyLines.map((lineText) => (
            <Text
              key={lineText}
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 13,
                  lineHeight: 18,
                },
              ]}
            >
              {lineText}
            </Text>
          ))}
          {detailRows.length > 0 ? (
            <View
              style={{
                borderTopColor: theme.borderSubtle,
                borderTopWidth: 1,
              }}
            >
              {detailRows.map((row, index) => (
                <View
                  key={`${item.id}-${row.labelText}-${index}`}
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "flex-start",
                      borderBottomColor:
                        index === detailRows.length - 1
                          ? "transparent"
                          : theme.borderSubtle,
                      borderBottomWidth:
                        index === detailRows.length - 1 ? 0 : 1,
                      flexDirection: isWideLayout ? "row" : "column",
                      gap: tokens.spacing.sm,
                      paddingVertical: 7,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 11,
                        fontWeight: "700",
                        lineHeight: 15,
                        width: isWideLayout ? 112 : "100%",
                      },
                    ]}
                  >
                    {row.labelText}
                  </Text>
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textPrimary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 13,
                        lineHeight: 18,
                        minWidth: 0,
                      },
                    ]}
                  >
                    {row.valueText}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}
        </View>
      );
    };

    const renderOutboundQuestionnaireCallsSection = () => (
      <View
        style={[
          styles.detailBodyCard,
          {
            backgroundColor: theme.surface,
            borderColor: theme.borderSubtle,
            borderRadius: 8,
            gap: tokens.spacing.sm,
            padding: 14,
          },
        ]}
      >
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "center",
              gap: tokens.spacing.sm,
              justifyContent: "space-between",
            },
          ]}
        >
          <View style={[styles.listGroup, { flex: 1, gap: 2 }]}>
            <Text
              style={[
                styles.navigationText,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: 15,
                  fontWeight: "700",
                  lineHeight: 19,
                },
              ]}
            >
              Questionnaire calls
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  lineHeight: 16,
                },
              ]}
            >
              Resume pre-op questionnaires or start a supervised repeat round.
            </Text>
          </View>
          <Pressable
            accessibilityLabel="Refresh questionnaire calls"
            accessibilityRole="button"
            onPress={() => onRefreshOutboundQuestionnaireCalls(selectedPatient.id)}
            style={({ pressed }) => [
              styles.detailHeaderAvatar,
              {
                backgroundColor: pressed ? theme.accentSubtle : theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: 9,
                height: clinicianModalControlHeight,
                width: clinicianModalControlHeight,
              },
            ]}
          >
            <LineIcon
              color={theme.accentStrong}
              name="sync"
              size={16}
              strokeWidth={2.3}
            />
          </Pressable>
        </View>

        {outboundQuestionnaireNoticeText ? (
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.success,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                lineHeight: 16,
              },
            ]}
          >
            {outboundQuestionnaireNoticeText}
          </Text>
        ) : null}

        {outboundQuestionnaireErrorText ? (
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.danger,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                lineHeight: 16,
              },
            ]}
          >
            {outboundQuestionnaireErrorText}
          </Text>
        ) : null}

        {outboundQuestionnaireIsLoading &&
        outboundQuestionnaireCalls.length === 0 ? (
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: 13,
                lineHeight: 18,
              },
            ]}
          >
            Loading questionnaire calls...
          </Text>
        ) : outboundQuestionnaireCalls.length === 0 ? (
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: 13,
                lineHeight: 18,
              },
            ]}
          >
            No outbound questionnaire campaign is linked to this patient yet.
          </Text>
        ) : (
          outboundQuestionnaireCalls.map((call) => {
            const pending =
              outboundQuestionnairePendingTargetId === call.targetId;
            const anyCallPending =
              outboundQuestionnairePendingTargetId != null;
            const pdfPending =
              outboundQuestionnairePdfPendingTargetId === call.targetId;
            const isTerminalQuestionnaireSession =
              call.sessionStatus === "completed" ||
              call.sessionStatus === "review_required";
            const progressText =
              call.sessionStatus === "review_required" &&
              call.totalQuestionCount > call.answeredQuestionCount
                ? `${call.answeredQuestionCount} captured; review required`
                : isTerminalQuestionnaireSession &&
              call.totalQuestionCount > call.answeredQuestionCount
                ? `${call.answeredQuestionCount} captured; conditional questions skipped`
                : call.totalQuestionCount > 0
                ? `${call.answeredQuestionCount}/${call.totalQuestionCount} answered`
                : "No questions counted yet";
            const statusText = formatOutboundCallStatusLabel(
              call.sessionStatus ?? call.targetStatus,
            );
            const actionLabel =
              pending
                ? "Starting..."
                : call.primaryActionLabel ?? "Unavailable";
            const answerPreviewItems = call.answerPreview ?? [];
            const answerPreviewSummaryText =
              call.answeredQuestionCount > 0 &&
              answerPreviewItems.length < call.answeredQuestionCount
                ? `Showing ${answerPreviewItems.length}/${call.answeredQuestionCount} answered questions`
                : `${answerPreviewItems.length} answered question${
                    answerPreviewItems.length === 1 ? "" : "s"
                  }`;

            return (
              <View
                key={call.targetId}
                style={[
                  styles.detailSectionCard,
                  {
                    backgroundColor: theme.backgroundSubtle,
                    borderColor: theme.borderSubtle,
                    borderRadius: 8,
                    gap: tokens.spacing.sm,
                    padding: 12,
                  },
                ]}
              >
                <View
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "flex-start",
                      gap: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.detailHeaderAvatar,
                      {
                        backgroundColor: theme.accentSubtle,
                        borderColor: "transparent",
                        borderRadius: 8,
                        height: 34,
                        width: 34,
                      },
                    ]}
                  >
                    <LineIcon
                      color={theme.accentStrong}
                      name="phone"
                      size={17}
                      strokeWidth={2.35}
                    />
                  </View>
                  <View style={[styles.listGroup, { flex: 1, gap: 4 }]}>
                    <Text
                      style={[
                        styles.navigationText,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.displayFontFamily,
                          fontSize: 14,
                          fontWeight: "700",
                          lineHeight: 18,
                        },
                      ]}
                    >
                      {call.campaignDisplayName}
                    </Text>
                    <Text
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 12,
                          lineHeight: 16,
                        },
                      ]}
                    >
                      {[
                        call.questionnaireDisplayName,
                        formatOutboundQuestionnaireVersionMeta(call) ?? "",
                        progressText,
                        call.currentQuestionKey
                          ? `Current step: ${formatOutboundQuestionKeyLabel(
                              call.currentQuestionKey,
                            )}`
                          : "",
                        call.phoneNumberLast4
                          ? `Phone ending ${call.phoneNumberLast4}`
                          : "",
                      ]
                        .filter((part) => part.trim().length > 0)
                        .join(" · ")}
                    </Text>
                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 6,
                          justifyContent: "flex-start",
                        },
                      ]}
                    >
                      {renderOptionBStatusPill(statusText)}
                      {call.outsideCallWindow
                        ? renderOptionBStatusPill("Outside hours")
                        : null}
                      {call.repeatWarning
                        ? renderOptionBStatusPill("Prior call")
                        : null}
                    </View>
                  </View>
                </View>
                {call.warningMessages.length > 0 ? (
                  <View
                    style={{
                      alignItems: "flex-start",
                      backgroundColor: theme.statusChip.warning.background,
                      borderColor: theme.statusChip.warning.border,
                      borderRadius: 10,
                      borderWidth: 1,
                      flexDirection: "row",
                      gap: 9,
                      paddingHorizontal: 12,
                      paddingVertical: 9,
                    }}
                  >
                    <View
                      style={{
                        alignItems: "center",
                        backgroundColor: theme.surface,
                        borderColor: theme.statusChip.warning.border,
                        borderRadius: 999,
                        borderWidth: 1,
                        height: 22,
                        justifyContent: "center",
                        marginTop: 1,
                        width: 22,
                      }}
                    >
                      <LineIcon
                        color={theme.statusChip.warning.text}
                        name="clock"
                        size={12}
                        strokeWidth={2.4}
                      />
                    </View>
                    <View style={{ flex: 1, gap: 1, minWidth: 0 }}>
                      <Text
                        style={[
                          styles.navigationText,
                          {
                            color: theme.statusChip.warning.text,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 12,
                            fontWeight: "800",
                            lineHeight: 15,
                          },
                        ]}
                      >
                        {call.outsideCallWindow
                          ? "Outside the campaign call window"
                          : "Before you dial"}
                      </Text>
                      <Text
                        style={[
                          styles.sectionSubtitle,
                          {
                            color: theme.statusChip.warning.text,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 11.5,
                            lineHeight: 15,
                          },
                        ]}
                      >
                        {call.warningMessages[0]}
                      </Text>
                    </View>
                  </View>
                ) : null}
                <View
                  style={[
                    styles.patientRowFooter,
                    {
                      gap: tokens.spacing.sm,
                      justifyContent: "flex-end",
                    },
                  ]}
                >
                  <Pressable
                    accessibilityLabel={actionLabel}
                    accessibilityRole="button"
                    disabled={anyCallPending || call.primaryAction == null}
                    onPress={() => onStartOutboundQuestionnaireCall(call)}
                    style={({ pressed }) => [
                      styles.patientSupportPrimaryButton,
                      {
                        backgroundColor:
                          anyCallPending || call.primaryAction == null
                            ? theme.borderSubtle
                            : pressed
                              ? theme.accentStrong
                              : theme.accent,
                        borderColor: "transparent",
                        borderRadius: 9,
                        flexDirection: "row",
                        gap: 6,
                        minHeight: isWideLayout
                          ? 32
                          : clinicianModalControlHeight,
                        opacity:
                          anyCallPending || call.primaryAction == null
                            ? 0.65
                            : 1,
                        paddingHorizontal: 13,
                        paddingVertical: 7,
                      },
                    ]}
                  >
                    <LineIcon
                      color={
                        anyCallPending || call.primaryAction == null
                          ? theme.textSecondary
                          : theme.accentContrast
                      }
                      name="phone"
                      size={13}
                      strokeWidth={2.4}
                    />
                    <Text
                      style={[
                        styles.badgeText,
                        {
                          color:
                            anyCallPending || call.primaryAction == null
                              ? theme.textSecondary
                              : theme.accentContrast,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11.5,
                          fontWeight: "700",
                          lineHeight: 14,
                        },
                      ]}
                    >
                      {actionLabel}
                    </Text>
                  </Pressable>
                </View>
                <HybridReviewPanel messages={call.automatedReviewMessages} theme={theme} fontFamily={layout.bodyFontFamily} />
                {answerPreviewItems.length > 0 ? (
                  <ClinicianExpandableModalRow
                    borderColor={theme.borderSubtle}
                    surfaceColor={theme.surface}
                    renderHeader={(open) => (
                      <View
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "center",
                            gap: tokens.spacing.sm,
                            justifyContent: "space-between",
                            paddingHorizontal: 10,
                            paddingVertical: 9,
                          },
                        ]}
                      >
                        <View
                          style={[
                            styles.detailHeaderAvatar,
                            {
                              backgroundColor: theme.backgroundSubtle,
                              borderColor: theme.borderSubtle,
                              borderRadius: 8,
                              height: 30,
                              width: 30,
                            },
                          ]}
                        >
                          <LineIcon
                            color={theme.accentStrong}
                            name="document"
                            size={15}
                            strokeWidth={2.3}
                          />
                        </View>
                        <View style={[styles.listGroup, { flex: 1, gap: 2 }]}>
                          <Text
                            style={[
                              styles.navigationText,
                              {
                                color: theme.textPrimary,
                                fontFamily: layout.displayFontFamily,
                                fontSize: 13,
                                fontWeight: "700",
                                lineHeight: 17,
                              },
                            ]}
                          >
                            Answer preview
                          </Text>
                          <Text
                            style={[
                              styles.sectionSubtitle,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 11,
                                lineHeight: 15,
                              },
                            ]}
                          >
                            {answerPreviewSummaryText}
                          </Text>
                        </View>
                        <View
                          style={{
                            transform: [{ rotate: open ? "90deg" : "0deg" }],
                          }}
                        >
                          <LineIcon
                            color={theme.textSecondary}
                            name="chevron-right"
                            size={15}
                            strokeWidth={2.2}
                          />
                        </View>
                      </View>
                    )}
                  >
                    <View
                      style={{
                        borderTopColor: theme.borderSubtle,
                        borderTopWidth: 1,
                        paddingHorizontal: 10,
                        paddingVertical: 9,
                      }}
                    >
                      <View
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "center",
                            gap: tokens.spacing.sm,
                            justifyContent: "space-between",
                            marginBottom: 8,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.sectionSubtitle,
                            {
                              color: theme.textSecondary,
                              flex: 1,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 10.5,
                              lineHeight: 14,
                            },
                          ]}
                        >
                          Review all captured answers in questionnaire order.
                        </Text>
                        <Pressable
                          accessibilityLabel="Download questionnaire answer PDF"
                          accessibilityRole="button"
                          accessibilityState={{
                            disabled: pdfPending || answerPreviewItems.length === 0,
                          }}
                          aria-disabled={
                            pdfPending || answerPreviewItems.length === 0
                          }
                          disabled={pdfPending || answerPreviewItems.length === 0}
                          onPress={() =>
                            onDownloadOutboundQuestionnaireAnswersPdf(call)
                          }
                          style={({ pressed }) => [
                            styles.patientSupportSecondaryButton,
                            {
                              backgroundColor:
                                pdfPending || answerPreviewItems.length === 0
                                  ? theme.backgroundSubtle
                                  : pressed
                                    ? theme.backgroundSubtle
                                    : theme.surface,
                              borderColor:
                                pdfPending || answerPreviewItems.length === 0
                                  ? theme.borderSubtle
                                  : theme.accentStrong,
                              borderRadius: 8,
                              flexDirection: "row",
                              gap: 6,
                              minHeight: clinicianModalControlHeight,
                              minWidth: 118,
                              opacity:
                                pdfPending || answerPreviewItems.length === 0
                                  ? 0.65
                                  : 1,
                              paddingHorizontal: 12,
                              paddingVertical: 7,
                            },
                          ]}
                        >
                          <LineIcon
                            color={
                              pdfPending || answerPreviewItems.length === 0
                                ? theme.textSecondary
                                : theme.accentStrong
                            }
                            name="document"
                            size={13}
                            strokeWidth={2.4}
                          />
                          <Text
                            style={[
                              styles.badgeText,
                              {
                                color:
                                  pdfPending || answerPreviewItems.length === 0
                                    ? theme.textSecondary
                                    : theme.accentStrong,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 11,
                                fontWeight: "700",
                                lineHeight: 14,
                              },
                            ]}
                          >
                            {pdfPending ? "Preparing..." : "Download PDF"}
                          </Text>
                        </Pressable>
                      </View>
                      <ScrollView
                        nestedScrollEnabled
                        showsVerticalScrollIndicator
                        style={{ maxHeight: 330 }}
                        contentContainerStyle={{ gap: 9, paddingBottom: 2 }}
                      >
                        {answerPreviewItems.map((answer) => {
                          const answeredAtText =
                            formatOutboundAnswerPreviewTimestampText(
                              answer.answeredAt,
                            );
                          const reviewLabels =
                            buildOutboundAnswerReviewLabels(answer);
                          const answerPromptText =
                            resolveQuestionnaireReviewPromptText(
                              answer.questionKey,
                              answer.promptText,
                            );

                          return (
                            <View
                              key={`${call.targetId}-${answer.questionKey}`}
                              style={{
                                backgroundColor: theme.backgroundSubtle,
                                borderColor: theme.borderSubtle,
                                borderRadius: 8,
                                borderWidth: 1,
                                gap: 6,
                                paddingHorizontal: 9,
                                paddingVertical: 8,
                              }}
                            >
                              <Text
                                style={[
                                  styles.metricLabel,
                                  {
                                    color: theme.textTertiary,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: 9.5,
                                    fontWeight: "800",
                                    lineHeight: 12,
                                    textTransform: "uppercase",
                                  },
                                ]}
                              >
                                Question
                              </Text>
                              <Text
                                style={[
                                  styles.sectionSubtitle,
                                  {
                                    color: theme.textPrimary,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: 12,
                                    fontWeight: "600",
                                    lineHeight: 16,
                                  },
                                ]}
                              >
                                {answerPromptText}
                              </Text>
                              <Text
                                style={[
                                  styles.metricLabel,
                                  {
                                    color: theme.textTertiary,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: 9.5,
                                    fontWeight: "800",
                                    lineHeight: 12,
                                    textTransform: "uppercase",
                                  },
                                ]}
                              >
                                Patient answer
                              </Text>
                              <Text
                                style={[
                                  styles.sectionSubtitle,
                                  {
                                    color: theme.textSecondary,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: 12,
                                    lineHeight: 16,
                                  },
                                ]}
                              >
                                {answer.answerSummary}
                              </Text>
                              {reviewLabels.length > 0 ? (
                                <View
                                  style={[
                                    styles.patientRowHeader,
                                    {
                                      alignItems: "center",
                                      flexWrap: "wrap",
                                      gap: 6,
                                      justifyContent: "flex-start",
                                    },
                                  ]}
                                >
                                  {reviewLabels.map((label) => (
                                    <Fragment
                                      key={`${answer.questionKey}-${label}`}
                                    >
                                      {renderOptionBStatusPill(label)}
                                    </Fragment>
                                  ))}
                                </View>
                              ) : null}
                              {answeredAtText ? (
                                <Text
                                  style={[
                                    styles.sectionSubtitle,
                                    {
                                      color: theme.textSecondary,
                                      fontFamily: layout.bodyFontFamily,
                                      fontSize: 10,
                                      lineHeight: 13,
                                    },
                                  ]}
                                >
                                  {answeredAtText}
                                </Text>
                              ) : null}
                            </View>
                          );
                        })}
                      </ScrollView>
                    </View>
                  </ClinicianExpandableModalRow>
                ) : null}
              </View>
            );
          })
        )}
      </View>
    );

    const renderInlineClinicalEditorBody = (contextTitle: string) => {
      if (inlineClinicalEditor == null) {
        return null;
      }

      if (inlineClinicalEditor.discardCloseTarget != null) {
        const discardCloseTarget = inlineClinicalEditor.discardCloseTarget;

        return (
          <View
            accessibilityLabel="Discard inline clinical editor changes"
            accessibilityRole="alert"
            style={{
              alignItems: "center",
              flex: 1,
              justifyContent: "center",
              padding: tokens.spacing.lg,
            }}
          >
            <View
              style={[
                styles.listGroup,
                {
                  backgroundColor: theme.surface,
                  borderColor: theme.borderStrong,
                  borderRadius: workforceOperationalLayout.panelRadius,
                  borderWidth: 1,
                  gap: tokens.spacing.md,
                  maxWidth: 520,
                  padding: tokens.spacing.lg,
                  width: "100%",
                },
              ]}
            >
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                    fontSize: 18,
                    lineHeight: 23,
                  },
                ]}
              >
                Discard unsaved changes?
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 13,
                    lineHeight: 19,
                  },
                ]}
              >
                This clinical edit has not been saved. You can keep editing or
                discard it.
              </Text>
              <View
                style={[
                  styles.patientRowHeader,
                  {
                    flexWrap: "wrap",
                    gap: tokens.spacing.sm,
                    justifyContent: "flex-end",
                  },
                ]}
              >
                <Pressable
                  accessibilityLabel="Keep editing clinical entry"
                  accessibilityRole="button"
                  onPress={() =>
                    setInlineClinicalEditor((current) =>
                      current == null
                        ? current
                        : {
                            ...current,
                            discardCloseTarget: null,
                          },
                    )
                  }
                  style={({ pressed }) => [
                    styles.patientSupportSecondaryButton,
                    {
                      backgroundColor: pressed
                        ? theme.backgroundSubtle
                        : theme.surface,
                      borderColor: theme.borderStrong,
                      borderRadius: workforceOperationalLayout.controlRadius,
                      flexGrow: isWideLayout ? 0 : 1,
                      minHeight: clinicianModalControlHeight,
                      minWidth: 120,
                      paddingHorizontal: 14,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.badgeText,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        lineHeight: 15,
                      },
                    ]}
                  >
                    Keep editing
                  </Text>
                </Pressable>
                <Pressable
                  accessibilityLabel="Discard inline clinical editor changes"
                  accessibilityRole="button"
                  onPress={() => {
                    setInlineClinicalEditor(null);

                    if (discardCloseTarget === "modal") {
                      setSelectedDetailTabId("information");
                    }
                  }}
                  style={({ pressed }) => [
                    styles.patientSupportPrimaryButton,
                    {
                      backgroundColor: pressed
                        ? theme.statusChip.danger.text
                        : theme.danger,
                      borderColor: theme.danger,
                      borderRadius: workforceOperationalLayout.controlRadius,
                      flexGrow: isWideLayout ? 0 : 1,
                      minHeight: clinicianModalControlHeight,
                      minWidth: 152,
                      paddingHorizontal: 14,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.badgeText,
                      {
                        color: theme.accentContrast,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        lineHeight: 15,
                      },
                    ]}
                  >
                    Discard changes
                  </Text>
                </Pressable>
              </View>
            </View>
          </View>
        );
      }

      const isRecordEditor = inlineClinicalEditor.kind === "record";
      const recordContextTitle = contextTitle
        .replace(/s$/u, "")
        .toLowerCase();
      const recordKindLabel = isRecordEditor
        ? formatClinicalRecordKindLabel(inlineClinicalEditor.targetKind)
        : "";
      const recordDetailsPlaceholder =
        isRecordEditor && inlineClinicalEditor.targetKind === "appointment"
          ? "Date, time, clinician, program, location, and visit notes"
          : isRecordEditor && inlineClinicalEditor.targetKind === "procedure"
            ? "Procedure timing, prep status, clinical notes, and follow-up"
            : isRecordEditor && inlineClinicalEditor.targetKind === "medication"
              ? "Medication name, dose, schedule, status, and notes"
              : isRecordEditor && inlineClinicalEditor.targetKind === "document"
                ? "Document status, source, review notes, and download details"
                : "Record details";
      const titleText = isRecordEditor
        ? inlineClinicalEditor.recordId == null
          ? `Add ${recordContextTitle}`
          : `Edit ${recordContextTitle}`
        : inlineClinicalEditor.noteId == null
          ? "Add note"
          : "Edit note";
      const primaryValue = isRecordEditor
        ? inlineClinicalEditor.detailText
        : inlineClinicalEditor.bodyText;
      const canSave =
        inlineClinicalEditor.title.trim().length > 0 &&
        primaryValue.trim().length > 0 &&
        !isWorkbenchActionSubmitting;

      return (
        <ScrollView
          style={[
            styles.patientSupportModalScroll,
            {
              flex: 1,
            },
          ]}
          contentContainerStyle={[
            styles.patientSupportModalContent,
            {
              gap: tokens.spacing.lg,
              paddingHorizontal: tokens.spacing.lg,
              paddingVertical: tokens.spacing.md,
            },
          ]}
          showsVerticalScrollIndicator={false}
        >
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: "flex-start",
                gap: 18,
              },
            ]}
          >
            <View style={[styles.listGroup, { flex: 1.55, gap: 14 }]}>
              {renderCaseSummarySectionCard({
                titleText,
                children: (
                  <View style={[styles.listGroup, { gap: tokens.spacing.md }]}>
                    <View style={[styles.listGroup, { gap: 6 }]}>
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 11,
                            fontWeight: "700",
                            lineHeight: 14,
                            textTransform: "uppercase",
                          },
                        ]}
                      >
                        Title
                      </Text>
                      <TextInput
                        editable={!isWorkbenchActionSubmitting}
                        onChangeText={(value) =>
                          setInlineClinicalEditor((current) =>
                            current == null
                              ? current
                              : {
                                  ...current,
                                  title: value,
                                },
                          )
                        }
                        placeholder={
                          isRecordEditor
                            ? `${recordKindLabel} title`
                            : "Note title"
                        }
                        placeholderTextColor={theme.textTertiary}
                        style={[
                          styles.patientSupportInput,
                          {
                            backgroundColor: theme.backgroundSubtle,
                            borderColor: theme.borderSubtle,
                            borderRadius: 8,
                            color: theme.textPrimary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 15,
                            lineHeight: 20,
                            minHeight: 42,
                            paddingHorizontal: 12,
                            paddingVertical: 8,
                          },
                        ]}
                        value={inlineClinicalEditor.title}
                      />
                    </View>

                    <View style={[styles.listGroup, { gap: 6 }]}>
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 11,
                            fontWeight: "700",
                            lineHeight: 14,
                            textTransform: "uppercase",
                          },
                        ]}
                      >
                        {isRecordEditor ? `${recordKindLabel} details` : "Note"}
                      </Text>
                      <TextInput
                        editable={!isWorkbenchActionSubmitting}
                        multiline={true}
                        onChangeText={(value) =>
                          setInlineClinicalEditor((current) =>
                            current == null
                              ? current
                              : current.kind === "record"
                                ? {
                                    ...current,
                                    detailText: value,
                                  }
                                : {
                                    ...current,
                                    bodyText: value,
                                  },
                          )
                        }
                        placeholder={
                          isRecordEditor
                            ? recordDetailsPlaceholder
                            : "Write the clinical note"
                        }
                        placeholderTextColor={theme.textTertiary}
                        style={[
                          styles.patientSupportInput,
                          {
                            backgroundColor: theme.backgroundSubtle,
                            borderColor: theme.borderSubtle,
                            borderRadius: 8,
                            color: theme.textPrimary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 15,
                            lineHeight: 21,
                            minHeight: isRecordEditor ? 132 : 156,
                            paddingHorizontal: 12,
                            paddingVertical: 10,
                            textAlignVertical: "top",
                          },
                        ]}
                        value={primaryValue}
                      />
                    </View>

                    {!isRecordEditor ? (
                      <View
                        accessibilityLabel="Clinical note visibility"
                        role="radiogroup"
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: tokens.spacing.sm,
                          },
                        ]}
                      >
                        {(["patient-visible", "clinical"] as const).map(
                          (visibility, visibilityIndex) => {
                            const isSelected =
                              inlineClinicalEditor.visibility === visibility;

                            return (
                              <Pressable
                                accessibilityRole="radio"
                                accessibilityState={{
                                  checked: isSelected,
                                  disabled: isWorkbenchActionSubmitting,
                                }}
                                aria-checked={isSelected}
                                aria-disabled={isWorkbenchActionSubmitting}
                                disabled={isWorkbenchActionSubmitting}
                                key={visibility}
                                onPress={() =>
                                  setInlineClinicalEditor((current) =>
                                    current == null || current.kind !== "note"
                                      ? current
                                      : {
                                          ...current,
                                          visibility,
                                        },
                                  )
                                }
                                tabIndex={isSelected ? 0 : -1}
                                {...(Platform.OS === "web"
                                  ? {
                                      onKeyDown: (event: unknown) =>
                                        handleHorizontalTabKeyDown(
                                          event as WebTabKeyDownEvent,
                                          visibilityIndex,
                                          2,
                                          (nextIndex) => {
                                            const nextVisibility = (
                                              [
                                                "patient-visible",
                                                "clinical",
                                              ] as const
                                            )[nextIndex];

                                            setInlineClinicalEditor((current) =>
                                              current == null ||
                                              current.kind !== "note"
                                                ? current
                                                : {
                                                    ...current,
                                                    visibility: nextVisibility,
                                                  },
                                            );
                                          },
                                        ),
                                    }
                                  : {})}
                                style={({ pressed }) => [
                                  styles.inlinePill,
                                  {
                                    backgroundColor: isSelected
                                      ? theme.accentSubtle
                                      : pressed
                                        ? theme.backgroundSubtle
                                        : theme.surface,
                                    borderColor: isSelected
                                      ? theme.accentStrong
                                      : theme.borderSubtle,
                                    borderRadius: 8,
                                    opacity: isWorkbenchActionSubmitting
                                      ? 0.6
                                      : 1,
                                    paddingHorizontal: 10,
                                    paddingVertical: 6,
                                  },
                                ]}
                              >
                                <Text
                                  style={[
                                    styles.badgeText,
                                    {
                                      color: isSelected
                                        ? theme.accentStrong
                                        : theme.textSecondary,
                                      fontFamily: layout.bodyFontFamily,
                                      fontSize: 12,
                                      lineHeight: 15,
                                    },
                                  ]}
                                >
                                  {visibility === "clinical"
                                    ? "Clinical only"
                                    : "Patient visible"}
                                </Text>
                              </Pressable>
                            );
                          },
                        )}
                      </View>
                    ) : null}

                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "center",
                          gap: tokens.spacing.sm,
                          justifyContent: "flex-end",
                        },
                      ]}
                    >
                      <Pressable
                        accessibilityLabel="Cancel clinical edit"
                        accessibilityRole="button"
                        accessibilityState={{
                          disabled: isWorkbenchActionSubmitting,
                        }}
                        aria-disabled={isWorkbenchActionSubmitting}
                        disabled={isWorkbenchActionSubmitting}
                        onPress={() =>
                          requestCloseInlineClinicalEditor("editor")
                        }
                        style={({ pressed }) => [
                          styles.patientSupportSecondaryButton,
                          {
                            backgroundColor: pressed
                              ? theme.backgroundSubtle
                              : theme.surface,
                            borderColor: theme.borderSubtle,
                            borderRadius: 8,
                            minHeight: clinicianModalControlHeight,
                            minWidth: 84,
                            opacity: isWorkbenchActionSubmitting ? 0.6 : 1,
                            paddingHorizontal: 14,
                            paddingVertical: 8,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.badgeText,
                            {
                              color: theme.textSecondary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 12,
                              lineHeight: 15,
                            },
                          ]}
                        >
                          Cancel
                        </Text>
                      </Pressable>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityState={{ disabled: !canSave }}
                        aria-disabled={!canSave}
                        disabled={!canSave}
                        onPress={submitInlineClinicalEditor}
                        style={({ pressed }) => [
                          styles.patientSupportPrimaryButton,
                          {
                            backgroundColor: canSave
                              ? pressed
                                ? theme.accentStrong
                                : theme.accent
                              : theme.borderSubtle,
                            borderColor: "transparent",
                            borderRadius: 8,
                            minHeight: clinicianModalControlHeight,
                            minWidth: 124,
                            opacity: canSave ? 1 : 0.7,
                            paddingHorizontal: 14,
                            paddingVertical: 8,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.badgeText,
                            {
                              color: canSave
                                ? theme.headerText
                                : theme.textSecondary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 12,
                              lineHeight: 15,
                            },
                          ]}
                        >
                          {isWorkbenchActionSubmitting ? "Saving" : "Save"}
                        </Text>
                      </Pressable>
                    </View>
                  </View>
                ),
              })}
            </View>

            <View style={[styles.listGroup, { flex: 1, gap: 10 }]}>
              <View
                style={[
                  styles.detailBodyCard,
                  {
                    backgroundColor: theme.backgroundSubtle,
                    borderColor: theme.borderSubtle,
                    borderRadius: 8,
                    gap: 8,
                    padding: 12,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 11,
                      fontWeight: "700",
                      lineHeight: 14,
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  Editing in
                </Text>
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.displayFontFamily,
                      fontSize: 16,
                      lineHeight: 20,
                    },
                  ]}
                >
                  {contextTitle}
                </Text>
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      lineHeight: 16,
                    },
                  ]}
                >
                  {selectedPatient.displayName}
                </Text>
              </View>
              <View
                style={[
                  styles.detailBodyCard,
                  {
                    backgroundColor: theme.backgroundSubtle,
                    borderColor: theme.borderSubtle,
                    borderRadius: 8,
                    gap: 8,
                    padding: 12,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 11,
                      fontWeight: "700",
                      lineHeight: 14,
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  Context
                </Text>
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 13,
                      lineHeight: 18,
                    },
                  ]}
                >
                  {[
                    resolveProductClinicalText(selectedPatient.programText),
                    contactWindowText != null
                      ? `Contact window ${contactWindowText}`
                      : "",
                  ]
                    .filter((part) => part.trim().length > 0)
                    .join(" · ")}
                </Text>
              </View>
            </View>
          </View>
        </ScrollView>
      );
    };

    const renderCaseSummaryBody = () =>
      inlineClinicalEditor == null ? (
      <ScrollView
        style={[
          styles.patientSupportModalScroll,
          {
            flex: 1,
          },
        ]}
        contentContainerStyle={[
          styles.patientSupportModalContent,
          {
            gap: tokens.spacing.lg,
            paddingHorizontal: isWideLayout
              ? tokens.spacing.lg
              : tokens.spacing.md,
            paddingVertical: tokens.spacing.md,
          },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "flex-start",
              flexDirection: isWideLayout ? "row" : "column",
              gap: 18,
            },
          ]}
        >
          <View
            style={[
              styles.listGroup,
              {
                flex: isWideLayout ? 1.55 : 0,
                gap: 14,
                width: "100%",
              },
            ]}
          >
            {renderCaseSummarySectionCard({
              titleText: "Clinical snapshot",
              children: (
                <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
                  {caseSummarySnapshotRows.map((rowText) => (
                    <View
                      key={rowText}
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "flex-start",
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <View
                        style={[
                          styles.detailHeaderAvatar,
                          {
                            backgroundColor: theme.accentSubtle,
                            borderColor: "transparent",
                            borderRadius: 7,
                            height: 18,
                            marginTop: 2,
                            width: 18,
                          },
                        ]}
                      >
                        <LineIcon
                          color={theme.accentStrong}
                          name="check"
                          size={12}
                          strokeWidth={2.5}
                        />
                      </View>
                      <Text
                        style={[
                          styles.sectionSubtitle,
                          {
                            color: theme.textPrimary,
                            flex: 1,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 14,
                            lineHeight: 20,
                          },
                        ]}
                      >
                        {rowText}
                      </Text>
                    </View>
                  ))}
                </View>
              ),
            })}

            {renderCaseSummarySectionCard({
              titleText: "Recent note",
              children:
                latestPatientClinicalNote == null ? (
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 15,
                        lineHeight: 21,
                      },
                    ]}
                  >
                    No clinical note is on file yet.
                  </Text>
                ) : (
                  <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "flex-start",
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <View style={[styles.listGroup, { flex: 1, gap: 4 }]}>
                        <View
                          style={[
                            styles.patientRowHeader,
                            {
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: tokens.spacing.xs,
                              justifyContent: "flex-start",
                            },
                          ]}
                        >
                          <View
                            style={[
                              styles.inlinePill,
                              {
                                backgroundColor: theme.accentSubtle,
                                borderColor: "transparent",
                                borderRadius: 7,
                                paddingHorizontal: 9,
                                paddingVertical: 3,
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.badgeText,
                                {
                                  color: theme.accentStrong,
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: 10,
                                  lineHeight: 13,
                                },
                              ]}
                            >
                              NOTE
                            </Text>
                          </View>
                          <Text
                            style={[
                              styles.navigationText,
                              {
                                color: theme.textPrimary,
                                flexShrink: 1,
                                fontFamily: layout.displayFontFamily,
                                fontSize: 17,
                                fontWeight: "700",
                                lineHeight: 21,
                                minWidth: 0,
                              },
                            ]}
                          >
                            {resolveProductClinicalText(
                              latestPatientClinicalNote.titleText,
                            )}
                          </Text>
                        </View>
                        <Text
                          numberOfLines={4}
                          style={[
                            styles.sectionSubtitle,
                            {
                              color: theme.textSecondary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 15,
                              lineHeight: 22,
                            },
                          ]}
                        >
                          {resolveProductClinicalNoteBodyText(
                            latestPatientClinicalNote.bodyText,
                          )}
                        </Text>
                        <View
                          style={[
                            styles.patientRowHeader,
                            {
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: tokens.spacing.sm,
                              justifyContent: "flex-start",
                            },
                          ]}
                        >
                          <Text
                            style={[
                              styles.metricLabel,
                              {
                                color: theme.textTertiary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 13,
                                lineHeight: 17,
                              },
                            ]}
                          >
                            {`${resolveProductClinicalText(
                              latestPatientClinicalNote.authoredByText,
                            )} · ${
                              latestPatientClinicalNote.visibility === "clinical"
                                ? "Clinical only"
                                : "Patient visible"
                            }`}
                          </Text>
                          {renderOptionBStatusPill(
                            latestPatientClinicalNote.visibility === "clinical"
                              ? "Clinical"
                              : "Patient visible",
                          )}
                        </View>
                      </View>
                      <View
                        style={[
                          styles.patientRowHeader,
                          { alignItems: "center", flexShrink: 0, gap: 6 },
                        ]}
                      >
                        {buildClinicalNoteActionConfigs(
                          latestPatientClinicalNote,
                        ).map((actionConfig) => {
                          const resolvedActionConfig =
                            actionConfig.label === "Edit note"
                              ? {
                                  ...actionConfig,
                                  onPress: () =>
                                    openInlineNoteEditor({
                                      defaultBodyText:
                                        latestPatientClinicalNote.bodyText,
                                      defaultTitle:
                                        latestPatientClinicalNote.titleText,
                                      defaultVisibility:
                                        latestPatientClinicalNote.visibility,
                                      noteId: latestPatientClinicalNote.id,
                                      targetId:
                                        latestPatientClinicalNote.targetId,
                                      targetKind:
                                        latestPatientClinicalNote.targetKind,
                                    }),
                                }
                              : actionConfig;
                          const actionColor = resolveClinicianModalActionColor(
                            resolvedActionConfig.tone,
                          );
                          const actionIconName = resolveModalActionIconName(
                            resolvedActionConfig.label,
                            resolveClinicianModalActionIcon(
                              resolvedActionConfig.tone,
                            ),
                          );

                          return (
                            <Pressable
                              accessibilityLabel={resolvedActionConfig.label}
                              accessibilityRole="button"
                              accessibilityState={{
                                disabled: resolvedActionConfig.disabled,
                              }}
                              aria-disabled={resolvedActionConfig.disabled}
                              disabled={resolvedActionConfig.disabled}
                              key={`recent-note-${resolvedActionConfig.label}`}
                              onPress={resolvedActionConfig.onPress}
                              style={({ pressed }) => [
                                styles.detailHeaderAvatar,
                                {
                                  backgroundColor: pressed
                                    ? theme.backgroundSubtle
                                    : theme.surface,
                                  borderColor: resolvedActionConfig.disabled
                                    ? theme.borderSubtle
                                    : actionColor,
                                  borderRadius: 9,
                                  height: clinicianModalControlHeight,
                                  opacity: resolvedActionConfig.disabled ? 0.5 : 1,
                                  width: clinicianModalControlHeight,
                                },
                              ]}
                            >
                              <LineIcon
                                color={
                                  resolvedActionConfig.disabled
                                    ? theme.textSecondary
                                    : actionColor
                                }
                                name={actionIconName}
                                size={16}
                                strokeWidth={2.3}
                              />
                            </Pressable>
                          );
                        })}
                      </View>
                    </View>
                  </View>
                ),
            })}

            {renderCaseSummarySectionCard({
              titleText: "Care timeline",
              children:
                caseSummaryTimelineRows.length === 0 ? (
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 15,
                        lineHeight: 21,
                      },
                    ]}
                  >
                    No recent care activity is available yet.
                  </Text>
                ) : (
                  <View style={[styles.listGroup, { gap: 0 }]}>
                    {caseSummaryTimelineRows.map((row, rowIndex) => (
                      <View
                        key={row.id}
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "stretch",
                            borderBottomColor:
                              rowIndex === caseSummaryTimelineRows.length - 1
                                ? "transparent"
                                : theme.borderSubtle,
                            borderBottomWidth:
                              rowIndex === caseSummaryTimelineRows.length - 1
                                ? 0
                                : 1,
                            gap: tokens.spacing.sm,
                            paddingVertical: tokens.spacing.sm,
                          },
                        ]}
                      >
                        <View
                          style={[
                            styles.detailHeaderAvatar,
                            {
                              backgroundColor: theme.backgroundSubtle,
                              borderColor: theme.borderSubtle,
                              borderRadius: 8,
                              height: 32,
                              marginTop: 2,
                              width: 32,
                            },
                          ]}
                        >
                          <LineIcon
                            color={theme.accentStrong}
                            name={row.badgeText === "VISIT" ? "appointment" : "document"}
                            size={17}
                            strokeWidth={2.3}
                          />
                        </View>
                        <View style={[styles.listGroup, { flex: 1, gap: 3 }]}>
                          <Text
                            style={[
                              styles.navigationText,
                              {
                                color: theme.textPrimary,
                                fontFamily: layout.displayFontFamily,
                                fontSize: 15,
                                fontWeight: "700",
                                lineHeight: 20,
                              },
                            ]}
                          >
                            {row.titleText}
                          </Text>
                          <Text
                            numberOfLines={2}
                            style={[
                              styles.sectionSubtitle,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 14,
                                lineHeight: 20,
                              },
                            ]}
                          >
                            {row.bodyText}
                          </Text>
                        </View>
                        <Text
                          numberOfLines={2}
                          style={[
                            styles.metricLabel,
                            {
                              color: theme.textTertiary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 13,
                              lineHeight: 17,
                              textAlign: "right",
                              width: 116,
                            },
                          ]}
                        >
                          {row.whenText}
                        </Text>
                      </View>
                    ))}
                  </View>
                ),
            })}
          </View>

          <View
            style={[
              styles.listGroup,
              {
                flex: isWideLayout ? 1 : 0,
                gap: 10,
                width: "100%",
              },
            ]}
          >
            {caseSummarySideCards.map((card) => (
              <View
                key={card.id}
                style={[
                  styles.detailBodyCard,
                  {
                    backgroundColor: theme.backgroundSubtle,
                    borderColor: theme.borderSubtle,
                    borderRadius: 8,
                    gap: 6,
                    minHeight: 78,
                    padding: 12,
                  },
                ]}
              >
                <View
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "center",
                      gap: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.detailHeaderAvatar,
                      {
                        backgroundColor: theme.surface,
                        borderColor: theme.borderSubtle,
                        borderRadius: 8,
                        height: 28,
                        width: 28,
                      },
                    ]}
                  >
                    <LineIcon
                      color={theme.accentStrong}
                      name={card.iconName}
                      size={15}
                      strokeWidth={2.35}
                    />
                  </View>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 11,
                        fontWeight: "700",
                        lineHeight: 14,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    {card.labelText}
                  </Text>
                </View>
                <Text
                  numberOfLines={3}
                  style={[
                    styles.sectionTitle,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.displayFontFamily,
                      fontSize: 16,
                      lineHeight: 20,
                    },
                  ]}
                >
                  {card.valueText}
                </Text>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      lineHeight: 15,
                    },
                  ]}
                >
                  {card.detailText}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
    ) : (
      renderInlineClinicalEditorBody("Case summary")
    );

    const renderAppointmentsBody = () =>
      inlineClinicalEditor == null ? (
      <ScrollView
        style={[
          styles.patientSupportModalScroll,
          {
            flex: 1,
          },
        ]}
        contentContainerStyle={[
          styles.patientSupportModalContent,
          {
            gap: tokens.spacing.lg,
            paddingHorizontal: isWideLayout
              ? tokens.spacing.lg
              : tokens.spacing.md,
            paddingVertical: tokens.spacing.md,
          },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "flex-start",
              flexDirection: isWideLayout ? "row" : "column",
              gap: 18,
            },
          ]}
        >
          <View
            style={[
              styles.listGroup,
              {
                flex: isWideLayout ? 1.55 : 0,
                gap: 14,
                width: "100%",
              },
            ]}
          >
            {renderCaseSummarySectionCard({
              titleText: "Appointment snapshot",
              children: (
                <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
                  {appointmentSnapshotRows.map((rowText) => (
                    <View
                      key={rowText}
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "flex-start",
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <View
                        style={[
                          styles.detailHeaderAvatar,
                          {
                            backgroundColor: theme.accentSubtle,
                            borderColor: "transparent",
                            borderRadius: 7,
                            height: 18,
                            marginTop: 2,
                            width: 18,
                          },
                        ]}
                      >
                        <LineIcon
                          color={theme.accentStrong}
                          name="check"
                          size={12}
                          strokeWidth={2.5}
                        />
                      </View>
                      <Text
                        style={[
                          styles.sectionSubtitle,
                          {
                            color: theme.textPrimary,
                            flex: 1,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 14,
                            lineHeight: 20,
                          },
                        ]}
                      >
                        {rowText}
                      </Text>
                    </View>
                  ))}
                </View>
              ),
            })}

            {renderCaseSummarySectionCard({
              titleText: "Upcoming visit",
              children:
                nextAppointmentItem == null ? (
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 15,
                        lineHeight: 21,
                      },
                    ]}
                  >
                    No upcoming appointment is scheduled yet.
                  </Text>
                ) : (
                  <View
                    style={[
                      styles.listGroup,
                      {
                        gap: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "center",
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.navigationText,
                          {
                            color: theme.textPrimary,
                            flex: 1,
                            fontFamily: layout.displayFontFamily,
                            fontSize: 17,
                            fontWeight: "700",
                            lineHeight: 21,
                            minWidth: 0,
                          },
                        ]}
                      >
                        {appointmentTitleText}
                      </Text>
                      {nextAppointmentActionConfigs.map((actionConfig) => {
                        const resolvedActionConfig =
                          actionConfig.label === "Edit"
                            ? {
                                ...actionConfig,
                                onPress: () =>
                                  openInlineRecordEditor({
                                    defaultDetailText:
                                      nextAppointmentItem.bodyLines.join("\n"),
                                    defaultTitle:
                                      nextAppointmentItem.titleText,
                                    recordId: nextAppointmentItem.id,
                                    targetKind: "appointment",
                                  }),
                              }
                            : actionConfig.label === "Add note"
                              ? {
                                  ...actionConfig,
                                  onPress: () =>
                                    openInlineNoteEditor({
                                      defaultBodyText: "",
                                      defaultTitle: `${nextAppointmentItem.titleText} note`,
                                      targetId: nextAppointmentItem.id,
                                      targetKind: "appointment",
                                    }),
                                }
                              : actionConfig;
                        const actionColor = resolveClinicianModalActionColor(
                          resolvedActionConfig.tone,
                        );
                        const actionIconName = resolveModalActionIconName(
                          resolvedActionConfig.label,
                          resolveClinicianModalActionIcon(
                            resolvedActionConfig.tone,
                          ),
                        );

                        return (
                          <Pressable
                            accessibilityLabel={resolvedActionConfig.label}
                            accessibilityRole="button"
                            accessibilityState={{
                              disabled: resolvedActionConfig.disabled,
                            }}
                            aria-disabled={resolvedActionConfig.disabled}
                            disabled={resolvedActionConfig.disabled}
                            key={`appointment-${resolvedActionConfig.label}`}
                            onPress={resolvedActionConfig.onPress}
                            style={({ pressed }) => [
                              styles.detailHeaderAvatar,
                              {
                                backgroundColor: pressed
                                  ? theme.backgroundSubtle
                                  : theme.surface,
                                borderColor: resolvedActionConfig.disabled
                                  ? theme.borderSubtle
                                  : actionColor,
                                borderRadius: 9,
                                height: isWideLayout
                                  ? 32
                                  : clinicianModalControlHeight,
                                opacity: resolvedActionConfig.disabled ? 0.5 : 1,
                                width: isWideLayout
                                  ? 32
                                  : clinicianModalControlHeight,
                              },
                            ]}
                          >
                            <LineIcon
                              color={
                                resolvedActionConfig.disabled
                                  ? theme.textSecondary
                                  : actionColor
                              }
                              name={actionIconName}
                              size={15}
                              strokeWidth={2.3}
                            />
                          </Pressable>
                        );
                      })}
                    </View>
                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "center",
                          gap: tokens.spacing.md,
                        },
                      ]}
                    >
                      <View
                        style={[
                          styles.detailHeaderAvatar,
                          {
                            backgroundColor: theme.backgroundSubtle,
                            borderColor: theme.borderSubtle,
                            borderRadius: 8,
                            height: 52,
                            width: 64,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.metricLabel,
                            {
                              color: theme.accentStrong,
                              fontFamily: layout.displayFontFamily,
                              fontSize: 11,
                              fontWeight: "800",
                              lineHeight: 14,
                              textAlign: "center",
                            },
                          ]}
                        >
                          {nextAppointmentItem.dateLabel || "VISIT"}
                        </Text>
                        <Text
                          style={[
                            styles.sectionSubtitle,
                            {
                              color: theme.textSecondary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 10,
                              lineHeight: 13,
                              textAlign: "center",
                            },
                          ]}
                        >
                          {nextAppointmentItem.timeLabel}
                        </Text>
                      </View>
                      <View style={[styles.listGroup, { flex: 1, gap: 4 }]}>
                        <Text
                          style={[
                            styles.sectionTitle,
                            {
                              color: theme.textPrimary,
                              fontFamily: layout.displayFontFamily,
                              fontSize: 16,
                              lineHeight: 20,
                            },
                          ]}
                        >
                          {appointmentDateTimeText}
                        </Text>
                        <View
                          style={[
                            styles.patientRowHeader,
                            {
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: tokens.spacing.sm,
                            },
                          ]}
                        >
                          <Text
                            numberOfLines={2}
                            style={[
                              styles.sectionSubtitle,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 14,
                                lineHeight: 20,
                              },
                            ]}
                          >
                            {[
                              resolveProductClinicalText(
                                selectedPatient.coordinatorText,
                              ),
                              formatClinicianProgramContextText(
                                resolveProductClinicalText(
                                  selectedPatient.programText,
                                ),
                              ),
                            ]
                              .filter((part) => part.trim().length > 0)
                              .join(" · ")}
                          </Text>
                          {renderOptionBStatusPill(nextAppointmentItem.statusText)}
                        </View>
                      </View>
                    </View>
                    {renderClinicalRecordDetails(nextAppointmentItem)}
                  </View>
                ),
            })}

            {renderCaseSummarySectionCard({
              titleText: "Visit timeline",
              children:
                appointmentRecordItems.length === 0 ? (
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 15,
                        lineHeight: 21,
                      },
                    ]}
                  >
                    No visit timeline is available yet.
                  </Text>
                ) : (
                  <View style={[styles.listGroup, { gap: 0 }]}>
                    {appointmentRecordItems.map((item, itemIndex) => (
                      <View
                        key={`appointment-timeline-${item.id}`}
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "stretch",
                            borderBottomColor:
                              itemIndex === appointmentRecordItems.length - 1
                                ? "transparent"
                                : theme.borderSubtle,
                            borderBottomWidth:
                              itemIndex === appointmentRecordItems.length - 1
                                ? 0
                                : 1,
                            gap: tokens.spacing.sm,
                            paddingVertical: tokens.spacing.sm,
                          },
                        ]}
                      >
                        <View
                          style={[
                            styles.detailHeaderAvatar,
                            {
                              backgroundColor: theme.backgroundSubtle,
                              borderColor: theme.borderSubtle,
                              borderRadius: 8,
                              height: 32,
                              marginTop: 2,
                              width: 32,
                            },
                          ]}
                        >
                          <LineIcon
                            color={theme.accentStrong}
                            name="appointment"
                            size={17}
                            strokeWidth={2.3}
                          />
                        </View>
                        <View style={[styles.listGroup, { flex: 1, gap: 3 }]}>
                          <Text
                            style={[
                              styles.navigationText,
                              {
                                color: theme.textPrimary,
                                fontFamily: layout.displayFontFamily,
                                fontSize: 15,
                                fontWeight: "700",
                                lineHeight: 20,
                              },
                            ]}
                          >
                            {item.titleText}
                          </Text>
                          <Text
                            numberOfLines={2}
                            style={[
                              styles.sectionSubtitle,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 14,
                                lineHeight: 20,
                              },
                            ]}
                          >
                            {item.statusText}
                          </Text>
                        </View>
                        <Text
                          numberOfLines={2}
                          style={[
                            styles.metricLabel,
                            {
                              color: theme.textTertiary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 13,
                              lineHeight: 17,
                              textAlign: "right",
                              width: 116,
                            },
                          ]}
                        >
                          {[item.dateLabel, item.timeLabel]
                            .filter((part) => part.trim().length > 0)
                            .join("\n")}
                        </Text>
                      </View>
                    ))}
                  </View>
                ),
            })}
          </View>

          <View
            style={[
              styles.listGroup,
              {
                flex: isWideLayout ? 1 : 0,
                gap: 10,
                width: "100%",
              },
            ]}
          >
            {appointmentSideCards.map((card) => (
              <View
                key={card.id}
                style={[
                  styles.detailBodyCard,
                  {
                    backgroundColor: theme.backgroundSubtle,
                    borderColor: theme.borderSubtle,
                    borderRadius: 8,
                    gap: 6,
                    minHeight: 78,
                    padding: 12,
                  },
                ]}
              >
                <View
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "center",
                      gap: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.detailHeaderAvatar,
                      {
                        backgroundColor: theme.surface,
                        borderColor: theme.borderSubtle,
                        borderRadius: 8,
                        height: 28,
                        width: 28,
                      },
                    ]}
                  >
                    <LineIcon
                      color={theme.accentStrong}
                      name={card.iconName}
                      size={15}
                      strokeWidth={2.35}
                    />
                  </View>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 11,
                        fontWeight: "700",
                        lineHeight: 14,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    {card.labelText}
                  </Text>
                </View>
                <Text
                  numberOfLines={2}
                  style={[
                    styles.sectionTitle,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.displayFontFamily,
                      fontSize: card.valueText.length > 18 ? 14 : 16,
                      lineHeight: card.valueText.length > 18 ? 18 : 20,
                    },
                  ]}
                >
                  {card.valueText}
                </Text>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      lineHeight: 15,
                    },
                  ]}
                >
                  {card.detailText}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
    ) : (
      renderInlineClinicalEditorBody("Appointments")
    );

    const renderProceduresBody = () =>
      inlineClinicalEditor == null ? (
      <ScrollView
        style={[
          styles.patientSupportModalScroll,
          {
            flex: 1,
          },
        ]}
        contentContainerStyle={[
          styles.patientSupportModalContent,
          {
            gap: tokens.spacing.lg,
            paddingHorizontal: isWideLayout
              ? tokens.spacing.lg
              : tokens.spacing.md,
            paddingVertical: tokens.spacing.md,
          },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "flex-start",
              flexDirection: isWideLayout ? "row" : "column",
              gap: 18,
            },
          ]}
        >
          <View
            style={[
              styles.listGroup,
              {
                flex: isWideLayout ? 1.55 : 0,
                gap: 14,
                width: "100%",
              },
            ]}
          >
            {renderCaseSummarySectionCard({
              titleText: "Procedure snapshot",
              children: (
                <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
                  {procedureSnapshotRows.map((rowText) => (
                    <View
                      key={rowText}
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "flex-start",
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <View
                        style={[
                          styles.detailHeaderAvatar,
                          {
                            backgroundColor: theme.accentSubtle,
                            borderColor: "transparent",
                            borderRadius: 7,
                            height: 18,
                            marginTop: 2,
                            width: 18,
                          },
                        ]}
                      >
                        <LineIcon
                          color={theme.accentStrong}
                          name="check"
                          size={12}
                          strokeWidth={2.5}
                        />
                      </View>
                      <Text
                        style={[
                          styles.sectionSubtitle,
                          {
                            color: theme.textPrimary,
                            flex: 1,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 14,
                            lineHeight: 20,
                          },
                        ]}
                      >
                        {rowText}
                      </Text>
                    </View>
                  ))}
                </View>
              ),
            })}

            {renderCaseSummarySectionCard({
              titleText: "Upcoming procedure",
              children:
                nextProcedureItem == null ? (
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 15,
                        lineHeight: 21,
                      },
                    ]}
                  >
                    No upcoming procedure is scheduled yet.
                  </Text>
                ) : (
                  <View
                    style={[
                      styles.listGroup,
                      {
                        gap: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "center",
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.navigationText,
                          {
                            color: theme.textPrimary,
                            flex: 1,
                            fontFamily: layout.displayFontFamily,
                            fontSize: 17,
                            fontWeight: "700",
                            lineHeight: 21,
                            minWidth: 0,
                          },
                        ]}
                      >
                        {procedureTitleText}
                      </Text>
                      {nextProcedureActionConfigs.map((actionConfig) => {
                        const resolvedActionConfig =
                          actionConfig.label === "Edit"
                            ? {
                                ...actionConfig,
                                onPress: () =>
                                  openInlineRecordEditor({
                                    defaultDetailText:
                                      nextProcedureItem.bodyLines.join("\n"),
                                    defaultTitle: nextProcedureItem.titleText,
                                    recordId: nextProcedureItem.id,
                                    targetKind: "procedure",
                                  }),
                              }
                            : actionConfig.label === "Add note"
                              ? {
                                  ...actionConfig,
                                  onPress: () =>
                                    openInlineNoteEditor({
                                      defaultBodyText: "",
                                      defaultTitle: `${nextProcedureItem.titleText} note`,
                                      targetId: nextProcedureItem.id,
                                      targetKind: "procedure",
                                    }),
                                }
                              : actionConfig;
                        const actionColor = resolveClinicianModalActionColor(
                          resolvedActionConfig.tone,
                        );
                        const actionIconName = resolveModalActionIconName(
                          resolvedActionConfig.label,
                          resolveClinicianModalActionIcon(
                            resolvedActionConfig.tone,
                          ),
                        );

                        return (
                          <Pressable
                            accessibilityLabel={resolvedActionConfig.label}
                            accessibilityRole="button"
                            accessibilityState={{
                              disabled: resolvedActionConfig.disabled,
                            }}
                            aria-disabled={resolvedActionConfig.disabled}
                            disabled={resolvedActionConfig.disabled}
                            key={`procedure-${resolvedActionConfig.label}`}
                            onPress={resolvedActionConfig.onPress}
                            style={({ pressed }) => [
                              styles.detailHeaderAvatar,
                              {
                                backgroundColor: pressed
                                  ? theme.backgroundSubtle
                                  : theme.surface,
                                borderColor: resolvedActionConfig.disabled
                                  ? theme.borderSubtle
                                  : actionColor,
                                borderRadius: 9,
                                height: isWideLayout
                                  ? 32
                                  : clinicianModalControlHeight,
                                opacity: resolvedActionConfig.disabled ? 0.5 : 1,
                                width: isWideLayout
                                  ? 32
                                  : clinicianModalControlHeight,
                              },
                            ]}
                          >
                            <LineIcon
                              color={
                                resolvedActionConfig.disabled
                                  ? theme.textSecondary
                                  : actionColor
                              }
                              name={actionIconName}
                              size={15}
                              strokeWidth={2.3}
                            />
                          </Pressable>
                        );
                      })}
                    </View>
                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "center",
                          gap: tokens.spacing.md,
                        },
                      ]}
                    >
                      <View
                        style={[
                          styles.detailHeaderAvatar,
                          {
                            backgroundColor: theme.backgroundSubtle,
                            borderColor: theme.borderSubtle,
                            borderRadius: 8,
                            height: 52,
                            width: 64,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.metricLabel,
                            {
                              color: theme.accentStrong,
                              fontFamily: layout.displayFontFamily,
                              fontSize: 11,
                              fontWeight: "800",
                              lineHeight: 14,
                              textAlign: "center",
                            },
                          ]}
                        >
                          {nextProcedureItem.dateLabel || "PROC"}
                        </Text>
                        <Text
                          style={[
                            styles.sectionSubtitle,
                            {
                              color: theme.textSecondary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 10,
                              lineHeight: 13,
                              textAlign: "center",
                            },
                          ]}
                        >
                          {nextProcedureItem.timeLabel}
                        </Text>
                      </View>
                      <View style={[styles.listGroup, { flex: 1, gap: 4 }]}>
                        <Text
                          style={[
                            styles.sectionTitle,
                            {
                              color: theme.textPrimary,
                              fontFamily: layout.displayFontFamily,
                              fontSize: 16,
                              lineHeight: 20,
                            },
                          ]}
                        >
                          {procedureDateTimeText}
                        </Text>
                        <View
                          style={[
                            styles.patientRowHeader,
                            {
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: tokens.spacing.sm,
                            },
                          ]}
                        >
                          <Text
                            numberOfLines={2}
                            style={[
                              styles.sectionSubtitle,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 14,
                                lineHeight: 20,
                              },
                            ]}
                          >
                            {[
                              procedureClinicianText,
                              formatClinicianProgramContextText(
                                procedureProgramText,
                              ),
                              appointmentDateTimeText !== "Not scheduled"
                                ? "Related visit scheduled"
                                : "Pre-op milestone",
                            ]
                              .filter((part) => part.trim().length > 0)
                              .join(" · ")}
                          </Text>
                          {renderOptionBStatusPill(nextProcedureItem.statusText)}
                        </View>
                      </View>
                    </View>
                    {renderClinicalRecordDetails(nextProcedureItem)}
                  </View>
                ),
            })}

            {renderCaseSummarySectionCard({
              titleText: "Procedure timeline",
              children:
                procedureRecordItems.length === 0 ? (
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 15,
                        lineHeight: 21,
                      },
                    ]}
                  >
                    No procedure timeline is available yet.
                  </Text>
                ) : (
                  <View style={[styles.listGroup, { gap: 0 }]}>
                    {procedureRecordItems.map((item, itemIndex) => (
                      <View
                        key={`procedure-timeline-${item.id}`}
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "stretch",
                            borderBottomColor:
                              itemIndex === procedureRecordItems.length - 1
                                ? "transparent"
                                : theme.borderSubtle,
                            borderBottomWidth:
                              itemIndex === procedureRecordItems.length - 1
                                ? 0
                                : 1,
                            gap: tokens.spacing.sm,
                            paddingVertical: tokens.spacing.sm,
                          },
                        ]}
                      >
                        <View
                          style={[
                            styles.detailHeaderAvatar,
                            {
                              backgroundColor: theme.backgroundSubtle,
                              borderColor: theme.borderSubtle,
                              borderRadius: 8,
                              height: 32,
                              marginTop: 2,
                              width: 32,
                            },
                          ]}
                        >
                          <LineIcon
                            color={theme.accentStrong}
                            name="procedure"
                            size={17}
                            strokeWidth={2.3}
                          />
                        </View>
                        <View style={[styles.listGroup, { flex: 1, gap: 3 }]}>
                          <Text
                            style={[
                              styles.navigationText,
                              {
                                color: theme.textPrimary,
                                fontFamily: layout.displayFontFamily,
                                fontSize: 15,
                                fontWeight: "700",
                                lineHeight: 20,
                              },
                            ]}
                          >
                            {item.titleText}
                          </Text>
                          <Text
                            numberOfLines={2}
                            style={[
                              styles.sectionSubtitle,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 14,
                                lineHeight: 20,
                              },
                            ]}
                          >
                            {item.statusText}
                          </Text>
                        </View>
                        <Text
                          numberOfLines={2}
                          style={[
                            styles.metricLabel,
                            {
                              color: theme.textTertiary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 13,
                              lineHeight: 17,
                              textAlign: "right",
                              width: 116,
                            },
                          ]}
                        >
                          {[item.dateLabel, item.timeLabel]
                            .filter((part) => part.trim().length > 0)
                            .join("\n")}
                        </Text>
                      </View>
                    ))}
                  </View>
                ),
            })}
          </View>

          <View
            style={[
              styles.listGroup,
              {
                flex: isWideLayout ? 1 : 0,
                gap: 10,
                width: "100%",
              },
            ]}
          >
            {procedureSideCards.map((card) => (
              <View
                key={card.id}
                style={[
                  styles.detailBodyCard,
                  {
                    backgroundColor: theme.backgroundSubtle,
                    borderColor: theme.borderSubtle,
                    borderRadius: 8,
                    gap: 6,
                    minHeight: 78,
                    padding: 12,
                  },
                ]}
              >
                <View
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "center",
                      gap: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.detailHeaderAvatar,
                      {
                        backgroundColor: theme.surface,
                        borderColor: theme.borderSubtle,
                        borderRadius: 8,
                        height: 28,
                        width: 28,
                      },
                    ]}
                  >
                    <LineIcon
                      color={theme.accentStrong}
                      name={card.iconName}
                      size={15}
                      strokeWidth={2.35}
                    />
                  </View>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 11,
                        fontWeight: "700",
                        lineHeight: 14,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    {card.labelText}
                  </Text>
                </View>
                <Text
                  numberOfLines={2}
                  style={[
                    styles.sectionTitle,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.displayFontFamily,
                      fontSize: card.valueText.length > 18 ? 14 : 16,
                      lineHeight: card.valueText.length > 18 ? 18 : 20,
                    },
                  ]}
                >
                  {card.valueText}
                </Text>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      lineHeight: 15,
                    },
                  ]}
                >
                  {card.detailText}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
    ) : (
      renderInlineClinicalEditorBody("Procedures")
    );

    const renderDocumentsBody = () => {
      const renderDocumentRecordCard = (
        item: OptionBTimelineItem | null,
        emptyText: string,
      ) => {
        if (item == null) {
          return (
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 15,
                  lineHeight: 21,
                },
              ]}
            >
              {emptyText}
            </Text>
          );
        }

        const itemActionConfigs = [
          ...(item.actionConfig == null ? [] : [item.actionConfig]),
          ...(item.actionConfigs ?? []),
        ];
        const createdByText = getDocumentMetricValue(
          item,
          /^created by$/iu,
        );
        const createdAtText = getDocumentMetricValue(
          item,
          /^created$/iu,
        );
        const metaText = [createdByText, createdAtText]
          .filter((part) => part.trim().length > 0)
          .join(" · ");

        return (
          <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
            <View
              style={[
                styles.patientRowHeader,
                {
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: tokens.spacing.sm,
                },
              ]}
            >
              <View
                style={[
                  styles.inlinePill,
                  {
                    alignItems: "center",
                    backgroundColor: theme.accentSubtle,
                    borderColor: "transparent",
                    borderRadius: 7,
                    justifyContent: "center",
                    minHeight: 24,
                    minWidth: 38,
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.badgeText,
                    {
                      color: theme.accentStrong,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 10,
                      fontWeight: "800",
                      letterSpacing: 0,
                      lineHeight: 13,
                    },
                  ]}
                >
                  {item.badgeText}
                </Text>
              </View>
              <View
                style={[
                  styles.listGroup,
                  {
                    flex: 1,
                    gap: 3,
                    minWidth: isWideLayout ? 0 : 160,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.displayFontFamily,
                      fontSize: 17,
                      fontWeight: "700",
                      lineHeight: 21,
                    },
                  ]}
                >
                  {item.titleText}
                </Text>
                <Text
                  numberOfLines={2}
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 13,
                      fontWeight: "700",
                      lineHeight: 18,
                    },
                  ]}
                >
                  {item.bodyLines[0] ?? metaText}
                  {metaText.length > 0 ? ` · ${metaText}` : ""}
                </Text>
              </View>
              {renderOptionBStatusPill(item.statusText)}
              <View
                style={[
                  styles.patientRowHeader,
                  {
                    alignItems: "center",
                    flexShrink: 0,
                    gap: 8,
                    justifyContent: isWideLayout
                      ? "flex-start"
                      : "flex-end",
                    width: isWideLayout ? undefined : "100%",
                  },
                ]}
              >
                {itemActionConfigs.map((actionConfig) => {
                  const resolvedActionConfig =
                    actionConfig.label === "Edit"
                      ? {
                          ...actionConfig,
                          onPress: () =>
                            openInlineRecordEditor({
                              defaultDetailText: item.bodyLines.join("\n"),
                              defaultTitle: item.titleText,
                              recordId: item.id,
                              targetKind: "document",
                            }),
                        }
                      : actionConfig.label === "Add note"
                        ? {
                            ...actionConfig,
                            onPress: () =>
                              openInlineNoteEditor({
                                defaultBodyText: "",
                                defaultTitle: `${item.titleText} note`,
                                targetId: item.id,
                                targetKind: "document",
                              }),
                          }
                        : actionConfig;
                  const actionColor = resolveClinicianModalActionColor(
                    resolvedActionConfig.tone,
                  );
                  const actionIconName = resolveModalActionIconName(
                    resolvedActionConfig.label,
                    resolveClinicianModalActionIcon(resolvedActionConfig.tone),
                  );

                  return (
                    <Pressable
                      accessibilityLabel={resolvedActionConfig.label}
                      accessibilityRole="button"
                      accessibilityState={{
                        disabled: resolvedActionConfig.disabled,
                      }}
                      aria-disabled={resolvedActionConfig.disabled}
                      disabled={resolvedActionConfig.disabled}
                      key={`document-${item.id}-${resolvedActionConfig.label}`}
                      onPress={resolvedActionConfig.onPress}
                      style={({ pressed }) => [
                        styles.detailHeaderAvatar,
                        {
                          backgroundColor: pressed
                            ? theme.backgroundSubtle
                            : theme.surface,
                          borderColor: resolvedActionConfig.disabled
                            ? theme.borderSubtle
                            : actionColor,
                          borderRadius: 9,
                          height: isWideLayout
                            ? 32
                            : clinicianModalControlHeight,
                          opacity: resolvedActionConfig.disabled ? 0.5 : 1,
                          width: isWideLayout
                            ? 32
                            : clinicianModalControlHeight,
                        },
                      ]}
                    >
                      <LineIcon
                        color={
                          resolvedActionConfig.disabled
                            ? theme.textSecondary
                            : actionColor
                        }
                        name={actionIconName}
                        size={15}
                        strokeWidth={2.3}
                      />
                    </Pressable>
                  );
                })}
              </View>
            </View>
          </View>
        );
      };

      return inlineClinicalEditor == null ? (
        <ScrollView
          style={[
            styles.patientSupportModalScroll,
            {
              flex: 1,
            },
          ]}
          contentContainerStyle={[
            styles.patientSupportModalContent,
            {
              gap: tokens.spacing.lg,
              paddingHorizontal: isWideLayout
                ? tokens.spacing.lg
                : tokens.spacing.md,
              paddingVertical: tokens.spacing.md,
            },
          ]}
          showsVerticalScrollIndicator={false}
        >
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: "flex-start",
                flexDirection: isWideLayout ? "row" : "column",
                gap: 18,
              },
            ]}
          >
            <View
              style={[
                styles.listGroup,
                {
                  flex: isWideLayout ? 1.8 : 0,
                  gap: 14,
                  width: "100%",
                },
              ]}
            >
              {renderCaseSummarySectionCard({
                titleText: "Document snapshot",
                children: (
                  <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
                    {documentSnapshotRows.map((rowText) => (
                      <View
                        key={rowText}
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "flex-start",
                            gap: tokens.spacing.sm,
                          },
                        ]}
                      >
                        <View
                          style={[
                            styles.detailHeaderAvatar,
                            {
                              backgroundColor: theme.accentSubtle,
                              borderColor: "transparent",
                              borderRadius: 7,
                              height: 18,
                              marginTop: 2,
                              width: 18,
                            },
                          ]}
                        >
                          <LineIcon
                            color={theme.accentStrong}
                            name="check"
                            size={12}
                            strokeWidth={2.5}
                          />
                        </View>
                        <Text
                          style={[
                            styles.sectionSubtitle,
                            {
                              color: theme.textPrimary,
                              flex: 1,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 14,
                              lineHeight: 20,
                            },
                          ]}
                        >
                          {rowText}
                        </Text>
                      </View>
                    ))}
                  </View>
                ),
              })}

              {renderCaseSummarySectionCard({
                titleText: "Generated chart pack",
                children: renderDocumentRecordCard(
                  chartPackTimelineItem,
                  "The generated chart pack is not available yet.",
                ),
              })}

              {renderCaseSummarySectionCard({
                titleText: "Available for review",
                children: renderDocumentRecordCard(
                  primaryReadyDocumentItem,
                  "No document is currently available for review.",
                ),
              })}

              {renderCaseSummarySectionCard({
                titleText: "Review queue",
                children: renderDocumentRecordCard(
                  primaryReviewDocumentItem,
                  "No documents are waiting for clinician review.",
                ),
              })}
            </View>

            <View
              style={[
                styles.listGroup,
                {
                  flex: isWideLayout ? 0.95 : 0,
                  gap: 10,
                  width: "100%",
                },
              ]}
            >
              {documentSideCards.map((card) => (
                <View
                  key={card.id}
                  style={[
                    styles.detailBodyCard,
                    {
                      backgroundColor: theme.backgroundSubtle,
                      borderColor: theme.borderSubtle,
                      borderRadius: 8,
                      gap: 6,
                      minHeight: 78,
                      padding: 12,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.patientRowHeader,
                      {
                        alignItems: "center",
                        gap: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.detailHeaderAvatar,
                        {
                          backgroundColor: theme.surface,
                          borderColor: theme.borderSubtle,
                          borderRadius: 8,
                          height: 28,
                          width: 28,
                        },
                      ]}
                    >
                      <LineIcon
                        color={theme.accentStrong}
                        name={card.iconName}
                        size={15}
                        strokeWidth={2.35}
                      />
                    </View>
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          flex: 1,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          fontWeight: "700",
                          lineHeight: 14,
                          textTransform: "uppercase",
                        },
                      ]}
                    >
                      {card.labelText}
                    </Text>
                  </View>
                  <Text
                    numberOfLines={2}
                    style={[
                      styles.sectionTitle,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.displayFontFamily,
                        fontSize: card.valueText.length > 18 ? 14 : 16,
                        lineHeight: card.valueText.length > 18 ? 18 : 20,
                      },
                    ]}
                  >
                    {card.valueText}
                  </Text>
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        lineHeight: 15,
                      },
                    ]}
                  >
                    {card.detailText}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </ScrollView>
      ) : (
        renderInlineClinicalEditorBody("Documents")
      );
    };

    const renderOptionBStatusPill = (statusText: string) => {
      const normalizedStatus = statusText.toLowerCase();
      const isArchived = /archived|superseded/iu.test(normalizedStatus);
      const isPositive = /completed|ready|recorded|active|approved/iu.test(
        normalizedStatus,
      );
      const isWarning = /handoff|pending|scheduled|needs|review/iu.test(
        normalizedStatus,
      );
      const chipPalette = isArchived
        ? {
            backgroundColor: theme.backgroundSubtle,
            borderColor: theme.borderStrong,
            textColor: theme.textSecondary,
          }
        : isPositive
          ? {
              backgroundColor: theme.statusChip.success.background,
              borderColor: theme.statusChip.success.border,
              textColor: theme.statusChip.success.text,
            }
          : isWarning
            ? {
                backgroundColor: theme.statusChip.warning.background,
                borderColor: theme.statusChip.warning.border,
                textColor: theme.statusChip.warning.text,
              }
            : {
                backgroundColor: theme.accentSubtle,
                borderColor: theme.accentSubtle,
                textColor: theme.accentStrong,
              };

      return (
        <View
          style={[
            styles.inlinePill,
            {
              alignItems: "center",
              alignSelf: "flex-start",
              backgroundColor: chipPalette.backgroundColor,
              borderColor: chipPalette.borderColor,
              borderRadius: 999,
              minHeight: 20,
              paddingHorizontal: tokens.spacing.sm,
              paddingVertical: 2,
            },
          ]}
        >
          <Text
            numberOfLines={1}
            style={[
              styles.pillValueText,
              {
                color: chipPalette.textColor,
                fontFamily: layout.bodyFontFamily,
                fontSize: 10,
                fontWeight: "700",
                lineHeight: 12,
              },
            ]}
          >
            {statusText}
          </Text>
        </View>
      );
    };

    const renderOptionBTimelineItem = (
      item: OptionBTimelineItem,
      isLastInGroup: boolean,
    ) => {
      const useExpandable = selectedDetailSection.id === "medications";
      const visibleMetrics = item.metrics.filter(
        (metric) => metric.valueText.trim().length > 0,
      );
      const itemActionConfigs = [
        ...(item.actionConfig == null ? [] : [item.actionConfig]),
        ...(item.actionConfigs ?? []),
      ];
      const itemIsCompleted =
        /completed|performed|recorded|ready/iu.test(item.statusText) ||
        /completed|download ready|available for review/iu.test(item.groupLabel);
      const hasTimelineDate =
        item.dateLabel.trim().length > 0 || item.timeLabel.trim().length > 0;
      const metaSummaryText = visibleMetrics
        .filter(
          (metric) =>
            metric.valueText.trim().toLowerCase() !==
            item.statusText.trim().toLowerCase(),
        )
        .map((metric) => metric.valueText)
        .join("  ·  ");

      const renderBadge = () => (
        <View
          style={[
            styles.inlinePill,
            {
              alignItems: "center",
              backgroundColor: theme.accentSubtle,
              borderColor: "transparent",
              borderRadius: 6,
              justifyContent: "center",
              minHeight: 20,
              minWidth: 38,
              paddingHorizontal: 7,
              paddingVertical: 2,
            },
          ]}
        >
          <Text
            numberOfLines={1}
            style={[
              styles.badgeText,
              {
                color: theme.accentStrong,
                fontFamily: layout.bodyFontFamily,
                fontSize: 9,
                fontWeight: "800",
                letterSpacing: 0.4,
                lineHeight: 12,
              },
            ]}
          >
            {item.badgeText}
          </Text>
        </View>
      );

      const renderMetaSummary = (includeStatus: boolean) =>
        metaSummaryText.length === 0 && !includeStatus ? null : (
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: "center",
                flexWrap: "wrap",
                gap: 8,
                marginTop: 1,
              },
            ]}
          >
            {metaSummaryText.length > 0 ? (
              <Text
                numberOfLines={1}
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textTertiary,
                    flexShrink: 1,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    lineHeight: 13,
                    minWidth: 0,
                  },
                ]}
              >
                {metaSummaryText}
              </Text>
            ) : null}
            {includeStatus ? renderOptionBStatusPill(item.statusText) : null}
          </View>
        );

      const renderIconActions = () =>
        itemActionConfigs.length === 0 ? null : (
          <View
            style={[
              styles.patientRowHeader,
              { alignItems: "center", flexShrink: 0, gap: 6 },
            ]}
          >
            {itemActionConfigs.map((actionConfig) => {
              const actionColor = resolveClinicianModalActionColor(
                actionConfig.tone,
              );
              const actionIconName = resolveModalActionIconName(
                actionConfig.label,
                resolveClinicianModalActionIcon(actionConfig.tone),
              );

              return (
                <Pressable
                  accessibilityLabel={actionConfig.label}
                  accessibilityRole="button"
                  accessibilityState={{ disabled: actionConfig.disabled }}
                  aria-disabled={actionConfig.disabled}
                  disabled={actionConfig.disabled}
                  key={`${item.id}-${actionConfig.label}`}
                  onPress={actionConfig.onPress}
                  style={({ pressed }) => [
                    styles.detailHeaderAvatar,
                    {
                      backgroundColor: pressed
                        ? theme.backgroundSubtle
                        : theme.surface,
                      borderColor: actionConfig.disabled
                        ? theme.borderSubtle
                        : actionColor,
                      borderRadius: 8,
                      height: isWideLayout
                        ? 30
                        : clinicianModalControlHeight,
                      opacity: actionConfig.disabled ? 0.5 : 1,
                      width: isWideLayout
                        ? 30
                        : clinicianModalControlHeight,
                    },
                  ]}
                >
                  <LineIcon
                    color={
                      actionConfig.disabled ? theme.textSecondary : actionColor
                    }
                    name={actionIconName}
                    size={14}
                    strokeWidth={2.3}
                  />
                </Pressable>
              );
            })}
          </View>
        );

      const renderFullActions = () =>
        itemActionConfigs.length === 0 ? null : (
          <View
            style={[
              styles.patientRowHeader,
              { flexWrap: "wrap", gap: 8, marginTop: tokens.spacing.xs },
            ]}
          >
            {itemActionConfigs.map((actionConfig) => (
              <View key={`${item.id}-${actionConfig.label}`}>
                {renderClinicianModalActionButton({
                  disabled: actionConfig.disabled,
                  label: actionConfig.label,
                  minWidth: actionConfig.label.length > 10 ? 122 : 92,
                  onPress: actionConfig.onPress,
                  tone: actionConfig.tone,
                })}
              </View>
            ))}
          </View>
        );

      const renderBodyLines = (maxLines: number) =>
        item.bodyLines.map((lineText) => (
          <Text
            key={lineText}
            numberOfLines={maxLines}
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                lineHeight: 16,
              },
            ]}
          >
            {lineText}
          </Text>
        ));

      // ===== Option B — expandable card (medications) =====
      if (useExpandable) {
        return (
          <ClinicianExpandableModalRow
            key={item.id}
            borderColor={theme.borderSubtle}
            surfaceColor={theme.surface}
            renderHeader={(open) => (
              <View
                style={[
                  styles.patientRowHeader,
                  {
                    alignItems: "center",
                    gap: tokens.spacing.sm,
                    paddingHorizontal: tokens.spacing.sm,
                    paddingVertical: 10,
                  },
                ]}
              >
                {renderBadge()}
                <Text
                  numberOfLines={isWideLayout ? 1 : 2}
                  style={[
                    styles.navigationText,
                    {
                      color: theme.textPrimary,
                      flex: 1,
                      fontFamily: layout.displayFontFamily,
                      fontSize: 13,
                      fontWeight: "700",
                      lineHeight: 16,
                      minWidth: 0,
                    },
                  ]}
                >
                  {item.titleText}
                </Text>
                {renderOptionBStatusPill(item.statusText)}
                {hasTimelineDate ? (
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textTertiary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        lineHeight: 12,
                      },
                    ]}
                  >
                    {`${item.dateLabel}${
                      item.timeLabel ? `, ${item.timeLabel}` : ""
                    }`}
                  </Text>
                ) : null}
                <View
                  style={{
                    transform: [{ rotate: open ? "90deg" : "0deg" }],
                  }}
                >
                  <LineIcon
                    color={open ? theme.accentStrong : theme.textTertiary}
                    name="chevron-right"
                    size={14}
                    strokeWidth={2.4}
                  />
                </View>
              </View>
            )}
          >
            <View
              style={{
                borderTopColor: theme.borderSubtle,
                borderTopWidth: 1,
                gap: tokens.spacing.xs,
                paddingHorizontal: tokens.spacing.sm,
                paddingVertical: tokens.spacing.sm,
              }}
            >
              {renderClinicalRecordDetails(item)}
              {renderMetaSummary(false)}
              {renderFullActions()}
            </View>
          </ClinicianExpandableModalRow>
        );
      }

      // ===== Option A — scannable timeline (notes / documents / procedures) =====
      const timelineRailWidth = hasTimelineDate ? 66 : 22;
      const timelineRailCenterX = hasTimelineDate ? 33 : 11;

      return (
        <View
          key={item.id}
          style={[
            styles.patientRowHeader,
            {
              alignItems: "stretch",
              borderBottomColor: isLastInGroup
                ? "transparent"
                : theme.borderSubtle,
              borderBottomWidth: isLastInGroup ? 0 : 1,
              gap: tokens.spacing.sm,
              paddingBottom: tokens.spacing.sm,
              paddingTop: tokens.spacing.sm,
            },
          ]}
        >
          <View
            style={[
              styles.listGroup,
              {
                alignItems: "center",
                flexShrink: 0,
                gap: 2,
                paddingTop: 2,
                position: "relative",
                width: timelineRailWidth,
              },
            ]}
          >
            <View
              style={{
                backgroundColor: theme.borderSubtle,
                bottom: isLastInGroup ? 32 : -tokens.spacing.sm,
                left: timelineRailCenterX,
                position: "absolute",
                top: 18,
                width: 1,
              }}
            />
            <View
              style={[
                styles.detailHeaderAvatar,
                {
                  backgroundColor: theme.surface,
                  borderColor: itemIsCompleted
                    ? theme.success
                    : theme.borderStrong,
                  borderRadius: 8,
                  height: 16,
                  width: 16,
                },
              ]}
            >
              <Text
                style={[
                  styles.badgeText,
                  {
                    color: itemIsCompleted ? theme.success : "transparent",
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    lineHeight: 12,
                  },
                ]}
              >
                {itemIsCompleted ? "✓" : ""}
              </Text>
            </View>
            {hasTimelineDate ? (
              <>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.displayFontFamily,
                      fontSize: 10,
                      fontWeight: "700",
                      lineHeight: 13,
                      textAlign: "center",
                    },
                  ]}
                >
                  {item.dateLabel}
                </Text>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textTertiary,
                      fontFamily: layout.bodyFontFamily,
                          fontSize:
                            workforceOperationalTypography.metadata.fontSize,
                          lineHeight:
                            workforceOperationalTypography.metadata.lineHeight,
                      textAlign: "center",
                    },
                  ]}
                >
                  {item.timeLabel}
                </Text>
              </>
            ) : null}
          </View>

          <View style={[styles.listGroup, { flex: 1, gap: 3, minWidth: 0 }]}>
            <View
              style={[
                styles.patientRowHeader,
                { alignItems: "center", gap: tokens.spacing.xs },
              ]}
            >
              {renderBadge()}
              <Text
                numberOfLines={2}
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    flex: 1,
                    fontFamily: layout.displayFontFamily,
                    fontSize: 13,
                    fontWeight: "700",
                    lineHeight: 16,
                    minWidth: 0,
                  },
                ]}
              >
                {item.titleText}
              </Text>
              {renderIconActions()}
            </View>
            {renderBodyLines(2)}
            {renderMetaSummary(true)}
          </View>
        </View>
      );
    };

    return (
      <Modal
        accessibilityLabel={`${selectedPatient.displayName} ${selectedDetailSection.titleText}`}
        animationType="fade"
        accessibilityViewIsModal={true}
        onRequestClose={closeModal}
        transparent={true}
        visible={true}
      >
        <View style={styles.patientSupportModalOverlay}>
          <View
            pointerEvents="none"
            style={styles.patientSupportModalBackdrop}
          />
          <View
            style={[
              styles.clinicianDetailModalPanel,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: isWideLayout
                  ? workforceOperationalLayout.panelRadius
                  : workforceOperationalLayout.controlRadius,
                height: isWideLayout ? "90%" : "94%",
                maxWidth: workforceOperationalLayout.detailDrawerWidth,
                minHeight: 0,
                shadowColor: theme.textPrimary,
                shadowOffset: { height: 22, width: 0 },
                shadowOpacity: 0.24,
                shadowRadius: 28,
                width: "100%",
              },
            ]}
          >
            <View
              style={[
                styles.clinicianDetailModalSplitLayout,
                {
                  flex: 1,
                },
              ]}
            >
              {isWideLayout ? (
                <View
                  style={[
                    styles.clinicianDetailModalRail,
                    {
                      backgroundColor: theme.backgroundSubtle,
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                      borderColor: clinicianModalChrome.borderColor,
                      borderTopLeftRadius:
                        workforceOperationalLayout.panelRadius,
                      borderTopRightRadius: 0,
                      gap: tokens.spacing.sm,
                      padding: tokens.spacing.md,
                      width: workforceOperationalLayout.queueWidth,
                    },
                  ]}
                >
                <View
                  style={[
                    styles.clinicianDetailModalRailHeader,
                    {
                      gap: tokens.spacing.xs,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.patientRowHeader,
                      {
                        alignItems: "center",
                        gap: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.detailHeaderAvatar,
                        {
                          backgroundColor: theme.surface,
                          borderColor: theme.borderSubtle,
                          borderRadius: 21,
                          height: 42,
                          width: 42,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.detailHeaderAvatarText,
                          {
                            color: clinicianModalChrome.leadTextColor,
                            fontFamily: layout.displayFontFamily,
                            fontSize: 15,
                          },
                        ]}
                      >
                        {resolvePatientInitials(selectedPatient.displayName)}
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.listGroup,
                        {
                          flex: 1,
                          gap: 2,
                          minWidth: 0,
                        },
                      ]}
                    >
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.navigationText,
                          {
                            color: theme.textPrimary,
                            fontFamily: layout.displayFontFamily,
                            fontSize: 13,
                            lineHeight: 16,
                          },
                        ]}
                      >
                        {selectedPatient.displayName}
                      </Text>
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.sectionSubtitle,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 11,
                            lineHeight: 14,
                          },
                        ]}
                      >
                        {selectedPatient.listMetaText}
                      </Text>
                    </View>
                  </View>

                  <View
                    style={[
                      styles.clinicianDetailModalMetaRow,
                      {
                        gap: tokens.spacing.xs,
                      },
                    ]}
                  >
                    {sectionStateChips.map((chip) => (
                      <View
                        key={chip.id}
                        style={[
                          styles.inlinePill,
                          {
                            alignItems: "center",
                            backgroundColor: theme.surface,
                            borderColor: theme.borderSubtle,
                            borderRadius: 10,
                            justifyContent: "center",
                            minHeight: 22,
                            paddingHorizontal: tokens.spacing.sm,
                            paddingVertical: 2,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.pillValueText,
                            {
                              color: clinicianModalChrome.metaPillTextColor,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 10,
                              lineHeight: 12,
                            },
                          ]}
                        >
                          {chip.labelText}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>

                {railCaseContextRows.length > 0 ? (
                  <View
                    style={[
                      styles.detailBodyCard,
                      {
                        backgroundColor: theme.surface,
                        borderColor: theme.borderSubtle,
                        borderRadius: 8,
                        gap: 10,
                        padding: 12,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: clinicianModalChrome.leadTextColor,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          fontWeight: "800",
                          lineHeight: 14,
                          textTransform: "uppercase",
                        },
                      ]}
                    >
                      Case context
                    </Text>
                    {railCaseContextRows.map((row) => (
                      <View
                        key={row.id}
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "center",
                            gap: 10,
                            justifyContent: "flex-start",
                          },
                        ]}
                      >
                        <View
                          style={[
                            styles.detailHeaderAvatar,
                            {
                              backgroundColor: theme.backgroundSubtle,
                              borderColor: theme.borderSubtle,
                              borderRadius: 8,
                              height: 30,
                              width: 30,
                            },
                          ]}
                        >
                          <LineIcon
                            color={theme.accentStrong}
                            name={row.iconName}
                            size={16}
                            strokeWidth={2.35}
                          />
                        </View>
                        <View
                          style={[
                            styles.listGroup,
                            {
                              flex: 1,
                              gap: 1,
                              minWidth: 0,
                            },
                          ]}
                        >
                          <Text
                            numberOfLines={1}
                            style={[
                              styles.metricLabel,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 11,
                                fontWeight: "700",
                                lineHeight: 14,
                              },
                            ]}
                          >
                            {row.labelText}
                          </Text>
                          <Text
                            numberOfLines={2}
                            style={[
                              styles.navigationText,
                              {
                                color: theme.textPrimary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 14,
                                lineHeight: 18,
                              },
                            ]}
                          >
                            {row.valueText}
                          </Text>
                        </View>
                      </View>
                    ))}
                  </View>
                ) : null}

                {railNextVisitText.trim().length > 0 ? (
                  <View
                    style={[
                      styles.detailBodyCard,
                      {
                        backgroundColor: theme.surface,
                        borderColor: theme.borderSubtle,
                        borderRadius: 8,
                        gap: 8,
                        padding: 12,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "center",
                          gap: 10,
                          justifyContent: "flex-start",
                        },
                      ]}
                    >
                      <View
                        style={[
                          styles.detailHeaderAvatar,
                          {
                            backgroundColor: theme.backgroundSubtle,
                            borderColor: theme.borderSubtle,
                            borderRadius: 8,
                            height: 34,
                            width: 34,
                          },
                        ]}
                      >
                        <LineIcon
                          color={theme.accentStrong}
                          name="appointment"
                          size={18}
                          strokeWidth={2.35}
                        />
                      </View>
                      <View style={[styles.listGroup, { flex: 1, gap: 2 }]}>
                        <Text
                          style={[
                            styles.metricLabel,
                            {
                              color: clinicianModalChrome.leadTextColor,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 11,
                              fontWeight: "800",
                              lineHeight: 14,
                              textTransform: "uppercase",
                            },
                          ]}
                        >
                          Next visit
                        </Text>
                        <Text
                          numberOfLines={3}
                          style={[
                            styles.navigationText,
                            {
                              color: theme.textPrimary,
                              fontFamily: layout.displayFontFamily,
                              fontSize: 14,
                              fontWeight: "700",
                              lineHeight: 18,
                            },
                          ]}
                        >
                          {railNextVisitText}
                        </Text>
                      </View>
                    </View>
                    <View
                      style={[
                        styles.inlinePill,
                        {
                          alignSelf: "flex-start",
                          backgroundColor: theme.accentSubtle,
                          borderColor: "transparent",
                          borderRadius: 8,
                          paddingHorizontal: 8,
                          paddingVertical: 3,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.badgeText,
                          {
                            color: theme.accentStrong,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 10,
                            lineHeight: 13,
                          },
                        ]}
                      >
                        Upcoming
                      </Text>
                    </View>
                  </View>
                ) : null}

                {railGlanceGridRows.length > 0 ? (
                  <View
                    style={[
                      styles.detailBodyCard,
                      {
                        backgroundColor: theme.surface,
                        borderColor: theme.borderSubtle,
                        borderRadius: 8,
                        gap: 10,
                        padding: 12,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: clinicianModalChrome.leadTextColor,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          fontWeight: "800",
                          lineHeight: 14,
                          textTransform: "uppercase",
                        },
                      ]}
                    >
                      At a glance
                    </Text>
                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "stretch",
                          flexWrap: "wrap",
                          gap: 8,
                          justifyContent: "flex-start",
                        },
                      ]}
                    >
                    {railGlanceGridRows.map((row) => (
                      <View
                        key={row.id}
                        style={{
                          alignItems: "center",
                          backgroundColor: theme.surface,
                          borderColor: theme.borderStrong,
                          borderRadius: 10,
                          borderWidth: 1,
                          flexBasis: "47%",
                          flexGrow: 1,
                          gap: 2,
                          minHeight: 56,
                          justifyContent: "center",
                          paddingHorizontal: 8,
                          paddingVertical: 8,
                        }}
                      >
                        <Text
                          numberOfLines={row.valueText.length <= 6 ? 1 : 3}
                          style={[
                            styles.navigationText,
                            {
                              color: theme.accentStrong,
                              fontFamily: layout.displayFontFamily,
                              fontSize: row.valueText.length <= 6 ? 17 : 12,
                              fontWeight: "800",
                              lineHeight:
                                row.valueText.length <= 6 ? 21 : 15,
                              textAlign: "center",
                            },
                          ]}
                        >
                          {row.valueText}
                        </Text>
                        <Text
                          numberOfLines={2}
                          style={[
                            styles.metricLabel,
                            {
                              color: theme.textTertiary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 9,
                              fontWeight: "800",
                              letterSpacing: 0.4,
                              lineHeight: 11,
                              textAlign: "center",
                              textTransform: "uppercase",
                            },
                          ]}
                        >
                          {row.labelText}
                        </Text>
                      </View>
                    ))}
                    </View>
                  </View>
                ) : null}
                </View>
              ) : null}
              <View
                style={[
                  styles.clinicianDetailModalMain,
                  {
                    backgroundColor: theme.surface,
                  },
                ]}
              >
                <View
                  style={[
                    styles.clinicianDetailModalMainHeader,
                    {
                      alignItems: "flex-start",
                      backgroundColor: theme.surface,
                      borderBottomColor: theme.borderSubtle,
                      flexWrap: isWideLayout ? "nowrap" : "wrap",
                      gap: isSummaryStyleModal ? 10 : tokens.spacing.sm,
                      paddingHorizontal: isWideLayout
                        ? tokens.spacing.lg
                        : tokens.spacing.md,
                      paddingVertical: isSummaryStyleModal ? 12 : tokens.spacing.md,
                      position: "relative",
                    },
                  ]}
                >
                  <View
                    style={{
                      alignItems: "center",
                      backgroundColor: theme.accentSubtle,
                      borderRadius: workforceOperationalLayout.controlRadius,
                      height: 40,
                      justifyContent: "center",
                      width: 40,
                    }}
                  >
                    <LineIcon
                      color={theme.accentStrong}
                      name={detailSectionIconName[selectedDetailSection.id]}
                      size={19}
                    />
                  </View>
                  <View
                    style={[
                      styles.listGroup,
                      {
                        flex: 1,
                        gap: tokens.spacing.xs,
                        minWidth: 0,
                        paddingRight: isWideLayout
                          ? 0
                          : clinicianModalControlHeight + 8,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.accentStrong,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          fontWeight: "700",
                          letterSpacing: 0.4,
                          textTransform: "uppercase",
                        },
                      ]}
                    >
                      {selectedPatient.displayName}
                    </Text>
                    <Text
                      style={[
                        styles.sectionTitle,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.displayFontFamily,
                          fontSize: isSummaryStyleModal ? 20 : 18,
                          lineHeight: isSummaryStyleModal ? 24 : 22,
                        },
                      ]}
                    >
                      {selectedDetailSection.titleText}
                    </Text>
                    {!isWideLayout ? (
                      <Text
                        numberOfLines={2}
                        style={[
                          styles.sectionSubtitle,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize:
                              workforceOperationalTypography.metadata.fontSize,
                            lineHeight:
                              workforceOperationalTypography.metadata.lineHeight,
                          },
                        ]}
                      >
                        {modalPatientIdentityText}
                      </Text>
                    ) : null}
                    <Text
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: isSummaryStyleModal ? 14 : 13,
                          lineHeight: isSummaryStyleModal ? 18 : 17,
                        },
                      ]}
                    >
                      {sectionSummaryText}
                    </Text>
                  </View>
                  {headerActionConfigs.length === 0 ? null : (
                    <View
                      style={{
                        flexShrink: 0,
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: 8,
                        justifyContent: isWideLayout
                          ? "flex-end"
                          : "flex-start",
                        maxWidth: isWideLayout ? 292 : undefined,
                        width: isWideLayout ? undefined : "100%",
                      }}
                    >
                      {headerActionConfigs.map((actionConfig) => (
                        <View key={`header-${actionConfig.label}`}>
                          {renderClinicianModalActionButton({
                            disabled: actionConfig.disabled,
                            label: actionConfig.label,
                            minWidth: 112,
                            onPress: actionConfig.onPress,
                            tone: actionConfig.tone,
                          })}
                        </View>
                      ))}
                    </View>
                  )}
                  <Pressable
                    accessibilityLabel={`Close ${selectedDetailSection.titleText}`}
                    accessibilityRole="button"
                    onPress={closeModal}
                    style={[
                      styles.patientSupportModalCloseButton,
                      {
                        backgroundColor:
                          clinicianModalChrome.closeButtonBackgroundColor,
                        borderColor: clinicianModalChrome.closeButtonBorderColor,
                        borderRadius: workforceOperationalLayout.controlRadius,
                        height: clinicianModalControlHeight,
                        position: isWideLayout ? "relative" : "absolute",
                        right: isWideLayout ? undefined : tokens.spacing.md,
                        top: isWideLayout ? undefined : tokens.spacing.md,
                        width: clinicianModalControlHeight,
                      },
                    ]}
                  >
                    <LineIcon
                      color={clinicianModalChrome.closeButtonTextColor}
                      name="x"
                      size={18}
                    />
                  </Pressable>
                </View>

                {isCaseSummaryModal ? (
                  renderCaseSummaryBody()
                ) : isAppointmentsModal ? (
                  renderAppointmentsBody()
                ) : isProceduresModal ? (
                  renderProceduresBody()
                ) : isDocumentsModal ? (
                  renderDocumentsBody()
                ) : inlineClinicalEditor != null ? (
                  renderInlineClinicalEditorBody(selectedDetailSection.titleText)
                ) : (
                  <>
                    <View
                      accessibilityLabel={`${selectedDetailSection.titleText} views`}
                      role="tablist"
                      style={[
                        styles.patientRowHeader,
                        {
                          borderBottomColor: theme.borderSubtle,
                          borderBottomWidth: 1,
                          gap: isWideLayout
                            ? tokens.spacing.lg
                            : tokens.spacing.sm,
                          justifyContent: "flex-start",
                          paddingHorizontal: isWideLayout
                            ? tokens.spacing.lg
                            : tokens.spacing.md,
                        },
                      ]}
                    >
                      {modalTabs.map((tab, tabIndex) => {
                        const isSelected =
                          tab.id === selectedDetailModalViewId;

                        return (
                          <Pressable
                            key={tab.id}
                            accessibilityLabel={tab.labelText}
                            accessibilityRole="tab"
                            accessibilityState={{ selected: isSelected }}
                            aria-selected={isSelected}
                            onPress={() => setSelectedDetailModalViewId(tab.id)}
                            tabIndex={isSelected ? 0 : -1}
                            {...(Platform.OS === "web"
                              ? {
                                  onKeyDown: (event: unknown) =>
                                    handleHorizontalTabKeyDown(
                                      event as WebTabKeyDownEvent,
                                      tabIndex,
                                      modalTabs.length,
                                      (nextIndex) =>
                                        setSelectedDetailModalViewId(
                                          modalTabs[nextIndex].id,
                                        ),
                                    ),
                                }
                              : {})}
                            style={({ pressed }) => ({
                              backgroundColor: pressed
                                ? theme.backgroundSubtle
                                : "transparent",
                              gap: tokens.spacing.xs,
                              justifyContent: "flex-end",
                              minHeight: isWideLayout
                                ? undefined
                                : clinicianModalControlHeight,
                              paddingTop: tokens.spacing.sm,
                            })}
                          >
                            <View
                              style={{
                                alignItems: "center",
                                flexDirection: "row",
                                gap: 6,
                                paddingHorizontal: 2,
                              }}
                            >
                              <Text
                                numberOfLines={1}
                                style={[
                                  styles.navigationText,
                                  {
                                    color: isSelected
                                      ? theme.accentStrong
                                      : theme.textSecondary,
                                    fontFamily: layout.bodyFontFamily,
                                    fontSize: 12,
                                    fontWeight: isSelected ? "800" : "700",
                                    lineHeight: 15,
                                  },
                                ]}
                              >
                                {tab.labelText}
                              </Text>
                              <View
                                style={{
                                  backgroundColor: isSelected
                                    ? theme.accentSubtle
                                    : theme.backgroundSubtle,
                                  borderRadius: 999,
                                  paddingHorizontal: 7,
                                  paddingVertical: 1,
                                }}
                              >
                                <Text
                                  style={[
                                    styles.pillValueText,
                                    {
                                      color: isSelected
                                        ? theme.accentStrong
                                        : theme.textSecondary,
                                      fontFamily: layout.bodyFontFamily,
                                      fontSize: 10,
                                      fontWeight: "800",
                                      lineHeight: 13,
                                    },
                                  ]}
                                >
                                  {tab.countText}
                                </Text>
                              </View>
                            </View>
                            <View
                              style={{
                                backgroundColor: isSelected
                                  ? theme.accent
                                  : "transparent",
                                borderRadius: 2,
                                height: 2.5,
                                width: "100%",
                              }}
                            />
                          </Pressable>
                        );
                      })}
                    </View>

                    <ScrollView
                      style={[
                        styles.patientSupportModalScroll,
                        {
                          flex: 1,
                        },
                      ]}
                      contentContainerStyle={[
                        styles.patientSupportModalContent,
                        {
                          gap: tokens.spacing.lg,
                          paddingHorizontal: isWideLayout
                            ? tokens.spacing.lg
                            : tokens.spacing.md,
                          paddingVertical: tokens.spacing.md,
                        },
                      ]}
                      showsVerticalScrollIndicator={false}
                    >
                      {selectedDetailSection.id === "phone-calls" &&
                      selectedDetailModalViewId === "all"
                        ? renderOutboundQuestionnaireCallsSection()
                        : null}
                      {groupedTimelineItems.length > 0 ? (
                        groupedTimelineItems.map((group) => (
                          <View
                            key={group.groupLabel}
                            style={[
                              styles.listGroup,
                              {
                                gap: tokens.spacing.xs,
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.metricLabel,
                                {
                                  color: theme.textPrimary,
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: 10,
                                  lineHeight: 12,
                                  textTransform: "uppercase",
                                },
                              ]}
                            >
                              {group.groupLabel}
                            </Text>
                            <View
                              style={[
                                styles.listGroup,
                                {
                                  gap:
                                    selectedDetailSection.id === "medications"
                                      ? 8
                                      : 0,
                                },
                              ]}
                            >
                              {group.items.map((item, itemIndex) =>
                                renderOptionBTimelineItem(
                                  item,
                                  itemIndex === group.items.length - 1,
                                ),
                              )}
                            </View>
                          </View>
                        ))
                      ) : selectedDetailSection.id === "phone-calls" &&
                        selectedDetailModalViewId === "all" &&
                        outboundQuestionnaireCalls.length > 0 ? null : (
                        <View
                          style={{
                            alignItems: "center",
                            gap: 6,
                            paddingVertical: 28,
                          }}
                        >
                          <View
                            style={{
                              alignItems: "center",
                              backgroundColor: theme.backgroundSubtle,
                              borderColor: theme.borderStrong,
                              borderRadius: 999,
                              borderWidth: 1,
                              height: 40,
                              justifyContent: "center",
                              width: 40,
                            }}
                          >
                            <LineIcon
                              color={theme.textSecondary}
                              name={
                                detailSectionIconName[
                                  selectedDetailSection.id
                                ]
                              }
                              size={19}
                            />
                          </View>
                          <Text
                            style={[
                              styles.navigationText,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 13,
                                fontWeight: "700",
                              },
                            ]}
                          >
                            Nothing here yet
                          </Text>
                          <Text
                            style={[
                              styles.sectionSubtitle,
                              {
                                color: theme.textTertiary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: tokens.typography.fontSize.sm,
                                lineHeight: tokens.typography.lineHeight.md,
                                textAlign: "center",
                              },
                            ]}
                          >
                            {emptyTimelineText}
                          </Text>
                        </View>
                      )}
                    </ScrollView>
                  </>
                )}
              </View>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  return (
    <>
      <View
        style={[
          styles.surfaceCard,
          {
            backgroundColor: selectedPatient
              ? theme.surface
              : theme.surfaceElevated,
            borderColor: theme.borderStrong,
            borderLeftWidth: undefined,
            borderRadius: workforceOperationalLayout.panelRadius,
            borderWidth: selectedPatient && isWideLayout ? 1 : undefined,
            flexShrink: 0,
            gap: selectedPatient ? 0 : tokens.spacing.md,
            height: showLegacyInlineAccordion
              ? undefined
              : selectedPatient && isWideLayout
                ? wideLegacyStageHeight
                : undefined,
            maxHeight: showLegacyInlineAccordion
              ? undefined
              : selectedPatient && isWideLayout
                ? wideLegacyStageHeight
                : undefined,
            maxWidth: selectedPatient
              ? isWideLayout
                ? workforceOperationalLayout.contextRailWidth
                : undefined
              : 392,
            minHeight: showLegacyInlineAccordion
              ? undefined
              : selectedPatient && isWideLayout
                ? wideLegacyStageHeight
                : undefined,
            overflow:
              selectedPatient && !showLegacyInlineAccordion
                ? "hidden"
                : "visible",
            padding: selectedPatient ? 0 : tokens.spacing.lg,
            shadowOpacity: 0,
            width:
              selectedPatient && isWideLayout
                ? workforceOperationalLayout.contextRailWidth
                : "100%",
          },
        ]}
      >
      {selectedPatient == null ? (
        <Text
          style={[
            styles.surfaceTitle,
            {
              color: theme.textPrimary,
              fontFamily: layout.displayFontFamily,
              fontSize: tokens.typography.fontSize.lg,
              lineHeight: tokens.typography.lineHeight.lg,
            },
          ]}
        >
          {t(translationKeys.shell.workspaceDetailsTitle)}
        </Text>
      ) : null}

      {selectedPatient == null || selectedDetailSection == null ? (
        <View
          style={[
            styles.detailBodyCard,
            {
              backgroundColor: theme.surfaceMuted,
              borderColor: theme.borderSubtle,
              borderRadius: layout.controlRadius,
              gap: tokens.spacing.sm,
              padding: tokens.spacing.md,
            },
          ]}
        >
          <Text
            style={[
              styles.surfaceTitle,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: tokens.typography.fontSize.lg,
                lineHeight: tokens.typography.lineHeight.lg,
              },
            ]}
          >
            {t(translationKeys.shell.workspaceNoPatientSelectedTitle)}
          </Text>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {t(translationKeys.shell.workspaceNoPatientSelectedBody)}
          </Text>
        </View>
      ) : (
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={{
            alignSelf: "stretch",
            flexGrow: selectedPatient && isWideLayout ? 1 : 0,
            flexShrink: 1,
            width: "100%",
          }}
          contentContainerStyle={{ gap: 0 }}
        >
          <View
            style={[
              styles.legacyClinicianInfoHeader,
              {
                backgroundColor: theme.backgroundSubtle,
                borderColor: theme.borderSubtle,
                gap: tokens.spacing.xs,
                paddingHorizontal: tokens.spacing.md,
                paddingVertical: 8,
              },
            ]}
          >
            <View
              style={[
                styles.legacyClinicianInfoHeaderTitleRow,
                {
                  gap: tokens.spacing.xs,
                },
              ]}
            >
              <View
                style={[
                  styles.legacyClinicianInfoLead,
                  {
                    backgroundColor: theme.surfaceMuted,
                    borderColor: theme.borderSubtle,
                    borderRadius: 9,
                    minHeight: 28,
                    width: 28,
                  },
                ]}
              >
                <LineIcon
                  color={theme.accentStrong}
                  name="info"
                  size={19}
                  strokeWidth={2.4}
                />
              </View>

              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 14,
                    fontWeight: "700",
                  },
                ]}
              >
                Patient context
              </Text>
            </View>
          </View>

          <View
            style={[
              styles.legacyClinicianInfoBody,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                gap: 2,
                paddingHorizontal: 12,
                paddingVertical: 8,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: 15,
                  lineHeight: 19,
                },
              ]}
            >
              {selectedPatient.displayName}
            </Text>
            {renderPatientRecordRailContent({
              layout,
              onOpenPatientProfileSection,
              pendingRedFlagCount: selectedPatient.pendingRedFlagCount,
              record: selectedPatient.record,
              theme,
              tokens,
            })}
            {selectedPatient.summaryText.trim().length > 0 ? (
              <View
                style={[
                  styles.detailSectionGroup,
                  {
                    gap: 1,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 10,
                      fontWeight: "700",
                      letterSpacing: 0,
                      lineHeight: 12,
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  Current status
                </Text>
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 13,
                      lineHeight: 16,
                    },
                  ]}
                  numberOfLines={2}
                >
                  {resolveProductClinicalText(selectedPatient.summaryText)}
                </Text>
              </View>
            ) : null}
          </View>

          {selectedPatient.requiresAttention ? (
            <View
              style={[
                styles.patientRowHeader,
                {
                  backgroundColor: theme.surface,
                  borderBottomColor: theme.borderSubtle,
                  borderBottomWidth: 1,
                  gap: 8,
                  justifyContent: "space-between",
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                },
              ]}
            >
              <Text
                numberOfLines={2}
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    flex: 1,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    fontWeight: "700",
                    lineHeight: 12,
                    textTransform: "uppercase",
                  },
                ]}
              >
                Attention required
              </Text>
              {renderWorkbenchActionButton({
                disabled: isWorkbenchActionSubmitting,
                label: isWorkbenchActionSubmitting
                  ? t(translationKeys.shell.workspaceActionSubmitting)
                  : t(translationKeys.shell.workspaceActionClearAttention),
                labelLines: 1,
                minWidth: 116,
                onPress: () =>
                  onSubmitWorkbenchAction({
                    expectedMaterialChangeToken:
                      selectedPatient.materialChangeToken,
                    kind: "clear-patient-attention",
                    patientId: selectedPatient.id,
                  }),
                size: "compact",
                theme,
                tone: "warning",
                tokens,
                width: 116,
              })}
            </View>
          ) : null}

          <View
            style={[
              styles.sectionChromeStack,
              {
                flex: showLegacyInlineAccordion ? 0 : 1,
                flexShrink: showLegacyInlineAccordion ? 0 : 1,
                gap: 3,
                minHeight: showLegacyInlineAccordion ? undefined : 0,
                overflow: showLegacyInlineAccordion ? "visible" : "hidden",
              },
            ]}
          >
            {detailSections
              .filter(
                (section) =>
                  section.id !== "information" &&
                  section.id !== "phone-calls",
              )
              .map((section) => {
                const isSelected = section.id === selectedDetailTabId;
                const badgeCount = resolveDetailSectionBadgeCount(
                  section,
                  selectedPatient,
                );
                const chromeColors = getLegacySectionChromeColors(
                  resolveDetailSectionTone(section.id),
                  isSelected,
                  theme,
                );

                return (
                  <View
                    key={section.id}
                    style={[
                      styles.sectionChromeStack,
                      {
                        gap: 3,
                      },
                    ]}
                  >
                    <Pressable
                      accessibilityLabel={section.titleText}
                      accessibilityRole="button"
                      accessibilityState={{
                        expanded: showLegacyDetailModal && isSelected,
                      }}
                      aria-expanded={showLegacyDetailModal && isSelected}
                      onPress={() =>
                        setSelectedDetailTabId(
                          showLegacyDetailModal
                            ? section.id
                            : section.id === "information"
                              ? "information"
                              : isSelected
                                ? "information"
                                : section.id,
                        )
                      }
                      style={[
                        styles.sectionChromeButton,
                        {
                          backgroundColor: chromeColors.backgroundColor,
                          borderColor: chromeColors.borderColor,
                          borderRadius: 9,
                          gap: 7,
                          minHeight: workforceOperationalLayout.controlHeight,
                          paddingHorizontal: 8,
                          paddingVertical: 3,
                          shadowColor: theme.textPrimary,
                          shadowOffset: { width: 0, height: 0 },
                          shadowOpacity: 0,
                          shadowRadius: 0,
                        },
                      ]}
                    >
                      <View
                        style={[
                          styles.sectionChromeLead,
                          {
                            backgroundColor: chromeColors.leadBackgroundColor,
                            borderColor: chromeColors.leadBorderColor,
                            borderRadius: 8,
                            minHeight: 28,
                            width: 28,
                          },
                        ]}
                      >
                        <LineIcon
                          color={chromeColors.titleColor}
                          name={detailSectionIconName[section.id]}
                          size={16}
                        />
                      </View>

                      <View
                        style={[
                          styles.sectionChromeCopy,
                          {
                            gap: tokens.spacing.xs,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.navigationText,
                            {
                              color: chromeColors.titleColor,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 12,
                              fontWeight: "700",
                            },
                          ]}
                        >
                          {section.titleText}
                        </Text>
                      </View>

                      {badgeCount ? (
                        <View
                          style={[
                            styles.sectionChromeCountBadge,
                            {
                              alignSelf: "center",
                              backgroundColor:
                                chromeColors.countBackgroundColor,
                              borderColor: chromeColors.countBorderColor,
                              borderRadius: 999,
                              minHeight: 20,
                              minWidth: 26,
                              paddingHorizontal: 8,
                              paddingVertical: 2,
                            },
                          ]}
                        >
                          <Text
                            style={[
                              styles.sectionChromeCountText,
                              {
                                color: chromeColors.countTextColor,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 11,
                                fontWeight: "800",
                                lineHeight: 14,
                                textAlign: "center",
                              },
                            ]}
                          >
                            {badgeCount}
                          </Text>
                        </View>
                      ) : null}
                    </Pressable>
                    {!showLegacyDetailModal && isSelected
                      ? renderSelectedDetailStage()
                      : null}
                  </View>
                );
              })}
          </View>
        </ScrollView>
      )}
      </View>
      {renderDetailModal()}
    </>
  );
}

export function getMessageBubbleBackground(
  message: ChatMessageViewModel,
  theme: AppShellTheme,
  dashboardVariant: ChatDashboardVariant,
) {
  if (dashboardVariant === "patient") {
    if (message.senderKind === "patient") {
      return theme.accent;
    }

    if (message.senderKind === "clinician") {
      return theme.success;
    }

    return theme.accentSubtle;
  }

  if (message.senderKind === "clinician") {
    return theme.accent;
  }

  if (message.senderKind === "system") {
    return theme.surfaceMuted;
  }

  return theme.surface;
}

export function getMessageBubbleBorderColor(
  message: ChatMessageViewModel,
  theme: AppShellTheme,
  dashboardVariant: ChatDashboardVariant,
) {
  if (dashboardVariant === "patient") {
    if (message.senderKind === "patient") {
      return theme.accent;
    }

    if (message.senderKind === "clinician") {
      return theme.success;
    }

    return theme.borderStrong;
  }

  if (message.senderKind === "clinician") {
    return theme.accentStrong;
  }

  if (message.senderKind === "patient") {
    return theme.accent;
  }

  if (message.senderKind === "system") {
    return theme.borderSubtle;
  }

  return theme.borderStrong;
}

export function getMessageSenderTextColor(
  message: ChatMessageViewModel,
  theme: AppShellTheme,
  dashboardVariant: ChatDashboardVariant,
) {
  if (dashboardVariant === "patient") {
    return theme.textPrimary;
  }

  if (message.senderKind === "clinician") {
    return theme.headerText;
  }

  if (message.senderKind === "system") {
    return theme.textSecondary;
  }

  return theme.textSecondary;
}

export function getMessageTimestampTextColor(
  message: ChatMessageViewModel,
  theme: AppShellTheme,
  dashboardVariant: ChatDashboardVariant,
) {
  if (dashboardVariant === "patient") {
    if (message.senderKind === "patient") {
      return theme.headerTextMuted;
    }

    return theme.accent;
  }

  if (message.senderKind === "clinician") {
    return theme.headerTextMuted;
  }

  if (message.senderKind === "system") {
    return theme.textSecondary;
  }

  return theme.textSecondary;
}

export function getMessageTextColor(
  message: ChatMessageViewModel,
  theme: AppShellTheme,
  dashboardVariant: ChatDashboardVariant,
) {
  if (dashboardVariant === "patient") {
    return theme.accentStrong;
  }

  if (message.senderKind === "clinician") {
    return theme.headerText;
  }

  if (message.senderKind === "system") {
    return theme.textPrimary;
  }

  return theme.textPrimary;
}

export function getMessageBodyPanelBackground(
  message: ChatMessageViewModel,
  theme: AppShellTheme,
  dashboardVariant: ChatDashboardVariant,
) {
  if (dashboardVariant === "patient") {
    if (message.senderKind === "system") {
      return "transparent";
    }

    return theme.accentSubtle;
  }

  if (message.senderKind === "patient") {
    return theme.surfaceMuted;
  }

  if (message.senderKind === "system") {
    return theme.backgroundSubtle;
  }

  return "transparent";
}

export function getMessageBodyPanelBorderColor(
  message: ChatMessageViewModel,
  theme: AppShellTheme,
  dashboardVariant: ChatDashboardVariant,
) {
  if (dashboardVariant === "patient") {
    return "transparent";
  }

  if (message.senderKind === "patient") {
    return "transparent";
  }

  if (message.senderKind === "system") {
    return theme.borderSubtle;
  }

  return "transparent";
}

export function getMessageDeliveryBackground(
  deliveryState: ChatMessageViewModel["deliveryState"],
  theme: AppShellTheme,
) {
  if (deliveryState === "delivered") {
    return theme.statusChip.success.background;
  }

  return theme.backgroundSubtle;
}

export function getMessageDeliveryTextColor(
  deliveryState: ChatMessageViewModel["deliveryState"],
  theme: AppShellTheme,
) {
  if (deliveryState === "delivered") {
    return theme.statusChip.success.text;
  }

  return theme.textSecondary;
}

function resolveMessageReviewLabelKey(
  state: NonNullable<ChatMessageViewModel["review"]>["state"],
): TranslationKey {
  if (state === "pending-approval") {
    return translationKeys.shell.workspaceChatReviewPendingApproval;
  }

  if (state === "approved") {
    return translationKeys.shell.workspaceChatReviewApproved;
  }

  if (state === "approved-edited") {
    return translationKeys.shell.workspaceChatReviewApprovedEdited;
  }

  return translationKeys.shell.workspaceChatReviewRejected;
}

function resolveDocumentAvailabilityLabelKey(
  availability: PatientWorkspaceViewModel["documentSummaries"][number]["availability"],
): TranslationKey {
  return availability === "download-ready"
    ? translationKeys.shell.workspaceDocumentDownloadReady
    : translationKeys.shell.workspaceDocumentMetadataOnly;
}

function groupDocumentSummariesByAvailability(
  documents: PatientWorkspaceViewModel["documentSummaries"],
) {
  return [
    {
      availability: "download-ready" as const,
      items: documents.filter(
        (document) => document.availability === "download-ready",
      ),
    },
    {
      availability: "metadata-only" as const,
      items: documents.filter(
        (document) => document.availability === "metadata-only",
      ),
    },
  ].filter((group) => group.items.length > 0);
}

export function getMessageReviewBackground(
  state: NonNullable<ChatMessageViewModel["review"]>["state"],
  theme: AppShellTheme,
) {
  if (state === "pending-approval") {
    return theme.statusChip.warning.background;
  }

  if (state === "approved" || state === "approved-edited") {
    return theme.statusChip.info.background;
  }

  return theme.statusChip.danger.background;
}

export function getMessageReviewTextColor(
  state: NonNullable<ChatMessageViewModel["review"]>["state"],
  theme: AppShellTheme,
) {
  if (state === "pending-approval") {
    return theme.warning;
  }

  if (state === "approved" || state === "approved-edited") {
    return theme.info;
  }

  return theme.textPrimary;
}

function getDetailRowBackground(
  kind: DetailRowKind,
  theme: AppShellTheme,
) {
  if (kind === "approval") {
    return theme.surfaceMuted;
  }

  if (kind === "delivery") {
    return theme.surfaceMuted;
  }

  return theme.surfaceMuted;
}

function getDetailRowBadgeBackground(
  kind: DetailRowKind,
  theme: AppShellTheme,
) {
  if (kind === "task-assignment") {
    return theme.accentSubtle;
  }

  if (kind === "approval") {
    return theme.surface;
  }

  if (kind === "delivery") {
    return theme.surface;
  }

  if (kind === "document") {
    return theme.surface;
  }

  return theme.surface;
}

function getDetailRowBadgeTextColor(
  kind: DetailRowKind,
  theme: AppShellTheme,
) {
  if (kind === "task-assignment") {
    return theme.accentStrong;
  }

  if (kind === "approval") {
    return theme.warning;
  }

  if (kind === "delivery") {
    return theme.info;
  }

  if (kind === "document") {
    return theme.textSecondary;
  }

  return theme.textSecondary;
}

function getDetailSummaryMetricBackground(
  tone: "neutral" | "accent" | "warning" | "info",
  theme: AppShellTheme,
) {
  if (tone === "accent") {
    return theme.accentSubtle;
  }

  if (tone === "warning") {
    return theme.surfaceMuted;
  }

  if (tone === "info") {
    return theme.surfaceMuted;
  }

  return theme.surfaceMuted;
}

function getDetailSummaryMetricTextColor(
  tone: "neutral" | "accent" | "warning" | "info",
  theme: AppShellTheme,
) {
  if (tone === "accent") {
    return theme.accentStrong;
  }

  if (tone === "warning") {
    return theme.warning;
  }

  if (tone === "info") {
    return theme.info;
  }

  return theme.textPrimary;
}

function getWorkbenchPanelItemBackground(
  tone: "neutral" | "accent" | "warning" | "info",
  theme: AppShellTheme,
) {
  if (tone === "accent") {
    return theme.accentSubtle;
  }

  if (tone === "warning") {
    return theme.surfaceMuted;
  }

  if (tone === "info") {
    return theme.surfaceMuted;
  }

  return theme.surfaceMuted;
}

function getWorkbenchPanelBadgeBackground(
  item: WorkbenchPanelViewModel["items"][number],
  theme: AppShellTheme,
) {
  if (item.badgeLabelKey === translationKeys.shell.workspaceDetailRowTask) {
    return theme.accentSubtle;
  }

  if (item.badgeLabelKey === translationKeys.shell.workspaceDetailRowApproval) {
    return theme.surface;
  }

  if (item.badgeLabelKey === translationKeys.shell.workspaceDetailRowDelivery) {
    return theme.surface;
  }

  if (item.badgeLabelKey === translationKeys.shell.workspaceDetailRowDocument) {
    return theme.surface;
  }

  if (item.badgeLabelKey === translationKeys.shell.workspaceListAttentionOnly) {
    return theme.background;
  }

  return theme.surface;
}

function getWorkbenchPanelBadgeTextColor(
  item: WorkbenchPanelViewModel["items"][number],
  theme: AppShellTheme,
) {
  if (item.badgeLabelKey === translationKeys.shell.workspaceDetailRowTask) {
    return theme.accentStrong;
  }

  if (item.badgeLabelKey === translationKeys.shell.workspaceDetailRowApproval) {
    return theme.warning;
  }

  if (item.badgeLabelKey === translationKeys.shell.workspaceDetailRowDelivery) {
    return theme.info;
  }

  if (item.badgeLabelKey === translationKeys.shell.workspaceDetailRowDocument) {
    return theme.textSecondary;
  }

  if (item.badgeLabelKey === translationKeys.shell.workspaceListAttentionOnly) {
    return theme.warning;
  }

  return theme.textSecondary;
}

function resolveWorkbenchPanelLeadText(
  item: WorkbenchPanelViewModel["items"][number],
): string {
  if (item.targetPatientId && item.targetDetailTabId === "information") {
    return resolvePatientInitials(item.titleText);
  }

  if (item.badgeLabelKey === translationKeys.shell.workspaceDetailRowDocument) {
    return "DOC";
  }

  if (item.badgeLabelKey === translationKeys.shell.workspaceDetailRowTask) {
    return "TASK";
  }

  if (item.badgeLabelKey === translationKeys.shell.workspaceDetailRowApproval) {
    return "APP";
  }

  if (item.badgeLabelKey === translationKeys.shell.workspaceDetailRowDelivery) {
    return "SEND";
  }

  return (
    item.titleText
      .trim()
      .split(/\s+/u)
      .map((part) => part[0] ?? "")
      .join("")
      .slice(0, 4)
      .toUpperCase() || "ITEM"
  );
}

function getWorkbenchPanelLeadColors(
  item: WorkbenchPanelViewModel["items"][number],
  theme: AppShellTheme,
) {
  if (item.tone === "accent") {
    return {
      backgroundColor: theme.surface,
      borderColor: theme.borderSubtle,
      textColor: theme.accentStrong,
    };
  }

  if (item.tone === "warning") {
    return {
      backgroundColor: theme.surface,
      borderColor: theme.borderSubtle,
      textColor: theme.warning,
    };
  }

  if (item.tone === "info") {
    return {
      backgroundColor: theme.surface,
      borderColor: theme.borderSubtle,
      textColor: theme.info,
    };
  }

  return {
    backgroundColor: theme.surface,
    borderColor: theme.borderSubtle,
    textColor: theme.textPrimary,
  };
}

function resolveWorkbenchPanelSectionLeadText(
  panelId: WorkbenchPanelId,
): string {
  if (panelId === "tasks") {
    return "TASK";
  }

  if (panelId === "documents") {
    return "DOC";
  }

  return "IN";
}

function resolveWorkbenchPanelSectionTone(
  panelId: WorkbenchPanelId,
): LegacySectionChromeTone {
  if (panelId === "tasks") {
    return "warning";
  }

  if (panelId === "documents") {
    return "info";
  }

  return "accent";
}

function resolveDetailSectionLeadText(sectionId: DetailTabId): string {
  switch (sectionId) {
    case "information":
      return "INFO";
    case "case-summary":
      return "CASE";
    case "appointments":
      return "APT";
    case "procedures":
      return "PROC";
    case "medications":
      return "RX";
    case "documents":
      return "DOC";
    case "phone-calls":
      return "CALL";
  }
}

function resolveDetailSectionTone(
  sectionId: DetailTabId,
): LegacySectionChromeTone {
  switch (sectionId) {
    case "case-summary":
      return "accent";
    case "appointments":
      return "accent";
    case "procedures":
      return "warning";
    case "medications":
      return "info";
    case "documents":
      return "neutral";
    case "phone-calls":
      return "info";
    case "information":
    default:
      return "neutral";
  }
}

function resolveDetailSectionBadgeCount(
  section: DetailSectionViewModel,
  selectedPatient: PatientWorkspaceViewModel | null,
): string | null {
  if (section.id === "information") {
    return null;
  }

  if (section.id === "case-summary") {
    const count =
      (selectedPatient?.taskAssignments.filter((task) => task.canComplete)
        .length ?? 0) +
      (selectedPatient?.approvalSummaries.filter((approval) =>
        /pending|review|needs|open/iu.test(approval.statusText),
      ).length ?? 0) +
      (selectedPatient?.deliverySummaries.filter((delivery) =>
        /pending|approval|held|needs/iu.test(delivery.statusText),
      ).length ?? 0);

    return count > 0 ? String(count) : null;
  }

  if (section.id === "documents") {
    const count = selectedPatient?.documentSummaries.length ?? 0;

    return count > 0 ? String(count) : null;
  }

  if (section.id === "phone-calls") {
    const count = selectedPatient?.voiceCallSummaries.length ?? 0;

    return count > 0 ? String(count) : null;
  }

  const count = section.rows.filter((row) => row.valueText.length > 0).length;

  return count > 0 ? String(count) : null;
}
